import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Alert,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  faArrowUpRightFromSquare,
  faArrowsRotate,
  faRotateRight,
  faBuilding,
  faCheck,
  faMagnifyingGlass,
  faChevronDown,
  faCircleCheck,
  faCopy,
  faEye,
  faFileLines,
  faListUl,
  faPlus,
  faUser,
  faUserPen,
  faPrint,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DarkModeContext } from "../../context/DarkModeContext";
import Footer from "../../components/comunes/Footer";
import TituloPagina from "../../components/comunes/TituloPagina";
import logo from "../../assets/img/logo.png";
import zocoLogoComparador from "../../assets/img/Zoco.png";
import logoMercadoPagoComparador from "../../assets/img/logomp.png";
import logoPaywayComparador from "../../assets/img/logopayway.png";
import logoNaranjaComparador from "../../assets/img/logonx.png";
import logoGetnetComparador from "../../assets/img/logogetnet.png";
import logoViumiComparador from "../../assets/img/logoviumi.png";
import logoNaveComparador from "../../assets/img/Logo-Nave.png";
import comparadorCrossIcon from "../../assets/icons/Vector.png";
import comparadorCheckIcon from "../../assets/icons/Vector (1).png";
import { getToken } from "../../utils/auth";
import { descargarOrdenPagoDesdePayload } from "../../helpers/legacyOrdenPagoPdf";
import { crmPostventaApi } from "./crmPostventaApi";
import ClienteSelectorModal from "./ClienteSelectorModal";
import ZocoCheckPanel from "./ZocoCheckPanel";
import ZocoCheckCampaignsPanel from "./ZocoCheckCampaignsPanel";
import LegacyAlertCenter from "./alert-center/LegacyAlertCenter";
import AltasBajasWorkflow from "./altas-bajas/AltasBajasWorkflow";
import ModificarLegajoPanel from "./altas-bajas/ModificarLegajoPanel";
import GestionProcesoPanel from "../crmpostventa-v2/GestionProcesoPanel";
import ModalesPanel from "./ModalesPanel";
import OrdenPagoSimulador from "../../components/simuladorLiquidaciones/OrdenPago";
import TablaUsuariosLiquidaciones from "../../components/comunes/TablaUsuariosLiquidaciones";
import "./crm-postventa.css";

const STORAGE_KEY = "crm_postventa_cliente_id";

const makeToday = () => new Date().toISOString().slice(0, 10);
const makeLocalToday = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};
const decodeTokenPayload = (token) => {
  if (!token) return null;

  try {
    const payload = token.split(".")[1];
    if (!payload) return null;

    return JSON.parse(window.atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
};
const makeMonthStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};
const makeCurrentMonth = () => makeToday().slice(0, 7);
const makeNowDateTimeLocal = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 16);
};
const toDateTimeLocalInput = (value) => {
  if (!value || value === "-") return "";
  const normalized = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(normalized)) {
    const [datePart, timePart] = normalized.split(" ");
    const [day, month, year] = datePart.split("/");
    return `${year}-${month}-${day}T${timePart}`;
  }

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};
const buildMonthDateRange = (monthValue) => {
  const fallback = makeCurrentMonth();
  const [yearText, monthText] = String(monthValue || fallback).split("-");
  const year = Number(yearText);
  const month = Number(monthText);

  if (!year || !month) {
    return {
      mes: fallback,
      desde: `${fallback}-01`,
      hasta: makeToday(),
    };
  }

  const normalizedMonth = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
  const lastDay = new Date(year, month, 0).getDate();

  return {
    mes: normalizedMonth,
    desde: `${normalizedMonth}-01`,
    hasta: `${normalizedMonth}-${String(lastDay).padStart(2, "0")}`,
  };
};
const normalizeDigits = (value) => String(value || "").replace(/\D/g, "");
const normalizeCuitValue = (value) => normalizeDigits(value).slice(0, 11);
const normalizeEditablePhoneValue = (value) => normalizeDigits(value).slice(0, 20);
const isLegacyPlaceholderValue = (value) => String(value ?? "").trim() === "-";
const sanitizeLegacyTextValue = (value) => {
  const normalized = String(value ?? "").trim();
  return isLegacyPlaceholderValue(normalized) ? "" : normalized;
};
const sanitizeLegacyNumericValue = (value) => normalizeDigits(String(value ?? ""));
const normalizeTerminalStatusText = (value) => String(value || "").trim();
const isPhoneFieldLabel = (label = "") => normalizeText(label).startsWith("telefono");
const formatGestionRuleLabel = (regla = {}) => String(regla?.aliasVista || regla?.nombreAccion || "Sin acción").trim();
const formatMissingFieldsHtmlList = (fields = []) =>
  `<div style="text-align:left;">Faltan completar:<br/><ul style="margin:8px 0 0 18px;padding:0;">${fields
    .map((field) => `<li>${field}</li>`)
    .join("")}</ul></div>`;
const resolveTerminalStatusLabel = (terminal = {}) => {
  const estadoAlta = normalizeTerminalStatusText(terminal.estadoAlta || terminal.estado);
  const estadoOperativo = normalizeTerminalStatusText(terminal.estadoOperativo);

  if (/extraviad/i.test(estadoAlta)) return "Extraviada";
  if (/baja/i.test(estadoAlta)) return "Baja";
  if (estadoOperativo && !/^ok$/i.test(estadoOperativo)) {
    return estadoAlta && /^alta$/i.test(estadoAlta) ? `ALTA / ${estadoOperativo}` : estadoOperativo;
  }
  if (/alta/i.test(estadoAlta) && /^ok$/i.test(estadoOperativo)) return "ALTA / OK";
  if (estadoAlta) return estadoAlta;
  if (estadoOperativo) return estadoOperativo;
  return "Sin datos";
};
const buildAltaMissingFields = (altaWizardData = {}, formularioPayload = null) => {
  const missing = [];
  const addMissing = (label, condition) => {
    if (condition) {
      missing.push(label);
    }
  };

  const alta = altaWizardData.alta || {};
  const legajo = altaWizardData.legajo || {};
  const usuario = altaWizardData.usuario || {};

  addMissing("N° comercio solicitado", !sanitizeLegacyTextValue(alta.numeroComercioSolicitado));
  addMissing("Rubro", !sanitizeLegacyTextValue(alta.rubro));
  addMissing("Código actividad", !sanitizeLegacyNumericValue(alta.codActividad));
  addMissing("Actividad", !sanitizeLegacyTextValue(alta.actividad));
  addMissing("Código actividad rentas", !sanitizeLegacyNumericValue(alta.codActividadRentas));
  addMissing("Actividad rentas", !sanitizeLegacyTextValue(alta.actividadRentas));
  addMissing("Banco", !sanitizeLegacyTextValue(alta.banco));
  addMissing("Tipo de cuenta", !sanitizeLegacyTextValue(alta.tipoCuenta));
  addMissing("Celular / WhatsApp", !normalizeEditablePhoneValue(usuario.celular || legajo.celularWhatsapp));
  addMissing("Puesto / cargo / referencia 1", !sanitizeLegacyTextValue(alta.referenciaTelefono));

  if (String(alta.tipoAlta || "") === "AgregaTerminal" || String(alta.altas || "") === "AGREGA") {
    addMissing("Terminal / comercio base para AGREGA", !altaWizardData.agregaBaseClienteId);
  }

  if (formularioPayload) {
    addMissing("Terminal base", !formularioPayload.TerminalN);
    addMissing("Código postal comercio", !formularioPayload.CodigoPostalComercio);
  }

  return missing.filter((item, index, array) => array.indexOf(item) === index);
};
const getFirstValue = (item, keys = []) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
};
const normalizeLegacyIdentifier = (value) => {
  if (value === undefined || value === null) return "";

  const normalized = String(value).trim();
  if (!normalized) return "";

  return normalized.replace(/\.0+$/g, "");
};
const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
const pickApiValue = (item, camelKey, pascalKey = null) => {
  if (!item || typeof item !== "object") {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(item, camelKey)) {
    return item[camelKey];
  }

  const fallbackKey = pascalKey || `${camelKey.charAt(0).toUpperCase()}${camelKey.slice(1)}`;
  if (Object.prototype.hasOwnProperty.call(item, fallbackKey)) {
    return item[fallbackKey];
  }

  return undefined;
};

const parseFichaHistoryChange = (line) => {
  const text = String(line || "").trim();
  if (!text) {
    return null;
  }

  const [fieldPart, ...valueParts] = text.split(":");
  const valueText = valueParts.join(":").trim();

  if (!fieldPart || !valueText) {
    return { raw: text };
  }

  const arrowMatch = valueText.match(/^(.*?)\s*->\s*(.*)$/);
  if (!arrowMatch) {
    return {
      field: fieldPart.trim(),
      rawValue: valueText,
    };
  }

  return {
    field: fieldPart.trim(),
    before: arrowMatch[1].trim() || "-",
    after: arrowMatch[2].trim() || "-",
  };
};

const FichaHistoryChanges = ({ values }) => {
  const changes = Array.isArray(values)
    ? values.map(parseFichaHistoryChange).filter(Boolean)
    : [];

  if (!changes.length) {
    return <span className="crm-postventa-ficha-history-muted">-</span>;
  }

  return (
    <div className="crm-postventa-ficha-history-detail-list">
      {changes.map((change, index) => (
        <article className="crm-postventa-ficha-history-detail" key={`history-change-${index}`}>
          {change.field ? (
            <>
              <strong className="crm-postventa-ficha-history-field">{change.field}</strong>
              {change.before !== undefined || change.after !== undefined ? (
                <div className="crm-postventa-ficha-history-values">
                  <span>
                    <small>Antes</small>
                    <b>{change.before}</b>
                  </span>
                  <span>
                    <small>Ahora</small>
                    <b>{change.after}</b>
                  </span>
                </div>
              ) : (
                <p>{change.rawValue}</p>
              )}
            </>
          ) : (
            <p>{change.raw}</p>
          )}
        </article>
      ))}
    </div>
  );
};
const normalizeAlertItem = (item = {}) => ({
  ...item,
  comercio: pickApiValue(item, "comercio"),
  cuit: pickApiValue(item, "cuit"),
  terminal: pickApiValue(item, "terminal"),
  tipoAlerta: pickApiValue(item, "tipoAlerta"),
  provincia: pickApiValue(item, "provincia"),
  telefono: pickApiValue(item, "telefono"),
  responsable: pickApiValue(item, "responsable"),
  estado: pickApiValue(item, "estado"),
  scorePrioridad: pickApiValue(item, "scorePrioridad"),
  prioridad: pickApiValue(item, "prioridad"),
  segmentoComercial: pickApiValue(item, "segmentoComercial"),
  patronFacturacion: pickApiValue(item, "patronFacturacion"),
  tieneSeguimientoActivo: pickApiValue(item, "tieneSeguimientoActivo"),
  motivoCodigo: pickApiValue(item, "motivoCodigo"),
  montoHoy: pickApiValue(item, "montoHoy"),
  promedio7Dias: pickApiValue(item, "promedio7Dias"),
  variacion: pickApiValue(item, "variacion"),
  facturacion30Dias: pickApiValue(item, "facturacion30Dias"),
  resumen: pickApiValue(item, "resumen"),
  motivoPrioridad: pickApiValue(item, "motivoPrioridad"),
  mensajeSugerido: pickApiValue(item, "mensajeSugerido"),
  ultimoZocoCheck: pickApiValue(item, "ultimoZocoCheck"),
  ultimaActividad: pickApiValue(item, "ultimaActividad"),
  campania: pickApiValue(item, "campania"),
  accionSugerida: pickApiValue(item, "accionSugerida"),
  ultimaGestion: pickApiValue(item, "ultimaGestion"),
  proximoContacto: pickApiValue(item, "proximoContacto"),
  esAutomatica: pickApiValue(item, "esAutomatica"),
});
const normalizeAlertKpiItem = (item = {}) => ({
  ...item,
  label: pickApiValue(item, "label"),
  value: pickApiValue(item, "value"),
  tone: pickApiValue(item, "tone"),
  description: pickApiValue(item, "description"),
});
const normalizeAlertasResponse = (response) => {
  if (!response || typeof response !== "object") {
    return response;
  }

  const normalizeList = (value) => (Array.isArray(value) ? value.map((item) => normalizeAlertItem(item)) : []);
  const normalizeKpiList = (value) => (Array.isArray(value) ? value.map((item) => normalizeAlertKpiItem(item)) : []);

  return {
    ...response,
    fechaReferencia: pickApiValue(response, "fechaReferencia"),
    kpis: normalizeKpiList(pickApiValue(response, "kpis")),
    estadosGestion: Array.isArray(pickApiValue(response, "estadosGestion")) ? pickApiValue(response, "estadosGestion") : [],
    alertasCriticas: normalizeList(pickApiValue(response, "alertasCriticas")),
    alertasFacturacion: normalizeList(pickApiValue(response, "alertasFacturacion")),
    alertasZocoCheck: normalizeList(pickApiValue(response, "alertasZocoCheck")),
    alertasTerminalExtraviada: normalizeList(pickApiValue(response, "alertasTerminalExtraviada")),
    filas: normalizeList(pickApiValue(response, "filas")),
    tieneDatosReales: Boolean(pickApiValue(response, "tieneDatosReales")),
    message: pickApiValue(response, "message", "Message"),
  };
};
const formatLegacyDisplayDate = (value) => {
  if (!value) return "-";
  if (/^\d{2}-\d{2}-\d{4}$/.test(String(value))) {
    return String(value);
  }

  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) {
    return String(value);
  }

  return `${day}-${month}-${year}`;
};
const formatLegacyMonthLabel = (value) => {
  if (!value) return "-";
  const [year, month] = String(value).split("-");
  if (!year || !month) {
    return String(value);
  }

  return `${month}/${String(year).slice(-2)}`;
};
const parseFlexibleAmount = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  let normalized = String(value).trim();
  if (!normalized) {
    return null;
  }

  normalized = normalized.replace(/\$/g, "").replace(/\s/g, "").replace(/[^0-9,.-]/g, "");
  if (!normalized) {
    return null;
  }

  const dotCount = (normalized.match(/\./g) || []).length;
  const commaCount = (normalized.match(/,/g) || []).length;
  const lastComma = normalized.lastIndexOf(",");
  const lastDot = normalized.lastIndexOf(".");
  const hasBoth = lastComma >= 0 && lastDot >= 0;
  const lastSepIndex = Math.max(lastComma, lastDot);

  if (lastSepIndex >= 0) {
    const digitsAfter = normalized.length - lastSepIndex - 1;
    const hasSingleSep = dotCount + commaCount === 1;
    const shouldTreatAsThousandsOnly = hasSingleSep && digitsAfter === 3 && !hasBoth;

    if (shouldTreatAsThousandsOnly) {
      normalized = normalized.replace(/[.,]/g, "");
    } else {
      const integerPart = normalized.slice(0, lastSepIndex).replace(/[.,]/g, "");
      const decimalPart = normalized.slice(lastSepIndex + 1).replace(/[.,]/g, "");
      normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    }
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};
const parseLegacyDate = (value) => {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const date = new Date(`${raw.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const [day, month, year] = raw.split(/[/-]/);
  if (!day || !month || !year) {
    return null;
  }

  const iso = `${year.padStart(4, "20")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};
const formatCurrencyCell = (value) => {
  const parsed = parseFlexibleAmount(value);
  if (parsed === null) {
    return "-";
  }

  return parsed.toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
const normalizeCuponLegacyRow = (item = {}) => {
  const fechaOperacion = getFirstValue(item, ["fechaOperacion", "FechaOperacion", "fecha operacion", "Fecha Operacion"]);
  const fechaDePago = getFirstValue(item, ["fechaDePago", "FechaDePago", "fecha de pago", "Fecha de Pago"]);
  const nroDeCupon = normalizeLegacyIdentifier(getFirstValue(item, ["nroDeCupon", "NroDeCupon", "Nro# de Cupon"]));
  const nroDeComercio = normalizeLegacyIdentifier(getFirstValue(item, ["nroDeComercio", "NroDeComercio", "nroDeTerminal", "NroDeTerminal", "Nro# de Comercio"]));
  const nroDeTarjeta = normalizeLegacyIdentifier(getFirstValue(item, ["nroDeTarjeta", "NroDeTarjeta", "Nro# de Tarjeta"]));
  const brutoRaw = getFirstValue(item, ["totalBruto", "TotalBruto", "bruto", "Bruto", "importe", "Importe", "Total Bruto"]);
  const totalBruto = parseFlexibleAmount(brutoRaw);
  const cuotas = normalizeLegacyIdentifier(getFirstValue(item, ["cuotas", "Cuotas"]));
  const tarjeta = getFirstValue(item, ["tarjeta", "Tarjeta"]);
  const provincia = getFirstValue(item, ["provincia", "Provincia"]);
  const nombreComercio = getFirstValue(item, ["nombreComercio", "NombreComercio", "Nombre Comercio"]);
  const cuit = normalizeDigits(getFirstValue(item, ["cuit", "Cuit", "CUIT"]));

  return {
    fechaOperacion: formatLegacyDisplayDate(fechaOperacion),
    fechaDePago: formatLegacyDisplayDate(fechaDePago),
    fechaOperacionDate: parseLegacyDate(fechaOperacion),
    fechaDePagoDate: parseLegacyDate(fechaDePago),
    nroDeCupon: nroDeCupon || "-",
    nroDeComercio: nroDeComercio || "-",
    nroDeTarjeta: nroDeTarjeta || "-",
    totalBruto,
    brutoRaw,
    cuotas: cuotas || "-",
    tarjeta: tarjeta || "-",
    provincia: provincia || "-",
    nombreComercio: nombreComercio || "-",
    cuit: cuit || "-",
  };
};
const buildCuponDedupeKey = (item = {}) =>
  [
    normalizeLegacyIdentifier(item.nroDeCupon),
    normalizeLegacyIdentifier(item.nroDeComercio),
    formatLegacyDisplayDate(item.fechaOperacion),
    item.totalBruto === null ? "" : Number(item.totalBruto).toFixed(4),
    normalizeText(item.tarjeta),
  ].join("|");
const buildCuponSummary = (rows = []) => {
  const fechasOperacion = rows.map((item) => item.fechaOperacionDate).filter(Boolean).sort((a, b) => a - b);
  const fechasPago = rows.map((item) => item.fechaDePagoDate).filter(Boolean).sort((a, b) => a - b);
  const totalBruto = rows.reduce((acc, item) => acc + (Number.isFinite(item.totalBruto) ? item.totalBruto : 0), 0);

  const formatDate = (value) => (value ? value.toLocaleDateString("es-AR") : "-");

  return {
    cantidad: rows.length,
    totalBruto,
    fechaOperacionMin: formatDate(fechasOperacion[0]),
    fechaOperacionMax: formatDate(fechasOperacion[fechasOperacion.length - 1]),
    fechaPagoMin: formatDate(fechasPago[0]),
    fechaPagoMax: formatDate(fechasPago[fechasPago.length - 1]),
  };
};
const sanitizeCuponesMessage = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "";
  }

  return normalized.toLowerCase().includes("lista de sas")
    ? "No hay cupones para este cliente/terminal."
    : normalized;
};
const formatCurrencyDetailed = (value) =>
  Number(value || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const formatPhoneLine = (phone, reference) => {
  const normalizedPhone = String(phone || "").trim();
  const normalizedReference = String(reference || "").trim();
  if (!normalizedPhone && !normalizedReference) {
    return "-";
  }

  return [normalizedPhone, normalizedReference ? `(${normalizedReference})` : ""].filter(Boolean).join(" ");
};
const resolvePreferredWhatsappPhone = (item = {}) =>
  normalizeWhatsappPhone(item?.telefono) || normalizeWhatsappPhone(item?.telefonoAlternativo) || "";
const buildFacturacionPhoneLines = (item = {}) => {
  const primary = formatPhoneLine(item?.telefono, item?.referenciaTelefono);
  const secondary = formatPhoneLine(item?.telefonoAlternativo, item?.referenciaTelefonoAlternativo);
  return [primary, secondary].filter((value, index, array) => value !== "-" && array.indexOf(value) === index);
};
const normalizeFichaKeyVariants = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) {
    return [];
  }

  const compact = normalized.replace(/[^a-z0-9ñ\s]/gi, "").replace(/\s+/g, " ").trim();
  return [normalized, compact, compact.replace(/\s+/g, "")].filter((item, index, array) => item && array.indexOf(item) === index);
};
const buildFichaValueMapByLabel = (ficha = {}) => {
  const edicion = ficha?.edicion || ficha?.Edicion || {};
  const camposEdicion = edicion?.campos || edicion?.Campos || [];

  return [ficha?.datosGenerales, ficha?.contacto, ficha?.fiscal, ficha?.operativo, camposEdicion]
    .flat()
    .filter(Boolean)
    .reduce((acc, item) => {
      const value = item?.value ?? item?.Value ?? item?.valor ?? item?.Valor ?? "";
      const keys = [
        item?.label,
        item?.Label,
        item?.campo,
        item?.Campo,
        item?.field,
        item?.Field,
        item?.nombre,
        item?.Nombre,
      ].flatMap((key) => normalizeFichaKeyVariants(key));

      keys.forEach((key) => {
        if (key && (!(key in acc) || sanitizeLegacyTextValue(value))) {
          acc[key] = value;
        }
      });

      return acc;
    }, {});
};
const pickFichaMapValue = (map = {}, ...keys) => {
  for (const key of keys) {
    for (const variant of normalizeFichaKeyVariants(key)) {
      const value = map[variant];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
  }

  return "";
};

const ALTA_LEGAJO_DOC_TYPES = [
  "DNI FRENTE",
  "DNI DORSO",
  "DOCUMENTACIÓN SOCIETARIA (ESTATUTO, PODERES)",
  "CONSTANCIA ARCA/AFIP",
  "CONSTANCIA RENTAS",
  "CONSTANCIA CBU A NOMBRE DEL TITULAR / RAZÓN SOCIAL",
  "FICHA ABM",
  "OTROS",
];

const LEGACY_ALTA_TIPO_CUENTA_OPTIONS = [
  { value: "Caja de Ahorros", label: "Caja de Ahorros" },
  { value: "Cuenta Corriente", label: "Cuenta Corriente" },
  { value: "Billetera Virtual", label: "Billetera Virtual" },
];

const LEGACY_DEFAULT_ALTAS_BY_TIPO = {
  NuevoAliado: "ALTA NUEVA",
  AgregaTerminal: "AGREGA",
};

const ALTA_FINAL_CHECKLIST_ITEMS = [
  "Legajo",
  "CUIT",
  "NOMBRE DE FANTASIA",
  "DOMICILIO",
  "NUMERO DE TERMINAL",
  "NUMERO DE COMERCIO",
  "RENTAS",
  "AFIP",
  "RENAPER",
  "CABAL 88649069834",
  "NARANJA 630046058",
  "AMEX 8235945854",
  "SUCREDITO",
  "TARJETA SOL",
  "CREDICASH",
  "TUYA",
  "OTRA TARJETA",
  "DASH",
  "FISERV",
  "ANTICIPO DE PAGO",
  "CBU",
  "ALIAS",
].map((label, index) => ({
  id: `alta-final-${index + 1}`,
  label,
}));

const makeAltaFinalChecklistState = () =>
  ALTA_FINAL_CHECKLIST_ITEMS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {});

const findProvinciaAlicuota = (provincias = [], provinciaValue = "") =>
  provincias.find((item) => item.value === String(provinciaValue || ""))?.alicuota || "";

const mapCondicionAfipToEstado = (condicionAfip = "") =>
  String(condicionAfip || "").toLowerCase() === "monotributista"
    ? "MONOTRIBUTISTA"
    : "RESPONSABLE INSCRIPTO";

const mapEstadoAfipToCondicion = (estadoAfip = "") =>
  String(estadoAfip || "").toUpperCase() === "MONOTRIBUTISTA"
    ? "Monotributista"
    : "Responsable Inscripto";

const mapExentoToLegacyFlags = (exento = "") => {
  const normalized = String(exento || "").toUpperCase();
  return {
    afipExentoGanancia: normalized === "EXENTO GANANCIA",
    afipExentoIva: normalized === "EXENTO IVA",
  };
};

const mapEstadoRentasToLegacyFlags = (estadoRentas = "") => {
  const normalized = String(estadoRentas || "").toUpperCase();
  return {
    rentasIncripto: normalized === "INSCRIPTO",
    rentasConvenioMultilateral: normalized === "CM",
    rentasExento: normalized === "EXENTO",
  };
};

const emptyAltaWizardData = () => ({
  usuarioId: null,
  legajoId: null,
  formularioAltaId: null,
  agregaBaseClienteId: null,
  usuario: {
    cuit: "",
    nombre: "",
    email: "",
    tipoUsuario: 0,
  },
  legajo: {
    nroLegajo: "",
    terminal: "",
    nombreDeFantasia: "",
    provincia: "",
    tipoPersona: "fisica",
    razonSocial: "",
    calle: "",
    numero: "",
    pisoDepto: "",
    localidad: "",
    codigoPostal: "",
    emailContacto: "",
    celularWhatsapp: "",
    asesor: "",
  },
  alta: {
    condicionAFIP: "Monotributista",
    actividadLaboralProfesional: "",
    pais: "Argentina",
    nacionalidad: "Argentina",
    fechaNacimiento: "",
    celular: "",
    telFijo: "",
    referenciaTelefono: "",
    telefonoAlternativo: "",
    referenciaTelefonoAlternativo: "",
    correo: "",
    apoderado: "",
    localidadTitular: "",
    codigoPostalTitular: "",
    domicilioComercio: "",
    localidadComercio: "",
    codigoPostalComercio: "",
    afipExentoGanancia: false,
    afipExentoIva: false,
    rentasIncripto: false,
    rentasConvenioMultilateral: false,
    rentasExento: false,
    banco: "",
    tipoCuenta: "Caja de Ahorros",
    cbucvu: "",
    alias: "",
    cuitbancario: "",
    titularRazonSocial: "",
    numeroComericoActual: "",
    numeroComercioSolicitado: "",
    tipoAlta: "NuevoAliado",
    estado: "ALTA",
    fechaDeAlta: makeLocalToday(),
    linkDePago: "NO",
    provincia: "",
    provincia3: "",
    nombreDelAsesor: "",
    estadoAfip: "MONOTRIBUTISTA",
    exento: "NO",
    estadoRentas: "",
    altas: "ALTA NUEVA",
    baja: "",
    alicuota: "",
    rubro: "",
    rubroSecundario: "",
    nroLocal: "",
    codActividad: "",
    actividad: "",
    actividad2: "",
    codActividadRentas: "",
    actividadRentas: "",
    observaciones: "",
  },
});

const emptyBajaDraft = () => ({
  terminal: "",
  motivo: "BAJA",
  observacion: "",
  fechaBaja: makeToday().replace(/-/g, "/"),
});

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getFileExtension = (file) => {
  const name = String(file?.name || "");
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === name.length - 1) {
    return "";
  }

  return name.slice(dotIndex + 1).trim().toLowerCase();
};

const boolToLegacyFlag = (value) => (value ? "true" : "");

const nextLegajoSuggestion = (legajos = []) => {
  const maxValue = legajos.reduce((acc, item) => {
    const raw = Number(item?.nroLegajo ?? item?.NroLegajo ?? item?.nroLegajo1 ?? 0);
    return Number.isFinite(raw) && raw > acc ? raw : acc;
  }, 0);

  return String(maxValue + 1 || 1);
};

const buildAltaWizardFormPayload = (wizardData, token) => {
  const { usuario, legajo, alta } = wizardData;
  const razonSocial = sanitizeLegacyTextValue(legajo.razonSocial || usuario.nombre);
  const domicilioTitular = sanitizeLegacyTextValue(alta.localidadTitular || legajo.localidad);
  const domicilioComercio = sanitizeLegacyTextValue(alta.localidadComercio || legajo.localidad);
  const cuitTitular = normalizeCuitValue(usuario.cuit);
  const cuitBancario = normalizeCuitValue(alta.cuitbancario || cuitTitular);
  const provinciaAlta = sanitizeLegacyTextValue(alta.provincia || legajo.provincia);
  const provinciaFiscal = sanitizeLegacyTextValue(alta.provincia3 || provinciaAlta);
  const estadoAfip = alta.estadoAfip || mapCondicionAfipToEstado(alta.condicionAFIP);
  const exentoFlags = mapExentoToLegacyFlags(alta.exento);
  const rentasFlags = mapEstadoRentasToLegacyFlags(alta.estadoRentas);
  const terminal = sanitizeLegacyNumericValue(legajo.terminal);
  const codigoPostalTitular = sanitizeLegacyNumericValue(alta.codigoPostalTitular || legajo.codigoPostal);
  const celular = sanitizeLegacyNumericValue(alta.celular || legajo.celularWhatsapp);
  const telFijo = sanitizeLegacyNumericValue(alta.telefonoAlternativo || alta.telFijo || legajo.celularWhatsapp);
  const codigoPostalComercio = sanitizeLegacyNumericValue(alta.codigoPostalComercio || legajo.codigoPostal);

  return {
    token,
    condicionAFIP: mapEstadoAfipToCondicion(estadoAfip),
    TipoPersona: sanitizeLegacyTextValue(legajo.tipoPersona) || "fisica",
    TerminalN: terminal,
    ApellidoYnombre: sanitizeLegacyTextValue(usuario.nombre),
    Cuittitular: cuitTitular,
    ActividadLaboralProfesional: sanitizeLegacyTextValue(alta.actividadLaboralProfesional),
    Calle: sanitizeLegacyTextValue(legajo.calle),
    NumeroCalle: sanitizeLegacyTextValue(legajo.numero),
    LocalidadTitular: domicilioTitular,
    Provincia: provinciaAlta,
    Pais: sanitizeLegacyTextValue(alta.pais) || "Argentina",
    CodigoPostalTitular: codigoPostalTitular,
    Nacionalidad: sanitizeLegacyTextValue(alta.nacionalidad) || "Argentina",
    FechaNacimiento: sanitizeLegacyTextValue(alta.fechaNacimiento),
    FechaApertura: sanitizeLegacyTextValue(alta.fechaDeAlta),
    Celular: celular,
    TelFijo: telFijo,
    Correo: sanitizeLegacyTextValue(alta.correo || legajo.emailContacto || usuario.email),
    Apoderado: sanitizeLegacyTextValue(alta.referenciaTelefono || alta.apoderado),
    NombreFantasia: sanitizeLegacyTextValue(legajo.nombreDeFantasia),
    Domicilio: sanitizeLegacyTextValue(alta.domicilioComercio || [legajo.calle, legajo.numero].filter(Boolean).join(" ")),
    LocalidadComercio: domicilioComercio,
    CodigoPostalComercio: codigoPostalComercio,
    AfipExentoGanancia: boolToLegacyFlag(exentoFlags.afipExentoGanancia),
    AfipExentoIva: boolToLegacyFlag(exentoFlags.afipExentoIva),
    RentasIncripto: boolToLegacyFlag(rentasFlags.rentasIncripto),
    RentasConvenioMultilateral: boolToLegacyFlag(rentasFlags.rentasConvenioMultilateral),
    RentasExento: boolToLegacyFlag(rentasFlags.rentasExento),
    Banco: sanitizeLegacyTextValue(alta.banco),
    TipoCuenta: sanitizeLegacyTextValue(alta.tipoCuenta),
    Cbucvu: sanitizeLegacyNumericValue(alta.cbucvu),
    Alias: sanitizeLegacyTextValue(alta.alias),
    Cuitbancario: cuitBancario,
    TitularRazonSocial: sanitizeLegacyTextValue(alta.titularRazonSocial) || razonSocial,
    NumeroComericoActual: sanitizeLegacyTextValue(alta.numeroComericoActual),
    NumeroComercioSolicitado: sanitizeLegacyTextValue(alta.numeroComercioSolicitado),
    TipoAlta: sanitizeLegacyTextValue(alta.tipoAlta),
    Estado: sanitizeLegacyTextValue(alta.estado) || "ALTA",
    LinkDePago: sanitizeLegacyTextValue(alta.linkDePago) || "NO",
    NombreDelAsesor: sanitizeLegacyTextValue(alta.nombreDelAsesor || legajo.asesor),
    EstadoAfip: estadoAfip,
    Exento: sanitizeLegacyTextValue(alta.exento) || "NO",
    EstadoRentas: sanitizeLegacyTextValue(alta.estadoRentas),
    Provincia3: provinciaFiscal,
    Altas: sanitizeLegacyTextValue(alta.altas) || LEGACY_DEFAULT_ALTAS_BY_TIPO[alta.tipoAlta] || "ALTA NUEVA",
    Baja: sanitizeLegacyTextValue(alta.baja),
    NuevoPanel: "true",
  };
};

const currency = (value) =>
  typeof value === "number"
    ? value.toLocaleString("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 })
    : "Sin datos";

const percent = (value) =>
  typeof value === "number"
    ? `${(value * 100).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`
    : "Sin datos";

const hasBillingValue = (value) => value !== undefined && value !== null && value !== "" && Number.isFinite(Number(value));
const formatBillingCurrency = (value) => {
  if (!hasBillingValue(value)) {
    return "Sin datos";
  }

  const parsed = Number(value);
  return parsed.toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: Number.isInteger(parsed) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};
const formatBillingDate = (value) => {
  if (!value || value === "-") return "Sin datos";
  const raw = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [year, month, day] = raw.slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
  }
  return raw;
};
const formatHeaderDateTime = (date) =>
  `${date.toLocaleDateString("es-AR")} | ${date.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false })}`;
const getRangeValidationMessage = (range = {}) => {
  if (!range.desde || !range.hasta) {
    return "El rango de fechas seleccionado no es válido.";
  }

  if (Number.isNaN(new Date(`${range.desde}T00:00:00`).getTime()) || Number.isNaN(new Date(`${range.hasta}T00:00:00`).getTime())) {
    return "El rango de fechas seleccionado no es válido.";
  }

  if (range.desde > range.hasta) {
    return "El rango de fechas seleccionado no es válido.";
  }

  return "";
};
const openNativeDatePicker = (event) => {
  const input = event.currentTarget;
  if (typeof input.showPicker === "function") {
    input.showPicker();
  }
};
const openNativeDatePickerFromKeyboard = (event) => {
  if (event.key === "Enter" || event.key === " ") {
    openNativeDatePicker(event);
  }
};
const formatOrderDate = (value) => {
  if (!value || value === "-") return "-";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [year, month, day] = raw.slice(0, 10).split("-");
    return `${day}-${month}-${year}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [day, month, year] = raw.split("/");
    return `${day}-${month}-${year}`;
  }
  return raw;
};
const formatOrderMonthLabel = (value) => {
  if (!value) return "Sin datos";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "Sin datos";
  return date.toLocaleDateString("es-AR", { month: "long", year: "numeric" });
};
const formatMonthInputLabel = (value) => {
  if (!value) return "Sin datos";
  const [year, month] = String(value).split("-");
  if (!year || !month) return "Sin datos";
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return "Sin datos";
  return date.toLocaleDateString("es-AR", { month: "long", year: "numeric" });
};
const formatOrderCurrency = (value) => {
  if (value === undefined || value === null || value === "" || !Number.isFinite(Number(value))) {
    return "-";
  }

  return Number(value).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
const PLAZOS_TIPO_OPTIONS = [
  { value: "Debito", label: "Débito" },
  { value: "Alimentar", label: "Alimentar" },
  { value: "Credito1Pago", label: "Crédito 1 Pago" },
  { value: "Credito2Pago", label: "Crédito 2 o más Pagos" },
  { value: "CreditoCuotaSimple", label: "Crédito Cuota Simple" },
  { value: "Naranja", label: "Naranja" },
];
const PLAZOS_TARJETAS_POR_TIPO = {
  Debito: [
    { value: "Maestro/Electron Visa", label: "Bancarizada" },
    { value: "MC Debit/Visa Debit", label: "Billetera virtual" },
  ],
  Alimentar: [{ value: "Banco Nación/Provinciales", label: "Banco Nación/Provinciales" }],
  Credito1Pago: [
    { value: "Bancarizadas", label: "Bancarizadas" },
    { value: "No bancarizadas", label: "No bancarizadas" },
    { value: "Recargables", label: "Recargables" },
  ],
  Credito2Pago: [
    { value: "Bancarizadas", label: "Bancarizadas" },
    { value: "No bancarizadas", label: "No bancarizadas" },
  ],
  CreditoCuotaSimple: [{ value: "Bancarizadas", label: "Bancarizadas" }],
  Naranja: [{ value: "Naranja", label: "Naranja" }],
};
const PLAZOS_REGLAS_ACREDITACION = [
  {
    category: "DÉBITO",
    description: "Bancarizadas (Tarjetas de Débito de bancos virtuales, Brubank, Mercadopago y todas las recargables)",
    days: "24 hs hábiles",
  },
  {
    category: "DÉBITO",
    description: "Billeteras virtuales (Bancos Nacionales, Provinciales y privados)",
    days: "48 hs hábiles",
  },
  { category: "ALIMENTAR", description: "Banco Nación/Provinciales", days: "48 hs hábiles" },
  { category: "CRÉDITO 1 PAGO", description: "Bancarizadas (Amex)", days: "10 días hábiles" },
  { category: "CRÉDITO 1 PAGO", description: "Bancarizadas (Cabal)", days: "18 días hábiles" },
  { category: "CRÉDITO 1 PAGO", description: "Bancarizadas (Visa - Master-Argencard)", days: "48 hs hábiles" },
  { category: "CRÉDITO 1 PAGO", description: "No Bancarizadas (Naranja Visa, Naranja Master, Cencosud, etc)", days: "5 días hábiles" },
  {
    category: "CRÉDITO 1 PAGO",
    description: "Recargables (Tarjetas de débito de bancos virtuales, Uala, Brubank, Mercadopago y todas las recargables)",
    days: "48 hs hábiles",
  },
  { category: "CRÉDITO 2 O MÁS PAGOS", description: "Bancarizadas (Amex - Cabal)", days: "10 días hábiles" },
  { category: "CRÉDITO 2 O MÁS PAGOS", description: "No Bancarizadas (Naranja Visa, Naranja Master, Cencosud)", days: "5 días hábiles" },
  { category: "CRÉDITO CUOTA SIMPLE", description: "Solo Bancarizadas habilitadas (Bancos Nacionales, Provinciales y Privados)", days: "48 hs hábiles" },
  { category: "CRÉDITO CUOTA SIMPLE", description: "Solo bancarizadas habilitadas (Amex)", days: "10 días hábiles" },
  { category: "FECHA DE PAGO", description: "Naranja", days: "Cierre: 24 cada mes - Pago 14" },
];
const PLAZOS_RESULT_KEYS = ["Naranja", "10Dias", "5Dias", "48Hs", "24Hs"];
const getPlazosResultValue = (resultado, key) => {
  if (!resultado || typeof resultado !== "object") {
    return undefined;
  }

  const candidates = [
    key,
    `${key.charAt(0).toLowerCase()}${key.slice(1)}`,
    key.replace("Dias", "dias"),
    key.replace("Días", "dias"),
  ];
  for (const candidate of candidates) {
    if (Object.prototype.hasOwnProperty.call(resultado, candidate)) {
      return resultado[candidate];
    }
  }

  return undefined;
};
const extractPlazosPrimaryResult = (payload) => {
  const resultado = payload?.resultado || payload?.Resultado || null;
  if (!resultado) return null;

  for (const key of PLAZOS_RESULT_KEYS) {
    const hasDetails = getPlazosResultValue(resultado, `TieneDetalles${key}`);
    const date = getPlazosResultValue(resultado, `NuevaFecha${key}`);
    if (!hasDetails || !date) {
      continue;
    }

    return {
      date,
      type: getPlazosResultValue(resultado, "Tarjeta") || "-",
      detail: getPlazosResultValue(resultado, `Detalle${key}`) || getPlazosResultValue(resultado, `Detalle${key.toLowerCase()}`) || "Sin datos",
      description: getPlazosResultValue(resultado, `Detalles${key}`) || getPlazosResultValue(resultado, `TarjetaDetalles${key}`) || "",
    };
  }

  return null;
};
const MONTOS_MODE_OPTIONS = [
  { value: "Neto", label: "QUIERO RECIBIR" },
  { value: "Bruto", label: "QUIERO COBRAR" },
  { value: "SinInteres", label: "SIN INTERÉS" },
];
const MONTOS_TIPO_OPTIONS = [
  { value: "Debito", label: "Débito" },
  { value: "Credito", label: "Crédito" },
];
const formatMontoCurrency = (value) => {
  const parsed = parseFlexibleAmount(value);
  if (parsed === null) {
    return "-";
  }

  return parsed.toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
const normalizeMontoForApi = (value) => {
  const parsed = parseFlexibleAmount(value);
  return parsed === null ? "" : parsed.toFixed(2);
};
const buildMontoCuotaLabel = (tarjetaLabel, cuota) => {
  const parsed = Number(cuota);
  const isNaranja = String(tarjetaLabel || "").toLowerCase() === "naranja";

  if (isNaranja && parsed === 3) return "PlanZ";
  if (parsed === 13) return "Cuota simple 3";
  if (parsed === 16) return "Cuota simple 6";
  if (!isNaranja && parsed > 0) return `Super Simple ${parsed}`;
  return `Cuota ${Number.isFinite(parsed) ? parsed : cuota}`;
};
const buildMontoResultRows = (result = {}) => [
  { label: "Arancel + IVA", value: result.comisionMasIva },
  { label: "Costo financiero + IVA", value: String(result.cuota || "") === "1" ? "0" : result.costoTarjeta },
  { label: "Costo por anticipo + IVA", value: result.costoAnticipo },
  { label: "Ret. Prov. (IIBB)", value: result.alicuotaFinal },
  { label: "Cost. Transc.", value: result.debcredFinal },
];
const COMPARADOR_TIPO_OPTIONS = [
  { value: "credito", label: "Credito" },
  { value: "debito", label: "Debito" },
];
const COMPARADOR_CUOTA_OPTIONS = ["1", "3", "6", "9", "12"].map((value) => ({ value, label: value }));
const COMPARADOR_COMPETENCIAS = [
  { value: "payway", label: "PayWay" },
  { value: "mercadopago", label: "Mercado Pago" },
  { value: "naranjax", label: "Naranja X" },
  { value: "getnet", label: "Getnet" },
  { value: "viumi", label: "Viumi" },
  { value: "nave", label: "Nave" },
];
const COMPARADOR_ZOCO_REWARDS = [
  { value: "1", label: "Zoco Starter" },
  { value: "2", label: "Zoco Advanced" },
  { value: "3", label: "Zoco Elite" },
];
const COMPARADOR_LOGOS = {
  payway: logoPaywayComparador,
  mercadopago: logoMercadoPagoComparador,
  naranjax: logoNaranjaComparador,
  getnet: logoGetnetComparador,
  viumi: logoViumiComparador,
  nave: logoNaveComparador,
};
const COMPARADOR_FEATURES = [
  { label: "Costo mensual", competencia: "Sí", zoco: "No", toneCompetencia: "bad", toneZoco: "good" },
  { label: "Trámite tarjeta", competencia: "Sí", zoco: "No", toneCompetencia: "bad", toneZoco: "good" },
  { label: "Panel All-in-One 100% gratis", competencia: false, zoco: true },
  { label: "Comprobante de retención mensual", competencia: false, zoco: true },
  { label: "Terminales 100% bonificadas", competencia: false, zoco: true },
  { label: "Canal de atencion directo", competencia: false, zoco: true },
  { label: "Plazo de resolución", competencia: "5 a 9 días hábiles", zoco: "24 hs hábiles", toneCompetencia: "bad", toneZoco: "good" },
  { label: "Alta Cuota simple", competencia: "30 días", zoco: "24 horas", toneCompetencia: "bad", toneZoco: "good" },
  { label: "Alta Alimentar", competencia: "30 a 90 días", zoco: "24 horas", toneCompetencia: "bad", toneZoco: "good" },
  { label: "Alta servicio", competencia: "15 días", zoco: "En el acto", toneCompetencia: "bad", toneZoco: "good" },
  { label: "Servicio Técnico", competencia: "Plazo de 5 días", zoco: "Cambio directo", toneCompetencia: "bad", toneZoco: "good" },
  { label: "Tiempo de acreditación", competencia: false, zoco: true },
  { label: "Dinero a cualquier cuenta o billetera", competencia: false, zoco: true },
  { label: "Seguridad ante un contracargo", competencia: false, zoco: true },
];
const COMPARADOR_ECONOMIC_COMPARISON = {
  "ARANCEL": "lower",
  "IVA SOBRE ARANCEL": "lower",
  "COSTO TRANSC.": "lower",
  "COSTO TRANSC. DEBITO/CREDITO": "lower",
  "COSTO FINANCIERO": "lower",
  "IVA COSTO FINANCIERO": "lower",
  "COSTO ANTICIPO": "lower",
  "IVA COSTO ANTICIPO": "lower",
  "PERCEPCIONES IVA": "lower",
  "RETENCIONES INGRESOS BRUTOS": "lower",
  "TOTAL DEDUCCIONES": "lower",
  "TOTAL A DEPOSITAR": "higher",
};
const COMPARADOR_ECONOMIC_ORDER = [
  "VENTAS BRUTAS",
  "ARANCEL",
  "IVA SOBRE ARANCEL",
  "COSTO TRANSC.",
  "COSTO TRANSC. DEBITO/CREDITO",
  "COSTO FINANCIERO",
  "IVA COSTO FINANCIERO",
  "COSTO ANTICIPO",
  "IVA COSTO ANTICIPO",
  "PERCEPCIONES IVA",
  "RETENCIONES INGRESOS BRUTOS",
  "TOTAL DEDUCCIONES",
  "TOTAL A DEPOSITAR",
];
const getComparadorValue = (item, ...keys) => {
  if (!item || typeof item !== "object") return undefined;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(item, key)) return item[key];
    const camel = key.charAt(0).toLowerCase() + key.slice(1);
    if (Object.prototype.hasOwnProperty.call(item, camel)) return item[camel];
  }
  return undefined;
};
const getComparadorItems = (resultado, key) => {
  const value = getComparadorValue(resultado, key);
  return Array.isArray(value) ? value : [];
};
const normalizeComparadorName = (value) => String(value || "").trim().toUpperCase();
const getComparadorCompanyLabel = (value) => COMPARADOR_COMPETENCIAS.find((item) => item.value === value)?.label || "Competencia";
const buildComparadorRows = (resultado) => {
  const competenciaItems = getComparadorItems(resultado, "Payway");
  const zocoItems = getComparadorItems(resultado, "Zoco");
  const names = new Set([
    ...COMPARADOR_ECONOMIC_ORDER,
    ...competenciaItems.map((item) => normalizeComparadorName(getComparadorValue(item, "Nombre"))),
    ...zocoItems.map((item) => normalizeComparadorName(getComparadorValue(item, "Nombre"))),
  ]);

  return [...names]
    .filter(Boolean)
    .map((name) => ({
      name,
      competencia: competenciaItems.find((item) => normalizeComparadorName(getComparadorValue(item, "Nombre")) === name),
      zoco: zocoItems.find((item) => normalizeComparadorName(getComparadorValue(item, "Nombre")) === name),
    }))
    .filter((row) => row.competencia || row.zoco);
};
const formatComparadorPercent = (value) => {
  const parsed = parseFlexibleAmount(value);
  if (parsed === null) return "-";
  return `${parsed.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};
const getComparadorWinner = (row = {}) => {
  const mode = COMPARADOR_ECONOMIC_COMPARISON[row.name];
  if (!mode) return "notComparable";

  const competencia = parseFlexibleAmount(getComparadorValue(row.competencia, "Monto"));
  const zoco = parseFlexibleAmount(getComparadorValue(row.zoco, "Monto"));
  if (competencia === null || zoco === null) return "notComparable";
  if (Math.abs(competencia - zoco) < 0.005) return "tie";

  if (mode === "higher") {
    return zoco > competencia ? "zoco" : "competencia";
  }

  return zoco < competencia ? "zoco" : "competencia";
};
const getComparadorValueTone = (winner, side) => {
  if (winner !== "zoco") return "";
  return side === "zoco" ? "is-winner is-good" : "is-bad";
};
const getComparadorFeatureWinner = (feature = {}) => {
  if (feature.winner) return feature.winner;
  const competenciaIsGood = feature.toneCompetencia === "good" || feature.competencia === true;
  const zocoIsGood = feature.toneZoco === "good" || feature.zoco === true;
  if (competenciaIsGood && !zocoIsGood) return "competencia";
  if (zocoIsGood && !competenciaIsGood) return "zoco";
  return "tie";
};
const formatComparadorText = (value) =>
  String(value ?? "-")
    .replace(/\u00c3\u00ad/g, "\u00ed")
    .replace(/\u00c3\u00a1/g, "\u00e1")
    .replace(/\u00c3\u00a9/g, "\u00e9")
    .replace(/\u00c3\u00b3/g, "\u00f3")
    .replace(/\u00c3\u00ba/g, "\u00fa")
    .replace(/\u00c3\u0089/g, "\u00c9")
    .replace(/\u00c3\u0093/g, "\u00d3");
const formatComparadorConceptLabel = (value) => {
  const normalized = formatComparadorText(value);
  if (!normalized || normalized === "-") return "-";

  return normalized
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bIva\b/g, "IVA")
    .replace(/\bDebito\b/g, "Debito")
    .replace(/\bCredito\b/g, "Credito");
};
const validateComparadorForm = (form) => {
  const amount = parseFlexibleAmount(form.monto);
  if (amount === null || amount <= 0 || amount > 9999999999) return "El monto ingresado no es valido.";
  if (!/^\d+$/.test(String(form.terminales || ""))) return "La cantidad de POS no es valida.";
  const terminales = Number(form.terminales);
  if (!Number.isInteger(terminales) || terminales <= 0 || terminales > 999) return "La cantidad de POS no es valida.";
  if (!form.tipoPago) return "Selecciona el tipo de pago.";
  if (form.tipoPago === "credito" && !form.cuota) return "Selecciona la cuota.";
  if (!form.competencia) return "Selecciona la compania.";
  if (!form.zoco) return "Selecciona Zoco Rewards.";
  return "";
};
const ORDENES_TIPO_OPTIONS = [
  { value: "Credito", label: "Credito" },
  { value: "Debito", label: "Debito" },
];
const ORDENES_MODALIDAD_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "bancarizada", label: "Bancarizada" },
];
const createOrdenesStableId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `orden-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};
const normalizeOrdenesAmountForDisplay = (value) => {
  const parsed = parseFlexibleAmount(value);
  if (parsed === null) return "";
  return parsed.toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};
const validateOrdenesForm = (form) => {
  const amount = parseFlexibleAmount(form.monto);
  if (amount === null || amount <= 0 || amount > 9999999999) return "El monto ingresado no es valido.";
  if (!form.tipo) return "Selecciona el tipo de pago.";
  if (!form.tarjeta) return "Selecciona una tarjeta.";
  if (form.tipo === "Credito" && !form.cuota) return "Selecciona la cantidad de cuotas.";
  if (form.tipo === "Credito" && form.cuota === "1" && !form.tipoCuota) return "Selecciona la modalidad.";
  return "";
};
const buildOrdenesPayload = ({ form, token, tarjetaLabel }) => {
  const cuota = form.tipo === "Debito" ? "0" : form.cuota;
  return {
    Token: token,
    Monto: normalizeMontoForApi(form.monto),
    Cuota: cuota,
    TipoNetoBruto: "Bruto",
    TipoDebCred: form.tipo,
    TipoTarjeta: form.tarjeta,
    Tarjeta: tarjetaLabel,
    TipoCuota: cuota === "1" ? form.tipoCuota || "normal" : null,
  };
};

const GESTION_STATES = ["Pendiente", "Contactado", "Derivado", "Cerrado", "Descartado", "Otro"];
const GESTION_EDIT_STATES = ["Pendiente", "Contactado", "Derivado", "Cerrado", "Sin respuesta", "Descartado", "Otro"];
const GESTION_CLOSE_REASONS = ["Resuelto", "Cliente contactado", "Derivado", "Sin respuesta", "Descartado", "Otro"];
const GESTION_SCOPE_LABELS = {
  ipc: "Gestion IPC",
  operativa: "Alerta operativa",
  proactiva: "Proactiva",
};
const GESTION_SCOPE_ACTION_LABELS = {
  ipc: "Nueva gestion",
  operativa: "Nueva gestion de alerta operativa",
  proactiva: "Nueva gestion proactiva",
};
const GESTION_SCOPE_EMPTY_MESSAGES = {
  ipc: "No hay gestiones IPC registradas para este cliente.",
  operativa: "No hay alertas operativas registradas para este cliente.",
  proactiva: "No hay gestiones proactivas registradas para este cliente.",
};
const GESTION_SCOPE_STATES = {
  ipc: ["Pendiente", "Contactado", "Derivado", "Cerrado", "Descartado", "Otro"],
  operativa: ["Pendiente", "Contactado", "Derivado", "Cerrado", "Descartado", "Otro"],
  proactiva: ["Pendiente", "Contactado", "Derivado", "Cerrado", "Descartado", "Otro"],
};
const CONTACT_CHANNEL_OPTIONS = ["WhatsApp", "Llamada", "Mail"];
const CALL_RESULT_OPTIONS = ["No atiende", "Ocupado", "Numero incorrecto", "Contesto", "Otro"];
const GESTION_IPC_TYPES = ["Modificacion", "Reclamo", "Consulta"];
const GESTION_IPC_REASON_OPTIONS = {
  Modificacion: ["Datos bancarios", "Datos fiscales", "Titularidad", "Domicilio", "Datos de terminal", "Otros"],
  Reclamo: ["Liquidacion", "Servicio tecnico", "Configuracion terminal", "Plazos de acreditacion", "Panel central"],
  Consulta: ["Tasas", "Plazos de acreditacion", "Operativa", "Simuladores", "Ordenes de pago", "Consultas especificas", "Rollos"],
};
const GESTION_SCOPE_RULE_CODES = {
  operativa: new Set([
    "MAN_ALERTA_OPERATIVA",
    "MAN_ZOCO_CHECK",
    "MAN_TERMINAL_EXTRAVIADA",
    "ONBOARDING_24H",
    "ONBOARDING_SIN_VENTAS_3D",
    "ONBOARDING_SIN_VENTAS_7D",
    "ONBOARDING_RIESGO_15D",
    "ONBOARDING_PRIMERA_ACREDITACION",
  ]),
  proactiva: new Set([
    "MAN_COORDINAR_RETIRO_TERMINAL",
    "MAN_RECUPERACION_COMERCIAL",
    "MAN_VISITA_COMERCIAL",
    "MAN_OTRO",
  ]),
};
const INACTIVOS_PROACTIVA_RULE_CODES = new Set([
  "MAN_RECUPERACION_COMERCIAL",
  "MAN_COORDINAR_RETIRO_TERMINAL",
  "MAN_VISITA_COMERCIAL",
  "MAN_OTRO",
]);
const GESTION_UNIFICADA_TABS = [
  { id: "onboarding", label: "ONBOARDING" },
  { id: "zoco-check", label: "ZOCO CHECK" },
  { id: "terminal-sin-facturacion", label: "SIN FACTURACIÓN" },
  { id: "recupero-terminal", label: "RECUPERO" },
  { id: "terminal-extraviada", label: "EXTRAVIADA" },
  { id: "derivaciones", label: "DERIVACIONES" },
  { id: "ipc", label: "GESTIONES IPC" },
  { id: "rollo", label: "ROLLO" },
];
const SECTION_PATHS = {
  alertas: "/administracion/crm-postventa/alertas",
  ficha: "/administracion/crm-postventa/ficha",
  facturacion: "/administracion/crm-postventa/facturacion",
  "ordenes-pago": "/administracion/crm-postventa/ordenes-pago",
  cupones: "/administracion/crm-postventa/cupones",
  herramientas: "/administracion/crm-postventa/herramientas",
  "altas-bajas": "/administracion/crm-postventa/altas-bajas",
  blanqueos: "/administracion/crm-postventa/blanqueos",
  "campanias-zoco-check": "/administracion/crm-postventa/campanias-zoco-check",
  gestion: "/administracion/crm-postventa/gestion",
  seguimiento: "/administracion/crm-postventa/seguimiento",
  modales: "/administracion/crm-postventa/modales",
};

const toneForGestionState = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("resuelto") || normalized.includes("cerrado")) return "success";
  if (normalized.includes("escalado")) return "danger";
  if (normalized.includes("contactado") || normalized.includes("proceso")) return "warning";
  if (normalized.includes("pendiente")) return "default";
  return "default";
};

const toneForPriority = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("cr")) return "danger";
  if (normalized.includes("alta")) return "warning";
  if (normalized.includes("media")) return "default";
  return "success";
};

const toneForSegment = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "a") return "danger";
  if (normalized === "b") return "warning";
  if (normalized === "c") return "default";
  return "success";
};

const toneForPattern = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("diario")) return "danger";
  if (normalized.includes("fin_de_semana")) return "warning";
  if (normalized.includes("intermitente")) return "default";
  return "success";
};

const getTimelineTipoDetalle = (item) => {
  const raw = String(item?.tipo || "");
  const [, specific] = raw.split(":");
  return specific || item?.titulo || "-";
};

const PostventaBadge = ({ value, tone = "default" }) => (
  <span className={`crm-postventa-badge tone-${tone}`}>{value || "-"}</span>
);

const normalizeReadOnlyValue = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized || normalized === "undefined" || normalized === "null" || normalized === "NaN" || normalized === "Invalid Date") {
    return "-";
  }

  return normalized;
};

const CompactFichaBadge = ({ label, value, accent = false }) => (
  <span className="crm-postventa-ficha-chip">
    <span>{label}</span>
    <strong className={accent ? "is-accent" : ""}>{normalizeReadOnlyValue(value)}</strong>
  </span>
);

const ReadOnlyField = ({ label, value, className = "", multiline = false, children = null }) => (
  <div className={`crm-postventa-readonly-field ${className}`.trim()}>
    <span className="crm-postventa-readonly-field__label">{label}</span>
    <div className={`crm-postventa-readonly-field__value ${multiline ? "is-multiline" : ""}`}>
      {children || normalizeReadOnlyValue(value)}
    </div>
  </div>
);

const ClientInfoCard = ({ title, children }) => (
  <section className="crm-postventa-ficha-card">
    <h4>{title}</h4>
    {children}
  </section>
);

const DataList = ({ items = [] }) => (
  <dl className="crm-postventa-ficha-list">
    {items.map((item) => (
      <div className="crm-postventa-ficha-item" key={`${item.label}-${item.value}`}>
        <dt>{item.label}</dt>
        <dd>
          {item.isBadge ? (
            <PostventaBadge value={item.value} tone={item.tone} />
          ) : (
            <>
              <span>{item.value || "-"}</span>
              {(() => {
                const whatsappPhone = isPhoneFieldLabel(item.label) ? normalizeWhatsappPhone(item.value) : "";
                return whatsappPhone ? (
                  <a
                    className="btn btn-outline-success btn-sm ms-2"
                    href={`https://wa.me/${whatsappPhone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WhatsApp
                  </a>
                ) : null;
              })()}
            </>
          )}
        </dd>
      </div>
    ))}
  </dl>
);

const buildClienteGroupKey = (cliente = {}) => {
  const cuit = String(cliente.cuit || "").trim();
  const nombre = String(cliente.nombreDeFantasia || cliente.razonSocial || "")
    .trim()
    .toLowerCase();
  return `${cuit}::${nombre}`;
};

const normalizeTerminalOptions = (terminales = []) =>
  terminales
    .filter((item) => item?.clienteId)
    .sort((a, b) => {
      const left = Number(String(a.terminal || "").replace(/\D/g, "")) || Number.MAX_SAFE_INTEGER;
      const right = Number(String(b.terminal || "").replace(/\D/g, "")) || Number.MAX_SAFE_INTEGER;
      return left - right || String(a.comercio || "").localeCompare(String(b.comercio || ""));
    })
    .map((item, index) => ({
      clienteId: item.clienteId,
      label: item.label || `Terminal ${index + 1}`,
      terminal: item.terminal || item.nDeTerminal || "",
      comercio: item.comercio || item.nDeComercio || "",
      estado: item.estado || "",
      estadoAlta: item.estadoAlta || item.estado || "",
      estadoOperativo: item.estadoOperativo || "",
      legajo: item.legajo || "",
      resumen:
        item.resumen ||
        [item.terminal ? `Terminal ${item.terminal}` : "", item.comercio ? `Comercio ${item.comercio}` : ""]
          .filter(Boolean)
          .join(" | "),
    }));

const mergeTerminalOptions = (...sources) => {
  const merged = new Map();

  sources
    .flat()
    .forEach((item) => {
      if (!item?.clienteId) {
        return;
      }

      const current = merged.get(item.clienteId) || {};
      merged.set(item.clienteId, {
        ...current,
        ...item,
        label: item.label || current.label,
        resumen: item.resumen || current.resumen,
      });
    });

  return normalizeTerminalOptions(Array.from(merged.values()));
};

const normalizeBajaLookupResults = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((item, index) => ({
      key: [
        item?.clienteId ?? item?.ClienteId ?? `sin-${index}`,
        normalizeDigits(item?.nDeTerminal || item?.NDeTerminal || item?.terminal),
        normalizeDigits(item?.nDeComercio || item?.NDeComercio || item?.comercio),
      ].join("|"),
      terminal: String(item?.nDeTerminal ?? item?.NDeTerminal ?? item?.terminal ?? "").trim(),
      comercio: String(item?.nDeComercio ?? item?.NDeComercio ?? item?.comercio ?? "").trim(),
      nombre: String(item?.nombreDeFantasia ?? item?.NombreDeFantasia ?? item?.razonSocial ?? item?.RazonSocial ?? "").trim(),
      cuit: String(item?.cuit ?? item?.Cuit ?? "").trim(),
      estado: String(item?.estado ?? item?.Estado ?? "").trim(),
    }))
    .filter((item) => item.terminal || item.comercio || item.nombre)
    .sort((left, right) => {
      const byTerminal = String(left.terminal).localeCompare(String(right.terminal));
      if (byTerminal !== 0) {
        return byTerminal;
      }

      return String(left.nombre).localeCompare(String(right.nombre));
    });

const normalizeAgregaLookupResults = (rows = []) => {
  const bestByCommerce = new Map();

  (Array.isArray(rows) ? rows : []).forEach((item, index) => {
    const clienteId = item?.clienteId ?? item?.ClienteId ?? 0;
    const terminal = String(item?.nDeTerminal ?? item?.NDeTerminal ?? item?.terminal ?? "").trim();
    const comercio = String(item?.nDeComercio ?? item?.NDeComercio ?? item?.comercio ?? "").trim();
    const cuit = String(item?.cuit ?? item?.Cuit ?? "").trim();
    const nombreDeFantasia = String(item?.nombreDeFantasia ?? item?.NombreDeFantasia ?? "").trim();
    const razonSocial = String(item?.razonSocial ?? item?.RazonSocial ?? "").trim();
    const estado = String(item?.estado ?? item?.Estado ?? "").trim();
    const key = normalizeDigits(comercio)
      ? `${normalizeCuitValue(cuit)}|${normalizeDigits(comercio)}`
      : `${clienteId || `sin-${index}`}|${normalizeDigits(terminal)}`;
    const normalized = {
      ...item,
      clienteId,
      nDeTerminal: terminal,
      nDeComercio: comercio,
      cuit,
      nombreDeFantasia,
      razonSocial,
      estado,
      _score:
        (String(estado).toUpperCase() === "ALTA" ? 100 : 0) +
        [terminal, comercio, cuit, nombreDeFantasia, razonSocial].filter(Boolean).length,
    };
    const current = bestByCommerce.get(key);

    if (!current || normalized._score > current._score) {
      bestByCommerce.set(key, normalized);
    }
  });

  return Array.from(bestByCommerce.values())
    .sort((left, right) => {
      const byName = String(left.nombreDeFantasia || left.razonSocial).localeCompare(String(right.nombreDeFantasia || right.razonSocial));
      if (byName !== 0) {
        return byName;
      }

      return normalizeDigits(left.nDeComercio).localeCompare(normalizeDigits(right.nDeComercio));
    })
    .map(({ _score, ...item }) => item);
};

const buildTerminalesFromFacturacion = (filas = []) =>
  normalizeTerminalOptions(
    Array.from(
      filas.reduce((acc, row) => {
        if (!row?.clienteId) {
          return acc;
        }

        if (!acc.has(row.clienteId)) {
          acc.set(row.clienteId, {
            clienteId: row.clienteId,
            terminal: row.terminal,
            comercio: row.comercio,
            estado: row.estadoAlerta,
            estadoOperativo: row.estadoAlerta,
            resumen: [row.terminal ? `Terminal ${row.terminal}` : "", row.comercio ? `Comercio ${row.comercio}` : ""]
              .filter(Boolean)
              .join(" | "),
          });
        }

        return acc;
      }, new Map()).values()
    )
  );

const agruparClientesPorGrupo = (clientes = []) =>
  Array.from(
    clientes.reduce((acc, cliente) => {
      const groupKey = buildClienteGroupKey(cliente);
      if (!groupKey || groupKey === "::") {
        return acc;
      }

      if (!acc.has(groupKey)) {
        acc.set(groupKey, []);
      }

      acc.get(groupKey).push(cliente);
      return acc;
    }, new Map()).entries()
  )
    .map(([groupKey, items]) => {
      const representative =
        items.find((item) => String(item.estado || "").toUpperCase() === "ALTA") ||
        items[0];
      const terminales = normalizeTerminalOptions(
        items.map((item) => ({
          clienteId: item.clienteId,
          terminal: item.nDeTerminal,
          comercio: item.nDeComercio,
          estado: item.estado,
          legajo: item.legajo,
        }))
      );
      const comerciosUnicos = new Set(items.map((item) => item.nDeComercio).filter(Boolean)).size;

      return {
        ...representative,
        groupKey,
        terminales,
        cantidadTerminales: terminales.length,
        cantidadComercios: comerciosUnicos,
        resumen: [
          representative.razonSocial || representative.nombreDeFantasia || "",
          representative.cuit ? `CUIT ${representative.cuit}` : "",
          terminales.length ? `${terminales.length} terminal(es)` : "",
          comerciosUnicos ? `${comerciosUnicos} comercio(s)` : "",
        ]
          .filter(Boolean)
          .join(" | "),
      };
    })
    .sort((a, b) => String(a.nombreDeFantasia || a.razonSocial).localeCompare(String(b.nombreDeFantasia || b.razonSocial)));

const buildFichaEditDraft = (campos = []) =>
  campos.reduce((acc, item) => {
    if (item?.key) {
      acc[item.key] = item.value || "";
    }
    return acc;
  }, {});

const FICHA_FIXED_OPTIONS = {
  estado: [
    { value: "ALTA", label: "Alta" },
    { value: "BAJA", label: "Baja" },
    { value: "SERVICIO.TECNICO", label: "Servicio técnico" },
    { value: "DISPONIBLE", label: "Disponible" },
    { value: "EXTRAVIADA", label: "Extraviada" },
  ],
  siNo: [
    { value: "SI", label: "SI" },
    { value: "NO", label: "NO" },
  ],
  tipoCuenta: [
    { value: "CAJA AHORRO", label: "CAJA AHORRO" },
    { value: "CUENTA CORRIENTE", label: "CUENTA CORRIENTE" },
    { value: "BILLETERA VIRTUAL", label: "BILLETERA VIRTUAL" },
  ],
  estadoAfip: [
    { value: "EXENTO", label: "EXENTO" },
    { value: "INHABILITADO", label: "INHABILITADO" },
    { value: "MONOTRIBUTISTA", label: "MONOTRIBUTISTA" },
    { value: "RESPONSABLE INSCRIPTO", label: "RESPONSABLE INSCRIPTO" },
  ],
  exento: [
    { value: "NO", label: "NO" },
    { value: "EXENTO GANANCIA", label: "EXENTO GANANCIA" },
    { value: "EXENTO IVA", label: "EXENTO IVA" },
  ],
  estadoRentas: [
    { value: "ALTA EN PROCESO", label: "ALTA EN PROCESO" },
    { value: "CM", label: "CM" },
    { value: "EXENTO", label: "EXENTO" },
    { value: "INSCRIPTO", label: "INSCRIPTO" },
    { value: "NO INSCRIPTO", label: "NO INSCRIPTO" },
  ],
  altas: [
    { value: "AGREGA", label: "AGREGA" },
    { value: "AGREGA CT", label: "AGREGA CT" },
    { value: "ALTA CCTT", label: "ALTA CCTT" },
    { value: "ALTA NUEVA", label: "ALTA NUEVA" },
    { value: "ALTA SSTT", label: "ALTA SSTT" },
  ],
  bajas: [
    { value: "BAJA", label: "BAJA" },
    { value: "BAJA POR FACTURACION", label: "BAJA POR FACTURACION" },
    { value: "BAJA POR POSNET", label: "BAJA POR POSNET" },
    { value: "BAJA SSTT", label: "BAJA SSTT" },
    { value: "VERIFONE REUTILIZABLE", label: "VERIFONE REUTILIZABLE" },
    { value: "BAJA POR CAMBIO DE TERMINAL", label: "BAJA POR CAMBIO DE TERMINAL" },
  ],
};

const FICHA_SELECT_KEY_TO_SOURCE = {
  estado: "estado",
  nDeComercio: "comercios",
  legajo: "legajos",
  rubro: "rubros",
  rubroSecundario: "rubros",
  altaAhora12: "siNo",
  provincia: "provincias",
  nombreDelAsesor: "asesores",
  altas: "altas",
  baja: "bajas",
  linkDePago: "siNo",
  banco: "bancos",
  tipoDeCuenta: "tipoCuenta",
  estadoAfip: "estadoAfip",
  exento: "exento",
  estadoRentas: "estadoRentas",
  provincia3: "provincias",
};

const FICHA_READONLY_KEYS = new Set(["sasMutual", "estado", "nDeTerminal", "cbuCvu", "letraCbu", "largoCbu", "alicuota"]);
const CLIENT_EDIT_RADIO_KEYS = new Set(["altaAhora12", "linkDePago"]);
const CLIENT_EDIT_FULL_WIDTH_FIELDS = new Set([
  "sasMutual",
  "nombreDelAsesor",
  "razonSocial",
  "actividad",
  "actividad2",
  "domicilioFiscal",
  "actividadRentas",
  "domicilio",
  "localidad",
  "mail",
  "banco",
  "tipoDeCuenta",
  "nDeCuenta",
  "alias",
  "autorizadoParaCambiosAbm",
]);
const CLIENT_EDIT_SECTION_SPECS = [
  {
    key: "identificacion",
    title: "Identificación",
    sections: new Set(["Datos generales"]),
    order: [
      "nDeTerminal",
      "estado",
      "nDeComercio",
      "legajo",
      "nombreDeFantasia",
      "rubro",
      "rubroSecundario",
      "nroLocal",
      "fechaDeAlta",
      "fechaDeBaja",
      "altaAhora12",
      "tipoDeBaja",
      "nombreDelAsesor",
      "altas",
      "baja",
      "sasMutual",
    ],
  },
  {
    key: "fiscal",
    title: "Fiscal",
    sections: new Set(["Fiscal / rentas"]),
    order: [
      "razonSocial",
      "cuitRazonSocial",
      "estadoAfip",
      "codActividad",
      "exento",
      "actividad",
      "actividad2",
      "estadoRentas",
      "provincia3",
      "domicilioFiscal",
      "codActividadRentas",
      "alicuota",
      "actividadRentas",
    ],
  },
  {
    key: "contacto",
    title: "Contacto",
    sections: new Set(["Contacto"]),
    order: [
      "domicilio",
      "localidad",
      "codigoPostal",
      "provincia",
      "telefono",
      "referenciaTelefono",
      "telefonoAlternativo",
      "referenciaTelefonoAlternativo",
      "mail",
      "contactoAlternativo",
    ],
  },
  {
    key: "bancarioOperativo",
    title: "Bancario / Operativo",
    sections: new Set(["Bancario", "Operativo"]),
    order: [
      "banco",
      "tipoDeCuenta",
      "cbuCvu",
      "linkDePago",
      "nDeCuenta",
      "alias",
      "letraCbu",
      "largoCbu",
      "autorizadoParaCambiosAbm",
      "titular",
      "cuitCuil",
    ],
  },
];

const getClientEditSectionSpec = (field = {}) =>
  CLIENT_EDIT_SECTION_SPECS.find((section) => section.sections.has(field?.section))
  || CLIENT_EDIT_SECTION_SPECS.find((section) => section.order.includes(field?.key))
  || CLIENT_EDIT_SECTION_SPECS[0];

const buildClientEditSections = (fields = []) => {
  const grouped = CLIENT_EDIT_SECTION_SPECS.reduce((acc, section) => {
    acc[section.key] = { ...section, items: [] };
    return acc;
  }, {});

  fields.forEach((field, index) => {
    const section = getClientEditSectionSpec(field);
    grouped[section.key].items.push({
      ...field,
      __originalIndex: index,
    });
  });

  return CLIENT_EDIT_SECTION_SPECS.map((section) => {
    const orderIndex = new Map(section.order.map((key, index) => [key, index]));
    const items = [...grouped[section.key].items].sort((a, b) => {
      const aIndex = orderIndex.has(a.key) ? orderIndex.get(a.key) : Number.MAX_SAFE_INTEGER;
      const bIndex = orderIndex.has(b.key) ? orderIndex.get(b.key) : Number.MAX_SAFE_INTEGER;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return a.__originalIndex - b.__originalIndex;
    });

    return {
      key: section.key,
      title: section.title,
      items,
    };
  }).filter((section) => section.items.length > 0);
};

const isClientEditReadOnlyField = (field) => !field?.editable || FICHA_READONLY_KEYS.has(field?.key);
const isClientEditFullWidthField = (field) =>
  CLIENT_EDIT_FULL_WIDTH_FIELDS.has(field?.key) || field?.inputType === "textarea";

const normalizeOption = (value, label = value, extra = {}) => ({
  value: value == null ? "" : String(value),
  label: label == null ? String(value ?? "") : String(label),
  ...extra,
});

const dedupeOptions = (options = []) => {
  const seen = new Set();

  return options.filter((option) => {
    const key = String(option?.value ?? "");
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const resolveOptionValue = (options = [], value = "") => {
  const normalizedValue = sanitizeLegacyTextValue(value);
  if (!normalizedValue) {
    return "";
  }

  const exact = options.find((option) => String(option?.value ?? "") === normalizedValue);
  if (exact) {
    return String(exact.value);
  }

  const normalizedText = normalizeText(normalizedValue);
  const byText = options.find((option) =>
    [option?.value, option?.label].some((candidate) => normalizeText(candidate) === normalizedText)
  );

  return byText ? String(byText.value) : normalizedValue;
};

const withCurrentOption = (options = [], value = "", label = value) => {
  const currentValue = sanitizeLegacyTextValue(value);
  if (!currentValue || options.some((option) => String(option?.value ?? "") === currentValue)) {
    return options;
  }

  return [normalizeOption(currentValue, label || currentValue), ...options];
};

const mapFichaDraftToPayload = (draft = {}) => ({
  SasMutual: draft.sasMutual || "",
  Estado: draft.estado || "",
  NDeTerminal: draft.nDeTerminal || "",
  NDeComercio: draft.nDeComercio || "",
  NroLocal: draft.nroLocal || "",
  Legajo: draft.legajo || "",
  NombreDeFantasia: draft.nombreDeFantasia || "",
  Domicilio: draft.domicilio || "",
  Localidad: draft.localidad || "",
  CodigoPostal: draft.codigoPostal || "",
  Telefono: normalizeEditablePhoneValue(draft.telefono),
  ReferenciaTelefono: sanitizeLegacyTextValue(draft.referenciaTelefono),
  TelefonoAlternativo: normalizeEditablePhoneValue(draft.telefonoAlternativo),
  LinkDePago: draft.linkDePago || "",
  Rubro: draft.rubro || "",
  RubroSecundario: draft.rubroSecundario || "",
  AltaAhora12: draft.altaAhora12 || "",
  FechaDeAlta: draft.fechaDeAlta || "",
  FechaDeBaja: draft.fechaDeBaja || "",
  TipoDeBaja: draft.tipoDeBaja || "",
  Provincia: draft.provincia || "",
  NombreDelAsesor: draft.nombreDelAsesor || "",
  Altas: draft.altas || "",
  Baja: draft.baja || "",
  Mail: draft.mail || "",
  Titular: draft.titular || "",
  CuitCuil: draft.cuitCuil || "",
  Banco: draft.banco || "",
  TipoDeCuenta: draft.tipoDeCuenta || "",
  CbuCvu: draft.cbuCvu || "",
  NDeCuenta: draft.nDeCuenta || "",
  LetraCbu: draft.letraCbu || "",
  LargoCbu: draft.largoCbu || "",
  Alias: draft.alias || "",
  RazonSocial: draft.razonSocial || "",
  CuitRazonSocial: draft.cuitRazonSocial || "",
  EstadoAfip: draft.estadoAfip || "",
  CodActividad: draft.codActividad || "",
  Actividad: draft.actividad || "",
  Exento: draft.exento || "",
  EstadoRentas: draft.estadoRentas || "",
  Actividad2: draft.actividad2 || "",
  DomicilioFiscal: draft.domicilioFiscal || "",
  Provincia3: draft.provincia3 || "",
  Alicuota: draft.alicuota || "",
  CodActividadRentas: draft.codActividadRentas || "",
  ActividadRentas: draft.actividadRentas || "",
  AutorizadoParaCambiosAbm: draft.autorizadoParaCambiosAbm || "",
  ReferenciaTelefonoAlternativo: sanitizeLegacyTextValue(draft.referenciaTelefonoAlternativo || draft.contactoAlternativo),
  ContactoAlternativo: sanitizeLegacyTextValue(draft.referenciaTelefonoAlternativo || draft.contactoAlternativo),
});

const buildFichaCatalogOptions = (catalogos = {}, field) => {
  const source = field?.dataSource || FICHA_SELECT_KEY_TO_SOURCE[field?.key];
  if (!source) {
    return [];
  }

  if (FICHA_FIXED_OPTIONS[source]) {
    return FICHA_FIXED_OPTIONS[source];
  }

  return catalogos[source] || [];
};

const hasLetters = (value = "") => /[a-zA-Z]/.test(String(value));

const applyFichaFieldSideEffects = (prevDraft, fieldKey, nextValue, catalogos = {}) => {
  const nextDraft = {
    ...prevDraft,
    [fieldKey]: nextValue,
  };

  if (fieldKey === "tipoDeCuenta") {
    nextDraft.cbuCvu = nextValue === "BILLETERA VIRTUAL" ? "CVU" : nextValue ? "CBU" : "";
  }

  if (fieldKey === "nDeCuenta") {
    const rawValue = String(nextValue || "");
    nextDraft.letraCbu = rawValue ? (hasLetters(rawValue) ? "CORRECTO" : "INCORRECTO") : "";
    nextDraft.largoCbu = rawValue ? String(rawValue.length) : "";
  }

  if (fieldKey === "provincia3") {
    const provincias = catalogos.provincias || [];
    const selected = provincias.find((item) => item.value === nextValue);
    nextDraft.alicuota = selected?.alicuota || "";
  }

  return nextDraft;
};

async function fetchFichaCatalogos() {
  const [bancosRes, rubrosRes, provinciasRes, legajosRes, comerciosRes, asesoresRes] = await Promise.all([
    fetch("/api/Banco/lista"),
    fetch("/api/Rubro/lista"),
    fetch("/api/Provincia/listaprovincia"),
    fetch("/api/Legajo/lista"),
    fetch("/api/Comercio/lista"),
    fetch("/api/Usuarios/obtenergestoresyasesores"),
  ]);

  const responses = [bancosRes, rubrosRes, provinciasRes, legajosRes, comerciosRes, asesoresRes];
  const failed = responses.find((response) => !response.ok);
  if (failed) {
    throw new Error("No se pudieron cargar los catálogos de edición.");
  }

  const [bancosData, rubrosData, provinciasData, legajosData, comerciosData, asesoresData] = await Promise.all(
    responses.map((response) => response.json())
  );

  return {
    bancos: (Array.isArray(bancosData) ? bancosData : []).map((item) =>
      normalizeOption(item?.nomBanco ?? item?.NomBanco ?? "", item?.nomBanco ?? item?.NomBanco ?? "")
    ),
    rubros: (Array.isArray(rubrosData) ? rubrosData : []).map((item) => normalizeOption(item, item)),
    provincias: (Array.isArray(provinciasData) ? provinciasData : []).map((item) =>
      normalizeOption(item?.provincia ?? item?.Provincia ?? "", item?.provincia ?? item?.Provincia ?? "", {
        alicuota: item?.alicuota ?? item?.Alicuota ?? "",
      })
    ),
    legajos: (Array.isArray(legajosData) ? legajosData : []).map((item) =>
      normalizeOption(item?.nroLegajo ?? item?.NroLegajo ?? "", item?.nroLegajo ?? item?.NroLegajo ?? "")
    ),
    comercios: (Array.isArray(comerciosData) ? comerciosData : []).map((item) =>
      normalizeOption(item?.num ?? item?.Num ?? "", item?.numComercio ?? item?.NumComercio ?? item?.num ?? item?.Num ?? "")
    ),
    asesores: (Array.isArray(asesoresData) ? asesoresData : []).map((item) => normalizeOption(item, item)),
  };
}

async function fetchFichaCatalogosV2() {
  const fetchJsonWithFallback = async (urls = []) => {
    let lastError = null;

    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          lastError = new Error(`Error cargando ${url}: ${response.status}`);
          continue;
        }

        return response.json();
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error("No se pudieron cargar los catalogos de edicion.");
  };

  const [bancosData, rubrosData, provinciasData, legajosData, comerciosData, asesoresData] = await Promise.all([
    fetchJsonWithFallback(["/api/Banco/lista"]),
    fetchJsonWithFallback(["/api/Rubro/lista"]),
    fetchJsonWithFallback(["/api/Provincia/listaprovincia"]),
    fetchJsonWithFallback(["/api/Legajo/lista", "/api/Legajo/listacompleto"]),
    fetchJsonWithFallback(["/api/Comercio/lista"]),
    fetchJsonWithFallback(["/api/Usuarios/obtenergestoresyasesores"]),
  ]);

  return {
    bancos: dedupeOptions((Array.isArray(bancosData) ? bancosData : []).map((item) =>
      normalizeOption(item?.nomBanco ?? item?.NomBanco ?? "", item?.nomBanco ?? item?.NomBanco ?? "")
    )),
    rubros: dedupeOptions((Array.isArray(rubrosData) ? rubrosData : []).map((item) =>
      normalizeOption(item?.nomRubro ?? item?.NomRubro ?? item, item?.nomRubro ?? item?.NomRubro ?? item)
    )),
    provincias: dedupeOptions((Array.isArray(provinciasData) ? provinciasData : []).map((item) =>
      normalizeOption(item?.provincia ?? item?.Provincia ?? "", item?.provincia ?? item?.Provincia ?? "", {
        alicuota: item?.alicuota ?? item?.Alicuota ?? "",
      })
    )),
    legajos: dedupeOptions((Array.isArray(legajosData) ? legajosData : []).map((item) => {
      const legajoValue = item?.nroLegajo ?? item?.NroLegajo ?? "";
      const nombreFantasia = item?.nombreDeFantasia ?? item?.NombreDeFantasia ?? "";
      const label = nombreFantasia ? `${legajoValue} - ${nombreFantasia}` : legajoValue;
      return normalizeOption(legajoValue, label);
    })),
    comercios: dedupeOptions((Array.isArray(comerciosData) ? comerciosData : []).map((item) =>
      normalizeOption(
        item?.num ?? item?.Num ?? item?.numComercio ?? item?.NumComercio ?? "",
        item?.numComercio ?? item?.NumComercio ?? item?.num ?? item?.Num ?? ""
      )
    )),
    asesores: dedupeOptions((Array.isArray(asesoresData) ? asesoresData : []).map((item) =>
      normalizeOption(item?.nombre ?? item?.Nombre ?? item, item?.nombre ?? item?.Nombre ?? item)
    )),
  };
}

const resolveMotivoCodigoFromRegla = (reglas = [], reglaId) => {
  const regla = reglas.find((item) => Number(item?.id) === Number(reglaId));
  return regla?.nombreAccion || "";
};

const resolveRuleIdByMotivoCodigo = (reglas = [], motivoCodigo = "") => {
  const normalized = String(motivoCodigo || "").trim().toUpperCase();
  const regla = (Array.isArray(reglas) ? reglas : []).find(
    (item) => String(item?.nombreAccion || "").trim().toUpperCase() === normalized
  );
  return regla?.id ? String(regla.id) : "";
};

const resolveMotivoCodigoFromAlerta = (alerta = {}) => {
  const motivoCodigo = String(alerta?.motivoCodigo || "").trim();
  if (motivoCodigo) return motivoCodigo;
  const tipo = String(alerta?.tipoAlerta || "").toLowerCase();
  if (tipo.includes("zoco check")) return "MAN_ZOCO_CHECK";
  if (tipo.includes("extraviad")) return "MAN_TERMINAL_EXTRAVIADA";
  if (tipo.includes("onboarding")) return "MAN_ONBOARDING";
  if (tipo.includes("alerta operativa")) return "MAN_ALERTA_OPERATIVA";
  if (tipo.includes("roll")) return "FALTA_ROLLOS";
  if (tipo.includes("contacto vencido")) return "PROXIMO_CONTACTO_VENCIDO";
  if (tipo.includes("caida")) return "CAIDA_FACTURACION";
  if (tipo.includes("factur") || tipo.includes("caída") || tipo.includes("caida") || tipo.includes("actividad")) {
    return "FALTA_FACTURACION";
  }
  return "";
};

const filterManualRules = (reglas = []) =>
  (Array.isArray(reglas) ? reglas : []).filter((regla) =>
    String(regla?.nombreAccion || "").toUpperCase().startsWith("MAN_")
    && String(regla?.nombreAccion || "").toUpperCase() !== "MAN_ONBOARDING"
  );

const resolveGestionScopeByReason = (motivoCodigo = "") => {
  const normalized = String(motivoCodigo || "").trim().toUpperCase();
  if (GESTION_SCOPE_RULE_CODES.operativa.has(normalized) || normalized === "FALTA_FACTURACION" || normalized === "CAIDA_FACTURACION") {
    return "operativa";
  }

  if (GESTION_SCOPE_RULE_CODES.proactiva.has(normalized)) {
    return "proactiva";
  }

  return "ipc";
};

const resolveGestionScope = (item = {}) =>
  resolveGestionScopeByReason(item?.motivoCodigo || item?.MotivoCodigo || item?.nombreAccion);

const isOperationalProtocolRule = (motivoCodigo = "") =>
  String(motivoCodigo || "").trim().toUpperCase() === "MAN_ALERTA_OPERATIVA";

const filterGestionRulesByScope = (reglas = [], scope = "ipc") =>
  filterManualRules(reglas).filter((regla) => {
    const reason = String(regla?.nombreAccion || "").trim().toUpperCase();
    if (scope === "operativa") {
      return GESTION_SCOPE_RULE_CODES.operativa.has(reason);
    }

    if (scope === "proactiva") {
      return GESTION_SCOPE_RULE_CODES.proactiva.has(reason);
    }

    return !GESTION_SCOPE_RULE_CODES.operativa.has(reason) && !GESTION_SCOPE_RULE_CODES.proactiva.has(reason);
  });

const canUseOperationalProtocol = (scope, estadoGestion, motivoCodigo) =>
  scope === "operativa" && estadoGestion === "Contactado" && isOperationalProtocolRule(motivoCodigo);

const normalizeWhatsappPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
  if (!digits) return "";
  return digits.startsWith("54") ? digits : `54${digits}`;
};

const buildSuggestedObservation = (item, estadoGestion) => {
  const lines = [
    `Motivo: ${item?.tipoAlerta || item?.motivo || "-"}`,
    `Accion sugerida: ${item?.accionSugerida || "-"}`,
    item?.mensajeSugerido ? `Mensaje sugerido: ${item.mensajeSugerido}` : "",
    item?.resumen ? `Contexto: ${item.resumen}` : "",
    estadoGestion ? `Resultado: ${estadoGestion}` : "",
  ].filter(Boolean);

  return lines.join("\n");
};

const truncateText = (value, max = 120) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, Math.max(0, max - 3)).trimEnd()}...`;
};

const getGestionTypeLabel = (item = {}) => (item?.esAutomatica ? "Automatica" : (item?.tipo || "Manual"));
const buildIpcMotivoCodigo = (tipoIpc, motivoIpc) => {
  const tipoSlug = normalizeText(tipoIpc).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const motivoSlug = normalizeText(motivoIpc).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  if (!tipoSlug || !motivoSlug) {
    return "MAN_IPC";
  }

  return `MAN_IPC_${tipoSlug.toUpperCase()}_${motivoSlug.toUpperCase()}`;
};

const buildIpcObservation = (tipoIpc, motivoIpc, observacion) => {
  const detail = String(observacion || "").trim();
  const lines = [
    tipoIpc ? `Tipo IPC: ${tipoIpc}` : "",
    motivoIpc ? `Motivo IPC: ${motivoIpc}` : "",
    detail,
  ].filter(Boolean);

  return lines.join("\n");
};

const CrmPostventa = () => {
  const { darkMode } = useContext(DarkModeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const apiUrlToken = process.env.REACT_APP_API_TOKEN;

  const [cargandoSesion, setCargandoSesion] = useState(true);
  const [clienteModalOpen, setClienteModalOpen] = useState(false);
  const [clienteSearch, setClienteSearch] = useState("");
  const [clienteResults, setClienteResults] = useState([]);
  const [clienteLoading, setClienteLoading] = useState(false);
  const [clienteError, setClienteError] = useState("");
  const [clienteSeleccionadoId, setClienteSeleccionadoId] = useState(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  });
  const [clientePendiente, setClientePendiente] = useState(null);
  const clienteSearchRequestRef = useRef(0);
  const [terminalesGrupo, setTerminalesGrupo] = useState([]);
  const terminalTabsDragRef = useRef({ isDragging: false, startX: 0, scrollLeft: 0, moved: false });
  const [ficha, setFicha] = useState(null);
  const [fichaEditOpen, setFichaEditOpen] = useState(false);
  const [fichaEditDraft, setFichaEditDraft] = useState({});
  const editFichaTriggerRef = useRef(null);
  const clientEditCloseButtonRef = useRef(null);
  const [fichaCatalogos, setFichaCatalogos] = useState({
    bancos: [],
    rubros: [],
    provincias: [],
    legajos: [],
    comercios: [],
    asesores: [],
  });
  const [fichaCatalogosLoading, setFichaCatalogosLoading] = useState(false);
  const [facturacion, setFacturacion] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [inactivos, setInactivos] = useState(null);
  const [gestiones, setGestiones] = useState(null);
  const [seguimiento, setSeguimiento] = useState(null);
  const [ordenesPago, setOrdenesPago] = useState(null);
  const [ordenPagoDescargandoKey, setOrdenPagoDescargandoKey] = useState("");
  const [cupones, setCupones] = useState(null);
  const [rolloModalOpen, setRolloModalOpen] = useState(false);
  const [altasBajasMode, setAltasBajasMode] = useState("alta");
  const [altaWizardStep, setAltaWizardStep] = useState(1);
  const [altaWizardData, setAltaWizardData] = useState(() => emptyAltaWizardData());
  const [altaWizardLegajos, setAltaWizardLegajos] = useState([]);
  const [altaAgregaSearch, setAltaAgregaSearch] = useState("");
  const [altaAgregaResults, setAltaAgregaResults] = useState([]);
  const [altaAgregaLoading, setAltaAgregaLoading] = useState(false);
  const [altaLegajoDocs, setAltaLegajoDocs] = useState([]);
  const [altaFinalChecklistOpen, setAltaFinalChecklistOpen] = useState(false);
  const [altaFinalChecklistChecks, setAltaFinalChecklistChecks] = useState(() => makeAltaFinalChecklistState());
  const [altaLegajoDocDraft, setAltaLegajoDocDraft] = useState({
    nombreDocumento: ALTA_LEGAJO_DOC_TYPES[0],
    file: null,
  });
  const [altaWizardStatus, setAltaWizardStatus] = useState({
    blanqueo: "",
    legajo: "",
    alta: "",
  });
  const [bajaDraft, setBajaDraft] = useState(() => emptyBajaDraft());
  const [bajaLookupSearch, setBajaLookupSearch] = useState("");
  const [bajaLookupResults, setBajaLookupResults] = useState([]);
  const [bajaLookupLoading, setBajaLookupLoading] = useState(false);
  const [bajaFieldErrors, setBajaFieldErrors] = useState({});
  const bajaLookupRequestRef = useRef(0);
  const [errores, setErrores] = useState({});
  const [cargas, setCargas] = useState({
    ficha: false,
    facturacion: false,
    alertas: false,
    inactivos: false,
    ordenesPago: false,
    cupones: false,
    gestiones: false,
    seguimiento: false,
    guardandoFicha: false,
    guardandoGestion: false,
    guardandoAlertaGestion: false,
    guardandoRollo: false,
    altaBajaCatalogos: false,
    guardandoBlanqueo: false,
    guardandoLegajo: false,
    guardandoDocumentoLegajo: false,
    guardandoAltaWorkflow: false,
    guardandoBajaWorkflow: false,
  });
  const [analyticsRange, setAnalyticsRange] = useState({
    desde: makeMonthStart(),
    hasta: makeToday(),
  });
  const [gestionUnificadaTab, setGestionUnificadaTab] = useState("onboarding");
  const [gestionIpcFormOpen, setGestionIpcFormOpen] = useState(false);
  const [gestionIpcSidebarOpen, setGestionIpcSidebarOpen] = useState(false);
  const [gestionComposerScope, setGestionComposerScope] = useState("ipc");
  const [gestionDraft, setGestionDraft] = useState({
    reglaId: "",
    estadoGestion: "Pendiente",
    tipoIpc: "",
    ipcMotivo: "",
    canalContacto: "",
    resultadoLlamada: "",
    evidenciaContacto: "",
    observacion: "",
    fechaGestion: makeNowDateTimeLocal(),
    fechaProximoContacto: "",
  });
  const [rolloDraft, setRolloDraft] = useState({
    fechaEntrega: makeToday(),
    cantidad: "",
    observacion: "",
  });
  const [alertasFiltros, setAlertasFiltros] = useState({
    fecha: makeToday(),
    estado: "",
    prioridad: "",
    tipo: "",
    provincia: "",
    responsable: "",
    search: "",
  });
  const [alertasSearchDraft, setAlertasSearchDraft] = useState("");
  const [alertasFacturacionSearch, setAlertasFacturacionSearch] = useState("");
  const [alertasTerminalExtraviadaSearch, setAlertasTerminalExtraviadaSearch] = useState("");
  const [alertaHeaderClock, setAlertaHeaderClock] = useState(() => new Date());
  const [alertasCriticasExpandido, setAlertasCriticasExpandido] = useState(false);
  const [alertaCardColapsadas, setAlertaCardColapsadas] = useState(() => new Set());
  const [inactivoCardColapsadas, setInactivoCardColapsadas] = useState(() => new Set());

  useEffect(() => {
    const timer = window.setInterval(() => setAlertaHeaderClock(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const toggleCardColapsada = (setter, id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const [inactivosFiltros, setInactivosFiltros] = useState({
    fecha: makeToday(),
    provincia: "",
    responsable: "",
    diasSinOperar: "",
    search: "",
  });
  const [inactivosSearchDraft, setInactivosSearchDraft] = useState("");
  const [ordenesPagoFiltros, setOrdenesPagoFiltros] = useState({
    fecha: makeToday(),
    search: "",
  });
  const [cuponesFiltros, setCuponesFiltros] = useState({
    mes: makeCurrentMonth(),
    search: "",
  });
  const [herramientasTab, setHerramientasTab] = useState("plazos");
  const [plazosForm, setPlazosForm] = useState({
    fecha: "",
    tipo: "",
    tarjeta: "",
  });
  const [plazosResultado, setPlazosResultado] = useState(null);
  const [plazosError, setPlazosError] = useState("");
  const [plazosCalculando, setPlazosCalculando] = useState(false);
  const [montosCatalogo, setMontosCatalogo] = useState({});
  const [montosCatalogoLoading, setMontosCatalogoLoading] = useState(false);
  const [montosCatalogoError, setMontosCatalogoError] = useState("");
  const [montosForm, setMontosForm] = useState({
    modo: "Neto",
    monto: "",
    tipo: "",
    tarjeta: "",
    cuota: "",
    tipoCuota: "normal",
  });
  const [montosResultado, setMontosResultado] = useState(null);
  const [montosError, setMontosError] = useState("");
  const [montosCalculando, setMontosCalculando] = useState(false);
  const [comparadorForm, setComparadorForm] = useState({
    monto: "",
    terminales: "1",
    tipoPago: "credito",
    cuota: "1",
    competencia: "payway",
    zoco: "1",
  });
  const [comparadorResultado, setComparadorResultado] = useState(null);
  const [comparadorError, setComparadorError] = useState("");
  const [comparadorCalculando, setComparadorCalculando] = useState(false);
  const [ordenesForm, setOrdenesForm] = useState({
    monto: "",
    tipo: "Credito",
    tarjeta: "",
    cuota: "",
    tipoCuota: "bancarizada",
  });
  const [ordenesGuardadas, setOrdenesGuardadas] = useState([]);
  const [ordenesError, setOrdenesError] = useState("");
  const [ordenesFeedback, setOrdenesFeedback] = useState("");
  const [ordenesPreview, setOrdenesPreview] = useState([]);
  const [ordenesPreviewOpen, setOrdenesPreviewOpen] = useState(false);
  const [ordenesPreviewLoading, setOrdenesPreviewLoading] = useState(false);
  const [ordenesPrintingId, setOrdenesPrintingId] = useState("");
  const [ordenesDeletingId, setOrdenesDeletingId] = useState("");
  const [facturacionFiltroComercio, setFacturacionFiltroComercio] = useState("");
  const [facturacionDetalle, setFacturacionDetalle] = useState(null);
  const [facturacionAccionItem, setFacturacionAccionItem] = useState(null);
  const [facturacionCuitCopiadoKey, setFacturacionCuitCopiadoKey] = useState("");
  const [facturacionGestionItem, setFacturacionGestionItem] = useState(null);
  const [facturacionGestionDraft, setFacturacionGestionDraft] = useState({
    clienteId: 0,
    reglaId: "",
    estadoGestion: "Pendiente",
    canalContacto: "",
    resultadoLlamada: "",
    evidenciaContacto: "",
    observacion: "",
    fechaGestion: makeNowDateTimeLocal(),
    fechaProximoContacto: "",
  });
  const [timelineDetalle, setTimelineDetalle] = useState(null);
  const [seguimientoExpanded, setSeguimientoExpanded] = useState(false);
  const [gestionDetalleItem, setGestionDetalleItem] = useState(null);
  const [gestionEditItem, setGestionEditItem] = useState(null);
  const [gestionEditDraft, setGestionEditDraft] = useState({
    estadoResultante: "Pendiente",
    fechaProximoContacto: "",
    prioridad: "Media",
    observacion: "",
    canalContacto: "",
    resultadoLlamada: "",
    evidenciaContacto: "",
    resultadoZocoCheck: "",
  });
  const [gestionFinalizarItem, setGestionFinalizarItem] = useState(null);
  const [gestionFinalizarDraft, setGestionFinalizarDraft] = useState({
    estadoResultante: "Cerrado",
    motivoCierre: "Resuelto",
    observacionFinal: "",
    resultadoZocoCheck: "",
    resultadoLlamada: "",
    evidenciaContacto: "",
  });
  const [gestionesRollosExpanded, setGestionesRollosExpanded] = useState(false);
  const [alertaGestionDraft, setAlertaGestionDraft] = useState({
    clienteId: 0,
    reglaId: "",
    estadoGestion: "Pendiente",
    canalContacto: "",
    resultadoLlamada: "",
    evidenciaContacto: "",
    observacion: "",
    fechaGestion: makeNowDateTimeLocal(),
    fechaProximoContacto: "",
  });
  const [alertaSeleccionada, setAlertaSeleccionada] = useState(null);
  const [alertaGestionMode, setAlertaGestionMode] = useState("operativa");
  const [alertaAccionItem, setAlertaAccionItem] = useState(null);

  const section = useMemo(() => {
    if (location.pathname.includes("/facturacion")) return "facturacion";
    if (location.pathname.includes("/alertas")) return "alertas";
    if (location.pathname.includes("/ordenes-pago")) return "ordenes-pago";
    if (location.pathname.includes("/cupones")) return "cupones";
    if (location.pathname.includes("/ficha")) return "ficha";
    if (location.pathname.includes("/herramientas")) return "herramientas";
    if (location.pathname.includes("/altas-bajas")) return "altas-bajas";
    if (location.pathname.includes("/blanqueos")) return "blanqueos";
    if (location.pathname.includes("/campanias-zoco-check")) return "campanias-zoco-check";
    if (location.pathname.includes("/gestion")) return "gestion";
    if (location.pathname.includes("/modales")) return "modales";
    if (location.pathname.includes("/seguimiento")) return "seguimiento";
    return "alertas";
  }, [location.pathname]);
  const sessionPayload = useMemo(() => decodeTokenPayload(getToken()), []);
  const canManageZocoCheck = useMemo(() => {
    const rawRole = sessionPayload?.rol ?? sessionPayload?.role ?? sessionPayload?.Role ?? "";
    const roleNumber = Number(rawRole);
    const roleText = String(rawRole || "").toLowerCase();

    return roleNumber === 8
      || roleText.includes("admin")
      || roleText.includes("comercial")
      || roleText.includes("marketing");
  }, [sessionPayload]);

  const clienteGrupos = useMemo(() => agruparClientesPorGrupo(clienteResults), [clienteResults]);
  const currentGroupKey = useMemo(
    () => buildClienteGroupKey(clientePendiente || ficha?.header || {}),
    [clientePendiente, ficha?.header]
  );
  const clienteSeleccionadoData = useMemo(() => clientePendiente || ficha?.header || {}, [clientePendiente, ficha?.header]);
  const clienteSeleccionadoCuit = useMemo(
    () => normalizeDigits(clienteSeleccionadoData?.cuit || clienteSeleccionadoData?.Cuit),
    [clienteSeleccionadoData]
  );
  const manualAlertaRules = useMemo(() => filterGestionRulesByScope(alertas?.reglas || [], "operativa"), [alertas?.reglas]);
  const inactivosProactivaRules = useMemo(
    () =>
      filterGestionRulesByScope(inactivos?.reglas || alertas?.reglas || gestiones?.reglas || [], "proactiva").filter((item) =>
        INACTIVOS_PROACTIVA_RULE_CODES.has(String(item?.nombreAccion || "").trim().toUpperCase())
      ),
    [alertas?.reglas, gestiones?.reglas, inactivos?.reglas]
  );
  const gestionIpcRules = useMemo(() => filterGestionRulesByScope(gestiones?.reglas || [], "ipc"), [gestiones?.reglas]);
  const gestionOperativaRules = useMemo(() => filterGestionRulesByScope(gestiones?.reglas || [], "operativa"), [gestiones?.reglas]);
  const gestionProactivaRules = useMemo(() => filterGestionRulesByScope(gestiones?.reglas || [], "proactiva"), [gestiones?.reglas]);
  const gestionScopeRules = useMemo(() => ({
    ipc: gestionIpcRules,
    operativa: gestionOperativaRules,
    proactiva: gestionProactivaRules,
  }), [gestionIpcRules, gestionOperativaRules, gestionProactivaRules]);
  const currentGestionRules = useMemo(
    () => gestionScopeRules[gestionComposerScope] || [],
    [gestionComposerScope, gestionScopeRules]
  );
  const currentGestionStates = useMemo(
    () => GESTION_SCOPE_STATES[gestionComposerScope] || GESTION_STATES,
    [gestionComposerScope]
  );
  const currentIpcMotivoOptions = useMemo(
    () => GESTION_IPC_REASON_OPTIONS[gestionDraft.tipoIpc] || [],
    [gestionDraft.tipoIpc]
  );
  const gestionDraftMotivoCodigo = useMemo(
    () =>
      gestionComposerScope === "ipc"
        ? buildIpcMotivoCodigo(gestionDraft.tipoIpc, gestionDraft.ipcMotivo)
        : resolveMotivoCodigoFromRegla(gestiones?.reglas || [], gestionDraft.reglaId),
    [gestionComposerScope, gestionDraft.ipcMotivo, gestionDraft.reglaId, gestionDraft.tipoIpc, gestiones?.reglas]
  );
  const protocoloContacto = gestiones?.protocoloContacto || null;
  const usaProtocoloOperativo = useMemo(
    () => canUseOperationalProtocol(gestionComposerScope, gestionDraft.estadoGestion, gestionDraftMotivoCodigo),
    [gestionComposerScope, gestionDraft.estadoGestion, gestionDraftMotivoCodigo]
  );
  const canalesProtocoloHabilitados = useMemo(
    () => (usaProtocoloOperativo ? (protocoloContacto?.canalesHabilitados || []) : []),
    [protocoloContacto?.canalesHabilitados, usaProtocoloOperativo]
  );
  const gestionesPorScope = useMemo(() => {
    const historial = Array.isArray(gestiones?.historial) ? gestiones.historial : [];
    return {
      ipc: historial.filter((item) => resolveGestionScope(item) === "ipc"),
      operativa: historial.filter((item) => resolveGestionScope(item) === "operativa"),
      proactiva: historial.filter((item) => resolveGestionScope(item) === "proactiva"),
    };
  }, [gestiones?.historial]);

  useEffect(() => {
    setGestionDraft((prev) => ({
      ...prev,
      reglaId:
        currentGestionRules?.[0]?.id && !currentGestionRules.some((item) => String(item.id) === String(prev.reglaId))
          ? String(currentGestionRules[0].id)
          : prev.reglaId || (currentGestionRules?.[0]?.id ? String(currentGestionRules[0].id) : ""),
      estadoGestion: currentGestionStates.includes(prev.estadoGestion) ? prev.estadoGestion : (currentGestionStates[0] || "Pendiente"),
    }));
  }, [currentGestionRules, currentGestionStates]);

  useEffect(() => {
    if (!usaProtocoloOperativo) {
      return;
    }

    setGestionDraft((prev) => {
      const canalActual = String(prev.canalContacto || "");
      if (!canalActual || canalesProtocoloHabilitados.includes(canalActual)) {
        return prev;
      }

      return {
        ...prev,
        canalContacto: "",
        resultadoLlamada: "",
        evidenciaContacto: "",
      };
    });
  }, [canalesProtocoloHabilitados, usaProtocoloOperativo]);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem("token");
    navigate("/");
    window.location.reload();
  }, [navigate]);

  const copiarMensajeSugerido = useCallback(async (item, title = "CRM Postventa") => {
    const mensaje = String(item?.mensajeSugerido || "").trim();
    if (!mensaje) {
      Swal.fire(title, "No hay mensaje sugerido para copiar.", "info");
      return;
    }

    try {
      const reglaIdToSave = gestionComposerScope === "ipc"
        ? Number(currentGestionRules?.[0]?.id || gestionDraft.reglaId)
        : Number(gestionDraft.reglaId);
      const observacionToSave = gestionComposerScope === "ipc"
        ? buildIpcObservation(gestionDraft.tipoIpc, gestionDraft.ipcMotivo, gestionDraft.observacion)
        : gestionDraft.observacion;
      const motivoCodigoToSave = gestionComposerScope === "ipc"
        ? buildIpcMotivoCodigo(gestionDraft.tipoIpc, gestionDraft.ipcMotivo)
        : resolveMotivoCodigoFromRegla(gestiones?.reglas || [], gestionDraft.reglaId);

      if (!reglaIdToSave) {
        Swal.fire("Gestion", "No hay una regla IPC disponible para guardar la gestion.", "info");
        return;
      }
      await navigator.clipboard.writeText(mensaje);
      Swal.fire({
        icon: "success",
        title: "Mensaje copiado",
        text: "El mensaje sugerido quedo en el portapapeles.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire(title, "No se pudo copiar el mensaje sugerido.", "error");
    }
  }, []);

  const copyToClipboard = useCallback(async (texto) => {
    const value = String(texto || "").trim();
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      Swal.fire({
        icon: "success",
        title: "Mensaje copiado",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("CRM Postventa", "No se pudo copiar el mensaje.", "error");
    }
  }, []);

  const abrirWhatsappSugerido = useCallback((item, title = "CRM Postventa") => {
    const mensaje = String(item?.mensajeSugerido || "").trim();
    const telefono = resolvePreferredWhatsappPhone(item);

    if (!telefono) {
      Swal.fire(title, "El cliente no tiene telefono cargado.", "info");
      return;
    }

    const url = `https://wa.me/${telefono}?text=${encodeURIComponent(mensaje)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const descargarOrdenPago = useCallback(async (item) => {
    if (!item?.documentoKey || !item?.fechaSubida) {
      Swal.fire("Órdenes de pago", "La orden seleccionada no tiene los datos necesarios para descargar el PDF.", "info");
      return;
    }

    try {
      const payload = await crmPostventaApi.obtenerOrdenPagoPdf(clienteSeleccionadoId, item.fechaSubida, item.documentoKey);
      await descargarOrdenPagoDesdePayload({ fechaPago: item.fechaSubida, payload });
    } catch (error) {
      console.error("Error descargando orden de pago:", error);
      Swal.fire("Órdenes de pago", error.message || "No se pudo descargar el PDF.", "error");
    }
  }, [clienteSeleccionadoId]);

  const descargarOrdenPagoCompacta = useCallback(async (item) => {
    if (!item?.documentoKey || !item?.fechaSubida || ordenPagoDescargandoKey === item.documentoKey) {
      return;
    }

    setOrdenPagoDescargandoKey(item.documentoKey);
    try {
      const payload = await crmPostventaApi.obtenerOrdenPagoPdf(clienteSeleccionadoId, item.fechaSubida, item.documentoKey);
      await descargarOrdenPagoDesdePayload({ fechaPago: item.fechaSubida, payload });
    } catch (error) {
      console.error("Error descargando orden de pago:", error);
      Swal.fire("Ordenes de pago", error.message || "No se pudo descargar el PDF.", "error");
    } finally {
      setOrdenPagoDescargandoKey("");
    }
  }, [clienteSeleccionadoId, ordenPagoDescargandoKey]);

  const mostrarAccesoDenegado = useCallback(
    (title, text) => {
      Swal.fire({
        title,
        text,
        imageUrl: logo,
        imageWidth: 100,
        imageHeight: 30,
        imageAlt: "Logo",
        confirmButtonText: "Ok",
        timer: 5000,
        allowOutsideClick: true,
        customClass: {
          container: darkMode ? "swal2-container--dark" : "swal2-container--light",
          confirmButton: "my-swal-button",
        },
      }).then(() => clearSession());
    },
    [clearSession, darkMode]
  );

  const validarSesion = useCallback(async () => {
    const token = getToken();
    if (!token) {
      mostrarAccesoDenegado("Sesión expirada o token inválido.", "Inicie sesión nuevamente.");
      return;
    }

    try {
      const response = await fetch(apiUrlToken, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ Token: token }),
      });

      if (response.status === 401 || response.status === 403) {
        mostrarAccesoDenegado("Sesión expirada o token inválido.", "Inicie sesión nuevamente.");
        return;
      }

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const raw = await response.text();
      let rol = Number(raw);
      if (!Number.isFinite(rol)) {
        rol = Number(JSON.parse(raw));
      }

      if (rol !== 8) {
        Swal.fire({
          title: "Acceso denegado.",
          text: "No tiene permisos para este panel.",
          imageUrl: logo,
          imageWidth: 100,
          imageHeight: 30,
          imageAlt: "Logo",
          confirmButtonText: "Ok",
          timer: 5000,
          allowOutsideClick: true,
          customClass: {
            container: darkMode ? "swal2-container--dark" : "swal2-container--light",
            confirmButton: "my-swal-button",
          },
        }).then(() => navigate("/"));
        return;
      }
    } catch (error) {
      console.error("Error validando sesión CRM Postventa:", error);
      Swal.fire("Error", "No se pudo validar la sesión en este momento.", "error");
      return;
    }

    setCargandoSesion(false);
  }, [apiUrlToken, darkMode, mostrarAccesoDenegado, navigate]);

  const cargarClientes = useCallback(async (searchValue = "") => {
    const requestId = ++clienteSearchRequestRef.current;
    setClienteLoading(true);
    setClienteError("");
    try {
      const data = await crmPostventaApi.buscarClientes(searchValue);
      if (requestId !== clienteSearchRequestRef.current) {
        return;
      }
      setClienteResults(Array.isArray(data) ? data : []);
    } catch (error) {
      if (requestId !== clienteSearchRequestRef.current) {
        return;
      }
      console.error("Error buscando clientes:", error);
      setClienteResults([]);
      setClienteError("No pudimos cargar los clientes.");
    } finally {
      if (requestId === clienteSearchRequestRef.current) {
        setClienteLoading(false);
      }
    }
  }, []);

  const cargarAlertas = useCallback(async (filtrosActuales) => {
    setCargas((prev) => ({ ...prev, alertas: true }));
    try {
      const data = await crmPostventaApi.obtenerAlertas(filtrosActuales);
      setAlertas(normalizeAlertasResponse(data));
      setErrores((prev) => ({ ...prev, alertas: undefined }));
    } catch (error) {
      console.error("Error cargando alertas:", error);
      setAlertas(null);
      setErrores((prev) => ({ ...prev, alertas: error.message || "No se pudieron cargar las alertas." }));
    } finally {
      setCargas((prev) => ({ ...prev, alertas: false }));
    }
  }, []);

  const cargarInactivos = useCallback(async (filtrosActuales) => {
    setCargas((prev) => ({ ...prev, inactivos: true }));
    try {
      console.log("[CRM Postventa][Inactivos] Request", filtrosActuales);
      const data = await crmPostventaApi.obtenerInactivos(filtrosActuales);
      console.log("[CRM Postventa][Inactivos] Response", data);
      setInactivos(data);
      setErrores((prev) => ({ ...prev, inactivos: undefined }));
    } catch (error) {
      console.error("Error cargando comercios inactivos:", error);
      setInactivos(null);
      setErrores((prev) => ({ ...prev, inactivos: error.message || "No se pudieron cargar los comercios inactivos." }));
    } finally {
      setCargas((prev) => ({ ...prev, inactivos: false }));
    }
  }, []);

  const cargarOrdenesPago = useCallback(async (clienteId, filtrosActuales) => {
    if (!clienteId) {
      return;
    }

    setCargas((prev) => ({ ...prev, ordenesPago: true }));
    try {
      const fechaConsultada = filtrosActuales?.fecha || makeToday();
      const search = String(filtrosActuales?.search || "").trim();
      const response = await crmPostventaApi.obtenerOrdenesPago(clienteId, fechaConsultada, search);
      const filas = Array.isArray(response?.filas) ? response.filas : Array.isArray(response?.Filas) ? response.Filas : [];

      setOrdenesPago({
        ...response,
        filas,
      });
      setErrores((prev) => ({ ...prev, ordenesPago: undefined }));
    } catch (error) {
      console.error("Error cargando ordenes de pago:", error);
      setOrdenesPago(null);
      setErrores((prev) => ({ ...prev, ordenesPago: error.message || "No se pudieron cargar las ordenes de pago." }));
    } finally {
      setCargas((prev) => ({ ...prev, ordenesPago: false }));
    }
  }, []);

  const cargarCupones = useCallback(async (clienteId, filtrosActuales) => {
    if (!clienteId) {
      return;
    }

    setCargas((prev) => ({ ...prev, cupones: true }));
    try {
      const { mes, desde, hasta } = buildMonthDateRange(filtrosActuales?.mes);
      const search = String(filtrosActuales?.search || "").trim();
      const response = await crmPostventaApi.obtenerCupones(clienteId, desde, hasta, search);
      const rows = Array.from(
        (Array.isArray(response?.filas) ? response.filas : Array.isArray(response?.Filas) ? response.Filas : []).reduce((acc, item) => {
          const normalized = normalizeCuponLegacyRow(item);
          acc.set(buildCuponDedupeKey(normalized), normalized);
          return acc;
        }, new Map()).values()
      );
      const resumen = buildCuponSummary(rows);
      const backendMessage = sanitizeCuponesMessage(response?.message ?? response?.Message);

      setCupones({
        tieneDatosReales: Boolean(response?.tieneDatosReales ?? response?.TieneDatosReales ?? rows.length > 0),
        message: rows.length > 0 ? "" : backendMessage || "No hay cupones para este cliente/terminal.",
        mesConsultado: formatLegacyMonthLabel(mes),
        criterioFiltrado: (response?.cuitFiltrado ?? response?.CuitFiltrado ?? clienteSeleccionadoCuit) || "-",
        fechaDesde: response?.fechaDesde ?? response?.FechaDesde ?? desde,
        fechaHasta: response?.fechaHasta ?? response?.FechaHasta ?? hasta,
        resumen,
        filas: rows,
      });
      setErrores((prev) => ({ ...prev, cupones: undefined }));
    } catch (error) {
      console.error("Error cargando cupones:", error);
      setCupones(null);
      setErrores((prev) => ({
        ...prev,
        cupones: sanitizeCuponesMessage(error.message) || "No se pudieron cargar los cupones.",
      }));
    } finally {
      setCargas((prev) => ({ ...prev, cupones: false }));
    }
  }, [clienteSeleccionadoCuit]);

  const cargarModulo = useCallback(async (clienteId, range) => {
    if (!clienteId) {
      return;
    }

    setCargas((prev) => ({
      ...prev,
      ficha: true,
      facturacion: true,
      gestiones: true,
      seguimiento: true,
      guardandoGestion: false,
    }));
    setErrores({});

    const results = await Promise.allSettled([
      crmPostventaApi.obtenerFicha(clienteId),
      crmPostventaApi.obtenerFacturacion(clienteId, range.desde, range.hasta),
      crmPostventaApi.obtenerGestiones(clienteId),
      crmPostventaApi.obtenerSeguimiento(clienteId, range.desde, range.hasta),
    ]);

    const [fichaRes, facturacionRes, gestionesRes, seguimientoRes] = results;

    if (fichaRes.status === "fulfilled") {
      setFicha(fichaRes.value);
    } else {
      setFicha(null);
      setErrores((prev) => ({ ...prev, ficha: fichaRes.reason?.message || "No se pudo cargar la ficha." }));
    }

    if (facturacionRes.status === "fulfilled") {
      setFacturacion(facturacionRes.value);
    } else {
      setFacturacion(null);
      setErrores((prev) => ({ ...prev, facturacion: facturacionRes.reason?.message || "No se pudo cargar facturación." }));
    }

    if (gestionesRes.status === "fulfilled") {
      let gestionesValue = gestionesRes.value;
      const clienteKey =
        gestionesRes.value?.rollos?.clienteKey ||
        fichaRes.value?.header?.clienteKey ||
        crmPostventaApi.buildClienteKey(fichaRes.value?.header || {});

      if (clienteKey) {
        try {
          const rollos = await crmPostventaApi.obtenerRollos(clienteKey, clienteId);
          gestionesValue = {
            ...gestionesValue,
            rollos,
          };
        } catch (error) {
          console.error("Error cargando rollos:", error);
        }
      }

      setGestiones(gestionesValue);
      setGestionDraft((prev) => ({
        ...prev,
        reglaId: filterManualRules(gestionesValue?.reglas || [])?.[0]?.id ? String(filterManualRules(gestionesValue?.reglas || [])[0].id) : "",
      }));
    } else {
      setGestiones(null);
      setErrores((prev) => ({ ...prev, gestiones: gestionesRes.reason?.message || "No se pudieron cargar las gestiones." }));
    }

    if (seguimientoRes.status === "fulfilled") {
      setSeguimiento(seguimientoRes.value);
    } else {
      setSeguimiento(null);
      setErrores((prev) => ({ ...prev, seguimiento: seguimientoRes.reason?.message || "No se pudo cargar el seguimiento." }));
    }

    const terminalesDesdeFacturacion =
      facturacionRes.status === "fulfilled"
        ? buildTerminalesFromFacturacion(facturacionRes.value?.filas || [])
        : [];
    const terminalesDesdeFicha =
      fichaRes.status === "fulfilled"
        ? normalizeTerminalOptions(fichaRes.value?.header?.terminalesGrupo || [])
        : [];

    setTerminalesGrupo(mergeTerminalOptions(terminalesDesdeFicha, terminalesDesdeFacturacion));

    setCargas((prev) => ({
      ...prev,
      ficha: false,
      facturacion: false,
      gestiones: false,
      seguimiento: false,
    }));
  }, []);

  const seleccionarCliente = useCallback(
    (cliente) => {
      if (!cliente?.clienteId) {
        return;
      }

      localStorage.setItem(STORAGE_KEY, String(cliente.clienteId));
      setClienteSeleccionadoId(cliente.clienteId);
      setClientePendiente(cliente);
      const terminales = normalizeTerminalOptions(cliente.terminales || []);
      if (terminales.length) {
        setTerminalesGrupo(terminales);
      }
      setClienteModalOpen(false);
      setFacturacionFiltroComercio("");
      setFacturacionDetalle(null);
      setTimelineDetalle(null);
      setOrdenesPago(null);
      setCupones(null);
      navigate(SECTION_PATHS[section] || SECTION_PATHS.alertas);
    },
    [navigate, section]
  );

  const activateAltaMode = useCallback((mode = "alta") => {
    const isAgregaMode = mode === "agrega";
    const baseData = emptyAltaWizardData();
    setAltasBajasMode(mode);
    setAltaWizardStep(isAgregaMode ? 3 : 1);
    setAltaFinalChecklistOpen(false);
    setAltaFinalChecklistChecks(makeAltaFinalChecklistState());
    setAltaWizardData({
      ...baseData,
      alta: {
        ...baseData.alta,
        tipoAlta: isAgregaMode ? "AgregaTerminal" : "NuevoAliado",
        altas: isAgregaMode ? "AGREGA" : "ALTA NUEVA",
      },
    });
    setAltaWizardLegajos([]);
    setAltaAgregaSearch("");
    setAltaAgregaResults([]);
    setAltaAgregaLoading(false);
    setAltaLegajoDocs([]);
    setAltaFinalChecklistOpen(false);
    setAltaFinalChecklistChecks(makeAltaFinalChecklistState());
    setAltaLegajoDocDraft({
      nombreDocumento: ALTA_LEGAJO_DOC_TYPES[0],
      file: null,
    });
    setAltaWizardStatus({
      blanqueo: "",
      legajo: "",
      alta: "",
    });
  }, []);

  const resetAltasBajas = useCallback(() => {
    setAltasBajasMode("alta");
    setAltaWizardStep(1);
    setAltaWizardData(emptyAltaWizardData());
    setAltaWizardLegajos([]);
    setAltaAgregaSearch("");
    setAltaAgregaResults([]);
    setAltaAgregaLoading(false);
    setAltaLegajoDocs([]);
    setAltaFinalChecklistOpen(false);
    setAltaFinalChecklistChecks(makeAltaFinalChecklistState());
    setAltaLegajoDocDraft({
      nombreDocumento: ALTA_LEGAJO_DOC_TYPES[0],
      file: null,
    });
    setAltaWizardStatus({
      blanqueo: "",
      legajo: "",
      alta: "",
    });
    setGestionComposerScope("ipc");
    setBajaDraft(emptyBajaDraft());
    setBajaLookupSearch("");
    setBajaLookupResults([]);
  }, []);

  const aplicarLegajoAlWizard = useCallback((legajoItem) => {
    if (!legajoItem) {
      return;
    }

    setAltaWizardData((prev) => ({
      ...prev,
      legajoId: legajoItem.id ?? legajoItem.Id ?? prev.legajoId,
      legajo: {
        ...prev.legajo,
        nroLegajo: String(legajoItem.nroLegajo ?? legajoItem.NroLegajo ?? prev.legajo.nroLegajo ?? ""),
        terminal: String(legajoItem.terminal ?? legajoItem.Terminal ?? prev.legajo.terminal ?? ""),
        nombreDeFantasia: legajoItem.nombreDeFantasia ?? legajoItem.NombreDeFantasia ?? prev.legajo.nombreDeFantasia,
        provincia: legajoItem.provincia ?? legajoItem.Provincia ?? prev.legajo.provincia,
        tipoPersona: String(legajoItem.tipoPersona ?? legajoItem.TipoPersona ?? prev.legajo.tipoPersona ?? "fisica").toLowerCase(),
        razonSocial: legajoItem.razonSocial ?? legajoItem.RazonSocial ?? prev.legajo.razonSocial,
        calle: legajoItem.calle ?? legajoItem.Calle ?? prev.legajo.calle,
        numero: legajoItem.numero ?? legajoItem.Numero ?? prev.legajo.numero,
        pisoDepto: legajoItem.pisoDepto ?? legajoItem.PisoDepto ?? prev.legajo.pisoDepto,
        localidad: legajoItem.localidad ?? legajoItem.Localidad ?? prev.legajo.localidad,
        codigoPostal: legajoItem.codigoPostal ?? legajoItem.CodigoPostal ?? prev.legajo.codigoPostal,
        emailContacto: legajoItem.emailContacto ?? legajoItem.EmailContacto ?? prev.legajo.emailContacto,
        celularWhatsapp: legajoItem.celularWhatsapp ?? legajoItem.CelularWhatsapp ?? prev.legajo.celularWhatsapp,
        asesor: legajoItem.asesor ?? legajoItem.Asesor ?? prev.legajo.asesor,
      },
      alta: {
        ...prev.alta,
        correo: prev.alta.correo || legajoItem.emailContacto || legajoItem.EmailContacto || prev.usuario.email,
        celular: prev.alta.celular || legajoItem.celularWhatsapp || legajoItem.CelularWhatsapp,
        localidadTitular: prev.alta.localidadTitular || legajoItem.localidad || legajoItem.Localidad || "",
        codigoPostalTitular: prev.alta.codigoPostalTitular || legajoItem.codigoPostal || legajoItem.CodigoPostal || "",
        provincia: prev.alta.provincia || legajoItem.provincia || legajoItem.Provincia || "",
        provincia3: prev.alta.provincia3 || legajoItem.provincia || legajoItem.Provincia || "",
        nombreDelAsesor: prev.alta.nombreDelAsesor || legajoItem.asesor || legajoItem.Asesor || "",
        domicilioComercio:
          prev.alta.domicilioComercio ||
          [legajoItem.calle ?? legajoItem.Calle, legajoItem.numero ?? legajoItem.Numero]
            .filter(Boolean)
            .join(" "),
        localidadComercio: prev.alta.localidadComercio || legajoItem.localidad || legajoItem.Localidad || "",
        codigoPostalComercio: prev.alta.codigoPostalComercio || legajoItem.codigoPostal || legajoItem.CodigoPostal || "",
        titularRazonSocial: prev.alta.titularRazonSocial || legajoItem.razonSocial || legajoItem.RazonSocial || prev.usuario.nombre,
        alicuota:
          prev.alta.alicuota ||
          findProvinciaAlicuota(
            fichaCatalogos.provincias,
            prev.alta.provincia3 || legajoItem.provincia || legajoItem.Provincia || ""
          ),
      },
    }));
  }, [fichaCatalogos.provincias]);

  const aplicarBaseAgregaDesdeFicha = useCallback((clienteBase, fichaBase) => {
    if (!clienteBase || !fichaBase) {
      return;
    }

    const valores = buildFichaValueMapByLabel(fichaBase);
    const valor = (...keys) => pickFichaMapValue(valores, ...keys);
    const comercioBase = sanitizeLegacyTextValue(
      fichaBase?.header?.nDeComercio ||
      fichaBase?.header?.NDeComercio ||
      clienteBase?.nDeComercio ||
      clienteBase?.NDeComercio ||
      valor("N° de comercio", "nDeComercio", "comercio")
    );
    const provinciaContacto = sanitizeLegacyTextValue(valor("provincia"));
    const provinciaFiscal = sanitizeLegacyTextValue(valor("provincia fiscal", "provincia3"));
    const provinciaBase = provinciaContacto || provinciaFiscal;
    const asesorBase = sanitizeLegacyTextValue(valor("nombre del asesor", "nombreDelAsesor", "asesor"));
    const codigoPostalBase = sanitizeLegacyNumericValue(valor("código postal", "codigo postal", "codigoPostal", "codigo postal comercio"));
    const codActividadRentasBase = sanitizeLegacyNumericValue(
      valor("cód. actividad rentas", "cod. actividad rentas", "cod actividad rentas", "codActividadRentas", "código actividad rentas")
    );
    const numeroCuentaBase = sanitizeLegacyNumericValue(
      valor("n.° de cuenta", "n° de cuenta", "n de cuenta", "numero de cuenta", "número de cuenta", "nDeCuenta", "numeroCuenta")
    );
    const provinciaValue = resolveOptionValue(fichaCatalogos.provincias, provinciaBase);
    const provinciaFiscalValue = resolveOptionValue(fichaCatalogos.provincias, provinciaFiscal || provinciaBase);
    const asesorValue = resolveOptionValue(fichaCatalogos.asesores, asesorBase);
    const bancoValue = resolveOptionValue(fichaCatalogos.bancos, valor("banco"));
    const tipoCuentaValue = resolveOptionValue(LEGACY_ALTA_TIPO_CUENTA_OPTIONS, valor("tipo de cuenta", "tipoDeCuenta"));
    const rubroValue = resolveOptionValue(fichaCatalogos.rubros, valor("rubro"));
    const rubroSecundarioValue = resolveOptionValue(fichaCatalogos.rubros, valor("rubro secundario", "rubroSecundario"));
    const comercioValue = resolveOptionValue(fichaCatalogos.comercios, comercioBase) || comercioBase;
    setAltaWizardData((prev) => ({
      ...prev,
      agregaBaseClienteId: Number(clienteBase?.clienteId || clienteBase?.ClienteId || fichaBase?.header?.clienteId || 0) || null,
      usuario: {
        ...prev.usuario,
        cuit: normalizeCuitValue(
          fichaBase?.header?.cuit ||
          clienteBase?.cuit ||
          clienteBase?.Cuit ||
          valores["cuit/cuil"] ||
          valores.cuit ||
          prev.usuario.cuit
        ),
        nombre:
          sanitizeLegacyTextValue(
            fichaBase?.header?.razonSocial ||
            fichaBase?.header?.nombreDeFantasia ||
            clienteBase?.razonSocial ||
            clienteBase?.RazonSocial ||
            clienteBase?.nombreDeFantasia ||
            clienteBase?.NombreDeFantasia ||
            valores["razon social"] ||
            valores["nombre de fantasia"]
          ) || prev.usuario.nombre,
        email:
          sanitizeLegacyTextValue(
            fichaBase?.header?.mail ||
            clienteBase?.mail ||
            clienteBase?.Mail ||
            valores.mail
          ) || prev.usuario.email,
      },
      legajo: {
        ...prev.legajo,
        nroLegajo:
          sanitizeLegacyNumericValue(
            fichaBase?.header?.nroLegajo ||
            clienteBase?.nroLegajo ||
            valores.legajo ||
            valores["nro legajo"] ||
            valores["n° legajo"]
          ) || prev.legajo.nroLegajo,
        nombreDeFantasia:
          sanitizeLegacyTextValue(fichaBase?.header?.nombreDeFantasia) ||
          sanitizeLegacyTextValue(valores["nombre de fantasia"]) ||
          prev.legajo.nombreDeFantasia,
        razonSocial:
          sanitizeLegacyTextValue(fichaBase?.header?.razonSocial) ||
          sanitizeLegacyTextValue(valores["razon social"]) ||
          prev.legajo.razonSocial,
        provincia: provinciaValue || prev.legajo.provincia,
        calle: sanitizeLegacyTextValue(valor("domicilio", "dirección", "direccion")) || prev.legajo.calle,
        localidad: sanitizeLegacyTextValue(valor("localidad")) || prev.legajo.localidad,
        codigoPostal: codigoPostalBase || prev.legajo.codigoPostal,
        emailContacto: sanitizeLegacyTextValue(valor("mail", "email")) || prev.legajo.emailContacto,
        celularWhatsapp: normalizeEditablePhoneValue(valor("teléfono 1", "telefono 1", "telefono") || prev.legajo.celularWhatsapp),
        asesor: asesorValue || prev.legajo.asesor,
      },
      alta: {
        ...prev.alta,
        celular: normalizeEditablePhoneValue(valores["telefono 1"] || prev.alta.celular || prev.legajo.celularWhatsapp),
        referenciaTelefono: sanitizeLegacyTextValue(valores["puesto / cargo / referencia 1"] || prev.alta.referenciaTelefono),
        telefonoAlternativo: normalizeEditablePhoneValue(valores["telefono 2"] || prev.alta.telefonoAlternativo || prev.alta.telFijo),
        telFijo: normalizeEditablePhoneValue(valores["telefono 2"] || prev.alta.telFijo),
        referenciaTelefonoAlternativo: sanitizeLegacyTextValue(
          valores["puesto / cargo / referencia 2"] || prev.alta.referenciaTelefonoAlternativo
        ),
        correo: sanitizeLegacyTextValue(valores.mail) || prev.alta.correo || prev.legajo.emailContacto,
        apoderado: sanitizeLegacyTextValue(valores["puesto / cargo / referencia 1"] || prev.alta.apoderado),
        domicilioComercio: sanitizeLegacyTextValue(valor("domicilio", "dirección", "direccion")) || prev.alta.domicilioComercio,
        localidadComercio: sanitizeLegacyTextValue(valor("localidad")) || prev.alta.localidadComercio,
        codigoPostalComercio: codigoPostalBase || prev.alta.codigoPostalComercio,
        provincia: provinciaValue || prev.alta.provincia,
        provincia3: provinciaFiscalValue || prev.alta.provincia3,
        linkDePago: sanitizeLegacyTextValue(valores["link de pagos"] || prev.alta.linkDePago),
        banco: bancoValue || prev.alta.banco,
        tipoCuenta: tipoCuentaValue || prev.alta.tipoCuenta,
        cbucvu: numeroCuentaBase || sanitizeLegacyNumericValue(valor("cbu/cvu", "cbuCvu", "cbu cvu")) || prev.alta.cbucvu,
        alias: sanitizeLegacyTextValue(valores.alias) || prev.alta.alias,
        cuitbancario:
          normalizeCuitValue(valores["cuit razon social"] || prev.alta.cuitbancario || prev.usuario.cuit) || prev.alta.cuitbancario,
        titularRazonSocial: sanitizeLegacyTextValue(valores["razon social"] || prev.alta.titularRazonSocial),
        numeroComericoActual: comercioValue || prev.alta.numeroComericoActual,
        numeroComercioSolicitado: comercioValue || prev.alta.numeroComercioSolicitado,
        rubro: rubroValue || prev.alta.rubro,
        rubroSecundario: rubroSecundarioValue || prev.alta.rubroSecundario,
        codActividad: sanitizeLegacyNumericValue(valores["cod. actividad"] || prev.alta.codActividad),
        actividad: sanitizeLegacyTextValue(valores.actividad) || prev.alta.actividad,
        actividad2: sanitizeLegacyTextValue(valores["actividad 2"] || prev.alta.actividad2),
        estadoAfip: sanitizeLegacyTextValue(valores["estado afip"] || prev.alta.estadoAfip),
        exento: sanitizeLegacyTextValue(valores.exento) || prev.alta.exento,
        estadoRentas: sanitizeLegacyTextValue(valores["estado rentas"] || prev.alta.estadoRentas),
        codActividadRentas: codActividadRentasBase || prev.alta.codActividadRentas,
        actividadRentas: sanitizeLegacyTextValue(valor("actividad rentas", "actividadRentas") || prev.alta.actividadRentas),
        nombreDelAsesor: asesorValue || prev.alta.nombreDelAsesor,
        alicuota:
          sanitizeLegacyTextValue(valor("alicuota")) ||
          findProvinciaAlicuota(fichaCatalogos.provincias, provinciaFiscalValue || provinciaValue),
      },
    }));
  }, [fichaCatalogos.provincias]);

  const cargarDocumentosLegajo = useCallback(async (legajoId) => {
    if (!legajoId) {
      setAltaLegajoDocs([]);
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: true }));
    try {
      const data = await crmPostventaApi.listarDocumentosLegajo(legajoId);
      setAltaLegajoDocs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando documentos de legajo:", error);
      setAltaLegajoDocs([]);
    } finally {
      setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: false }));
    }
  }, []);

  const cargarContextoAlta = useCallback(async (usuarioId) => {
    setCargas((prev) => ({ ...prev, altaBajaCatalogos: true }));

    try {
      const todosLegajos = await crmPostventaApi.obtenerLegajos();
      const legajosUsuario = usuarioId
        ? await crmPostventaApi.obtenerLegajosPorUsuario(usuarioId).catch(() => [])
        : [];

      const legajosUsuarioLista = Array.isArray(legajosUsuario) ? legajosUsuario : [];
      setAltaWizardLegajos(legajosUsuarioLista);

      if (legajosUsuarioLista.length > 0) {
        const legajoPrioritario = [...legajosUsuarioLista].sort(
          (left, right) =>
            Number(right?.nroLegajo ?? right?.NroLegajo ?? 0) -
            Number(left?.nroLegajo ?? left?.NroLegajo ?? 0)
        )[0];
        aplicarLegajoAlWizard(legajoPrioritario);
        await cargarDocumentosLegajo(legajoPrioritario?.id ?? legajoPrioritario?.Id ?? 0);
      } else {
        setAltaWizardData((prev) => ({
          ...prev,
          legajoId: null,
          legajo: {
            ...prev.legajo,
            nroLegajo: nextLegajoSuggestion(Array.isArray(todosLegajos) ? todosLegajos : []),
          },
        }));
      }
    } catch (error) {
      console.error("Error cargando contexto de alta:", error);
      Swal.fire("Altas / Bajas", error.message || "No se pudo preparar el contexto del legajo.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, altaBajaCatalogos: false }));
    }
  }, [aplicarLegajoAlWizard, cargarDocumentosLegajo]);

  const handleAltaWizardUserChange = useCallback((field, value) => {
    setAltaWizardData((prev) => ({
      ...prev,
      usuario: {
        ...prev.usuario,
        [field]: value,
      },
    }));
  }, []);

  const handleAltaWizardLegajoChange = useCallback((field, value) => {
    setAltaWizardData((prev) => ({
      ...prev,
      legajo: {
        ...prev.legajo,
        [field]: value,
      },
      alta: field === "emailContacto"
        ? { ...prev.alta, correo: prev.alta.correo || value }
        : field === "celularWhatsapp"
          ? { ...prev.alta, celular: prev.alta.celular || value }
          : field === "provincia"
            ? {
                ...prev.alta,
                provincia: prev.alta.provincia || value,
                provincia3: prev.alta.provincia3 || value,
                alicuota: prev.alta.alicuota || findProvinciaAlicuota(fichaCatalogos.provincias, value),
              }
            : field === "asesor"
              ? { ...prev.alta, nombreDelAsesor: prev.alta.nombreDelAsesor || value }
          : prev.alta,
    }));
  }, [fichaCatalogos.provincias]);

  const handleAltaWizardAltaChange = useCallback((field, value) => {
    setAltaWizardData((prev) => {
      const nextAlta = {
        ...prev.alta,
        [field]: value,
      };

      if (field === "condicionAFIP") {
        nextAlta.estadoAfip = mapCondicionAfipToEstado(value);
        if (String(value || "").toLowerCase() === "monotributista") {
          nextAlta.exento = "NO";
        }
      }

      if (field === "estadoAfip") {
        nextAlta.condicionAFIP = mapEstadoAfipToCondicion(value);
        if (String(value || "").toUpperCase() === "MONOTRIBUTISTA") {
          nextAlta.exento = "NO";
        }
      }

      if (field === "tipoAlta") {
        nextAlta.altas = LEGACY_DEFAULT_ALTAS_BY_TIPO[value] || nextAlta.altas || "ALTA NUEVA";
      }

      if (field === "provincia") {
        nextAlta.provincia3 = nextAlta.provincia3 || value;
      }

      if (field === "provincia" || field === "provincia3") {
        nextAlta.alicuota = findProvinciaAlicuota(
          fichaCatalogos.provincias,
          field === "provincia3" ? value : nextAlta.provincia3 || value
        );
      }

      return {
        ...prev,
        agregaBaseClienteId:
          field === "tipoAlta" && value !== "AgregaTerminal"
            ? null
            : prev.agregaBaseClienteId,
        alta: nextAlta,
      };
    });
  }, [fichaCatalogos.provincias]);

  const buscarBaseAgrega = useCallback(async () => {
    const needle = String(altaAgregaSearch || "").trim();
    if (!needle) {
      setAltaAgregaResults([]);
      return;
    }

    setAltaAgregaLoading(true);
    try {
      const data = await crmPostventaApi.buscarClientes(needle);
      setAltaAgregaResults(normalizeAgregaLookupResults(data));
    } catch (error) {
      console.error("Error buscando base para AGREGA:", error);
      setAltaAgregaResults([]);
      Swal.fire("Paso 3", error.message || "No se pudo buscar el comercio o terminal base.", "error");
    } finally {
      setAltaAgregaLoading(false);
    }
  }, [altaAgregaSearch]);

  const seleccionarBaseAgrega = useCallback(async (clienteBase) => {
    if (!clienteBase?.clienteId) {
      return;
    }

    setAltaAgregaLoading(true);
    try {
      const fichaBase = await crmPostventaApi.obtenerFicha(clienteBase.clienteId);
      aplicarBaseAgregaDesdeFicha(clienteBase, fichaBase);
      setAltaAgregaSearch(
        [
          clienteBase.nombreDeFantasia || clienteBase.NombreDeFantasia || clienteBase.razonSocial || clienteBase.RazonSocial,
          (clienteBase.nDeComercio || clienteBase.NDeComercio) && `Comercio ${clienteBase.nDeComercio || clienteBase.NDeComercio}`,
        ]
          .filter(Boolean)
          .join(" | ")
      );
      setAltaAgregaResults([]);
      Swal.fire("Paso 3", "Se copiaron los datos fijos del comercio/terminal seleccionado.", "success");
    } catch (error) {
      console.error("Error copiando base AGREGA:", error);
      Swal.fire("Paso 3", error.message || "No se pudieron copiar los datos del comercio base.", "error");
    } finally {
      setAltaAgregaLoading(false);
    }
  }, [aplicarBaseAgregaDesdeFicha]);

  const handleAltaBlanqueoSubmit = useCallback(async (event) => {
    event.preventDefault();

    const cuit = normalizeCuitValue(altaWizardData.usuario.cuit);
    if (!cuit || !altaWizardData.usuario.nombre.trim() || !altaWizardData.usuario.email.trim()) {
      Swal.fire("Paso 1", "Completá CUIT, nombre y email para continuar.", "info");
      return;
    }

    if (cuit.length !== 11) {
      Swal.fire("Paso 1", "El CUIT/CUIL debe tener 11 dÃ­gitos. Se aceptan formatos con guiones y se normalizan automÃ¡ticamente.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoBlanqueo: true }));
    try {
      let usuarioPayload = null;

      try {
        usuarioPayload = await crmPostventaApi.buscarUsuarioPorCuit(cuit);
      } catch {
        usuarioPayload = null;
      }

      if (usuarioPayload?.id) {
        const actualizado = await crmPostventaApi.editarUsuarioAliado({
          Id: usuarioPayload.id,
          Usuario: cuit,
          Nombre: altaWizardData.usuario.nombre.trim(),
          Correo: altaWizardData.usuario.email.trim(),
          TipoUsuario: 0,
        });

        setAltaWizardData((prev) => ({
          ...prev,
          usuarioId: actualizado?.id ?? usuarioPayload.id,
          usuario: {
            ...prev.usuario,
            cuit,
            nombre: altaWizardData.usuario.nombre.trim(),
            email: altaWizardData.usuario.email.trim(),
            tipoUsuario: 0,
          },
        }));
        setAltaWizardStatus((prev) => ({
          ...prev,
          blanqueo: "Usuario existente actualizado y reutilizado.",
        }));
        await cargarContextoAlta(actualizado?.id ?? usuarioPayload.id);
      } else {
        const creado = await crmPostventaApi.crearUsuarioAliado({
          Usuario: cuit,
          Nombre: altaWizardData.usuario.nombre.trim(),
          Correo: altaWizardData.usuario.email.trim(),
          TipoUsuario: 0,
        });

        setAltaWizardData((prev) => ({
          ...prev,
          usuarioId: creado?.id ?? null,
          usuario: {
            ...prev.usuario,
            cuit,
            nombre: altaWizardData.usuario.nombre.trim(),
            email: altaWizardData.usuario.email.trim(),
            tipoUsuario: 0,
          },
        }));
        setAltaWizardStatus((prev) => ({
          ...prev,
          blanqueo: "Usuario creado correctamente desde el paso de blanqueo.",
        }));
        await cargarContextoAlta(creado?.id ?? null);
      }

      setAltaWizardStep(2);
    } catch (error) {
      console.error("Error guardando blanqueo:", error);
      Swal.fire("Paso 1", error.message || "No se pudo guardar el blanqueo.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoBlanqueo: false }));
    }
  }, [altaWizardData.usuario, cargarContextoAlta]);

  const handleAltaLegajoSelect = useCallback((event) => {
    const selectedId = Number(event.target.value || 0);
    if (!selectedId) {
      setAltaWizardData((prev) => ({
        ...prev,
        legajoId: null,
        legajo: {
          ...prev.legajo,
          nroLegajo: prev.legajo.nroLegajo || nextLegajoSuggestion(altaWizardLegajos),
        },
      }));
      setAltaLegajoDocs([]);
      return;
    }

    const selected = altaWizardLegajos.find((item) => Number(item?.id ?? item?.Id ?? 0) === selectedId);
    if (selected) {
      aplicarLegajoAlWizard(selected);
      cargarDocumentosLegajo(selectedId);
    }
  }, [altaWizardLegajos, aplicarLegajoAlWizard, cargarDocumentosLegajo]);

  const handleAltaLegajoSubmit = useCallback(async (event) => {
    event.preventDefault();

    const { legajo, usuarioId } = altaWizardData;
    if (!usuarioId) {
      Swal.fire("Paso 2", "Primero completá el blanqueo del usuario.", "info");
      return;
    }

    if (!legajo.nroLegajo || !legajo.terminal || !legajo.nombreDeFantasia || !legajo.provincia) {
      Swal.fire("Paso 2", "Completá al menos número de legajo, terminal, nombre de fantasía y provincia.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoLegajo: true }));
    try {
      const payload = {
        Id: altaWizardData.legajoId || undefined,
        NroLegajo: Number(legajo.nroLegajo),
        Terminal: legajo.terminal,
        NombreDeFantasia: legajo.nombreDeFantasia,
        Provincia: legajo.provincia,
        TipoPersona: legajo.tipoPersona,
        CuitTitularOSociedad: altaWizardData.usuario.cuit,
        RazonSocial: legajo.razonSocial,
        Calle: legajo.calle,
        Numero: legajo.numero,
        PisoDepto: legajo.pisoDepto,
        Localidad: legajo.localidad,
        CodigoPostal: legajo.codigoPostal,
        EmailContacto: legajo.emailContacto || altaWizardData.usuario.email,
        CelularWhatsapp: legajo.celularWhatsapp,
        Asesor: legajo.asesor,
        Iduser: usuarioId,
      };

      const response = altaWizardData.legajoId
        ? await crmPostventaApi.editarLegajo(payload)
        : await crmPostventaApi.crearLegajo(payload);

      const legajoId = response?.idLegajo ?? response?.id ?? response?.Id ?? altaWizardData.legajoId;
      setAltaWizardData((prev) => ({
        ...prev,
        legajoId,
      }));
      setAltaWizardStatus((prev) => ({
        ...prev,
        legajo: altaWizardData.legajoId
          ? "Legajo actualizado correctamente."
          : "Legajo creado correctamente. Ya podés cargar documentos o continuar.",
      }));

      await cargarContextoAlta(usuarioId);
      await cargarDocumentosLegajo(legajoId);
      setAltaWizardStep(3);
    } catch (error) {
      console.error("Error guardando legajo:", error);
      Swal.fire("Paso 2", error.message || "No se pudo guardar el legajo.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoLegajo: false }));
    }
  }, [altaWizardData, cargarContextoAlta, cargarDocumentosLegajo]);

  const handleAltaLegajoDocumento = useCallback(async (event) => {
    event.preventDefault();

    if (!altaWizardData.legajoId) {
      Swal.fire("Documentos", "Guardá primero el legajo para subir archivos.", "info");
      return;
    }

    if (!altaLegajoDocDraft.file) {
      Swal.fire("Documentos", "Seleccioná un archivo para subir.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: true }));
    try {
      const archivoBase64 = await fileToDataUrl(altaLegajoDocDraft.file);
      await crmPostventaApi.subirDocumentoLegajo({
        IdLegajo: altaWizardData.legajoId,
        NombreDocumento: altaLegajoDocDraft.nombreDocumento,
        DocumentoTipo: getFileExtension(altaLegajoDocDraft.file) || null,
        ArchivoBase64: archivoBase64,
      });

      setAltaLegajoDocDraft({
        nombreDocumento: ALTA_LEGAJO_DOC_TYPES[0],
        file: null,
      });
      await cargarDocumentosLegajo(altaWizardData.legajoId);
      Swal.fire("Documentos", "Archivo cargado correctamente.", "success");
    } catch (error) {
      console.error("Error subiendo documento:", error);
      Swal.fire("Documentos", error.message || "No se pudo subir el archivo.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: false }));
    }
  }, [altaLegajoDocDraft, altaWizardData.legajoId, cargarDocumentosLegajo]);

  const handleEliminarDocumentoLegajo = useCallback(async (idDocumento) => {
    if (!altaWizardData.legajoId || !idDocumento) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: true }));
    try {
      await crmPostventaApi.eliminarDocumentoLegajo(idDocumento);
      await cargarDocumentosLegajo(altaWizardData.legajoId);
    } catch (error) {
      console.error("Error eliminando documento:", error);
      Swal.fire("Documentos", error.message || "No se pudo eliminar el archivo.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: false }));
    }
  }, [altaWizardData.legajoId, cargarDocumentosLegajo]);

  const asegurarTerminalBaseDisponible = useCallback(async (terminal) => {
    const terminalNormalizada = normalizeDigits(terminal);
    if (!terminalNormalizada) {
      throw new Error("El número de terminal es obligatorio.");
    }

    const candidatos = await crmPostventaApi.buscarClientes(terminalNormalizada);
    const existeTerminal = Array.isArray(candidatos) && candidatos.some(
      (item) => normalizeDigits(item?.nDeTerminal || item?.NDeTerminal) === terminalNormalizada
    );

    if (!existeTerminal) {
      await crmPostventaApi.crearTerminalBaseAlta({
        NumeroTerminalAlta: terminalNormalizada,
        Observacion: "Registro base creado automáticamente desde CRM Postventa > Altas / Bajas.",
      });
    }
  }, []);

  const handleAltaWorkflowSubmit = useCallback(async (event) => {
    event?.preventDefault?.();

    const token = getToken();
    if (!token) {
      Swal.fire("Alta CRM", "No hay sesión activa.", "error");
      return;
    }

    if (!(String(altaWizardData.alta.tipoAlta || "") === "AgregaTerminal" || String(altaWizardData.alta.altas || "") === "AGREGA") && !altaWizardData.legajoId) {
      Swal.fire("Alta CRM", "Primero guardá el legajo.", "info");
      return;
    }

    const isAgregaMode =
      String(altaWizardData.alta.tipoAlta || "") === "AgregaTerminal" ||
      String(altaWizardData.alta.altas || "") === "AGREGA";

    if (false && !isAgregaMode && !altaWizardData.legajoId) {
      Swal.fire("Alta CRM", "Primero guardÃ¡ el legajo.", "info");
      return;
    }

    if (isAgregaMode && !altaWizardData.agregaBaseClienteId) {
      Swal.fire("Alta CRM", "SeleccionÃ¡ primero el comercio o terminal base para copiar los datos fijos.", "info");
      return;
    }

    if (isAgregaMode && !sanitizeLegacyNumericValue(altaWizardData.legajo.nroLegajo)) {
      Swal.fire("Alta CRM", "No se pudo recuperar el nÃºmero de legajo base del comercio seleccionado.", "info");
      return;
    }

    const cuitTitular = normalizeCuitValue(altaWizardData.usuario.cuit);
    const cuitBancario = normalizeCuitValue(altaWizardData.alta.cuitbancario || cuitTitular);
    if (cuitTitular.length !== 11) {
      Swal.fire("Paso 3", "El CUIT/CUIL del titular debe tener 11 dÃ­gitos. Se aceptan formatos con guiones y se normalizan antes de enviar.", "info");
      return;
    }

    if (cuitBancario && cuitBancario.length !== 11) {
      Swal.fire("Paso 3", "El CUIT bancario debe tener 11 dÃ­gitos.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoAltaWorkflow: true }));
    try {
      await asegurarTerminalBaseDisponible(altaWizardData.legajo.terminal);

      const formularioPayload = buildAltaWizardFormPayload(altaWizardData, token);
      const missingFields = buildAltaMissingFields(altaWizardData, formularioPayload);
      if (missingFields.length) {
        await Swal.fire({
          title: "Paso 3",
          html: formatMissingFieldsHtmlList(missingFields),
          icon: "info",
        });
        return;
      }

      const formularioCreado = await crmPostventaApi.crearFormularioAlta(formularioPayload);
      const formularioId = Number(formularioCreado?.id || 0);

      if (!formularioId) {
        throw new Error("No se pudo obtener el identificador del formulario de alta.");
      }

      await crmPostventaApi.crearAltaCrm({
        id: formularioId,
        token,
        NroLocal: sanitizeLegacyNumericValue(altaWizardData.alta.nroLocal) || "1",
        Legajo: sanitizeLegacyNumericValue(altaWizardData.legajo.nroLegajo),
        CodActividad: sanitizeLegacyNumericValue(altaWizardData.alta.codActividad),
        Rubro: sanitizeLegacyTextValue(altaWizardData.alta.rubro),
        RubroSecundario: sanitizeLegacyTextValue(altaWizardData.alta.rubroSecundario),
        Actividad: sanitizeLegacyTextValue(altaWizardData.alta.actividad),
        Actividad2: sanitizeLegacyTextValue(altaWizardData.alta.actividad2),
        CodActividadRentas: sanitizeLegacyNumericValue(altaWizardData.alta.codActividadRentas),
        ActividadRentas: sanitizeLegacyTextValue(altaWizardData.alta.actividadRentas),
        Observaciones: sanitizeLegacyTextValue(altaWizardData.alta.observaciones),
        Estado: sanitizeLegacyTextValue(altaWizardData.alta.estado) || "ALTA",
        FechaDeAlta: sanitizeLegacyTextValue(altaWizardData.alta.fechaDeAlta) || makeLocalToday(),
        LinkDePago: sanitizeLegacyTextValue(altaWizardData.alta.linkDePago) || "NO",
        NombreDelAsesor: sanitizeLegacyTextValue(altaWizardData.alta.nombreDelAsesor || altaWizardData.legajo.asesor),
        EstadoAfip: sanitizeLegacyTextValue(
          altaWizardData.alta.estadoAfip || mapCondicionAfipToEstado(altaWizardData.alta.condicionAFIP)
        ),
        Exento: sanitizeLegacyTextValue(altaWizardData.alta.exento) || "NO",
        EstadoRentas: sanitizeLegacyTextValue(altaWizardData.alta.estadoRentas),
        Provincia3: sanitizeLegacyTextValue(
          altaWizardData.alta.provincia3 || altaWizardData.alta.provincia || altaWizardData.legajo.provincia
        ),
        Altas:
          sanitizeLegacyTextValue(altaWizardData.alta.altas) ||
          LEGACY_DEFAULT_ALTAS_BY_TIPO[altaWizardData.alta.tipoAlta] ||
          "ALTA NUEVA",
        Baja: sanitizeLegacyTextValue(altaWizardData.alta.baja),
      });

      try {
        const candidatosAlta = await crmPostventaApi.buscarClientes(altaWizardData.legajo.terminal);
        const altaCreada = (Array.isArray(candidatosAlta) ? candidatosAlta : []).find(
          (item) =>
            normalizeDigits(item?.nDeTerminal || item?.NDeTerminal) === normalizeDigits(altaWizardData.legajo.terminal) &&
            String(item?.estado || item?.Estado || "").toUpperCase() === "ALTA"
        );

        if (altaCreada?.clienteId) {
          await crmPostventaApi.actualizarFicha(altaCreada.clienteId, {
            Telefono: normalizeEditablePhoneValue(altaWizardData.alta.celular || altaWizardData.legajo.celularWhatsapp),
            ReferenciaTelefono: sanitizeLegacyTextValue(altaWizardData.alta.referenciaTelefono || altaWizardData.alta.apoderado),
            TelefonoAlternativo: normalizeEditablePhoneValue(altaWizardData.alta.telefonoAlternativo || altaWizardData.alta.telFijo),
            ReferenciaTelefonoAlternativo: sanitizeLegacyTextValue(altaWizardData.alta.referenciaTelefonoAlternativo),
            ContactoAlternativo: sanitizeLegacyTextValue(altaWizardData.alta.referenciaTelefonoAlternativo),
          });
        }
      } catch (errorContacto) {
        console.error("Error sincronizando contactos post alta:", errorContacto);
      }

      setAltaWizardData((prev) => ({
        ...prev,
        formularioAltaId: formularioId,
      }));
      setAltaWizardStatus((prev) => ({
        ...prev,
        alta: "Alta CRM generada correctamente reutilizando formulario alta + alta final legacy.",
      }));
      setAltaFinalChecklistOpen(false);
      setAltaFinalChecklistChecks(makeAltaFinalChecklistState());
      Swal.fire("Alta CRM", "El alta se generó correctamente dentro del flujo guiado.", "success");
      setAltaWizardStep(3);
    } catch (error) {
      console.error("Error generando alta CRM:", error);
      Swal.fire("Alta CRM", error.message || "No se pudo completar el alta.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoAltaWorkflow: false }));
    }
  }, [altaWizardData, asegurarTerminalBaseDisponible]);

  const buscarOpcionesBaja = useCallback(async (searchValue, options = {}) => {
    const normalizedSearch = String(searchValue || "").trim();
    const requestId = ++bajaLookupRequestRef.current;
    if (!normalizedSearch) {
      setBajaLookupResults([]);
      setBajaLookupLoading(false);
      return;
    }

    setBajaLookupLoading(true);
    try {
      const data = await crmPostventaApi.buscarClientes(normalizedSearch);
      if (requestId !== bajaLookupRequestRef.current) {
        return;
      }
      setBajaLookupResults(normalizeBajaLookupResults(data));
    } catch (error) {
      if (requestId !== bajaLookupRequestRef.current) {
        return;
      }
      console.error("Error buscando terminal/comercio para baja:", error);
      setBajaLookupResults([]);
      if (!options.silent) {
        Swal.fire("Baja por terminal", error.message || "No se pudo buscar la terminal o el comercio.", "error");
      }
    } finally {
      if (requestId === bajaLookupRequestRef.current) {
        setBajaLookupLoading(false);
      }
    }
  }, []);

  const handleSeleccionBajaResultado = useCallback((selection) => {
    const selectedKey = typeof selection === "string" ? selection : selection?.target?.value;
    const selected = bajaLookupResults.find((item) => item.key === selectedKey);
    if (!selected) {
      return;
    }

    setBajaDraft((prev) => ({
      ...prev,
      terminal: selected.terminal || prev.terminal,
    }));
    setBajaFieldErrors((prev) => {
      if (!prev.terminal) {
        return prev;
      }

      const next = { ...prev };
      delete next.terminal;
      return next;
    });
    setBajaLookupSearch(selected.nombre || selected.terminal || selected.comercio || "");
    setBajaLookupResults([]);
  }, [bajaLookupResults]);

  const confirmarControlesBajaTerminal = useCallback(async (terminal) => {
    const checklistIds = {
      configuracion: "baja-check-configuracion-externa",
      anticipo: "baja-check-anticipo-activo",
    };

    const updateConfirmState = () => {
      const configuracion = document.getElementById(checklistIds.configuracion);
      const anticipo = document.getElementById(checklistIds.anticipo);
      const confirmButton = Swal.getConfirmButton();
      if (confirmButton) {
        confirmButton.disabled = !(configuracion?.checked && anticipo?.checked);
      }
    };

    const result = await Swal.fire({
      title: "Confirmar baja de terminal",
      html: `
        <div style="text-align:left">
          <p style="margin:0 0 12px">Antes de confirmar la baja de la terminal <strong>${terminal}</strong>, valida estos controles:</p>
          <label style="display:flex;gap:10px;align-items:center;margin:10px 0">
            <input id="${checklistIds.configuracion}" type="checkbox" />
            <span>Configuracion externa eliminada</span>
          </label>
          <label style="display:flex;gap:10px;align-items:center;margin:10px 0">
            <input id="${checklistIds.anticipo}" type="checkbox" />
            <span>Anticipo activo</span>
          </label>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirmar baja",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      didOpen: () => {
        const configuracion = document.getElementById(checklistIds.configuracion);
        const anticipo = document.getElementById(checklistIds.anticipo);
        configuracion?.addEventListener("change", updateConfirmState);
        anticipo?.addEventListener("change", updateConfirmState);
        updateConfirmState();
      },
      preConfirm: () => {
        const configuracion = document.getElementById(checklistIds.configuracion);
        const anticipo = document.getElementById(checklistIds.anticipo);
        if (!configuracion?.checked || !anticipo?.checked) {
          Swal.showValidationMessage("Tenes que tildar los dos controles para confirmar la baja.");
          return false;
        }
        return true;
      },
    });

    return result.isConfirmed;
  }, []);

  const handleBajaWorkflowSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!bajaDraft.terminal || !bajaDraft.observacion.trim()) {
      Swal.fire("Baja por terminal", "Indicá terminal y observación para continuar.", "info");
      return;
    }

    const confirmed = await confirmarControlesBajaTerminal(bajaDraft.terminal);
    if (!confirmed) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoBajaWorkflow: true }));
    try {
      await crmPostventaApi.ejecutarBajaTerminal({
        NumeroTerminalBaja: normalizeDigits(bajaDraft.terminal),
        Motivo: bajaDraft.motivo,
        Observacion: bajaDraft.observacion.trim(),
        FechaBaja: bajaDraft.fechaBaja,
      });

      Swal.fire("Baja por terminal", "La baja se registró correctamente usando la lógica legacy.", "success");
      setBajaDraft(emptyBajaDraft());
      setBajaLookupSearch("");
      setBajaLookupResults([]);
    } catch (error) {
      console.error("Error ejecutando baja:", error);
      Swal.fire("Baja por terminal", error.message || "No se pudo registrar la baja.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoBajaWorkflow: false }));
    }
  }, [bajaDraft, confirmarControlesBajaTerminal]);

  const handleBajaWorkflowSubmitV2 = useCallback(async (event) => {
    event.preventDefault();

    const nextErrors = {};
    if (!bajaDraft.terminal) {
      nextErrors.terminal = "Indicá la terminal para continuar.";
    }
    if (!bajaDraft.fechaBaja) {
      nextErrors.fechaBaja = "Ingresá una fecha de baja válida.";
    }
    if (!bajaDraft.observacion.trim()) {
      nextErrors.observacion = "Completá las observaciones para continuar.";
    }

    setBajaFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      return;
    }

    const confirmed = await confirmarControlesBajaTerminal(bajaDraft.terminal);
    if (!confirmed) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoBajaWorkflow: true }));
    try {
      await crmPostventaApi.ejecutarBajaTerminal({
        NumeroTerminalBaja: normalizeDigits(bajaDraft.terminal),
        Motivo: bajaDraft.motivo,
        Observacion: bajaDraft.observacion.trim(),
        FechaBaja: bajaDraft.fechaBaja,
      });

      Swal.fire("Baja por terminal", "La baja se registró correctamente usando la lógica legacy.", "success");
      setBajaDraft(emptyBajaDraft());
      setBajaLookupSearch("");
      setBajaLookupResults([]);
      setBajaFieldErrors({});
    } catch (error) {
      console.error("Error ejecutando baja:", error);
      Swal.fire("Baja por terminal", error.message || "No se pudo registrar la baja.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoBajaWorkflow: false }));
    }
  }, [bajaDraft, confirmarControlesBajaTerminal]);

  useEffect(() => {
    validarSesion();
  }, [validarSesion]);

  useEffect(() => {
    if (altasBajasMode !== "baja") {
      return undefined;
    }

    const normalizedSearch = String(bajaLookupSearch || "").trim();
    if (!normalizedSearch) {
      bajaLookupRequestRef.current += 1;
      setBajaLookupResults([]);
      setBajaLookupLoading(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      buscarOpcionesBaja(normalizedSearch, { silent: true });
    }, 320);

    return () => window.clearTimeout(timeoutId);
  }, [altasBajasMode, bajaLookupSearch, buscarOpcionesBaja]);

  useEffect(() => {
    let isMounted = true;

    const loadFichaCatalogos = async () => {
      setFichaCatalogosLoading(true);
      try {
        const data = await fetchFichaCatalogosV2();
        if (isMounted) {
          setFichaCatalogos(data);
        }
      } catch (error) {
        console.error("Error cargando catálogos de edición:", error);
      } finally {
        if (isMounted) {
          setFichaCatalogosLoading(false);
        }
      }
    };

    loadFichaCatalogos();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!clienteModalOpen) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      cargarClientes(clienteSearch);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [clienteModalOpen, clienteSearch, cargarClientes]);

  useEffect(() => {
    if (!clienteModalOpen || clientePendiente?.groupKey || !clienteGrupos.length || !currentGroupKey) {
      return;
    }

    const grupoActual = clienteGrupos.find((item) => item.groupKey === currentGroupKey);
    if (grupoActual) {
      setClientePendiente(grupoActual);
    }
  }, [clienteGrupos, clienteModalOpen, clientePendiente?.groupKey, currentGroupKey]);

  useEffect(() => {
    if (!cargandoSesion && clienteSeleccionadoId) {
      cargarModulo(clienteSeleccionadoId, analyticsRange);
    }
  }, [analyticsRange, cargarModulo, cargandoSesion, clienteSeleccionadoId]);

  useEffect(() => {
    if (!clienteSeleccionadoCuit) {
      return;
    }

    setOrdenesPagoFiltros((prev) =>
      prev.search === clienteSeleccionadoCuit
        ? prev
        : {
            ...prev,
            search: clienteSeleccionadoCuit,
          }
    );
  }, [clienteSeleccionadoCuit, clienteSeleccionadoId]);

  useEffect(() => {
    setInactivosFiltros((prev) =>
      prev.fecha === alertasFiltros.fecha
        ? prev
        : {
            ...prev,
            fecha: alertasFiltros.fecha,
          }
    );
  }, [alertasFiltros.fecha]);

  useEffect(() => {
    if (cargandoSesion || section !== "alertas") {
      return;
    }

    cargarAlertas(alertasFiltros);
  }, [
    alertasFiltros.estado,
    alertasFiltros.fecha,
    alertasFiltros.prioridad,
    alertasFiltros.provincia,
    alertasFiltros.responsable,
    alertasFiltros.tipo,
    cargarAlertas,
    cargandoSesion,
    section,
  ]);

  useEffect(() => {
    if (cargandoSesion || section !== "alertas") {
      return;
    }

    cargarInactivos(inactivosFiltros);
  }, [
    cargarInactivos,
    cargandoSesion,
    inactivosFiltros.diasSinOperar,
    inactivosFiltros.fecha,
    inactivosFiltros.provincia,
    inactivosFiltros.responsable,
    section,
  ]);

  useEffect(() => {
    if (cargandoSesion || section !== "ordenes-pago" || !clienteSeleccionadoId) {
      return;
    }

    cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros);
  }, [cargandoSesion, cargarOrdenesPago, clienteSeleccionadoId, section]);

  useEffect(() => {
    if (cargandoSesion || section !== "cupones" || !clienteSeleccionadoId) {
      return;
    }

    cargarCupones(clienteSeleccionadoId, cuponesFiltros);
  }, [cargandoSesion, cargarCupones, clienteSeleccionadoId, section]);

  useEffect(() => {
    if (section !== "herramientas" || !["montos", "ordenes"].includes(herramientasTab)) {
      return;
    }

    if (Object.keys(montosCatalogo || {}).length) {
      return;
    }

    let cancelled = false;
    const loadCatalog = async () => {
      setMontosCatalogoLoading(true);
      setMontosCatalogoError("");
      try {
        const response = await fetch(process.env.REACT_APP_API_CALCULADORA_TARJETA || "/api/calculadora/datostarjeta");
        if (!response.ok) {
          throw new Error("No pudimos cargar las tarjetas.");
        }

        const data = await response.json();
        if (!cancelled) {
          setMontosCatalogo(data && typeof data === "object" ? data : {});
        }
      } catch (error) {
        if (!cancelled) {
          setMontosCatalogoError(error.message || "No pudimos cargar las tarjetas.");
        }
      } finally {
        if (!cancelled) {
          setMontosCatalogoLoading(false);
        }
      }
    };

    loadCatalog();

    return () => {
      cancelled = true;
    };
  }, [herramientasTab, montosCatalogo, section]);

  useEffect(() => {
    if (section !== "herramientas" || herramientasTab !== "comparador") {
      return undefined;
    }

    const validationMessage = validateComparadorForm(comparadorForm);
    if (validationMessage) {
      setComparadorResultado(null);
      setComparadorCalculando(false);
      setComparadorError(comparadorForm.monto ? validationMessage : "");
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      const token = getToken();
      if (!token) {
        setComparadorResultado(null);
        setComparadorCalculando(false);
        setComparadorError("No tenes permisos para usar esta herramienta.");
        return;
      }

      setComparadorCalculando(true);
      setComparadorError("");
      try {
        const response = await fetch(process.env.REACT_APP_API_CALCULADORA || "/api/calculadora/calculadora", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify({
            Zoco: Number(comparadorForm.zoco),
            Monto: normalizeMontoForApi(comparadorForm.monto),
            Terminales: comparadorForm.terminales,
            TipoPago: comparadorForm.tipoPago,
            Competencia: comparadorForm.competencia,
            Cuota: comparadorForm.tipoPago === "debito" ? "0" : comparadorForm.cuota,
            Token: token,
          }),
        });

        if (response.status === 401) {
          throw new Error("No tenes permisos para usar esta herramienta.");
        }

        if (!response.ok) {
          throw new Error("No pudimos calcular la comparacion.");
        }

        const data = await response.json();
        if (!getComparadorItems(data, "Payway").length || !getComparadorItems(data, "Zoco").length) {
          throw new Error("No hay una configuracion disponible para la combinacion seleccionada.");
        }

        setComparadorResultado(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          setComparadorResultado(null);
          setComparadorError(error.message || "No pudimos calcular la comparacion.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setComparadorCalculando(false);
        }
      }
    }, 550);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [comparadorForm, herramientasTab, section]);

  const clienteHeader = ficha?.header || {
    clienteKey: clientePendiente?.clienteKey || "",
    nombreDeFantasia: clientePendiente?.nombreDeFantasia || "Sin cliente seleccionado",
    razonSocial: clientePendiente?.razonSocial || "",
    cuit: clientePendiente?.cuit || "",
    estado: clientePendiente?.estado || "",
    producto: clientePendiente?.producto || "",
    nDeTerminal: clientePendiente?.nDeTerminal || "",
    nDeComercio: clientePendiente?.nDeComercio || "",
    legajo: clientePendiente?.legajo || "",
  };

  const handleCambiarTerminal = useCallback((terminal) => {
    if (!terminal?.clienteId || terminal.clienteId === clienteSeleccionadoId) {
      return;
    }

    localStorage.setItem(STORAGE_KEY, String(terminal.clienteId));
    setClienteSeleccionadoId(terminal.clienteId);
    setFacturacionDetalle(null);
    setTimelineDetalle(null);
    setOrdenesPago(null);
    setCupones(null);
  }, [clienteSeleccionadoId]);

  const filasFacturacion = useMemo(() => {
    const allRows = Array.isArray(facturacion?.filas) ? facturacion.filas : [];
    if (!facturacionFiltroComercio) {
      return allRows;
    }

    return allRows.filter(
      (row) =>
        row.terminal === facturacionFiltroComercio ||
        row.comercio === facturacionFiltroComercio
    );
  }, [facturacion?.filas, facturacionFiltroComercio]);

  const filterAlertRowsBySearch = useCallback((rows, searchValue) => {
    const filas = Array.isArray(rows) ? rows : [];
    const needle = String(searchValue || "").trim().toLowerCase();

    if (!needle) {
      return filas;
    }

    return filas.filter((item) =>
      [
        item?.comercio,
        item?.cuit,
        item?.terminal,
        item?.provincia,
        item?.tipoAlerta,
        item?.responsable,
        item?.estado,
        item?.resumen,
        item?.telefono,
        item?.ultimaGestion,
        item?.proximoContacto,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, []);

  const alertasFacturacionFiltradas = useMemo(
    () => filterAlertRowsBySearch(alertas?.alertasFacturacion, alertasFacturacionSearch),
    [alertas?.alertasFacturacion, alertasFacturacionSearch, filterAlertRowsBySearch]
  );

  const alertasZocoCheckFiltradas = useMemo(
    () => filterAlertRowsBySearch(alertas?.alertasZocoCheck, ""),
    [alertas?.alertasZocoCheck, filterAlertRowsBySearch]
  );

  const alertasTerminalExtraviadaFiltradas = useMemo(
    () => filterAlertRowsBySearch(alertas?.alertasTerminalExtraviada, alertasTerminalExtraviadaSearch),
    [alertas?.alertasTerminalExtraviada, alertasTerminalExtraviadaSearch, filterAlertRowsBySearch]
  );

  const inactivosFiltrados = useMemo(() => {
    const filas = Array.isArray(inactivos?.filas) ? inactivos.filas : [];
    const needle = String(inactivosSearchDraft || "").trim().toLowerCase();

    if (!needle) {
      return filas;
    }

    return filas.filter((item) =>
      [
        item?.estado,
        item?.cuit,
        item?.comercio,
        item?.razonSocial,
        item?.terminal,
        item?.provincia,
        item?.telefono,
        item?.ultimaFechaFacturacion,
        item?.usuarioResponsable,
        item?.ultimaGestion,
        item?.proximoContacto,
        item?.accionSugerida,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [inactivos?.filas, inactivosSearchDraft]);

  const inactivosProvinciaOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(inactivos?.filas) ? inactivos.filas : [])
            .map((item) => String(item?.provincia || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, "es")),
    [inactivos?.filas]
  );

  const fichaCamposEdicion = useMemo(
    () => Array.isArray(ficha?.edicion?.campos) ? ficha.edicion.campos : [],
    [ficha?.edicion?.campos]
  );

  const altaWizardComercioOptions = useMemo(
    () =>
      dedupeOptions(
        fichaCatalogos.comercios.map((item) =>
          normalizeOption(item.label || item.value, item.label || item.value, {
            numero: item.value,
          })
        )
      ),
    [fichaCatalogos.comercios]
  );
  const isAltaAgregaMode = useMemo(
    () =>
      String(altaWizardData.alta.tipoAlta || "") === "AgregaTerminal" ||
      String(altaWizardData.alta.altas || "") === "AGREGA",
    [altaWizardData.alta.altas, altaWizardData.alta.tipoAlta]
  );
  const requiresAltaFinalChecklist = altasBajasMode === "alta" && !isAltaAgregaMode;
  const altaFinalChecklistCompletedCount = useMemo(
    () => ALTA_FINAL_CHECKLIST_ITEMS.filter((item) => Boolean(altaFinalChecklistChecks[item.id])).length,
    [altaFinalChecklistChecks]
  );
  const altaFinalChecklistComplete = altaFinalChecklistCompletedCount === ALTA_FINAL_CHECKLIST_ITEMS.length;
  const handleAltaFinalChecklistToggle = useCallback((itemId, checked) => {
    setAltaFinalChecklistChecks((prev) => ({
      ...prev,
      [itemId]: checked,
    }));
  }, []);
  const handleAltaStepThreeSubmit = useCallback(
    (event) => {
      event.preventDefault();

      if (!requiresAltaFinalChecklist) {
        handleAltaWorkflowSubmit();
        return;
      }

      setAltaFinalChecklistOpen(true);
    },
    [handleAltaWorkflowSubmit, requiresAltaFinalChecklist]
  );
  const handleAltaFinalChecklistConfirm = useCallback(() => {
    if (!altaFinalChecklistComplete || cargas.guardandoAltaWorkflow) {
      return;
    }

    setAltaFinalChecklistOpen(false);
    handleAltaWorkflowSubmit();
  }, [altaFinalChecklistComplete, cargas.guardandoAltaWorkflow, handleAltaWorkflowSubmit]);

  const clientEditSections = useMemo(
    () => buildClientEditSections(fichaCamposEdicion),
    [fichaCamposEdicion]
  );

  const abrirEdicionFicha = useCallback(() => {
    setFichaEditDraft(buildFichaEditDraft(fichaCamposEdicion));
    setFichaEditOpen(true);
  }, [fichaCamposEdicion]);

  const cerrarEdicionFicha = useCallback(() => {
    setFichaEditOpen(false);
  }, []);

  const guardarEdicionFicha = async (event) => {
    event.preventDefault();
    if (!clienteSeleccionadoId) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoFicha: true }));
    try {
      const resultado = await crmPostventaApi.actualizarFicha(
        clienteSeleccionadoId,
        mapFichaDraftToPayload(fichaEditDraft)
      );

      cerrarEdicionFicha();
      await cargarModulo(clienteSeleccionadoId, analyticsRange);

      const warningText = Array.isArray(resultado?.warnings) && resultado.warnings.length
        ? `\n\n${resultado.warnings.join("\n")}`
        : "";

      Swal.fire("Ficha", `${resultado?.message || "Datos actualizados correctamente."}${warningText}`, "success");
    } catch (error) {
      console.error("Error guardando ficha:", error);
      Swal.fire("Error", error.message || "No se pudieron actualizar los datos del cliente.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoFicha: false }));
    }
  };

  const handleFichaFieldChange = useCallback((field, nextValue) => {
    setFichaEditDraft((prev) => applyFichaFieldSideEffects(prev, field.key, nextValue, fichaCatalogos));
  }, [fichaCatalogos]);

  const renderFichaEditField = useCallback((field) => {
    const value = fichaEditDraft[field.key] ?? "";
    const baseOptions = buildFichaCatalogOptions(fichaCatalogos, field);
    const options = value && !baseOptions.some((option) => option.value === value)
      ? [...baseOptions, normalizeOption(value, value)]
      : baseOptions;
    const isReadOnly = isClientEditReadOnlyField(field);
    const source = field.dataSource || FICHA_SELECT_KEY_TO_SOURCE[field.key];
    const selectedOption = options.find((option) => option.value === value);
    const readOnlyDisplay = selectedOption?.label || value || "-";

    if (isReadOnly) {
      return <div className="client-edit-modal__readonly">{readOnlyDisplay}</div>;
    }

    if (CLIENT_EDIT_RADIO_KEYS.has(field.key)) {
      const radioOptions = options.length ? options : FICHA_FIXED_OPTIONS.siNo;

      return (
        <div className="client-edit-modal__radio-group" role="radiogroup" aria-label={field.label}>
          {radioOptions.map((option) => (
            <label key={`${field.key}-${option.value}`} className="client-edit-modal__radio-option">
              <input
                type="radio"
                name={`client-edit-${field.key}`}
                checked={value === option.value}
                onChange={() => handleFichaFieldChange(field, option.value)}
              />
              <span className="client-edit-modal__radio-indicator" aria-hidden="true" />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      );
    }

    if (field.inputType === "select") {
      return (
        <div className="client-edit-modal__select">
          <Form.Select
            className="client-edit-modal__control client-edit-modal__control--select"
            value={value}
            onChange={(event) => handleFichaFieldChange(field, event.target.value)}
          >
            <option value="">{source === "estado" && !field.editable ? "Sin valor" : "Seleccionar"}</option>
            {options.map((option) => (
              <option key={`${field.key}-${option.value}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          <span className="client-edit-modal__select-icon" aria-hidden="true">
            <FontAwesomeIcon icon={faChevronDown} />
          </span>
        </div>
      );
    }

    return (
      <Form.Control
        className={`client-edit-modal__control ${field.inputType === "textarea" ? "client-edit-modal__control--textarea" : ""}`}
        as={field.inputType === "textarea" ? "textarea" : undefined}
        rows={field.inputType === "textarea" ? 5 : undefined}
        type={field.inputType === "textarea" ? undefined : field.inputType || "text"}
        value={value}
        onChange={(event) => handleFichaFieldChange(field, event.target.value)}
      />
    );
  }, [fichaCatalogos, fichaEditDraft, handleFichaFieldChange]);

  const handleCrearGestion = async (event) => {
    event.preventDefault();
    if (!clienteSeleccionadoId) {
      return;
    }

    if (!gestionDraft.reglaId) {
      Swal.fire("Gestión", "Seleccione un motivo/acción para guardar la gestión.", "info");
      return;
    }

    if (!gestionDraft.estadoGestion) {
      Swal.fire("Gestión", "Seleccione un estado para la gestión.", "info");
      return;
    }

    if (gestionComposerScope === "ipc" && !gestionDraft.tipoIpc) {
      Swal.fire("Gestion", "Seleccione un Tipo IPC para guardar la gestion.", "info");
      return;
    }

    if (gestionComposerScope === "ipc" && !gestionDraft.ipcMotivo) {
      Swal.fire("Gestion", "Seleccione un motivo IPC para guardar la gestion.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoGestion: true }));
    try {
      const reglaIdToSave = gestionComposerScope === "ipc"
        ? Number(currentGestionRules?.[0]?.id || gestionDraft.reglaId)
        : Number(gestionDraft.reglaId);
      const observacionToSave = gestionComposerScope === "ipc"
        ? buildIpcObservation(gestionDraft.tipoIpc, gestionDraft.ipcMotivo, gestionDraft.observacion)
        : gestionDraft.observacion;
      const motivoCodigoToSave = gestionComposerScope === "ipc"
        ? buildIpcMotivoCodigo(gestionDraft.tipoIpc, gestionDraft.ipcMotivo)
        : resolveMotivoCodigoFromRegla(gestiones?.reglas || [], gestionDraft.reglaId);

      if (!reglaIdToSave) {
        Swal.fire("Gestion", "No hay una regla IPC disponible para guardar la gestion.", "info");
        return;
      }

      if (usaProtocoloOperativo && !gestionDraft.canalContacto) {
        Swal.fire("Gestion", "Debe seleccionar un canal de contacto para guardar una gestion contactada.", "info");
        return;
      }

      if (usaProtocoloOperativo && gestionDraft.canalContacto === "Llamada") {
        if (!gestionDraft.resultadoLlamada) {
          Swal.fire("Gestion", "Debe indicar el resultado de la llamada.", "info");
          return;
        }

        if (!String(observacionToSave || "").trim()) {
          Swal.fire("Gestion", "Debe completar una observacion para registrar la llamada.", "info");
          return;
        }

        if (!String(gestionDraft.evidenciaContacto || "").trim()) {
          Swal.fire("Gestion", "Debe completar la evidencia de la llamada.", "info");
          return;
        }
      }

      await crmPostventaApi.crearGestion(
        clienteSeleccionadoId,
        reglaIdToSave,
        gestionDraft.estadoGestion,
        observacionToSave,
        gestionDraft.fechaGestion,
        gestionDraft.fechaProximoContacto,
        motivoCodigoToSave,
        gestionDraft.canalContacto,
        gestionDraft.resultadoLlamada,
        gestionDraft.evidenciaContacto
      );

      Swal.fire("Ok", "La gestión se guardó correctamente.", "success");
      setGestionDraft((prev) => ({
        ...prev,
        tipoIpc: "",
        ipcMotivo: "",
        canalContacto: "",
        resultadoLlamada: "",
        evidenciaContacto: "",
        observacion: "",
        fechaProximoContacto: "",
      }));
      await cargarModulo(clienteSeleccionadoId, analyticsRange);
      navigate("/administracion/crm-postventa/gestion");
    } catch (error) {
      console.error("Error guardando gestión:", error);
      Swal.fire("Error", error.message || "No se pudo guardar la gestión.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoGestion: false }));
    }
  };

  const abrirEditarGestion = useCallback((item) => {
    setGestionEditItem(item);
    setGestionEditDraft({
      estadoResultante: item?.estado || "Pendiente",
      fechaProximoContacto: toDateTimeLocalInput(item?.proximoContacto),
      prioridad: item?.prioridad || "Media",
      observacion: item?.observacion || item?.descripcion || "",
      canalContacto: item?.canalContacto || "",
      resultadoLlamada: item?.resultadoLlamada || "",
      evidenciaContacto: item?.evidenciaContacto || "",
      resultadoZocoCheck: "",
    });
  }, []);

  const abrirFinalizarGestion = useCallback((item) => {
    setGestionFinalizarItem(item);
    setGestionFinalizarDraft({
      estadoResultante: "Cerrado",
      motivoCierre: "Resuelto",
      observacionFinal: item?.observacion || "",
      resultadoZocoCheck: "",
      resultadoLlamada: item?.resultadoLlamada || "",
      evidenciaContacto: item?.evidenciaContacto || "",
    });
  }, []);

  const handleGuardarEdicionGestion = useCallback(async (event) => {
    event.preventDefault();
    if (!clienteSeleccionadoId || !gestionEditItem?.id) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoGestion: true }));
    try {
      await crmPostventaApi.actualizarGestion({
        ClienteId: clienteSeleccionadoId,
        Id: gestionEditItem.id,
        EstadoResultante: gestionEditDraft.estadoResultante,
        Observacion: gestionEditDraft.observacion,
        FechaProximoContacto: gestionEditDraft.fechaProximoContacto || null,
        Prioridad: gestionEditDraft.prioridad,
        CanalContacto: gestionEditDraft.canalContacto,
        ResultadoLlamada: gestionEditDraft.resultadoLlamada,
        EvidenciaContacto: gestionEditDraft.evidenciaContacto,
        ResultadoZocoCheck: gestionEditDraft.resultadoZocoCheck,
      });

      setGestionEditItem(null);
      await cargarModulo(clienteSeleccionadoId, analyticsRange);
      Swal.fire("Gestión", "La gestión se actualizó correctamente.", "success");
    } catch (error) {
      console.error("Error actualizando gestión:", error);
      Swal.fire("Gestión", error.message || "No se pudo actualizar la gestión.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoGestion: false }));
    }
  }, [analyticsRange, cargarModulo, clienteSeleccionadoId, gestionEditDraft, gestionEditItem]);

  const handleFinalizarGestionExistente = useCallback(async (event) => {
    event.preventDefault();
    if (!clienteSeleccionadoId || !gestionFinalizarItem?.id) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoGestion: true }));
    try {
      await crmPostventaApi.finalizarGestion({
        ClienteId: clienteSeleccionadoId,
        Id: gestionFinalizarItem.id,
        EstadoResultante: gestionFinalizarDraft.motivoCierre === "Sin respuesta"
          ? "Sin respuesta"
          : gestionFinalizarDraft.motivoCierre === "Descartado"
            ? "Descartado"
            : gestionFinalizarDraft.estadoResultante,
        ObservacionFinal: gestionFinalizarDraft.observacionFinal,
        MotivoCierre: gestionFinalizarDraft.motivoCierre,
        ResultadoZocoCheck: gestionFinalizarDraft.resultadoZocoCheck,
        ResultadoLlamada: gestionFinalizarDraft.resultadoLlamada,
        EvidenciaContacto: gestionFinalizarDraft.evidenciaContacto,
      });

      setGestionFinalizarItem(null);
      await cargarModulo(clienteSeleccionadoId, analyticsRange);
      Swal.fire("Gestión", "La gestión se finalizó correctamente.", "success");
    } catch (error) {
      console.error("Error finalizando gestión:", error);
      Swal.fire("Gestión", error.message || "No se pudo finalizar la gestión.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoGestion: false }));
    }
  }, [analyticsRange, cargarModulo, clienteSeleccionadoId, gestionFinalizarDraft, gestionFinalizarItem]);

  const handleRegistrarRollo = () => {
    if (!clienteSeleccionadoId) {
      return;
    }

    setRolloDraft({
      fechaEntrega: makeToday(),
      cantidad: "",
      observacion: "",
    });
    setRolloModalOpen(true);
  };

  const handleGuardarRollo = async (event) => {
    event.preventDefault();

    if (!clienteSeleccionadoId) {
      return;
    }

    const cantidad = Number(rolloDraft.cantidad);
    if (!rolloDraft.fechaEntrega) {
      Swal.fire("Rollos", "La fecha de entrega es obligatoria.", "info");
      return;
    }

    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      Swal.fire("Rollos", "La cantidad es obligatoria y debe ser mayor a 0.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoRollo: true }));
    try {
      await crmPostventaApi.registrarRollo(
        {
          clienteId: clienteSeleccionadoId,
          clienteKey: gestiones?.rollos?.clienteKey || crmPostventaApi.buildClienteKey(clienteHeader),
          cuit: clienteHeader.cuit,
          nDeComercio: clienteHeader.nDeComercio,
        },
        rolloDraft.fechaEntrega,
        cantidad,
        rolloDraft.observacion
      );

      setRolloModalOpen(false);
      setRolloDraft({
        fechaEntrega: makeToday(),
        cantidad: "",
        observacion: "",
      });
      await cargarModulo(clienteSeleccionadoId, analyticsRange);

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Entrega registrada correctamente.",
        showConfirmButton: false,
        timer: 2400,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire("Error", error.message || "No se pudo registrar la entrega.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoRollo: false }));
    }
  };

  const showFacturacionDetail = (row) => {
    setFacturacionDetalle(row);
  };

  const aplicarFiltrosAlertas = useCallback(() => {
    const nextFilters = {
      ...alertasFiltros,
      search: alertasSearchDraft,
    };

    setAlertasFiltros(nextFilters);
    cargarAlertas(nextFilters);
  }, [alertasFiltros, alertasSearchDraft, cargarAlertas]);

  const resetAlertasFilters = useCallback(() => {
    const nextFilters = {
      fecha: makeToday(),
      estado: "",
      prioridad: "",
      tipo: "",
      provincia: "",
      responsable: "",
      search: "",
    };

    setAlertasSearchDraft("");
    setAlertasFacturacionSearch("");
    setAlertasTerminalExtraviadaSearch("");
    setAlertasCriticasExpandido(false);
    setAlertaCardColapsadas(new Set());
    setAlertasFiltros(nextFilters);
    cargarAlertas(nextFilters);
  }, [cargarAlertas]);

  const aplicarFiltrosInactivos = useCallback(() => {
    const nextFilters = {
      ...inactivosFiltros,
      fecha: alertasFiltros.fecha,
      search: inactivosSearchDraft,
    };

    setInactivosFiltros(nextFilters);
    cargarInactivos(nextFilters);
  }, [alertasFiltros.fecha, cargarInactivos, inactivosFiltros, inactivosSearchDraft]);

  const handleAlertasSearchKeyDown = useCallback((event) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    aplicarFiltrosAlertas();
  }, [aplicarFiltrosAlertas]);

  const abrirGestionFacturacion = useCallback((row, defaults = {}) => {
    if (!row?.clienteId) {
      Swal.fire("Facturacion", "La fila seleccionada no tiene un cliente asociado para registrar la gestion.", "info");
      return;
    }

    setFacturacionAccionItem(null);
    setFacturacionDetalle(null);
    setFacturacionGestionItem(row);
    setFacturacionGestionDraft({
      clienteId: row.clienteId,
      reglaId: manualAlertaRules?.[0]?.id ? String(manualAlertaRules[0].id) : "",
      estadoGestion: defaults.estadoGestion || "Pendiente",
      canalContacto: "",
      resultadoLlamada: "",
      evidenciaContacto: "",
      observacion:
        defaults.observacion ||
        `Gestion desde Facturacion. Comercio ${row.comercio || "-"} · Terminal ${row.terminal || "-"} · Estado ${row.estadoAlerta || "-"}.`,
      fechaGestion: makeNowDateTimeLocal(),
      fechaProximoContacto: "",
    });
  }, [manualAlertaRules]);

  const copiarCuitFacturacion = useCallback(async (row) => {
    const cuit = String(row?.cuit || "").trim();
    if (!cuit) {
      Swal.fire("Facturacion", "La fila seleccionada no tiene CUIT para copiar.", "info");
      return;
    }

    try {
      await navigator.clipboard.writeText(cuit);
      const feedbackKey = `${cuit}-${row?.terminal || ""}-${row?.comercio || ""}`;
      setFacturacionCuitCopiadoKey(feedbackKey);
      window.setTimeout(() => {
        setFacturacionCuitCopiadoKey((current) => (current === feedbackKey ? "" : current));
      }, 1800);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "CUIT copiado",
        showConfirmButton: false,
        timer: 1200,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire("Facturacion", "No se pudo copiar el CUIT.", "error");
    }
  }, []);

  const ejecutarAccionFacturacion = useCallback((accion, row) => {
    if (!row) {
      return;
    }

    switch (accion) {
      case "detalle":
        setFacturacionAccionItem(null);
        showFacturacionDetail(row);
        return;
      case "filtrar":
        setFacturacionAccionItem(null);
        setFacturacionFiltroComercio(row.comercio || row.terminal || "");
        return;
      case "copiarCuit":
        copiarCuitFacturacion(row);
        return;
      case "registrarGestion":
        abrirGestionFacturacion(row, {
          observacion: `Gestion desde Facturacion. Comercio ${row.comercio || "-"} · Terminal ${row.terminal || "-"} · Estado ${row.estadoAlerta || "-"}.`,
        });
        return;
      case "seguimiento":
        abrirGestionFacturacion(row, {
          estadoGestion: "Pendiente",
          observacion: `Seguimiento pendiente desde Facturacion para comercio ${row.comercio || "-"} y terminal ${row.terminal || "-"}.`,
        });
        return;
      default:
        return;
    }
  }, [abrirGestionFacturacion, copiarCuitFacturacion]);

  const guardarGestionDesdeFacturacion = async (event) => {
    event.preventDefault();

    if (!facturacionGestionDraft.clienteId || !facturacionGestionDraft.reglaId || !facturacionGestionDraft.estadoGestion) {
      Swal.fire("Facturacion", "Complete regla y estado antes de guardar la gestion.", "info");
      return;
    }

    if (facturacionGestionDraft.estadoGestion === "Contactado" && !facturacionGestionDraft.canalContacto) {
      Swal.fire("Facturacion", "Debe seleccionar un canal de contacto.", "info");
      return;
    }

    if (facturacionGestionDraft.estadoGestion === "Contactado" && facturacionGestionDraft.canalContacto === "Llamada") {
      if (!facturacionGestionDraft.resultadoLlamada || !String(facturacionGestionDraft.observacion || "").trim() || !String(facturacionGestionDraft.evidenciaContacto || "").trim()) {
        Swal.fire("Facturacion", "Para llamada debe completar resultado, observacion y evidencia.", "info");
        return;
      }
    }

    setCargas((prev) => ({ ...prev, guardandoGestion: true }));
    try {
      await crmPostventaApi.crearGestion(
        facturacionGestionDraft.clienteId,
        Number(facturacionGestionDraft.reglaId),
        facturacionGestionDraft.estadoGestion,
        facturacionGestionDraft.observacion,
        facturacionGestionDraft.fechaGestion,
        facturacionGestionDraft.fechaProximoContacto,
        "FALTA_FACTURACION",
        facturacionGestionDraft.canalContacto,
        facturacionGestionDraft.resultadoLlamada,
        facturacionGestionDraft.evidenciaContacto
      );

      Swal.fire("Ok", "La gestion se registro correctamente.", "success");
      setFacturacionGestionItem(null);
      await cargarModulo(clienteSeleccionadoId, analyticsRange);
    } catch (error) {
      console.error("Error guardando gestion desde facturacion:", error);
      Swal.fire("Error", error.message || "No se pudo registrar la gestion.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoGestion: false }));
    }
  };

  const abrirClienteDesdeAlerta = useCallback((row) => {
    if (!row?.clienteId) {
      Swal.fire("Alertas", "La alerta no tiene un cliente asociado para abrir la ficha.", "info");
      return;
    }

    localStorage.setItem(STORAGE_KEY, String(row.clienteId));
    setClienteSeleccionadoId(row.clienteId);
    setClientePendiente(null);
    setFacturacionDetalle(null);
    setTimelineDetalle(null);
    navigate("/administracion/crm-postventa/ficha");
  }, [navigate]);

  const goToGestion = (row, observacion) => {
    if (row?.clienteId) {
      localStorage.setItem(STORAGE_KEY, String(row.clienteId));
      setClienteSeleccionadoId(row.clienteId);
      setClientePendiente(null);
      setFacturacionDetalle(null);
      setTimelineDetalle(null);
    }

    const motivoCodigo = resolveMotivoCodigoFromAlerta(row);
    const targetScope = String(motivoCodigo || "").toUpperCase().startsWith("MAN_")
      ? resolveGestionScopeByReason(motivoCodigo)
      : gestionComposerScope;
    const reglasTargetScope = gestionScopeRules[targetScope] || [];

    setGestionComposerScope(targetScope);
    setGestionDraft((prev) => ({
      ...prev,
      reglaId: resolveRuleIdByMotivoCodigo(reglasTargetScope, motivoCodigo) || prev.reglaId,
      estadoGestion: prev.estadoGestion || "Pendiente",
      observacion,
    }));
    navigate("/administracion/crm-postventa/gestion");
  };

  const abrirAccionAlerta = useCallback((row, estadoGestion, observacionBase = "") => {
    if (!row?.clienteId) {
      Swal.fire("Alertas", "La alerta no tiene un cliente asociado para registrar una gestión.", "info");
      return;
    }

    const motivoCodigo = resolveMotivoCodigoFromAlerta(row);
    setAlertaGestionMode("operativa");
    setAlertaSeleccionada(row);
    setAlertaGestionDraft({
      clienteId: row.clienteId,
      reglaId: resolveRuleIdByMotivoCodigo(manualAlertaRules, motivoCodigo) || (manualAlertaRules?.[0]?.id ? String(manualAlertaRules[0].id) : ""),
      estadoGestion,
      canalContacto: "",
      resultadoLlamada: "",
      evidenciaContacto: "",
      observacion: observacionBase || buildSuggestedObservation(row, estadoGestion),
      fechaGestion: makeNowDateTimeLocal(),
      fechaProximoContacto: "",
    });
  }, [manualAlertaRules]);

  const abrirGestionInactiva = useCallback((row) => {
    if (!row?.clienteId) {
      Swal.fire("Terminales inactivas", "La fila seleccionada no tiene un cliente asociado para registrar una gestion.", "info");
      return;
    }

    setAlertaGestionMode("proactiva");
    setAlertaSeleccionada(row);
    setAlertaGestionDraft({
      clienteId: row.clienteId,
      reglaId: inactivosProactivaRules?.[0]?.id ? String(inactivosProactivaRules[0].id) : "",
      estadoGestion: "Pendiente",
      canalContacto: "",
      resultadoLlamada: "",
      evidenciaContacto: "",
      observacion: `Gestion proactiva por inactividad >90 dias. Comercio ${row.comercio || "-"} · Terminal ${row.terminal || "-"} · ${row.diasSinOperar || 0} dias sin operar.`,
      fechaGestion: makeNowDateTimeLocal(),
      fechaProximoContacto: "",
    });
  }, [inactivosProactivaRules]);

  const ejecutarAccionAlerta = useCallback((accion, row) => {
    if (!row) {
      return;
    }

    setAlertaAccionItem(null);

    switch (accion) {
      case "verCliente":
        abrirClienteDesdeAlerta(row);
        return;
      case "registrarGestion":
        goToGestion(row, buildSuggestedObservation(row, "Pendiente"));
        return;
      case "contactado":
        abrirAccionAlerta(row, "Contactado", buildSuggestedObservation(row, "Contactado"));
        return;
      case "posponer":
        abrirAccionAlerta(row, "Pendiente", buildSuggestedObservation(row, "Pendiente"));
        return;
      case "cerrar":
        abrirAccionAlerta(row, "Cerrado", buildSuggestedObservation(row, "Cerrado"));
        return;
      case "escalar":
        abrirAccionAlerta(row, "Escalado", buildSuggestedObservation(row, "Escalado"));
        return;
      case "copiarMensaje":
        copiarMensajeSugerido(row, "Alertas");
        return;
      case "whatsapp":
        abrirWhatsappSugerido(row, "Alertas");
        return;
      default:
        return;
    }
  }, [abrirAccionAlerta, abrirClienteDesdeAlerta, abrirWhatsappSugerido, copiarMensajeSugerido]);

  const guardarGestionDesdeAlerta = async (event) => {
    event.preventDefault();

    if (!alertaGestionDraft.clienteId || !alertaGestionDraft.reglaId || !alertaGestionDraft.estadoGestion) {
      Swal.fire("Alertas", "Complete regla y estado antes de guardar la gestión.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoAlertaGestion: true }));
    try {
      await crmPostventaApi.crearGestion(
        alertaGestionDraft.clienteId,
        Number(alertaGestionDraft.reglaId),
        alertaGestionDraft.estadoGestion,
        alertaGestionDraft.observacion,
        alertaGestionDraft.fechaGestion,
        alertaGestionDraft.fechaProximoContacto,
        alertaGestionMode === "proactiva"
          ? resolveMotivoCodigoFromRegla(inactivos?.reglas || alertas?.reglas || [], alertaGestionDraft.reglaId)
          : resolveMotivoCodigoFromAlerta(alertaSeleccionada)
      );

      Swal.fire("Ok", "La gestión de alerta se registró correctamente.", "success");
      setAlertaSeleccionada(null);
      if (alertaGestionMode === "proactiva") {
        await cargarInactivos(inactivosFiltros);
      } else {
        await cargarAlertas(alertasFiltros);
      }
      if (clienteSeleccionadoId === alertaGestionDraft.clienteId) {
        await cargarModulo(clienteSeleccionadoId, analyticsRange);
      }
    } catch (error) {
      console.error("Error guardando gestión desde alerta:", error);
      Swal.fire("Error", error.message || "No se pudo registrar la gestión.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoAlertaGestion: false }));
    }
  };

  const renderFicha = () => {
    if (cargas.ficha) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando ficha...</div>;
    }

    if (errores.ficha) {
      return <Alert variant="danger">{errores.ficha}</Alert>;
    }

    if (!ficha) {
      return <Alert variant="secondary">No hay ficha disponible para este cliente.</Alert>;
    }

    const header = ficha.header || clienteHeader;
    const valueMap = buildFichaValueMapByLabel(ficha);
    const fichaDateLabel = alertaHeaderClock.toLocaleDateString("es-AR");
    const fichaTimeLabel = alertaHeaderClock.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const pickFichaValue = (...keys) => {
      for (const key of keys) {
        if (key === undefined || key === null || key === "") {
          continue;
        }

        if (typeof key !== "string") {
          const fallbackValue = normalizeReadOnlyValue(key);
          if (fallbackValue !== "-") {
            return fallbackValue;
          }
          continue;
        }

        const normalizedKey = normalizeText(key);
        if (normalizedKey && Object.prototype.hasOwnProperty.call(valueMap, normalizedKey)) {
          const rawValue = normalizeReadOnlyValue(valueMap[normalizedKey]);
          if (rawValue !== "-") {
            return rawValue;
          }
        }
      }

      return "-";
    };
    const resolveAnticipoDisplay = (value) =>
      normalizeText(value).toLowerCase() === "activo" ? "Inactivo" : "Activo";
    const handleTerminalTabsPointerDown = (event) => {
      if (event.button !== 0) {
        return;
      }

      terminalTabsDragRef.current = {
        isDragging: true,
        startX: event.clientX,
        scrollLeft: event.currentTarget.scrollLeft,
        moved: false,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.style.cursor = "grabbing";
    };
    const handleTerminalTabsPointerMove = (event) => {
      const drag = terminalTabsDragRef.current;
      if (!drag.isDragging) {
        return;
      }

      const distance = event.clientX - drag.startX;
      if (Math.abs(distance) > 4) {
        drag.moved = true;
      }
      event.currentTarget.scrollLeft = drag.scrollLeft - distance;
    };
    const handleTerminalTabsPointerUp = (event) => {
      const drag = terminalTabsDragRef.current;
      if (!drag.isDragging) {
        return;
      }

      drag.isDragging = false;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      event.currentTarget.style.cursor = "";
    };
    const handleTerminalTabsClick = (event) => {
      if (!terminalTabsDragRef.current.moved) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      terminalTabsDragRef.current.moved = false;
    };

    return (
      <div className="crm-postventa-ficha-screen">
        <div className="crm-postventa-ficha-header">
          <div className="crm-postventa-ficha-header-copy">
            <span>CRM POSTVENTAS</span>
            <h2>Ficha del cliente</h2>
          </div>
          <div className="crm-postventa-ficha-header-clock">
            {fichaDateLabel} <span>|</span> {fichaTimeLabel}
          </div>
        </div>

        <section className="crm-postventa-ficha-summary-card">
          <div className="crm-postventa-ficha-summary-copy">
            <h3>{header.nombreDeFantasia || header.razonSocial || "Cliente"}</h3>
            <div className="crm-postventa-ficha-summary-badges">
              <CompactFichaBadge label="CUIT" value={header.cuit} />
              <CompactFichaBadge label="Estado" value={header.estado} accent={String(header.estado || "").toUpperCase() === "ALTA"} />
              <CompactFichaBadge label="Terminal" value={header.nDeTerminal} />
              <CompactFichaBadge label="Comercio" value={header.nDeComercio} />
              <CompactFichaBadge label="Legajo" value={header.legajo} />
            </div>
          </div>

          <button type="button" className="crm-postventa-ficha-change-client" onClick={() => setClienteModalOpen(true)}>
            <FontAwesomeIcon icon={faUserPen} />
            Cambiar Cliente
          </button>
        </section>

        <div className="crm-postventa-ficha-toolbar">
          <div
            className="crm-postventa-terminal-tabs crm-postventa-terminal-tabs--ficha"
            role="tablist"
            aria-label="Terminales del cliente"
            onPointerDown={handleTerminalTabsPointerDown}
            onPointerMove={handleTerminalTabsPointerMove}
            onPointerUp={handleTerminalTabsPointerUp}
            onPointerCancel={handleTerminalTabsPointerUp}
            onClick={handleTerminalTabsClick}
          >
            {terminalesGrupo.map((terminal, index) => (
              <button
                key={terminal.clienteId || terminal.nDeTerminal || terminal.terminal || index}
                type="button"
                role="tab"
                aria-selected={terminal.clienteId === clienteSeleccionadoId}
                className={`crm-postventa-terminal-tab crm-postventa-terminal-tab--ficha ${terminal.clienteId === clienteSeleccionadoId ? "is-active" : ""}`}
                onClick={() => handleCambiarTerminal(terminal)}
              >
                {`TERMINAL ${index + 1}`}
              </button>
            ))}
          </div>

          <div className="crm-postventa-ficha-actions">
            <button
              type="button"
              className="crm-postventa-ficha-edit-btn"
              ref={editFichaTriggerRef}
              onClick={abrirEdicionFicha}
              disabled={!ficha?.edicion?.permiteEdicion || !fichaCamposEdicion.length}
            >
              <FontAwesomeIcon icon={faUserPen} />
              Editar datos
            </button>
            <button
              type="button"
              className={`crm-postventa-ficha-refresh-btn ${cargas.ficha ? "is-loading" : ""}`}
              onClick={() => cargarModulo(clienteSeleccionadoId, analyticsRange)}
              aria-label="Actualizar ficha"
              disabled={cargas.ficha}
            >
              <FontAwesomeIcon icon={faArrowsRotate} />
            </button>
          </div>
        </div>

        <div className="crm-postventa-ficha-grid crm-postventa-ficha-grid--figma">
          <ClientInfoCard title="Identificación">
            <div className="crm-postventa-ficha-fields-grid">
              <ReadOnlyField label="N.° de terminal" value={header.nDeTerminal} />
              <ReadOnlyField
                label="Estado"
                value={header.estado}
                children={
                  <span className="crm-postventa-status-inline">
                    <span className="crm-postventa-status-inline__icon">
                      <FontAwesomeIcon icon={faCheck} />
                    </span>
                    {normalizeReadOnlyValue(header.estado)}
                  </span>
                }
              />
              <ReadOnlyField label="N.° de comercio" value={header.nDeComercio} />
              <ReadOnlyField label="Legajo" value={header.legajo} />
              <ReadOnlyField className="is-span-2" label="Nombre de fantasía" value={header.nombreDeFantasia || header.razonSocial} multiline />
              <ReadOnlyField label="Rubro" value={pickFichaValue("rubro", header.rubro)} />
              <ReadOnlyField label="Fecha de alta" value={pickFichaValue("fecha de alta", "fecha alta", header.fechaAlta)} />
              <ReadOnlyField label="Tipo de alta" value={pickFichaValue("tipo de alta", header.tipoAlta)} />
              <ReadOnlyField label="Fecha de baja" value={pickFichaValue("fecha de baja", header.fechaBaja)} />
              <ReadOnlyField label="Tipo de baja" value={pickFichaValue("tipo de baja", header.tipoBaja)} />
              <ReadOnlyField label="Producto" value={header.producto} />
            </div>
          </ClientInfoCard>

          <ClientInfoCard title="Contacto">
            <div className="crm-postventa-ficha-fields-grid">
              <ReadOnlyField className="is-span-2" label="Domicilio" value={pickFichaValue("domicilio", "direccion", "calle")} multiline />
              <ReadOnlyField className="is-span-2" label="Localidad" value={pickFichaValue("localidad")} multiline />
              <ReadOnlyField label="Código postal" value={pickFichaValue("codigo postal", "codigo postal comercio")} />
              <ReadOnlyField label="Provincia" value={pickFichaValue("provincia")} />
              <ReadOnlyField label="Teléfono 1" value={pickFichaValue("telefono", "telefono 1", "celular", "celular / whatsapp")} />
              <ReadOnlyField label="Puesto / cargo / referencia 1" value={pickFichaValue("referencia telefono", "puesto / cargo / referencia 1", "apoderado")} />
              <ReadOnlyField label="Teléfono 2" value={pickFichaValue("telefono alternativo", "telefono 2", "tel fijo")} />
              <ReadOnlyField label="Puesto / cargo / referencia 2" value={pickFichaValue("referencia telefono alternativo", "puesto / cargo / referencia 2")} />
              <ReadOnlyField className="is-span-2" label="Email" value={pickFichaValue("correo", "email", "mail", header.mail)} multiline />
            </div>
          </ClientInfoCard>

          <ClientInfoCard title="Fiscal">
            <div className="crm-postventa-ficha-fields-grid">
              <ReadOnlyField className="is-span-2" label="Razón social" value={pickFichaValue("razon social", header.razonSocial)} multiline />
              <ReadOnlyField label="CUIT razón social" value={pickFichaValue("cuit razon social", "cuit", header.cuit)} />
              <ReadOnlyField label="Estado AFIP" value={pickFichaValue("estado afip", "condicion afip")} />
              <ReadOnlyField label="Código de actividad" value={pickFichaValue("cod. actividad", "codigo de actividad", "codigo actividad", "cod actividad")} />
              <ReadOnlyField label="Exento" value={pickFichaValue("exento")} />
              <ReadOnlyField className="is-span-2" label="Actividad" value={pickFichaValue("actividad", "actividad laboral / profesional")} multiline />
              <ReadOnlyField className="is-span-2" label="Actividad 2" value={pickFichaValue("actividad 2", "actividad2")} multiline />
              <ReadOnlyField label="Estado rentas" value={pickFichaValue("estado rentas")} />
              <ReadOnlyField label="Provincia fiscal" value={pickFichaValue("provincia fiscal", "provincia3")} />
              <ReadOnlyField className="is-span-2" label="Dirección fiscal" value={pickFichaValue("direccion fiscal", "domicilio fiscal", "domicilio")} multiline />
              <ReadOnlyField label="Alicuota" value={pickFichaValue("alicuota")} />
              <ReadOnlyField label="Código de actividad rentas" value={pickFichaValue("cod. actividad rentas", "codigo de actividad rentas", "codigo actividad rentas", "cod actividad rentas")} />
              <ReadOnlyField className="is-span-2" label="Actividad rentas" value={pickFichaValue("actividad rentas")} multiline />
            </div>
          </ClientInfoCard>

          <ClientInfoCard title="Bancario / Operativo">
            <div className="crm-postventa-ficha-fields-grid">
              <ReadOnlyField className="is-span-2" label="Banco" value={pickFichaValue("banco")} />
              <ReadOnlyField className="is-span-2" label="Tipo de cuenta" value={pickFichaValue("tipo de cuenta")} />
              <ReadOnlyField label="CBU / CVU" value={pickFichaValue("cbu / cvu", "cbucvu", "cbu", "cvu")} />
              <ReadOnlyField label="Link de pago" value={pickFichaValue("link de pago")} />
              <ReadOnlyField className="is-span-2" label="N.° de cuenta" value={pickFichaValue("n de cuenta", "numero de cuenta", "n° de cuenta")} />
              <ReadOnlyField className="is-span-2" label="Alias" value={pickFichaValue("alias")} />
              <ReadOnlyField label="Autorizado para cambios ABM" value={pickFichaValue("autorizado para cambios abm", "autorizado cambios abm")} />
              <ReadOnlyField label="Anticipo activo" value={resolveAnticipoDisplay(pickFichaValue("anticipo activo"))} />
              <ReadOnlyField className="is-span-2" label="Excepción impuestos" value={pickFichaValue("excepcion impuestos", "excepción impuestos")} multiline />
            </div>
          </ClientInfoCard>
        </div>

        <section className="crm-postventa-ficha-card crm-postventa-ficha-history-card">
          <div className="crm-postventa-ficha-history-header">
            <div>
              <h4>Historial de cambios</h4>
              <span>
                {ficha?.historialCambios?.length || 0} {(ficha?.historialCambios?.length || 0) === 1 ? "movimiento registrado" : "movimientos registrados"}
              </span>
            </div>
          </div>
          {ficha?.historialCambios?.length ? (
            <div className="crm-postventa-ficha-history-scroll">
              <div className="crm-postventa-ficha-history-list">
                {ficha.historialCambios.map((item, index) => (
                  <article className="crm-postventa-ficha-history-entry" key={`ficha-hist-v2-${index}`}>
                    <div className="crm-postventa-ficha-history-meta">
                      <span className="crm-postventa-ficha-history-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faFileLines} />
                      </span>
                      <time>{item.fecha || "-"}</time>
                      <strong>{item.usuario || "-"}</strong>
                      <span>{item.tipo || "-"}</span>
                    </div>
                    <div className="crm-postventa-ficha-history-change-panel">
                      <FichaHistoryChanges values={item.valoresModificados} />
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ) : (
            <div className="crm-postventa-ficha-history-empty">No hay modificaciones CRM registradas para esta ficha.</div>
          )}
        </section>

        <div className="crm-postventa-ficha-grid">
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Identificación</h4>
            <DataList items={ficha.datosGenerales} />
          </section>
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Contacto</h4>
            <DataList items={ficha.contacto} />
          </section>
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Fiscal</h4>
            <DataList items={ficha.fiscal} />
          </section>
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Bancario / operativo</h4>
            <DataList items={ficha.operativo} />
          </section>
        </div>

        <section className={`crm-postventa-ficha-block mt-3 ${darkMode ? "is-dark" : ""}`}>
          <h4>Historial de cambios</h4>
          {ficha?.historialCambios?.length ? (
            <div className="crm-postventa-table-wrap crm-postventa-ficha-history-scroll">
              <Table className="crm-postventa-table mb-0" responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>Tipo</th>
                    <th>Valores modificados</th>
                  </tr>
                </thead>
                <tbody>
                  {ficha.historialCambios.map((item, index) => (
                    <tr key={`ficha-hist-${index}`}>
                      <td>{item.fecha || "-"}</td>
                      <td>{item.usuario || "-"}</td>
                      <td>{item.tipo || "-"}</td>
                      <td><FichaHistoryChanges values={item.valoresModificados} /></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="secondary" className="mb-0">No hay modificaciones CRM registradas para esta ficha.</Alert>
          )}
        </section>

        <Modal
          show={fichaEditOpen}
          onHide={cerrarEdicionFicha}
          onEntered={() => clientEditCloseButtonRef.current?.focus()}
          onExited={() => editFichaTriggerRef.current?.focus()}
          centered
          className="client-edit-modal"
          dialogClassName="client-edit-modal__dialog"
          contentClassName="client-edit-modal__content"
          backdropClassName="client-edit-modal__backdrop"
          aria-labelledby="client-edit-modal-title"
        >
          <Form className="client-edit-modal__form" onSubmit={guardarEdicionFicha}>
            <Modal.Header className="client-edit-modal__header">
              <h2 id="client-edit-modal-title" className="client-edit-modal__title">Editar datos del cliente</h2>
              <button
                ref={clientEditCloseButtonRef}
                type="button"
                className="client-edit-modal__close"
                aria-label="Cerrar edición de datos"
                onClick={cerrarEdicionFicha}
              >
                <span aria-hidden="true">×</span>
              </button>
            </Modal.Header>
            <Modal.Body className="client-edit-modal__body">
              <section className="client-edit-modal__summary">
                <strong className="client-edit-modal__summary-name">
                  {clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Cliente"}
                </strong>
                <span className="client-edit-modal__summary-pill">CUIT <b>{clienteHeader.cuit || "-"}</b></span>
                <span className="client-edit-modal__summary-pill">
                  Estado{" "}
                  <b className={String(clienteHeader.estado || "").toUpperCase() === "ALTA" ? "is-positive" : ""}>
                    {clienteHeader.estado || "-"}
                  </b>
                </span>
                <span className="client-edit-modal__summary-pill">Terminal <b>{clienteHeader.nDeTerminal || "-"}</b></span>
                <span className="client-edit-modal__summary-pill">Comercio <b>{clienteHeader.nDeComercio || "-"}</b></span>
                <span className="client-edit-modal__summary-pill">Legajo <b>{clienteHeader.legajo || "-"}</b></span>
              </section>

              {fichaCatalogosLoading ? (
                <Alert variant="secondary" className="mb-0">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando opciones de edici?n...
                </Alert>
              ) : (
                <>
                  <div className="client-edit-modal__grid">
                    {clientEditSections.map((section) => (
                      <section key={section.key} className="client-edit-modal__card">
                        <h3>{section.title}</h3>
                        <div className="client-edit-modal__fields">
                          {section.items.map((item) => (
                            <Form.Group
                              key={item.key}
                              className={`client-edit-modal__field ${isClientEditFullWidthField(item) ? "is-full" : ""}`}
                            >
                              <Form.Label>{item.label}</Form.Label>
                              {renderFichaEditField(item)}
                              {item.readOnlyReason ? (
                                <div className="client-edit-modal__help">{item.readOnlyReason}</div>
                              ) : null}
                            </Form.Group>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                  <div className="client-edit-modal__actions">
                    <Button
                      type="submit"
                      className="client-edit-modal__submit"
                      disabled={cargas.guardandoFicha || fichaCatalogosLoading}
                    >
                      {cargas.guardandoFicha ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          <span>Guardando...</span>
                        </>
                      ) : (
                        "Guardar cambios"
                      )}
                    </Button>
                  </div>
                </>
              )}
            </Modal.Body>
          </Form>
        </Modal>
      </div>
    );
  };

  const renderFacturacion = () => {
    if (cargas.facturacion) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando facturación...</div>;
    }

    if (errores.facturacion) {
      return <Alert variant="danger">{errores.facturacion}</Alert>;
    }

    if (!facturacion?.tieneDatosReales) {
      return <Alert variant="secondary">{facturacion?.message || "No hay datos reales de facturación disponibles para este cliente."}</Alert>;
    }

    return (
      <div className="crm-postventa-alert-layout d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-range">
            <Form.Control
              type="date"
              value={analyticsRange.desde}
              onChange={(event) => setAnalyticsRange((prev) => ({ ...prev, desde: event.target.value }))}
            />
            <Form.Control
              type="date"
              value={analyticsRange.hasta}
              onChange={(event) => setAnalyticsRange((prev) => ({ ...prev, hasta: event.target.value }))}
            />
            <Button variant="outline-success" onClick={() => cargarModulo(clienteSeleccionadoId, analyticsRange)}>
              <FontAwesomeIcon className="me-2" icon={faArrowsRotate} />
              Actualizar
            </Button>
          </div>
          {facturacionFiltroComercio ? (
            <Button variant="outline-secondary" onClick={() => setFacturacionFiltroComercio("")}>
              Ver todo el grupo
            </Button>
          ) : null}
        </div>

        <div className="crm-postventa-card-grid crm-postventa-alert-kpis">
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Fecha referencia</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {facturacion.resumen.fechaReferencia || facturacion.resumen.fechaHasta || "-"}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Monto hoy</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.montoHoy)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Ayer</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.montoAyer)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Promedio 3 días</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.promedio3Dias)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Promedio 7 días</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.promedio7Dias)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Terminales</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.terminales}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Con facturación</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.conFacturacion}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Sin facturación</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.sinFacturacion}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Caída fuerte</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.caidaFuerte}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Última actividad</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {facturacion.resumen.ultimaActividad || "Sin datos"}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">CUIT del grupo</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {facturacion.resumen.cuitDelGrupo || "-"}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Variación vs ayer</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {percent(facturacion.resumen.variacionVsAyer)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Variación vs 7 días</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {percent(facturacion.resumen.variacionVs7Dias)}
            </div>
          </div>
        </div>

        <div className="crm-postventa-insights crm-postventa-alert-insights">
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Motivo principal</span>
            <strong>{facturacion.insights.motivoPrincipal}</strong>
          </article>
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Insight</span>
            <strong>{facturacion.insights.insight}</strong>
          </article>
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Acción sugerida</span>
            <strong>{facturacion.insights.accionSugerida}</strong>
          </article>
        </div>

        <div className="crm-postventa-table-wrap crm-postventa-table-wrap-scroll crm-postventa-table-wrap-safe">
          <Table className="crm-postventa-table" responsive hover>
            <thead className="table-light">
              <tr>
                <th>CUIT</th>
                <th>Terminal</th>
                <th>Comercio</th>
                <th>Telefono</th>
                <th>Estado</th>
                <th>Hoy</th>
                <th>Ayer</th>
                <th>Prom. 3d</th>
                <th>Prom. 7d</th>
                <th>Var. vs ayer</th>
                <th>Var. vs 7d</th>
                <th>Días sin fact.</th>
                <th>Última actividad</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filasFacturacion.map((row) => (
                <tr key={`${row.cuit}-${row.terminal}-${row.comercio}`}>
                  <td className="text-nowrap">{row.cuit || "-"}</td>
                  <td>{row.terminal || "-"}</td>
                  <td>{row.comercio || "-"}</td>
                  <td>
                    {buildFacturacionPhoneLines(row).length ? (
                      buildFacturacionPhoneLines(row).map((line) => <div key={`${row.terminal}-${line}`}>{line}</div>)
                    ) : (
                      <div>-</div>
                    )}
                    {resolvePreferredWhatsappPhone(row) ? (
                      <Button
                        size="sm"
                        variant="outline-success"
                        className="crm-postventa-action-button mt-2"
                        onClick={() => abrirWhatsappSugerido(row, "Facturacion")}
                      >
                        WhatsApp
                      </Button>
                    ) : null}
                  </td>
                  <td>
                    <PostventaBadge
                      value={row.estadoAlerta}
                      tone={
                        row.estadoAlerta === "OK"
                          ? "success"
                          : row.estadoAlerta === "Inactivo" || row.estadoAlerta === "Caída vs promedio"
                            ? "warning"
                            : "danger"
                      }
                    />
                  </td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.montoHoy)}</td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.montoAyer)}</td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.promedio3Dias)}</td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.promedio7Dias)}</td>
                  <td className="text-nowrap">{percent(row.variacionVsAyer)}</td>
                  <td className="text-nowrap">{percent(row.variacion)}</td>
                  <td>{row.diasGrupoSinFacturar != null ? row.diasGrupoSinFacturar : "-"}</td>
                  <td className="text-nowrap">{row.ultimaActividad || "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="crm-postventa-action-button"
                      onClick={() => setFacturacionAccionItem(row)}
                    >
                      Acciones
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <Modal show={Boolean(facturacionAccionItem)} onHide={() => setFacturacionAccionItem(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Acciones de facturacion</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-grid gap-2">
            <div className="crm-postventa-subtle crm-postventa-action-summary p-0">
              <strong>{facturacionAccionItem?.nombreDeFantasia || "Cliente"}</strong>
              <span>Comercio {facturacionAccionItem?.comercio || "-"}</span>
              <span>Terminal {facturacionAccionItem?.terminal || "-"}</span>
              <span>CUIT {facturacionAccionItem?.cuit || "-"}</span>
              <span>Telefono {buildFacturacionPhoneLines(facturacionAccionItem).join(" | ") || "-"}</span>
            </div>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("detalle", facturacionAccionItem)}>
              Ver detalle
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("filtrar", facturacionAccionItem)}>
              Ver solo este comercio
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("copiarCuit", facturacionAccionItem)}>
              Copiar CUIT
            </Button>
            {resolvePreferredWhatsappPhone(facturacionAccionItem) ? (
              <Button variant="outline-success" onClick={() => abrirWhatsappSugerido(facturacionAccionItem, "Facturacion")}>
                Abrir WhatsApp
              </Button>
            ) : null}
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("registrarGestion", facturacionAccionItem)}>
              Registrar gestion
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("seguimiento", facturacionAccionItem)}>
              Marcar seguimiento
            </Button>
          </Modal.Body>
        </Modal>

        <Modal show={Boolean(facturacionGestionItem)} onHide={() => setFacturacionGestionItem(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Registrar gestion desde Facturacion</Modal.Title>
          </Modal.Header>
          <Form onSubmit={guardarGestionDesdeFacturacion}>
            <Modal.Body>
              <div className="crm-postventa-subtle crm-postventa-action-summary p-0 mb-3">
                <strong>{facturacionGestionItem?.nombreDeFantasia || "Cliente"}</strong>
                <span>Comercio {facturacionGestionItem?.comercio || "-"}</span>
                <span>Terminal {facturacionGestionItem?.terminal || "-"}</span>
                <span>CUIT {facturacionGestionItem?.cuit || "-"}</span>
                <span>Telefono {buildFacturacionPhoneLines(facturacionGestionItem).join(" | ") || "-"}</span>
              </div>
              <div className="crm-postventa-form-grid">
                <Form.Group>
                  <Form.Label>Regla / accion</Form.Label>
                  <Form.Select
                    value={facturacionGestionDraft.reglaId}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, reglaId: event.target.value }))}
                  >
                    <option value="">Seleccionar</option>
                    {manualAlertaRules.map((regla) => (
                      <option key={regla.id} value={regla.id}>{formatGestionRuleLabel(regla)}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={facturacionGestionDraft.estadoGestion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, estadoGestion: event.target.value }))}
                  >
                    {(GESTION_SCOPE_STATES.operativa || GESTION_STATES).map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Fecha / hora</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={facturacionGestionDraft.fechaGestion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, fechaGestion: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Proximo contacto</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={facturacionGestionDraft.fechaProximoContacto}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Observacion</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={facturacionGestionDraft.observacion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setFacturacionGestionItem(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                {cargas.guardandoGestion ? "Guardando..." : "Guardar gestion"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {facturacionDetalle ? (
          <div className="crm-postventa-detail-grid">
            <section className={`crm-postventa-detail-card ${darkMode ? "is-dark" : ""}`}>
              <div className="crm-postventa-panel-title">
                <h4>Detalle de facturación</h4>
                <Button variant="outline-secondary" size="sm" onClick={() => setFacturacionDetalle(null)}>
                  Cerrar
                </Button>
              </div>
              <p className="mb-3">
                {facturacionDetalle.nombreDeFantasia || "Cliente"} · Terminal {facturacionDetalle.terminal || "-"} · Comercio {facturacionDetalle.comercio || "-"}
              </p>
              <div className="crm-postventa-detail-table-wrap">
                <Table className="crm-postventa-table mb-0" hover>
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Total bruto</th>
                      <th>Operaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturacionDetalle.serieDiaria?.map((item) => (
                      <tr key={`${facturacionDetalle.terminal}-${item.fecha}`}>
                        <td>{item.fecha}</td>
                        <td>{formatCurrencyDetailed(item.totalBruto)}</td>
                        <td>{item.cantidadOperaciones || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </section>
            <aside className={`crm-postventa-detail-card ${darkMode ? "is-dark" : ""}`}>
              <h4 className="mb-3">Resumen rápido</h4>
              <DataList
                items={[
                  { label: "Estado", value: facturacionDetalle.estadoAlerta, isBadge: true, tone: facturacionDetalle.estadoAlerta === "OK" ? "success" : "danger" },
                  { label: "Telefono", value: buildFacturacionPhoneLines(facturacionDetalle).join(" | ") || "-" },
                  { label: "Monto hoy", value: formatCurrencyDetailed(facturacionDetalle.montoHoy) },
                  { label: "Promedio 7 días", value: formatCurrencyDetailed(facturacionDetalle.promedio7Dias) },
                  { label: "Variación", value: percent(facturacionDetalle.variacion) },
                  { label: "Última actividad", value: facturacionDetalle.ultimaActividad || "Sin datos" },
                  { label: "Acción sugerida", value: facturacionDetalle.accionSugerida || "-" },
                ]}
              />
            </aside>
          </div>
        ) : null}
      </div>
    );
  };

  const renderAlertas = () => {
    const gestionModalRules = alertaGestionMode === "proactiva" ? inactivosProactivaRules : manualAlertaRules;
    const gestionModalStates =
      alertaGestionMode === "proactiva"
        ? (GESTION_SCOPE_STATES.proactiva || GESTION_STATES)
        : (GESTION_SCOPE_STATES.operativa || GESTION_STATES);
    const gestionModalTitle =
      alertaGestionMode === "proactiva"
        ? "Gestionar terminal inactiva"
        : (alertaSeleccionada?.tipoAlerta || "Registrar gestión de alerta");

    return (
      <>
        <LegacyAlertCenter
          darkMode={darkMode}
          cargas={cargas.alertas}
          errores={errores.alertas}
          alertas={alertas}
          alertasFiltros={alertasFiltros}
          setAlertasFiltros={setAlertasFiltros}
          alertasSearchDraft={alertasSearchDraft}
          setAlertasSearchDraft={setAlertasSearchDraft}
          handleAlertasSearchKeyDown={handleAlertasSearchKeyDown}
          aplicarFiltrosAlertas={aplicarFiltrosAlertas}
          resetAlertasFilters={resetAlertasFilters}
          alertaHeaderClockLabel={alertaHeaderClockLabel}
          alertasCriticasExpandido={alertasCriticasExpandido}
          setAlertasCriticasExpandido={setAlertasCriticasExpandido}
          copyToClipboard={copyToClipboard}
          alertasFacturacionFiltradas={alertasFacturacionFiltradas}
          alertasFacturacionSearch={alertasFacturacionSearch}
          setAlertasFacturacionSearch={setAlertasFacturacionSearch}
          alertasTerminalExtraviadaFiltradas={alertasTerminalExtraviadaFiltradas}
          alertasTerminalExtraviadaSearch={alertasTerminalExtraviadaSearch}
          setAlertasTerminalExtraviadaSearch={setAlertasTerminalExtraviadaSearch}
          alertaCardColapsadas={alertaCardColapsadas}
          toggleAlertCard={(cardId) => toggleCardColapsada(setAlertaCardColapsadas, cardId)}
          setAlertaAccionItem={setAlertaAccionItem}
          currency={currency}
          percent={percent}
          PostventaBadge={PostventaBadge}
          toneForPriority={toneForPriority}
          toneForGestionState={toneForGestionState}
          toneForSegment={toneForSegment}
          toneForPattern={toneForPattern}
          renderZocoCheckPanel={() => (
            <ZocoCheckPanel
              darkMode={darkMode}
              rows={alertasZocoCheckFiltradas}
              totalRows={alertas?.alertasZocoCheck?.length || 0}
              reglas={alertas?.reglas || []}
              currentUserName={gestiones?.usuarioActual || ""}
              isAdmin={canManageZocoCheck}
              onRefresh={() => cargarAlertas(alertasFiltros)}
            />
          )}
          inactivos={inactivos}
          cargasInactivos={cargas.inactivos}
          erroresInactivos={errores.inactivos}
          inactivosFiltrados={inactivosFiltrados}
          inactivosSearchDraft={inactivosSearchDraft}
          setInactivosSearchDraft={setInactivosSearchDraft}
          inactivosFiltros={inactivosFiltros}
          setInactivosFiltros={setInactivosFiltros}
          inactivosProvinciaOptions={inactivosProvinciaOptions}
          aplicarFiltrosInactivos={aplicarFiltrosInactivos}
          inactivoCardColapsadas={inactivoCardColapsadas}
          toggleInactivoCard={(cardId) => toggleCardColapsada(setInactivoCardColapsadas, cardId)}
          ejecutarAccionAlerta={ejecutarAccionAlerta}
          abrirGestionInactiva={abrirGestionInactiva}
        />

        <Modal show={Boolean(alertaAccionItem)} onHide={() => setAlertaAccionItem(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Acciones de alerta</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-grid gap-2">
            <div className="crm-postventa-subtle p-0">
              {alertaAccionItem?.comercio || "Cliente"} · {alertaAccionItem?.tipoAlerta || "-"}
            </div>
            <div className="crm-postventa-subtle p-0">
              Acción sugerida: {alertaAccionItem?.accionSugerida || "-"}
            </div>
            <div className="crm-postventa-subtle p-0">
              Mensaje sugerido: {alertaAccionItem?.mensajeSugerido || "-"}
            </div>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("verCliente", alertaAccionItem)}>
              Ver cliente
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("copiarMensaje", alertaAccionItem)}>
              Copiar mensaje
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("whatsapp", alertaAccionItem)}>
              Abrir WhatsApp
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("registrarGestion", alertaAccionItem)}>
              Registrar gestión
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("contactado", alertaAccionItem)}>
              Marcar como contactado
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("posponer", alertaAccionItem)}>
              Posponer seguimiento
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("cerrar", alertaAccionItem)}>
              Cerrar alerta
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("escalar", alertaAccionItem)}>
              Escalar
            </Button>
          </Modal.Body>
        </Modal>

        <Modal show={Boolean(alertaSeleccionada)} onHide={() => setAlertaSeleccionada(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{gestionModalTitle}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={guardarGestionDesdeAlerta}>
            <Modal.Body>
              <p className="mb-3">{alertaSeleccionada?.comercio || "Cliente"} · {alertaSeleccionada?.cuit || "-"}</p>
              <div className="crm-postventa-form-grid">
                <Form.Group>
                  <Form.Label>Regla / acción</Form.Label>
                  <Form.Select
                    value={alertaGestionDraft.reglaId}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, reglaId: event.target.value }))}
                  >
                    <option value="">Seleccionar</option>
                    {gestionModalRules.map((regla) => (
                      <option key={regla.id} value={regla.id}>{formatGestionRuleLabel(regla)}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={alertaGestionDraft.estadoGestion}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, estadoGestion: event.target.value }))}
                  >
                    {gestionModalStates.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Fecha / hora</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={alertaGestionDraft.fechaGestion}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, fechaGestion: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Próximo contacto</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={alertaGestionDraft.fechaProximoContacto}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Observación</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={alertaGestionDraft.observacion}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                  />
                </Form.Group>
              </div>
            </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setAlertaSeleccionada(null)}>
              Cancelar
            </Button>
            <Button type="submit" variant="success" disabled={cargas.guardandoAlertaGestion}>
              {cargas.guardandoAlertaGestion ? "Guardando..." : "Guardar gestión"}
            </Button>
          </Modal.Footer>
        </Form>
        </Modal>
      </>
    );
  };

  const renderOrdenesPago = () => {
    if (cargas.ordenesPago) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando órdenes de pago...</div>;
    }

    if (errores.ordenesPago) {
      return <Alert variant="danger">{errores.ordenesPago}</Alert>;
    }

    return (
      <div className="d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-form-grid">
            <Form.Group>
              <Form.Label>Fecha consultada</Form.Label>
              <Form.Control
                type="date"
                value={ordenesPagoFiltros.fecha}
                onChange={(event) => setOrdenesPagoFiltros((prev) => ({ ...prev, fecha: event.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Buscar por CUIT, comercio o documento</Form.Label>
              <Form.Control
                value={ordenesPagoFiltros.search}
                onChange={(event) => setOrdenesPagoFiltros((prev) => ({ ...prev, search: event.target.value }))}
                placeholder="CUIT, comercio o documento"
              />
            </Form.Group>
          </div>
          <Button variant="outline-secondary" onClick={() => cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros)}>
            Actualizar
          </Button>
        </div>

        <div className="crm-postventa-subtle">
          Fecha consultada: {ordenesPago?.fechaPago || ordenesPagoFiltros.fecha || "-"} · Resultado: {ordenesPago?.filas?.length || 0} documento(s)
        </div>

        <div className="crm-postventa-table-wrap">
          <Table className="crm-postventa-table mb-0" responsive hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre documento</th>
                <th>CUIT</th>
                <th>Comercio / terminal</th>
                <th>Estado conciliación</th>
                <th>Origen PDF</th>
                <th>Fecha</th>
                <th>Total bruto</th>
                <th>PDF</th>
                <th>Detalle</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {ordenesPago?.filas?.length ? (
                ordenesPago.filas.map((item) => (
                  <tr key={item.documentoKey || `${item.documentoId}-${item.cuit}`}>
                    <td>{item.documentoId}</td>
                    <td>{item.nombreDocumento || "-"}</td>
                    <td>{item.cuit || "-"}</td>
                    <td>{[item.numeroComercio, item.numeroTerminal].filter(Boolean).join(" / ") || "-"}</td>
                    <td>{item.estadoConciliacion || "-"}</td>
                    <td>{item.origenDatosPdf || "-"}</td>
                    <td>{item.fechaSubida || "-"}</td>
                    <td>{formatCurrencyCell(item.totalBruto)}</td>
                    <td>{item.pdfDisponible ? "Disponible" : "Pendiente"}</td>
                    <td>{item.mensajeConciliacion || "-"}</td>
                    <td>
                      <Button variant="outline-secondary" size="sm" onClick={() => descargarOrdenPago(item)} disabled={!item.pdfDisponible}>
                        Descargar PDF
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11">{ordenesPago?.message || "No hay órdenes de pago para mostrar."}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const renderOrdenesPagoFigma = () => {
    const headerClockLabel = formatHeaderDateTime(alertaHeaderClock);
    const terminalTabs = terminalesGrupo.length
      ? terminalesGrupo
      : clienteSeleccionadoId
        ? [{ clienteId: clienteSeleccionadoId, label: "Terminal 1" }]
        : [];
    const filas = Array.isArray(ordenesPago?.filas) ? ordenesPago.filas : [];
    const hasRows = filas.length > 0;
    const hasSearch = Boolean(String(ordenesPagoFiltros.search || "").trim());
    const isLoading = cargas.ordenesPago;
    const clientCard = (
      <section className="crm-postventa-ordenes-client">
        <div>
          <h3>{clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Cliente seleccionado"}</h3>
          <div className="crm-postventa-ordenes-client-badges">
            <span>CUIT <strong>{clienteHeader.cuit || "-"}</strong></span>
            <span>Estado <strong className="is-positive">{clienteHeader.estado || "-"}</strong></span>
            <span>Terminal <strong>{clienteHeader.nDeTerminal || "-"}</strong></span>
            <span>Comercio <strong>{clienteHeader.nDeComercio || "-"}</strong></span>
            <span>Legajo <strong>{clienteHeader.legajo || "-"}</strong></span>
          </div>
        </div>
        <button type="button" className="crm-postventa-ordenes-client-button" onClick={() => setClienteModalOpen(true)}>
          <FontAwesomeIcon icon={faUserPen} />
          Cambiar Cliente
        </button>
      </section>
    );

    const shell = (children) => (
      <div className="crm-postventa-ordenes-screen">
        <div className="crm-postventa-ordenes-header">
          <div>
            <span>CRM POSTVENTAS</span>
            <h2>Ordenes de pago</h2>
          </div>
          <time>{headerClockLabel}</time>
        </div>
        {children}
      </div>
    );

    if (errores.ordenesPago) {
      return shell(
        <>
          {clientCard}
          <Alert variant="danger" className="crm-postventa-ordenes-message">
            <div>No pudimos cargar las ordenes de pago.</div>
            <Button size="sm" variant="outline-secondary" onClick={() => cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros)}>
              Reintentar
            </Button>
          </Alert>
        </>
      );
    }

    return shell(
      <>
        {isLoading && !ordenesPago ? (
          <>
            <div className="crm-postventa-ordenes-skeleton is-client" />
            <div className="crm-postventa-ordenes-skeleton is-tabs" />
            <div className="crm-postventa-ordenes-skeleton is-table" />
          </>
        ) : (
          <>
            {clientCard}

            <div className="crm-postventa-ordenes-toolbar">
              <div className="crm-postventa-ordenes-tabs" role="tablist" aria-label="Terminales de ordenes de pago">
                {terminalTabs.length ? (
                  terminalTabs.map((terminal, index) => (
                    <button
                      key={terminal.clienteId}
                      type="button"
                      role="tab"
                      aria-selected={terminal.clienteId === clienteSeleccionadoId}
                      className={`crm-postventa-ordenes-tab ${terminal.clienteId === clienteSeleccionadoId ? "is-active" : ""}`}
                      onClick={() => handleCambiarTerminal(terminal)}
                    >
                      TERMINAL {index + 1}
                    </button>
                  ))
                ) : (
                  <span className="crm-postventa-ordenes-empty-inline">El cliente seleccionado no tiene terminales disponibles.</span>
                )}
              </div>
              <button
                type="button"
                className={`crm-postventa-ordenes-refresh ${isLoading ? "is-loading" : ""}`}
                aria-label="Actualizar ordenes de pago"
                title="Actualizar ordenes de pago"
                disabled={isLoading}
                onClick={() => cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros)}
              >
                <FontAwesomeIcon icon={faArrowsRotate} />
              </button>
            </div>

            <section className="crm-postventa-ordenes-card">
              <div className="crm-postventa-ordenes-filters">
                <Form.Group controlId="ordenes-pago-fecha">
                  <Form.Label>Mes consultado</Form.Label>
                  <div className="crm-postventa-ordenes-date-control" data-month-label={formatOrderMonthLabel(ordenesPagoFiltros.fecha)}>
                    <Form.Control
                      type="date"
                      value={ordenesPagoFiltros.fecha}
                      onClick={openNativeDatePicker}
                      onKeyDown={openNativeDatePickerFromKeyboard}
                      onChange={(event) => setOrdenesPagoFiltros((prev) => ({ ...prev, fecha: event.target.value }))}
                    />
                  </div>
                </Form.Group>
                <Form.Group controlId="ordenes-pago-search">
                  <Form.Label>Buscar</Form.Label>
                  <div className="crm-postventa-ordenes-search-control">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    <Form.Control
                      value={ordenesPagoFiltros.search}
                      onChange={(event) => setOrdenesPagoFiltros((prev) => ({ ...prev, search: event.target.value }))}
                      placeholder="Buscar por documento, CUIT, comercio o terminal"
                    />
                  </div>
                </Form.Group>
              </div>

              <div className="crm-postventa-ordenes-table-scroll">
                {isLoading ? (
                  <div className="crm-postventa-ordenes-table-skeleton" aria-live="polite">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <span key={`ordenes-row-skeleton-${index}`} />
                    ))}
                  </div>
                ) : hasRows ? (
                  <Table className="crm-postventa-ordenes-table" hover>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nombre documento</th>
                        <th>CUIT</th>
                        <th>Fecha subida</th>
                        <th>Total bruto</th>
                        <th>PDF</th>
                        <th className="is-actions">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.map((item) => {
                        const downloading = ordenPagoDescargandoKey === item.documentoKey;
                        return (
                          <tr key={item.documentoKey || `${item.documentoId}-${item.cuit}`}>
                            <td>{item.documentoId ?? "-"}</td>
                            <td>{item.nombreDocumento || "-"}</td>
                            <td>{item.cuit || "-"}</td>
                            <td>{formatOrderDate(item.fechaSubida)}</td>
                            <td>{formatOrderCurrency(item.totalBruto)}</td>
                            <td>{item.pdfDisponible ? "Disponible" : "-"}</td>
                            <td className="is-actions">
                              <button
                                type="button"
                                className="crm-postventa-ordenes-download"
                                onClick={() => descargarOrdenPagoCompacta(item)}
                                disabled={!item.pdfDisponible || downloading}
                                aria-label={`Descargar PDF de ${item.nombreDocumento || "orden de pago"}`}
                              >
                                {downloading ? <Spinner animation="border" size="sm" /> : "Descargar PDF"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <div className="crm-postventa-ordenes-empty-block">
                    {hasSearch
                      ? "No encontramos ordenes de pago que coincidan con la busqueda."
                      : ordenesPago?.message || "No hay ordenes de pago para el mes seleccionado."}
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </>
    );
  };

  // eslint-disable-next-line no-unused-vars
  const renderCupones = () => {
    if (cargas.cupones) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando cupones...</div>;
    }

    if (errores.cupones) {
      return <Alert variant="danger">{errores.cupones}</Alert>;
    }

    return (
      <div className="d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-form-grid">
            <Form.Group>
              <Form.Label>Mes</Form.Label>
              <Form.Control
                type="month"
                value={cuponesFiltros.mes}
                onChange={(event) => setCuponesFiltros((prev) => ({ ...prev, mes: event.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Buscador local</Form.Label>
              <Form.Control
                value={cuponesFiltros.search}
                onChange={(event) => setCuponesFiltros((prev) => ({ ...prev, search: event.target.value }))}
                placeholder="Cupón, tarjeta, comercio, CUIT o provincia"
              />
            </Form.Group>
          </div>
          <Button variant="outline-secondary" onClick={() => cargarCupones(clienteSeleccionadoId, cuponesFiltros)}>
            Actualizar
          </Button>
        </div>

        <div className="crm-postventa-subtle">
          Mes consultado: {cupones?.mesConsultado || formatLegacyMonthLabel(cuponesFiltros.mes) || "-"} | Resultado: {cupones?.filas?.length || 0} cupon(es)
        </div>

        <div className="crm-postventa-card-grid">
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Cantidad de cupones</span>
            <div className="crm-postventa-card-value">{cupones?.resumen?.cantidad ?? 0}</div>
          </article>
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Total bruto</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyCell(cupones?.resumen?.totalBruto ?? 0)}
            </div>
          </article>
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Fecha operación</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {cupones?.resumen?.fechaOperacionMin || "-"} a {cupones?.resumen?.fechaOperacionMax || "-"}
            </div>
          </article>
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Fecha pago</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {cupones?.resumen?.fechaPagoMin || "-"} a {cupones?.resumen?.fechaPagoMax || "-"}
            </div>
          </article>
        </div>

        <div className="crm-postventa-subtle d-none">
          Mes consultado: {cupones?.mesConsultado || formatLegacyMonthLabel(cuponesFiltros.mes) || "-"} · Resultado: {cupones?.filas?.length || 0} cupon(es)
        </div>

        <div className="crm-postventa-subtle d-none">
          Rango: {cupones?.fechaDesde || cuponesFiltros.desde || "-"} a {cupones?.fechaHasta || cuponesFiltros.hasta || "-"} · Resultado: {cupones?.filas?.length || 0} cupón(es)
        </div>

        <div className="crm-postventa-table-wrap crm-postventa-table-wrap-scroll">
          <Table className="crm-postventa-table mb-0" responsive hover>
            <thead>
              <tr>
                <th>Fecha operación</th>
                <th>Fecha de pago</th>
                <th>N° cupón</th>
                <th>N° comercio</th>
                <th>N° tarjeta</th>
                <th>Bruto</th>
                <th>Cuotas</th>
                <th>Tarjeta</th>
                <th>Provincia</th>
                <th>Comercio</th>
                <th>CUIT</th>
              </tr>
            </thead>
            <tbody>
              {cupones?.filas?.length ? (
                cupones.filas.map((item) => (
                  <tr key={buildCuponDedupeKey(item)}>
                    <td>{item.fechaOperacion || "-"}</td>
                    <td>{item.fechaDePago || "-"}</td>
                    <td>{item.nroDeCupon || "-"}</td>
                    <td>{item.nroDeComercio || "-"}</td>
                    <td>{item.nroDeTarjeta || "-"}</td>
                    <td>{currency(item.totalBruto)}</td>
                    <td>{item.cuotas || "-"}</td>
                    <td>{item.tarjeta || "-"}</td>
                    <td>{item.provincia || "-"}</td>
                    <td>{item.nombreComercio || "-"}</td>
                    <td>{item.cuit || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11">{cupones?.message || "No hay cupones para mostrar."}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const renderCuponesFigma = () => {
    const headerClockLabel = formatHeaderDateTime(alertaHeaderClock);
    const terminalTabs = terminalesGrupo.length
      ? terminalesGrupo
      : clienteSeleccionadoId
        ? [{ clienteId: clienteSeleccionadoId, label: "Terminal 1" }]
        : [];
    const filas = Array.isArray(cupones?.filas) ? cupones.filas : [];
    const hasRows = filas.length > 0;
    const hasSearch = Boolean(String(cuponesFiltros.search || "").trim());
    const isLoading = cargas.cupones;
    const resumen = cupones?.resumen || {};
    const clientCard = (
      <section className="crm-postventa-cupones-client">
        <div>
          <h3>{clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Cliente seleccionado"}</h3>
          <div className="crm-postventa-cupones-client-badges">
            <span>CUIT <strong>{clienteHeader.cuit || "-"}</strong></span>
            <span>Estado <strong className="is-positive">{clienteHeader.estado || "-"}</strong></span>
            <span>Terminal <strong>{clienteHeader.nDeTerminal || "-"}</strong></span>
            <span>Comercio <strong>{clienteHeader.nDeComercio || "-"}</strong></span>
            <span>Legajo <strong>{clienteHeader.legajo || "-"}</strong></span>
          </div>
        </div>
        <button type="button" className="crm-postventa-cupones-client-button" onClick={() => setClienteModalOpen(true)}>
          <FontAwesomeIcon icon={faUserPen} />
          Cambiar Cliente
        </button>
      </section>
    );

    const shell = (children) => (
      <div className="crm-postventa-cupones-screen">
        <div className="crm-postventa-cupones-header">
          <div>
            <span>CRM POSTVENTAS</span>
            <h2>Cupones</h2>
          </div>
          <time>{headerClockLabel}</time>
        </div>
        {children}
      </div>
    );

    if (errores.cupones) {
      return shell(
        <>
          {clientCard}
          <Alert variant="danger" className="crm-postventa-cupones-message">
            <div>No pudimos cargar los cupones.</div>
            <Button size="sm" variant="outline-secondary" onClick={() => cargarCupones(clienteSeleccionadoId, cuponesFiltros)}>
              Reintentar
            </Button>
          </Alert>
        </>
      );
    }

    return shell(
      <>
        {isLoading && !cupones ? (
          <>
            <div className="crm-postventa-cupones-skeleton is-client" />
            <div className="crm-postventa-cupones-skeleton is-tabs" />
            <div className="crm-postventa-cupones-skeleton is-table" />
          </>
        ) : (
          <>
            {clientCard}

            <div className="crm-postventa-cupones-toolbar">
              <div className="crm-postventa-cupones-tabs" role="tablist" aria-label="Terminales de cupones">
                {terminalTabs.length ? (
                  terminalTabs.map((terminal, index) => (
                    <button
                      key={terminal.clienteId}
                      type="button"
                      role="tab"
                      aria-selected={terminal.clienteId === clienteSeleccionadoId}
                      className={`crm-postventa-cupones-tab ${terminal.clienteId === clienteSeleccionadoId ? "is-active" : ""}`}
                      onClick={() => handleCambiarTerminal(terminal)}
                    >
                      TERMINAL {index + 1}
                    </button>
                  ))
                ) : (
                  <span className="crm-postventa-cupones-empty-inline">El cliente seleccionado no tiene terminales disponibles.</span>
                )}
              </div>
              <button
                type="button"
                className={`crm-postventa-cupones-refresh ${isLoading ? "is-loading" : ""}`}
                aria-label="Actualizar cupones"
                title="Actualizar cupones"
                disabled={isLoading}
                onClick={() => cargarCupones(clienteSeleccionadoId, cuponesFiltros)}
              >
                <FontAwesomeIcon icon={faArrowsRotate} />
              </button>
            </div>

            <section className="crm-postventa-cupones-card">
              <div className="crm-postventa-cupones-filters">
                <Form.Group controlId="cupones-mes">
                  <Form.Label>Mes consultado</Form.Label>
                  <div className="crm-postventa-cupones-date-control" data-month-label={formatMonthInputLabel(cuponesFiltros.mes)}>
                    <Form.Control
                      type="month"
                      value={cuponesFiltros.mes}
                      onClick={openNativeDatePicker}
                      onKeyDown={openNativeDatePickerFromKeyboard}
                      onChange={(event) => setCuponesFiltros((prev) => ({ ...prev, mes: event.target.value }))}
                    />
                  </div>
                </Form.Group>
                <Form.Group controlId="cupones-search">
                  <Form.Label>Buscar</Form.Label>
                  <div className="crm-postventa-cupones-search-control">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    <Form.Control
                      value={cuponesFiltros.search}
                      onChange={(event) => setCuponesFiltros((prev) => ({ ...prev, search: event.target.value }))}
                      placeholder="Buscar por cupon, tarjeta, comercio, CUIT o provincia"
                    />
                  </div>
                </Form.Group>
              </div>

              <div className="crm-postventa-cupones-summary">
                <span>{cupones?.mesConsultado || formatLegacyMonthLabel(cuponesFiltros.mes) || "-"}</span>
                <strong>{hasRows ? `${filas.length} cupon(es)` : "Sin resultados"}</strong>
                <span>Total bruto {formatCurrencyCell(resumen.totalBruto ?? 0)}</span>
                <span>Operacion {resumen.fechaOperacionMin || "-"} a {resumen.fechaOperacionMax || "-"}</span>
                <span>Pago {resumen.fechaPagoMin || "-"} a {resumen.fechaPagoMax || "-"}</span>
              </div>

              <div className="crm-postventa-cupones-table-scroll">
                {isLoading ? (
                  <div className="crm-postventa-cupones-table-skeleton" aria-live="polite">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <span key={`cupones-row-skeleton-${index}`} />
                    ))}
                  </div>
                ) : hasRows ? (
                  <Table className="crm-postventa-cupones-table" hover>
                    <thead>
                      <tr>
                        <th>Fecha operacion</th>
                        <th>Fecha pago</th>
                        <th>Nro cupon</th>
                        <th>Comercio</th>
                        <th>Tarjeta</th>
                        <th>Total bruto</th>
                        <th>Cuotas</th>
                        <th>Marca</th>
                        <th>Provincia</th>
                        <th>Nombre comercio</th>
                        <th>CUIT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.map((item) => (
                        <tr key={buildCuponDedupeKey(item)}>
                          <td>{item.fechaOperacion || "-"}</td>
                          <td>{item.fechaDePago || "-"}</td>
                          <td>{item.nroDeCupon || "-"}</td>
                          <td>{item.nroDeComercio || "-"}</td>
                          <td>{item.nroDeTarjeta || "-"}</td>
                          <td>{formatCurrencyCell(item.totalBruto)}</td>
                          <td>{item.cuotas || "-"}</td>
                          <td>{item.tarjeta || "-"}</td>
                          <td>{item.provincia || "-"}</td>
                          <td>{item.nombreComercio || "-"}</td>
                          <td>{item.cuit || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="crm-postventa-cupones-empty-block">
                    {hasSearch
                      ? "No encontramos cupones que coincidan con la busqueda."
                      : cupones?.message || "No hay cupones para el mes seleccionado."}
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </>
    );
  };

  const cargarCatalogoMontos = useCallback(async () => {
    if (Object.keys(montosCatalogo || {}).length || montosCatalogoLoading) {
      return;
    }

    setMontosCatalogoLoading(true);
    setMontosCatalogoError("");
    try {
      const response = await fetch(process.env.REACT_APP_API_CALCULADORA_TARJETA || "/api/calculadora/datostarjeta");
      if (!response.ok) {
        throw new Error("No pudimos cargar las tarjetas.");
      }

    const data = await response.json();
    setMontosCatalogo(data && typeof data === "object" ? data : {});
  } catch (error) {
    setMontosCatalogoError(error.message || "No pudimos cargar las tarjetas.");
  } finally {
      setMontosCatalogoLoading(false);
    }
  }, [montosCatalogo, montosCatalogoLoading]);

  const calcularPlazosAcreditacion = async (event) => {
    event.preventDefault();

    if (!plazosForm.fecha || Number.isNaN(new Date(`${plazosForm.fecha}T00:00:00`).getTime())) {
      setPlazosError("La fecha ingresada no es válida.");
      setPlazosResultado(null);
      return;
    }

    if (!plazosForm.tipo || !plazosForm.tarjeta) {
      setPlazosError("No hay información suficiente para calcular la fecha de acreditación.");
      setPlazosResultado(null);
      return;
    }

    setPlazosCalculando(true);
    setPlazosError("");
    setPlazosResultado(null);
    try {
      const response = await fetch(process.env.REACT_APP_API_CALCULADORA_PLAZOS || "/api/calculadora/plazos", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fecha: plazosForm.fecha,
          tipo: plazosForm.tipo,
          tarjeta: plazosForm.tarjeta,
        }),
      });

      if (!response.ok) {
        throw new Error("No pudimos calcular la fecha de acreditación.");
      }

      const data = await response.json();
      const result = extractPlazosPrimaryResult(data);
      if (!result) {
        setPlazosError("No hay información suficiente para calcular la fecha de acreditación.");
        return;
      }

      setPlazosResultado(result);
    } catch (error) {
      setPlazosError(error.message || "No pudimos calcular la fecha de acreditación.");
    } finally {
      setPlazosCalculando(false);
    }
  };

  const calcularMontosSimulador = async (event) => {
    event.preventDefault();

    const montoApi = normalizeMontoForApi(montosForm.monto);
    if (!montoApi || Number(montoApi) <= 0) {
      setMontosError("El monto ingresado no es válido.");
      setMontosResultado(null);
      return;
    }

    if (!montosForm.tipo || !montosForm.tarjeta || !montosForm.cuota) {
      setMontosError("No hay una configuración disponible para esta combinación.");
      setMontosResultado(null);
      return;
    }

    const token = getToken();
    if (!token) {
      setMontosError("No tenés permisos para usar esta herramienta.");
      setMontosResultado(null);
      return;
    }

    const tarjetaLabel = Object.keys(montosCatalogo || {}).find((key) => key.toLowerCase() === montosForm.tarjeta) || montosForm.tarjeta;

    setMontosCalculando(true);
    setMontosError("");
    setMontosResultado(null);
    try {
      const response = await fetch(process.env.REACT_APP_API_CALCULADORA_USUARIOS || "/api/calculadora/calculadorausuarios", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Token: token,
          Monto: montoApi,
          Cuota: montosForm.tipo === "Debito" ? "0" : montosForm.cuota,
          TipoNetoBruto: montosForm.modo,
          TipoDebCred: montosForm.tipo,
          TipoTarjeta: montosForm.tarjeta,
          Tarjeta: tarjetaLabel,
          TipoCuota: montosForm.cuota === "1" ? montosForm.tipoCuota || "normal" : null,
        }),
      });

      if (response.status === 401) {
        throw new Error("No tenés permisos para usar esta herramienta.");
      }

      if (!response.ok) {
        throw new Error("No pudimos calcular el monto.");
      }

      const data = await response.json();
      setMontosResultado(data);
    } catch (error) {
      setMontosError(error.message || "No pudimos calcular el monto.");
    } finally {
      setMontosCalculando(false);
    }
  };

  const guardarOrdenSimulada = (event) => {
    event.preventDefault();

    const validationMessage = validateOrdenesForm(ordenesForm);
    if (validationMessage) {
      setOrdenesError(validationMessage);
      setOrdenesFeedback("");
      return;
    }

    const token = getToken();
    if (!token) {
      setOrdenesError("No tenes permisos para usar esta herramienta.");
      setOrdenesFeedback("");
      return;
    }

    const tarjetaLabel = Object.keys(montosCatalogo || {}).find((label) => label.toLowerCase() === ordenesForm.tarjeta) || "";
    const payload = buildOrdenesPayload({ form: ordenesForm, token, tarjetaLabel });
    const duplicate = ordenesGuardadas.some((item) =>
      item.payload.Monto === payload.Monto &&
      item.payload.Cuota === payload.Cuota &&
      item.payload.TipoDebCred === payload.TipoDebCred &&
      item.payload.TipoTarjeta === payload.TipoTarjeta &&
      item.payload.TipoCuota === payload.TipoCuota
    );

    if (duplicate) {
      setOrdenesError("Ese dato ya fue guardado.");
      setOrdenesFeedback("");
      return;
    }

    setOrdenesGuardadas((prev) => [
      ...prev,
      {
        id: createOrdenesStableId(),
        payload,
        montoLabel: formatMontoCurrency(payload.Monto),
        cuotaLabel: payload.TipoDebCred === "Debito" ? "0" : payload.Cuota,
        tarjetaLabel,
        modalidadLabel: ORDENES_MODALIDAD_OPTIONS.find((item) => item.value === payload.TipoCuota)?.label || "-",
      },
    ]);
    setOrdenesError("");
    setOrdenesFeedback("Dato guardado correctamente.");
    setOrdenesForm((prev) => ({ ...prev, monto: "" }));
  };

  const generarOrdenesPreview = async (items, options = {}) => {
    if (!items.length) {
      setOrdenesError("Guarda al menos un registro para generar la vista previa.");
      return;
    }

    setOrdenesPreviewLoading(true);
    setOrdenesError("");
    try {
      const response = await fetch(process.env.REACT_APP_API_CALCULADORA_LIQUIDACIONES || "/api/calculadora/calculadoraliquidaciones", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(items.map((item) => item.payload)),
      });

      if (response.status === 401) {
        throw new Error("No tenes permisos para usar esta herramienta.");
      }

      if (!response.ok) {
        throw new Error("No pudimos generar la vista previa.");
      }

      const data = await response.json();
      setOrdenesPreview(Array.isArray(data) ? data : []);
      setOrdenesPreviewOpen(true);
    } catch (error) {
      setOrdenesError(error.message || "No pudimos generar la vista previa.");
    } finally {
      setOrdenesPreviewLoading(false);
      if (options.printingId) {
        setOrdenesPrintingId("");
      }
    }
  };

  const imprimirOrdenGuardada = async (item) => {
    setOrdenesPrintingId(item.id);
    await generarOrdenesPreview([item], { printingId: item.id });
  };

  const eliminarOrdenGuardada = async (item) => {
    const result = await Swal.fire({
      title: "¿Querés eliminar este dato guardado?",
      text: `${item.montoLabel} | ${item.cuotaLabel} | ${item.tarjetaLabel}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#2D3035",
    });

    if (!result.isConfirmed) {
      return;
    }

    setOrdenesDeletingId(item.id);
    setOrdenesGuardadas((prev) => prev.filter((row) => row.id !== item.id));
    setOrdenesDeletingId("");
    setOrdenesFeedback("Dato eliminado.");
    setOrdenesError("");
  };

  const renderHerramientasIntegradas = () => {
    const headerClockLabel = formatHeaderDateTime(alertaHeaderClock);
    const tabs = [
      { id: "plazos", label: "SIMULADOR DE PLAZOS", disabled: false },
      { id: "montos", label: "SIMULADOR DE MONTOS", disabled: false },
      { id: "comparador", label: "COMPARADOR", disabled: false },
      { id: "ordenes", label: "SIMULADOR DE ÓRDENES", disabled: true },
    ];
    const tarjetasDisponibles = PLAZOS_TARJETAS_POR_TIPO[plazosForm.tipo] || [];
    const selectedTipoLabel = PLAZOS_TIPO_OPTIONS.find((item) => item.value === plazosForm.tipo)?.label || plazosResultado?.type || "-";
    const calcularDisabled = plazosCalculando || !plazosForm.fecha || !plazosForm.tipo || !plazosForm.tarjeta;
    const montosCatalogoEntries = Object.entries(montosCatalogo || {});
    const montosTarjetasDisponibles = montosCatalogoEntries
      .filter(([, cuotas]) => {
        const list = Array.isArray(cuotas) ? cuotas : [];
        if (montosForm.tipo === "Debito") {
          return list.some((item) => String(item.cuota ?? item.Cuota) === "0");
        }
        if (montosForm.tipo === "Credito") {
          return list.some((item) => String(item.cuota ?? item.Cuota) !== "0");
        }
        return false;
      })
      .map(([label]) => ({ value: label.toLowerCase(), label }));
    const montosTarjetaLabel = montosTarjetasDisponibles.find((item) => item.value === montosForm.tarjeta)?.label || "";
    const montosCuotasDisponibles = montosTarjetaLabel
      ? (Array.isArray(montosCatalogo[montosTarjetaLabel]) ? montosCatalogo[montosTarjetaLabel] : [])
          .filter((item) => {
            const cuotaValue = String(item.cuota ?? item.Cuota);
            return montosForm.tipo === "Debito" ? cuotaValue === "0" : cuotaValue !== "0";
          })
          .map((item) => {
            const cuotaValue = String(item.cuota ?? item.Cuota);
            return { value: cuotaValue, label: buildMontoCuotaLabel(montosTarjetaLabel, cuotaValue) };
          })
      : [];
    const montosRequiresTipoCuota = montosForm.tipo === "Credito" && montosForm.cuota === "1";
    const montosCalcularDisabled =
      montosCalculando ||
      montosCatalogoLoading ||
      !normalizeMontoForApi(montosForm.monto) ||
      !montosForm.tipo ||
      !montosForm.tarjeta ||
      !montosForm.cuota;
    const montosModeLabel = MONTOS_MODE_OPTIONS.find((item) => item.value === montosForm.modo)?.label || "";
    const montosAmountTitle =
      montosForm.modo === "Neto"
        ? "SI RECIBÍS"
        : montosForm.modo === "SinInteres"
          ? "PARA UNA VENTA DE"
          : "SI COBRÁS";
    const montosFinalLabel = montosForm.modo === "Neto" ? "Debés cobrar" : montosForm.modo === "SinInteres" ? "Recibís" : "Recibís";
    const montosInputLabel =
      montosForm.modo === "Neto"
        ? "Monto que querés recibir"
        : montosForm.modo === "SinInteres"
          ? "Monto de la venta"
          : "Monto que querés cobrar";
    const montosButtonLabel =
      montosForm.modo === "Neto"
        ? "Calcular monto a cobrar"
        : montosForm.modo === "SinInteres"
          ? "Calcular venta sin interés"
          : "Calcular monto a recibir";
    const montosTituloPrincipal =
      montosForm.modo === "SinInteres"
        ? montosResultado?.montoClientePaga
        : montosResultado?.montoinicial;
    const montosResultadoFinal = montosResultado?.montofinal;
    const ordenesCatalogoEntries = Object.entries(montosCatalogo || {});
    const ordenesTarjetasDisponibles = ordenesCatalogoEntries
      .filter(([, cuotas]) => {
        const list = Array.isArray(cuotas) ? cuotas : [];
        return ordenesForm.tipo === "Debito"
          ? list.some((item) => String(item.cuota ?? item.Cuota) === "0")
          : list.some((item) => String(item.cuota ?? item.Cuota) !== "0");
      })
      .map(([label]) => ({ value: label.toLowerCase(), label }));
    const ordenesTarjetaLabel = ordenesTarjetasDisponibles.find((item) => item.value === ordenesForm.tarjeta)?.label || "";
    const ordenesCuotasDisponibles = ordenesTarjetaLabel
      ? (Array.isArray(montosCatalogo[ordenesTarjetaLabel]) ? montosCatalogo[ordenesTarjetaLabel] : [])
          .filter((item) => {
            const cuotaValue = String(item.cuota ?? item.Cuota);
            return ordenesForm.tipo === "Debito" ? cuotaValue === "0" : cuotaValue !== "0";
          })
          .map((item) => {
            const cuotaValue = String(item.cuota ?? item.Cuota);
            return { value: cuotaValue, label: buildMontoCuotaLabel(ordenesTarjetaLabel, cuotaValue) };
          })
      : [];
    const ordenesRequiresTipoCuota = ordenesForm.tipo === "Credito" && ordenesForm.cuota === "1";
    const ordenesGuardarDisabled = montosCatalogoLoading || Boolean(validateOrdenesForm(ordenesForm));
    const ordenesPreviewDisabled = ordenesPreviewLoading || !ordenesGuardadas.length;
    const comparadorValidation = validateComparadorForm(comparadorForm);
    const comparadorCompanyLabel = getComparadorCompanyLabel(comparadorForm.competencia);
    const comparadorTipoLabel = COMPARADOR_TIPO_OPTIONS.find((item) => item.value === comparadorForm.tipoPago)?.label || "-";
    const comparadorRows = comparadorResultado ? buildComparadorRows(comparadorResultado) : [];
    const comparadorHasResultado = Boolean(comparadorResultado && comparadorRows.length);
    const comparadorTerminalesCosto = comparadorHasResultado ? formatMontoCurrency(comparadorResultado.costopaywey ?? comparadorResultado.costoPaywey) : "-";
    const comparadorAhorroMesRaw = comparadorResultado?.pormes ?? comparadorResultado?.porMes;
    const comparadorAhorroMes = parseFlexibleAmount(comparadorAhorroMesRaw);
    const comparadorAhorroEsPositivo = comparadorAhorroMes === null || comparadorAhorroMes >= 0;
    const updateComparadorForm = (patch) => {
      setComparadorForm((prev) => {
        const next = { ...prev, ...patch };
        if (patch.tipoPago === "debito") {
          next.cuota = "0";
        } else if (patch.tipoPago === "credito" && (!next.cuota || next.cuota === "0")) {
          next.cuota = "1";
        }
        return next;
      });
      setComparadorResultado(null);
      setComparadorError("");
    };
    const renderComparadorValue = (item, highlight = "") => {
      const monto = getComparadorValue(item, "Monto") ?? "-";
      const porcentaje = getComparadorValue(item, "Porcentaje");
      return (
        <div className={`crm-postventa-comparador-value ${highlight}`}>
          <strong>{monto || "-"}</strong>
          {porcentaje && porcentaje !== "-" ? <span>{porcentaje}</span> : null}
        </div>
      );
    };
    const renderComparadorFeatureValue = (value, tone, winnerClass = "") => {
      if (typeof value === "boolean") {
        const icon = value ? comparadorCheckIcon : comparadorCrossIcon;
        return (
          <span className={`crm-postventa-comparador-bool ${winnerClass}`} aria-label={value ? "Disponible" : "No disponible"}>
            <img src={icon} alt={value ? "Disponible" : "No disponible"} />
          </span>
        );
      }

      return <span className={`crm-postventa-comparador-text-value ${tone ? `is-${tone}` : ""} ${winnerClass}`}>{formatComparadorText(value || "-")}</span>;
    };
    const renderComparadorLogo = (type) => {
      const isZoco = type === "zoco";
      const src = isZoco ? zocoLogoComparador : COMPARADOR_LOGOS[comparadorForm.competencia];
      const label = isZoco ? "Zoco" : comparadorCompanyLabel;

      return src ? (
        <img
          className={`crm-postventa-comparador-logo ${isZoco ? "is-zoco" : ""}`}
          src={src}
          alt={label}
          loading="lazy"
        />
      ) : (
        <span className="crm-postventa-comparador-logo-fallback">{label}</span>
      );
    };

    return (
      <div className="crm-postventa-herramientas-screen">
        <div className="crm-postventa-herramientas-header">
          <div>
            <span>CRM POSTVENTAS</span>
            <h2>Herramientas</h2>
          </div>
          <time>{headerClockLabel}</time>
        </div>

        <div className="crm-postventa-herramientas-tabs" role="tablist" aria-label="Herramientas CRM Postventa">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={herramientasTab === tab.id}
              aria-disabled={false}
              className={`crm-postventa-herramientas-tab ${herramientasTab === tab.id ? "is-active" : ""}`}
              onClick={() => setHerramientasTab(tab.id)}
            >
              {tab.id === "ordenes" ? "SIMULADOR DE ÓRDENES" : tab.label}
            </button>
          ))}
        </div>

        {herramientasTab === "montos" ? (
          <>
            <section className="crm-postventa-montos-grid">
              <div>
                <form className="crm-postventa-montos-card" onSubmit={calcularMontosSimulador}>
                  <fieldset className="crm-postventa-montos-modes">
                    <legend>Modo de simulación</legend>
                    <div>
                      {MONTOS_MODE_OPTIONS.map((option) => (
                        <label key={option.value} className={`crm-postventa-montos-mode ${montosForm.modo === option.value ? "is-active" : ""}`}>
                          <input
                            type="radio"
                            name="herramientas-montos-modo"
                            value={option.value}
                            checked={montosForm.modo === option.value}
                            onChange={() => {
                              setMontosForm((prev) => ({ ...prev, modo: option.value }));
                              setMontosResultado(null);
                              setMontosError("");
                            }}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className="crm-postventa-montos-fields">
                    <Form.Group controlId="herramientas-montos-monto">
                      <Form.Label>{montosInputLabel}</Form.Label>
                      <div className="crm-postventa-montos-money-control">
                        <span>$</span>
                        <Form.Control
                          inputMode="decimal"
                          value={montosForm.monto}
                          placeholder="10.000"
                          onChange={(event) => {
                            setMontosForm((prev) => ({ ...prev, monto: event.target.value }));
                            setMontosResultado(null);
                            setMontosError("");
                          }}
                          onBlur={() => {
                            const parsed = parseFlexibleAmount(montosForm.monto);
                            if (parsed !== null) {
                              setMontosForm((prev) => ({ ...prev, monto: parsed.toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }));
                            }
                          }}
                        />
                      </div>
                    </Form.Group>

                    <Form.Group controlId="herramientas-montos-tipo">
                      <Form.Label>Tipo</Form.Label>
                      <Form.Select
                        value={montosForm.tipo}
                        disabled={montosCatalogoLoading}
                        onChange={(event) => {
                          const nextTipo = event.target.value;
                          setMontosForm((prev) => ({
                            ...prev,
                            tipo: nextTipo,
                            tarjeta: "",
                            cuota: nextTipo === "Debito" ? "0" : "",
                            tipoCuota: "normal",
                          }));
                          setMontosResultado(null);
                          setMontosError("");
                        }}
                      >
                        <option value="">Seleccionar</option>
                        {MONTOS_TIPO_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group controlId="herramientas-montos-tarjeta">
                      <Form.Label>¿Con qué tarjeta te pagan?</Form.Label>
                      <Form.Select
                        value={montosForm.tarjeta}
                        disabled={!montosForm.tipo || montosCatalogoLoading || !montosTarjetasDisponibles.length}
                        onChange={(event) => {
                          const nextTarjeta = event.target.value;
                          const tarjetaLabel = montosTarjetasDisponibles.find((item) => item.value === nextTarjeta)?.label || "";
                          const cuotas = tarjetaLabel && Array.isArray(montosCatalogo[tarjetaLabel])
                            ? montosCatalogo[tarjetaLabel]
                                .filter((item) => {
                                  const cuotaValue = String(item.cuota ?? item.Cuota);
                                  return montosForm.tipo === "Debito" ? cuotaValue === "0" : cuotaValue !== "0";
                                })
                                .map((item) => String(item.cuota ?? item.Cuota))
                            : [];
                          const defaultCuota = montosForm.tipo === "Debito" ? "0" : cuotas.includes("1") ? "1" : cuotas[0] || "";
                          setMontosForm((prev) => ({
                            ...prev,
                            tarjeta: nextTarjeta,
                            cuota: defaultCuota,
                            tipoCuota: "normal",
                          }));
                          setMontosResultado(null);
                          setMontosError("");
                        }}
                      >
                        <option value="">Seleccionar</option>
                        {montosTarjetasDisponibles.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group controlId="herramientas-montos-cuotas">
                      <Form.Label>¿En cuántas cuotas?</Form.Label>
                      <Form.Select
                        value={montosForm.cuota}
                        disabled={!montosForm.tarjeta || montosForm.tipo === "Debito" || !montosCuotasDisponibles.length}
                        onChange={(event) => {
                          setMontosForm((prev) => ({ ...prev, cuota: event.target.value, tipoCuota: event.target.value === "1" ? prev.tipoCuota || "normal" : "normal" }));
                          setMontosResultado(null);
                          setMontosError("");
                        }}
                      >
                        <option value="">Seleccionar</option>
                        {montosCuotasDisponibles.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>

                  {montosRequiresTipoCuota ? (
                    <fieldset className="crm-postventa-montos-submodes">
                      <legend>Modalidad</legend>
                      {["normal", "bancarizada"].map((item) => (
                        <label key={item}>
                          <input
                            type="radio"
                            name="herramientas-montos-tipo-cuota"
                            value={item}
                            checked={montosForm.tipoCuota === item}
                            onChange={(event) => {
                              setMontosForm((prev) => ({ ...prev, tipoCuota: event.target.value }));
                              setMontosResultado(null);
                            }}
                          />
                          <span>{item === "normal" ? "Normal" : "Bancarizada"}</span>
                        </label>
                      ))}
                    </fieldset>
                  ) : null}

                  {montosCatalogoLoading ? <div className="crm-postventa-montos-inline-message">Cargando tarjetas...</div> : null}
                  {montosCatalogoError ? (
                    <div className="crm-postventa-montos-inline-message">
                      {montosCatalogoError}
                      <button type="button" onClick={cargarCatalogoMontos}>Reintentar</button>
                    </div>
                  ) : null}
                  {!montosCatalogoLoading && montosForm.tipo && !montosTarjetasDisponibles.length ? (
                    <div className="crm-postventa-montos-inline-message">No hay tarjetas disponibles para el tipo seleccionado.</div>
                  ) : null}
                  {!montosCatalogoLoading && montosForm.tarjeta && montosForm.tipo === "Credito" && !montosCuotasDisponibles.length ? (
                    <div className="crm-postventa-montos-inline-message">No hay opciones de cuotas disponibles para esta tarjeta.</div>
                  ) : null}

                  <button type="submit" className="crm-postventa-montos-submit" disabled={montosCalcularDisabled}>
                    {montosCalculando ? <Spinner animation="border" size="sm" /> : montosButtonLabel}
                  </button>
                </form>
                <p className="crm-postventa-montos-note">
                  * Indicá si querés saber cuánto vas a cobrar, cuánto vas a recibir o simular una venta sin interés, seleccioná el tipo de pago, la tarjeta y las cuotas, y tocá en Calcular.
                </p>
              </div>

              <section className="crm-postventa-montos-card crm-postventa-montos-result" aria-live="polite">
                {montosError ? (
                  <div className="crm-postventa-montos-result-empty">{montosError}</div>
                ) : montosResultado ? (
                  <>
                    <header>
                      <span>{montosAmountTitle}</span>
                      <strong>{formatMontoCurrency(montosTituloPrincipal)}</strong>
                    </header>
                    <div className="crm-postventa-montos-result-meta">
                      <span>{montosModeLabel}</span>
                      <span>{montosForm.tipo === "Debito" ? "Débito" : "Crédito"}</span>
                      <span>{montosTarjetaLabel || "-"}</span>
                      <span>{montosCuotasDisponibles.find((item) => item.value === montosForm.cuota)?.label || "Cuota 0"}</span>
                    </div>
                    <div className="crm-postventa-montos-result-rows">
                      {buildMontoResultRows(montosResultado).map((row) => (
                        <div key={row.label}>
                          <span>{row.label}</span>
                          <strong>{formatMontoCurrency(row.value)}</strong>
                        </div>
                      ))}
                    </div>
                    <footer>
                      <span>{montosFinalLabel}</span>
                      <strong>{formatMontoCurrency(montosResultadoFinal)}</strong>
                    </footer>
                    {montosForm.tipo === "Credito" ? (
                      <p className="crm-postventa-montos-result-note">
                        El costo por anticipo puede variar según el plazo de acreditación que corresponda al tipo de pago seleccionado.
                      </p>
                    ) : null}
                  </>
                ) : (
                  <div className="crm-postventa-montos-result-empty">Completá los datos para simular el monto.</div>
                )}
              </section>
            </section>
          </>
        ) : herramientasTab === "comparador" ? (
          <section className="crm-postventa-comparador" aria-live="polite">
            <div className="crm-postventa-comparador-form-card">
              <Form.Group controlId="herramientas-comparador-monto">
                <Form.Label>Monto</Form.Label>
                <div className="crm-postventa-comparador-money">
                  <span>$</span>
                  <Form.Control
                    inputMode="decimal"
                    value={comparadorForm.monto}
                    placeholder="10.000.000"
                    onChange={(event) => updateComparadorForm({ monto: event.target.value })}
                    onBlur={() => {
                      const parsed = parseFlexibleAmount(comparadorForm.monto);
                      if (parsed !== null) {
                        updateComparadorForm({ monto: parsed.toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) });
                      }
                    }}
                  />
                </div>
              </Form.Group>

              <Form.Group controlId="herramientas-comparador-pos">
                <Form.Label>POS</Form.Label>
                <Form.Control
                  inputMode="numeric"
                  value={comparadorForm.terminales}
                  onChange={(event) => updateComparadorForm({ terminales: event.target.value.replace(/\D/g, "").slice(0, 3) })}
                />
              </Form.Group>

              <Form.Group controlId="herramientas-comparador-tipo">
                <Form.Label>Tipo de pago</Form.Label>
                <Form.Select value={comparadorForm.tipoPago} onChange={(event) => updateComparadorForm({ tipoPago: event.target.value })}>
                  {COMPARADOR_TIPO_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group controlId="herramientas-comparador-cuota">
                <Form.Label>Cuota</Form.Label>
                <Form.Select
                  value={comparadorForm.tipoPago === "debito" ? "0" : comparadorForm.cuota}
                  disabled={comparadorForm.tipoPago === "debito"}
                  onChange={(event) => updateComparadorForm({ cuota: event.target.value })}
                >
                  {comparadorForm.tipoPago === "debito" ? <option value="0">No aplica</option> : null}
                  {COMPARADOR_CUOTA_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group controlId="herramientas-comparador-compania">
                <Form.Label>Compañía</Form.Label>
                <Form.Select value={comparadorForm.competencia} onChange={(event) => updateComparadorForm({ competencia: event.target.value })}>
                  {COMPARADOR_COMPETENCIAS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group controlId="herramientas-comparador-rewards">
                <Form.Label>Zoco Rewards (No compartir)</Form.Label>
                <Form.Select value={comparadorForm.zoco} onChange={(event) => updateComparadorForm({ zoco: event.target.value })}>
                  {COMPARADOR_ZOCO_REWARDS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            {comparadorCalculando ? <div className="crm-postventa-comparador-status"><Spinner animation="border" size="sm" /> Calculando comparacion...</div> : null}
            {!comparadorCalculando && comparadorError ? <div className="crm-postventa-comparador-status is-error">{comparadorError}</div> : null}
            {!comparadorCalculando && !comparadorError && comparadorValidation && !comparadorForm.monto ? (
              <div className="crm-postventa-comparador-status">Completa los datos para realizar la comparacion.</div>
            ) : null}

            <div className="crm-postventa-comparador-summary">
              <article className="crm-postventa-comparador-terminal-card">
                <strong>{comparadorTerminalesCosto}</strong>
                  <span>COSTO DE TERMINALES COMPETENCIA | {comparadorHasResultado ? comparadorCompanyLabel.toUpperCase() : "SIN CALCULAR"}</span>
              </article>
              <article className={`crm-postventa-comparador-savings-card ${comparadorAhorroEsPositivo ? "is-positive" : "is-negative"}`}>
                <div>
                  <strong>{comparadorHasResultado ? formatComparadorPercent(comparadorResultado.porcentaje) : "-"}</strong>
                  <span>{comparadorAhorroEsPositivo ? "AHORRO EN %" : "DIFERENCIA EN %"}</span>
                </div>
                <div>
                  <strong>{comparadorHasResultado ? formatMontoCurrency(comparadorAhorroMesRaw) : "-"}</strong>
                  <span>{comparadorAhorroEsPositivo ? "AHORRO POR MES" : "DIFERENCIA POR MES"}</span>
                </div>
                <div>
                  <strong>{comparadorHasResultado ? formatMontoCurrency(comparadorResultado.poraniozoco ?? comparadorResultado.porAnioZoco) : "-"}</strong>
                  <span>{comparadorAhorroEsPositivo ? "AHORRO POR AÑO" : "DIFERENCIA POR AÑO"}</span>
                </div>
              </article>
            </div>

            <div className="crm-postventa-comparador-panels">
              <article className="crm-postventa-comparador-panel">
                <header>
                  <h3>{comparadorTipoLabel}</h3>
                  <div className="crm-postventa-comparador-columns">
                    <span>CATEGORIA</span>
                    <span>{renderComparadorLogo("competencia")}</span>
                    <span>{renderComparadorLogo("zoco")}</span>
                  </div>
                </header>
                {comparadorHasResultado ? (
                  <div className="crm-postventa-comparador-rows">
                    {comparadorRows.map((row) => {
                      const isTotal = row.name === "TOTAL A DEPOSITAR";
                      const isDeductions = row.name === "TOTAL DEDUCCIONES";
                      const winner = getComparadorWinner(row);
                      return (
                        <div key={row.name} className={`crm-postventa-comparador-row ${isTotal ? "is-total" : ""} ${isDeductions ? "is-deductions" : ""}`}>
                          <span>{formatComparadorConceptLabel(row.name)}</span>
                          {renderComparadorValue(row.competencia, getComparadorValueTone(winner, "competencia"))}
                          {renderComparadorValue(row.zoco, getComparadorValueTone(winner, "zoco"))}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="crm-postventa-comparador-empty">Completa los datos para comparar las alternativas.</div>
                )}
              </article>

              <article className="crm-postventa-comparador-panel">
                <header>
                  <h3>Características</h3>
                  <div className="crm-postventa-comparador-columns">
                    <span>CATEGORIA</span>
                    <span>{renderComparadorLogo("competencia")}</span>
                    <span>{renderComparadorLogo("zoco")}</span>
                  </div>
                </header>
                <div className="crm-postventa-comparador-rows">
                  {COMPARADOR_FEATURES.map((feature) => {
                    const winner = getComparadorFeatureWinner(feature);
                    const competenciaWinnerClass = "";
                    const zocoWinnerClass = winner === "zoco" ? "is-winner" : "";
                    return (
                      <div key={feature.label} className="crm-postventa-comparador-row">
                        <span>{formatComparadorText(feature.label)}</span>
                        <div>{renderComparadorFeatureValue(feature.competencia, feature.toneCompetencia, competenciaWinnerClass)}</div>
                        <div>{renderComparadorFeatureValue(feature.zoco, feature.toneZoco, zocoWinnerClass)}</div>
                      </div>
                    );
                  })}
                </div>
              </article>
            </div>
          </section>
        ) : herramientasTab === "ordenes" ? (
          <>
            <section className="crm-postventa-ordenes-simulador">
              <form className="crm-postventa-ordenes-simulador-card" onSubmit={guardarOrdenSimulada}>
                <Form.Group controlId="herramientas-ordenes-monto" className="crm-postventa-ordenes-field-full">
                  <Form.Label>Ingresar monto</Form.Label>
                  <div className="crm-postventa-ordenes-money">
                    <span>$</span>
                    <Form.Control
                      inputMode="decimal"
                      value={ordenesForm.monto}
                      placeholder="10.000"
                      onChange={(event) => {
                        setOrdenesForm((prev) => ({ ...prev, monto: event.target.value }));
                        setOrdenesError("");
                        setOrdenesFeedback("");
                      }}
                      onBlur={() => {
                        const formatted = normalizeOrdenesAmountForDisplay(ordenesForm.monto);
                        if (formatted) {
                          setOrdenesForm((prev) => ({ ...prev, monto: formatted }));
                        }
                      }}
                    />
                  </div>
                </Form.Group>

                <Form.Group controlId="herramientas-ordenes-tipo">
                  <Form.Label>Tipo de pago</Form.Label>
                  <Form.Select
                    value={ordenesForm.tipo}
                    disabled={montosCatalogoLoading}
                    onChange={(event) => {
                      const nextTipo = event.target.value;
                      setOrdenesForm((prev) => ({
                        ...prev,
                        tipo: nextTipo,
                        tarjeta: "",
                        cuota: nextTipo === "Debito" ? "0" : "",
                        tipoCuota: "bancarizada",
                      }));
                      setOrdenesError("");
                      setOrdenesFeedback("");
                    }}
                  >
                    {ORDENES_TIPO_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="herramientas-ordenes-tarjeta">
                  <Form.Label>¿Con qué tarjeta te pagan?</Form.Label>
                  <Form.Select
                    value={ordenesForm.tarjeta}
                    disabled={montosCatalogoLoading || !ordenesTarjetasDisponibles.length}
                    onChange={(event) => {
                      const nextTarjeta = event.target.value;
                      const tarjetaLabel = ordenesTarjetasDisponibles.find((item) => item.value === nextTarjeta)?.label || "";
                      const cuotas = tarjetaLabel && Array.isArray(montosCatalogo[tarjetaLabel])
                        ? montosCatalogo[tarjetaLabel]
                            .filter((item) => {
                              const cuotaValue = String(item.cuota ?? item.Cuota);
                              return ordenesForm.tipo === "Debito" ? cuotaValue === "0" : cuotaValue !== "0";
                            })
                            .map((item) => String(item.cuota ?? item.Cuota))
                        : [];
                      setOrdenesForm((prev) => ({
                        ...prev,
                        tarjeta: nextTarjeta,
                        cuota: prev.tipo === "Debito" ? "0" : cuotas.includes("1") ? "1" : cuotas[0] || "",
                        tipoCuota: "bancarizada",
                      }));
                      setOrdenesError("");
                      setOrdenesFeedback("");
                    }}
                  >
                    <option value="">Seleccionar</option>
                    {ordenesTarjetasDisponibles.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="herramientas-ordenes-cuotas">
                  <Form.Label>¿En cuántas cuotas?</Form.Label>
                  <Form.Select
                    value={ordenesForm.cuota}
                    disabled={ordenesForm.tipo === "Debito" || !ordenesForm.tarjeta || !ordenesCuotasDisponibles.length}
                    onChange={(event) => {
                      setOrdenesForm((prev) => ({ ...prev, cuota: event.target.value, tipoCuota: event.target.value === "1" ? prev.tipoCuota || "bancarizada" : "bancarizada" }));
                      setOrdenesError("");
                      setOrdenesFeedback("");
                    }}
                  >
                    <option value="">Seleccionar</option>
                    {ordenesCuotasDisponibles.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="herramientas-ordenes-modalidad">
                  <Form.Label>Modalidad</Form.Label>
                  <Form.Select
                    value={ordenesRequiresTipoCuota ? ordenesForm.tipoCuota : ""}
                    disabled={!ordenesRequiresTipoCuota}
                    onChange={(event) => {
                      setOrdenesForm((prev) => ({ ...prev, tipoCuota: event.target.value }));
                      setOrdenesError("");
                    }}
                  >
                    {!ordenesRequiresTipoCuota ? <option value="">No aplica</option> : null}
                    {ordenesRequiresTipoCuota ? ORDENES_MODALIDAD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    )) : null}
                  </Form.Select>
                </Form.Group>

                {montosCatalogoLoading ? <div className="crm-postventa-ordenes-simulador-message">Cargando tarjetas...</div> : null}
                {montosCatalogoError ? <div className="crm-postventa-ordenes-simulador-message is-error">{montosCatalogoError}</div> : null}
                {ordenesError ? <div className="crm-postventa-ordenes-simulador-message is-error">{ordenesError}</div> : null}
                {ordenesFeedback ? <div className="crm-postventa-ordenes-simulador-message is-success">{ordenesFeedback}</div> : null}

                <button type="submit" className="crm-postventa-ordenes-save" disabled={ordenesGuardarDisabled}>
                  Guardar datos
                </button>
                <button
                  type="button"
                  className="crm-postventa-ordenes-preview"
                  disabled={ordenesPreviewDisabled}
                  onClick={() => generarOrdenesPreview(ordenesGuardadas)}
                >
                  {ordenesPreviewLoading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faEye} />}
                  Vista previa
                </button>
              </form>

              <div>
                <section className="crm-postventa-ordenes-resumen-card">
                  <h3>Resumen de datos guardados</h3>
                  {ordenesGuardadas.length ? (
                    <div className="crm-postventa-ordenes-sim-table-wrap">
                      <table className="crm-postventa-ordenes-sim-table">
                        <thead>
                          <tr>
                            <th>MONTO</th>
                            <th>CUOTA</th>
                            <th>TIPO DE TARJETA</th>
                            <th>ACCIONES</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ordenesGuardadas.map((item) => (
                            <tr key={item.id}>
                              <td>{item.montoLabel}</td>
                              <td>{item.cuotaLabel}</td>
                              <td>{item.tarjetaLabel}</td>
                              <td>
                                <button
                                  type="button"
                                  className="crm-postventa-ordenes-print"
                                  aria-label={`Imprimir ${item.montoLabel}`}
                                  disabled={ordenesPrintingId === item.id}
                                  onClick={() => imprimirOrdenGuardada(item)}
                                >
                                  {ordenesPrintingId === item.id ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faPrint} />}
                                  Imprimir
                                </button>
                                <button
                                  type="button"
                                  className="crm-postventa-ordenes-delete"
                                  aria-label={`Eliminar ${item.montoLabel}`}
                                  disabled={ordenesDeletingId === item.id}
                                  onClick={() => eliminarOrdenGuardada(item)}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="crm-postventa-ordenes-empty">
                      <strong>Todavía no hay datos guardados.</strong>
                      <span>Completá el formulario para agregar el primer registro.</span>
                    </div>
                  )}
                </section>
                <p className="crm-postventa-ordenes-help">
                  * Ingresá el monto, seleccioná el tipo de pago, la tarjeta y la cantidad de cuotas. Guardá los datos para agregarlos al resumen y generar la vista previa de la orden.
                </p>
              </div>
            </section>

            <Modal show={ordenesPreviewOpen} onHide={() => setOrdenesPreviewOpen(false)} size="xl" centered>
              <Modal.Header closeButton>
                <Modal.Title>Vista previa</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {ordenesPreviewLoading ? (
                  <div className="crm-postventa-ordenes-preview-loading"><Spinner animation="border" size="sm" /> Generando vista previa...</div>
                ) : ordenesPreview.length ? (
                  <OrdenPagoSimulador historialDatos={ordenesPreview} />
                ) : (
                  <div className="crm-postventa-ordenes-empty">Guardá al menos un registro para generar la vista previa.</div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <button type="button" className="crm-postventa-ordenes-save is-inline" onClick={() => window.print()} disabled={!ordenesPreview.length}>
                  <FontAwesomeIcon icon={faPrint} /> Imprimir
                </button>
                <button type="button" className="crm-postventa-ordenes-preview" onClick={() => setOrdenesPreviewOpen(false)}>
                  Cerrar
                </button>
              </Modal.Footer>
            </Modal>
          </>
        ) : herramientasTab !== "plazos" ? (
          <section className="crm-postventa-herramientas-disabled-panel">
            Esta herramienta todavía no está integrada en CRM Postventa.
          </section>
        ) : (
          <>
            <section className="crm-postventa-plazos-grid">
              <div>
                <form className="crm-postventa-plazos-card" onSubmit={calcularPlazosAcreditacion}>
                  <Form.Group controlId="herramientas-plazos-fecha">
                    <Form.Label>Fecha</Form.Label>
                    <div className="crm-postventa-plazos-date-control">
                      <Form.Control
                        type="date"
                        value={plazosForm.fecha}
                        onClick={openNativeDatePicker}
                        onKeyDown={openNativeDatePickerFromKeyboard}
                        onChange={(event) => {
                          setPlazosForm((prev) => ({ ...prev, fecha: event.target.value }));
                          setPlazosResultado(null);
                          setPlazosError("");
                        }}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group controlId="herramientas-plazos-tipo">
                    <Form.Label>Tipo</Form.Label>
                    <Form.Select
                      value={plazosForm.tipo}
                      onChange={(event) => {
                        const nextTipo = event.target.value;
                        setPlazosForm((prev) => ({
                          ...prev,
                          tipo: nextTipo,
                          tarjeta: PLAZOS_TARJETAS_POR_TIPO[nextTipo]?.some((item) => item.value === prev.tarjeta) ? prev.tarjeta : "",
                        }));
                        setPlazosResultado(null);
                        setPlazosError("");
                      }}
                    >
                      <option value="">Seleccionar</option>
                      {PLAZOS_TIPO_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group controlId="herramientas-plazos-tarjeta">
                    <Form.Label>Tarjeta</Form.Label>
                    <Form.Select
                      value={plazosForm.tarjeta}
                      disabled={!plazosForm.tipo || !tarjetasDisponibles.length}
                      onChange={(event) => {
                        setPlazosForm((prev) => ({ ...prev, tarjeta: event.target.value }));
                        setPlazosResultado(null);
                        setPlazosError("");
                      }}
                    >
                      <option value="">Seleccionar</option>
                      {tarjetasDisponibles.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {!plazosForm.tipo || tarjetasDisponibles.length ? null : (
                    <div className="crm-postventa-plazos-inline-message">No hay tarjetas disponibles para el tipo seleccionado.</div>
                  )}

                  <button type="submit" className="crm-postventa-plazos-submit" disabled={calcularDisabled}>
                    {plazosCalculando ? <Spinner animation="border" size="sm" /> : "Calcular plazos de acreditación"}
                  </button>
                </form>
                <p className="crm-postventa-plazos-note">* Ingresá la fecha, luego seleccioná el tipo de pago y la tarjeta.</p>
              </div>

              <div>
                <section className="crm-postventa-plazos-card crm-postventa-plazos-result" aria-live="polite">
                  {plazosError ? (
                    <div className="crm-postventa-plazos-result-empty">{plazosError}</div>
                  ) : plazosResultado ? (
                    <>
                      <h3>RECIBÍS EL PAGO EL <strong>{plazosResultado.date}</strong></h3>
                      <div className="crm-postventa-plazos-result-blocks">
                        <article>
                          <span>Tipo de tarjeta</span>
                          <strong>{selectedTipoLabel}</strong>
                          {plazosResultado.description ? <p>{plazosResultado.description}</p> : null}
                        </article>
                        <article>
                          <span>Plazo</span>
                          <strong>{plazosResultado.detail}</strong>
                        </article>
                      </div>
                    </>
                  ) : (
                    <div className="crm-postventa-plazos-result-empty">Completá los datos para calcular la fecha de acreditación.</div>
                  )}
                </section>
                <p className="crm-postventa-plazos-note">* En caso de feriado o día no hábil, el pago es diferido al siguiente día hábil.</p>
              </div>
            </section>

            <section className="crm-postventa-plazos-table-section">
              <h3>Plazos de acreditación</h3>
              <div className="crm-postventa-plazos-table-scroll">
                {PLAZOS_REGLAS_ACREDITACION.length ? (
                  <Table className="crm-postventa-plazos-table" hover>
                    <thead>
                      <tr>
                        <th>Categoría</th>
                        <th>Descripción</th>
                        <th>Plazos de acreditación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PLAZOS_REGLAS_ACREDITACION.map((row, index) => (
                        <tr key={`${row.category}-${index}`}>
                          <td>{row.category}</td>
                          <td>{row.description}</td>
                          <td>{row.days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="crm-postventa-plazos-inline-message">No hay plazos de acreditación configurados.</div>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    );
  };

  // eslint-disable-next-line no-unused-vars
  const renderHerramientas = () => {
    const tools = [
      {
        title: "Simulador",
        description: "Acceso directo al simulador comercial actual del CRM.",
        path: "/administracion/AdminComparador",
      },
      {
        title: "Comparador",
        description: "Abrí el comparador actual sin duplicar lógica ni rutas.",
        path: "/administracion/AdminSimulador",
      },
      {
        title: "Simulador de órdenes",
        description: "Ingreso directo al simulador de liquidaciones / órdenes vigente.",
        path: "/admin/Simuladorliquidacion",
      },
    ];

    return (
      <div className="crm-postventa-tool-grid">
        {tools.map((tool) => (
          <article className="crm-postventa-tool-card" key={tool.path}>
            <span className="crm-postventa-card-label">Herramienta</span>
            <h4>{tool.title}</h4>
            <p>{tool.description}</p>
            <Button variant="success" onClick={() => navigate(tool.path)}>
              <FontAwesomeIcon className="me-2" icon={faArrowUpRightFromSquare} />
              Abrir
            </Button>
          </article>
        ))}
      </div>
    );
  };

  const renderAltasBajas = () => {
    const altasBajasDateLabel = alertaHeaderClock.toLocaleDateString("es-AR");
    const altasBajasTimeLabel = alertaHeaderClock.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    if (altasBajasMode === "modificar") {
      return (
        <AltasBajasWorkflow
          dateLabel={altasBajasDateLabel}
          timeLabel={altasBajasTimeLabel}
          mode={altasBajasMode}
          onModeChange={(nextMode) => {
            if (nextMode === "alta" || nextMode === "agrega") {
              activateAltaMode(nextMode);
              return;
            }
            setAltasBajasMode(nextMode);
          }}
          onRefresh={resetAltasBajas}
          isAltaAgregaMode={false}
          docTypes={ALTA_LEGAJO_DOC_TYPES}
          modificarContent={<ModificarLegajoPanel catalogos={fichaCatalogos} docTypes={ALTA_LEGAJO_DOC_TYPES} />}
        />
      );
    }

    const altaStepThreeContent = (
      <>
      <section className="crm-altas-bajas-page__card crm-altas-bajas-page__card--step-three">
        <Form onSubmit={handleAltaStepThreeSubmit} className="crm-altas-bajas-page__step-stack">
          <div className="crm-altas-bajas-page__step-heading">
            <span className={`crm-altas-bajas-page__step-marker ${altaWizardData.formularioAltaId ? "is-success" : "is-dark"}`}>
              <FontAwesomeIcon icon={faCheck} />
            </span>
            <div>
              <small>Paso 3</small>
              <h3>Alta CRM</h3>
            </div>
          </div>

          <div className="crm-altas-bajas-page__step-three-section">
            <div className="crm-altas-bajas-page__section-title">Datos personales / Comercio</div>
            <div className="crm-altas-bajas-page__form-grid">
              <Form.Group>
                <Form.Label>N.° terminal</Form.Label>
                <Form.Control value={altaWizardData.legajo.terminal} readOnly />
              </Form.Group>
              <Form.Group>
                <Form.Label>Legajo</Form.Label>
                <Form.Control value={altaWizardData.legajo.nroLegajo} readOnly />
              </Form.Group>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Control value={altaWizardData.alta.estado || "ALTA"} readOnly />
              </Form.Group>
              <Form.Group>
                <Form.Label>Fecha de alta</Form.Label>
                <Form.Control
                  type="date"
                  lang="es-AR"
                  value={altaWizardData.alta.fechaDeAlta || makeLocalToday()}
                  onChange={(event) => handleAltaWizardAltaChange("fechaDeAlta", event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Mail</Form.Label>
                <Form.Control value={altaWizardData.alta.correo} onChange={(event) => handleAltaWizardAltaChange("correo", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Teléfono 1</Form.Label>
                <Form.Control value={altaWizardData.alta.celular} onChange={(event) => handleAltaWizardAltaChange("celular", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Teléfono 2</Form.Label>
                <Form.Control
                  value={altaWizardData.alta.telefonoAlternativo || altaWizardData.alta.telFijo}
                  onChange={(event) => handleAltaWizardAltaChange("telefonoAlternativo", event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Puesto / cargo / referencia 1</Form.Label>
                <Form.Control
                  value={altaWizardData.alta.referenciaTelefono}
                  onChange={(event) => handleAltaWizardAltaChange("referenciaTelefono", event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Puesto / cargo / referencia 2</Form.Label>
                <Form.Control
                  value={altaWizardData.alta.referenciaTelefonoAlternativo}
                  onChange={(event) => handleAltaWizardAltaChange("referenciaTelefonoAlternativo", event.target.value)}
                />
              </Form.Group>
            </div>
          </div>

          <div className="crm-altas-bajas-page__step-three-section">
            <div className="crm-altas-bajas-page__section-title">Terminal / Comercio</div>
            <div className="crm-altas-bajas-page__form-grid">
              <Form.Group>
                <Form.Label>Nombre del asesor</Form.Label>
                <Form.Select
                  value={altaWizardData.alta.nombreDelAsesor}
                  onChange={(event) => handleAltaWizardAltaChange("nombreDelAsesor", event.target.value)}
                  disabled={cargas.altaBajaCatalogos}
                >
                  <option value="">Seleccione</option>
                  {fichaCatalogos.asesores.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Provincia</Form.Label>
                <Form.Select
                  value={altaWizardData.alta.provincia}
                  onChange={(event) => handleAltaWizardAltaChange("provincia", event.target.value)}
                  disabled={cargas.altaBajaCatalogos}
                >
                  <option value="">Seleccione</option>
                  {fichaCatalogos.provincias.map((item) => (
                    <option key={`provincia-${item.value}`} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Provincia fiscal</Form.Label>
                <Form.Select
                  value={altaWizardData.alta.provincia3}
                  onChange={(event) => handleAltaWizardAltaChange("provincia3", event.target.value)}
                  disabled={cargas.altaBajaCatalogos}
                >
                  <option value="">Seleccione</option>
                  {fichaCatalogos.provincias.map((item) => (
                    <option key={`provincia3-${item.value}`} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Alicuotas</Form.Label>
                <Form.Control value={altaWizardData.alta.alicuota} readOnly />
              </Form.Group>
            </div>
          </div>

          <div className="crm-altas-bajas-page__step-three-section">
            <div className="crm-altas-bajas-page__section-title">AFIP</div>
            <div className="crm-altas-bajas-page__form-grid">
              <Form.Group>
                <Form.Label>Condición AFIP</Form.Label>
                <Form.Select value={altaWizardData.alta.condicionAFIP} onChange={(event) => handleAltaWizardAltaChange("condicionAFIP", event.target.value)}>
                  <option value="Monotributista">Monotributista</option>
                  <option value="Responsable Inscripto">Responsable Inscripto</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Actividad laboral / profesional</Form.Label>
                <Form.Control
                  value={altaWizardData.alta.actividadLaboralProfesional}
                  onChange={(event) => handleAltaWizardAltaChange("actividadLaboralProfesional", event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Estado AFIP</Form.Label>
                <Form.Select value={altaWizardData.alta.estadoAfip} onChange={(event) => handleAltaWizardAltaChange("estadoAfip", event.target.value)}>
                  {FICHA_FIXED_OPTIONS.estadoAfip.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Exento</Form.Label>
                <Form.Select value={altaWizardData.alta.exento} onChange={(event) => handleAltaWizardAltaChange("exento", event.target.value)}>
                  {FICHA_FIXED_OPTIONS.exento.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          <div className="crm-altas-bajas-page__step-three-section">
            <div className="crm-altas-bajas-page__section-title">Rentas</div>
            <div className="crm-altas-bajas-page__form-grid">
              <Form.Group>
                <Form.Label>Estado rentas</Form.Label>
                <Form.Select value={altaWizardData.alta.estadoRentas} onChange={(event) => handleAltaWizardAltaChange("estadoRentas", event.target.value)}>
                  <option value="">Seleccione</option>
                  {FICHA_FIXED_OPTIONS.estadoRentas.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Domicilio comercio</Form.Label>
                <Form.Control
                  value={altaWizardData.alta.domicilioComercio}
                  onChange={(event) => handleAltaWizardAltaChange("domicilioComercio", event.target.value)}
                />
              </Form.Group>
              <div className="crm-altas-bajas-page__rentas-row crm-altas-bajas-page__span-2">
                <Form.Group>
                  <Form.Label>Localidad comercio</Form.Label>
                  <Form.Control
                    value={altaWizardData.alta.localidadComercio}
                    onChange={(event) => handleAltaWizardAltaChange("localidadComercio", event.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Código postal comercio</Form.Label>
                  <Form.Control
                    value={altaWizardData.alta.codigoPostalComercio}
                    onChange={(event) => handleAltaWizardAltaChange("codigoPostalComercio", event.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Link de pago</Form.Label>
                  <div className="crm-altas-bajas-page__radio-group" role="radiogroup" aria-label="Link de pago">
                    {FICHA_FIXED_OPTIONS.siNo.map((item) => (
                      <label key={item.value} className="crm-altas-bajas-page__radio-option">
                        <input
                          type="radio"
                          name="alta-link-de-pago"
                          checked={String(altaWizardData.alta.linkDePago || "") === String(item.value)}
                          onChange={() => handleAltaWizardAltaChange("linkDePago", item.value)}
                        />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>
                </Form.Group>
              </div>
            </div>
          </div>

          <div className="crm-altas-bajas-page__step-three-section">
            <div className="crm-altas-bajas-page__section-title">Bancario</div>
            <div className="crm-altas-bajas-page__form-grid">
              <Form.Group>
                <Form.Label>Banco</Form.Label>
                <Form.Select value={altaWizardData.alta.banco} onChange={(event) => handleAltaWizardAltaChange("banco", event.target.value)}>
                  <option value="">Seleccione</option>
                  {fichaCatalogos.bancos.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Tipo de cuenta</Form.Label>
                <Form.Select value={altaWizardData.alta.tipoCuenta} onChange={(event) => handleAltaWizardAltaChange("tipoCuenta", event.target.value)}>
                  {LEGACY_ALTA_TIPO_CUENTA_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>N° de cuenta / CBU</Form.Label>
                <Form.Control value={altaWizardData.alta.cbucvu} onChange={(event) => handleAltaWizardAltaChange("cbucvu", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Alias</Form.Label>
                <Form.Control value={altaWizardData.alta.alias} onChange={(event) => handleAltaWizardAltaChange("alias", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>CUIT bancario</Form.Label>
                <Form.Control
                  value={altaWizardData.alta.cuitbancario}
                  onChange={(event) => handleAltaWizardAltaChange("cuitbancario", event.target.value)}
                  placeholder="20-xxxxxxxx-x o 11 Dígitos"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Titular / Razón social bancaria</Form.Label>
                <Form.Control
                  value={altaWizardData.alta.titularRazonSocial}
                  onChange={(event) => handleAltaWizardAltaChange("titularRazonSocial", event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>N.° Comercio solicitado</Form.Label>
                <Form.Select
                  value={altaWizardData.alta.numeroComercioSolicitado}
                  onChange={(event) => handleAltaWizardAltaChange("numeroComercioSolicitado", event.target.value)}
                >
                  <option value="">Seleccione</option>
                  {altaWizardComercioOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>N.° Comercio actual</Form.Label>
                <Form.Control
                  value={altaWizardData.alta.numeroComericoActual}
                  onChange={(event) => handleAltaWizardAltaChange("numeroComericoActual", event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Tipo alta</Form.Label>
                <Form.Select value={altaWizardData.alta.tipoAlta} onChange={(event) => handleAltaWizardAltaChange("tipoAlta", event.target.value)}>
                  <option value="NuevoAliado">Nuevo aliado</option>
                  <option value="AgregaTerminal">Agrega terminal</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Rubro</Form.Label>
                <Form.Select value={altaWizardData.alta.rubro} onChange={(event) => handleAltaWizardAltaChange("rubro", event.target.value)}>
                  <option value="">Seleccione</option>
                  {fichaCatalogos.rubros.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Altas</Form.Label>
                <Form.Select value={altaWizardData.alta.altas} onChange={(event) => handleAltaWizardAltaChange("altas", event.target.value)}>
                  {FICHA_FIXED_OPTIONS.altas.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Baja</Form.Label>
                <Form.Select value={altaWizardData.alta.baja} onChange={(event) => handleAltaWizardAltaChange("baja", event.target.value)}>
                  <option value="">Sin valor</option>
                  {FICHA_FIXED_OPTIONS.bajas.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Rubro secundario</Form.Label>
                <Form.Select value={altaWizardData.alta.rubroSecundario} onChange={(event) => handleAltaWizardAltaChange("rubroSecundario", event.target.value)}>
                  <option value="">Seleccione</option>
                  {fichaCatalogos.rubros.map((item) => (
                    <option key={`rubro-sec-${item.value}`} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>N.° Local</Form.Label>
                <Form.Control value={altaWizardData.alta.nroLocal} onChange={(event) => handleAltaWizardAltaChange("nroLocal", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Código actividad</Form.Label>
                <Form.Control value={altaWizardData.alta.codActividad} onChange={(event) => handleAltaWizardAltaChange("codActividad", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Actividad</Form.Label>
                <Form.Control value={altaWizardData.alta.actividad} onChange={(event) => handleAltaWizardAltaChange("actividad", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Actividad 2</Form.Label>
                <Form.Control value={altaWizardData.alta.actividad2} onChange={(event) => handleAltaWizardAltaChange("actividad2", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Código actividad rentas</Form.Label>
                <Form.Control
                  value={altaWizardData.alta.codActividadRentas}
                  onChange={(event) => handleAltaWizardAltaChange("codActividadRentas", event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Actividad rentas</Form.Label>
                <Form.Control
                  value={altaWizardData.alta.actividadRentas}
                  onChange={(event) => handleAltaWizardAltaChange("actividadRentas", event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  className="crm-altas-bajas-page__textarea"
                  value={altaWizardData.alta.observaciones}
                  onChange={(event) => handleAltaWizardAltaChange("observaciones", event.target.value)}
                  placeholder="Observaciones que quedarán en el alta final."
                />
              </Form.Group>
            </div>
          </div>

          <div className="crm-altas-bajas-page__footer-row crm-altas-bajas-page__footer-row--step-three">
            <div className="crm-altas-bajas-page__helper-chip crm-altas-bajas-page__helper-chip--step-three">
              * El paso final crea el formulario de alta con los datos ya cargados en Blanqueo + Legajo y luego ejecuta el alta real del CRM viejo para no romper la lógica existente.
            </div>
            <div className="crm-altas-bajas-page__button-row">
              <button type="button" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--secondary" onClick={() => setAltaWizardStep(2)}>
                Volver al legajo
              </button>
              <button type="submit" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary" disabled={cargas.guardandoAltaWorkflow}>
                {cargas.guardandoAltaWorkflow ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Generando alta...
                  </>
                ) : (
                  "Generar alta"
                )}
              </button>
            </div>
          </div>
        </Form>
      </section>
      {requiresAltaFinalChecklist ? (
        <Modal
          show={altaFinalChecklistOpen}
          onHide={() => setAltaFinalChecklistOpen(false)}
          centered
          size="lg"
          className="crm-altas-bajas-page__alta-final-modal"
        >
          <Modal.Header closeButton>
            <div>
              <Modal.Title>Checklist final de alta</Modal.Title>
              <div className="crm-altas-bajas-page__alta-final-count">
                {altaFinalChecklistCompletedCount}/{ALTA_FINAL_CHECKLIST_ITEMS.length} controles validados
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="crm-altas-bajas-page__alta-final-grid">
              {ALTA_FINAL_CHECKLIST_ITEMS.map((item) => (
                <label key={item.id} className="crm-altas-bajas-page__alta-final-check">
                  <input
                    type="checkbox"
                    checked={Boolean(altaFinalChecklistChecks[item.id])}
                    onChange={(event) => handleAltaFinalChecklistToggle(item.id, event.target.checked)}
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--secondary"
              onClick={() => setAltaFinalChecklistOpen(false)}
              disabled={cargas.guardandoAltaWorkflow}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary"
              onClick={handleAltaFinalChecklistConfirm}
              disabled={!altaFinalChecklistComplete || cargas.guardandoAltaWorkflow}
            >
              {cargas.guardandoAltaWorkflow ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Generando alta...
                </>
              ) : (
                "Dar alta"
              )}
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
      </>
    );

    const bajaContent = (
      <div className="crm-altas-bajas-page__stack">
        <section className="crm-altas-bajas-page__card crm-altas-bajas-page__card--baja-search">
          <div className="crm-altas-bajas-page__search-card-header">
            <h3>Baja por terminal</h3>
          </div>

          <div className="crm-altas-bajas-page__search-box">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <Form.Control
              value={bajaLookupSearch}
              onChange={(event) => setBajaLookupSearch(event.target.value)}
              placeholder="Buscar por terminal, comercio, razón social..."
            />
          </div>

          <div className="crm-altas-bajas-page__search-help">Buscar terminal o comercio.</div>

          {bajaLookupLoading ? <div className="crm-altas-bajas-page__search-status">Buscando terminales...</div> : null}

          {bajaLookupResults.length ? (
            <div className="crm-altas-bajas-page__search-results" role="listbox" aria-label="Resultados de búsqueda">
              {bajaLookupResults.slice(0, 8).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="crm-altas-bajas-page__search-result"
                  onClick={() => handleSeleccionBajaResultado(item.key)}
                >
                  <strong>{item.nombre || "Sin nombre"}</strong>
                  <span>
                    Terminal: {item.terminal || "-"} · Comercio: {item.comercio || "-"} · CUIT: {item.cuit || "-"}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </section>

        <section className="crm-altas-bajas-page__card crm-altas-bajas-page__card--baja-form">
          <Form onSubmit={handleBajaWorkflowSubmitV2} className="crm-altas-bajas-page__baja-grid">
            <div className="crm-altas-bajas-page__baja-column">
              <Form.Group>
                <Form.Label>N.° de terminal</Form.Label>
                <Form.Control
                  value={bajaDraft.terminal}
                  onChange={(event) => {
                    const value = event.target.value;
                    setBajaDraft((prev) => ({ ...prev, terminal: value }));
                    setBajaFieldErrors((prev) => {
                      if (!prev.terminal) {
                        return prev;
                      }
                      const next = { ...prev };
                      delete next.terminal;
                      return next;
                    });
                  }}
                />
                {bajaFieldErrors.terminal ? <div className="crm-altas-bajas-page__field-error">{bajaFieldErrors.terminal}</div> : null}
              </Form.Group>

              <Form.Group>
                <Form.Label>Fecha de baja</Form.Label>
                <Form.Control
                  type="date"
                  lang="es-AR"
                  value={String(bajaDraft.fechaBaja || "").replace(/\//g, "-")}
                  onChange={(event) => {
                    const value = event.target.value.replace(/-/g, "/");
                    setBajaDraft((prev) => ({
                      ...prev,
                      fechaBaja: value,
                    }));
                    setBajaFieldErrors((prev) => {
                      if (!prev.fechaBaja) {
                        return prev;
                      }
                      const next = { ...prev };
                      delete next.fechaBaja;
                      return next;
                    });
                  }}
                />
                {bajaFieldErrors.fechaBaja ? <div className="crm-altas-bajas-page__field-error">{bajaFieldErrors.fechaBaja}</div> : null}
              </Form.Group>

              <div className="crm-altas-bajas-page__helper-chip crm-altas-bajas-page__helper-chip--baja">
                * Esta acción utiliza el flujo actual de baja de terminales. La búsqueda es opcional: la baja se ejecutará sobre la terminal cargada en el formulario.
              </div>
            </div>

            <div className="crm-altas-bajas-page__baja-column">
              <Form.Group>
                <Form.Label>Motivo</Form.Label>
                <Form.Select
                  value={bajaDraft.motivo}
                  onChange={(event) => setBajaDraft((prev) => ({ ...prev, motivo: event.target.value }))}
                >
                  <option value="BAJA">Baja</option>
                  <option value="BAJA POR FACTURACION">Baja por facturación</option>
                  <option value="BAJA POR POSNET">Baja por Posnet</option>
                  <option value="BAJA POR CAMBIO DE TERMINAL">Baja por cambio de terminal</option>
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  className="crm-altas-bajas-page__textarea crm-altas-bajas-page__textarea--baja"
                  value={bajaDraft.observacion}
                  onChange={(event) => {
                    const value = event.target.value;
                    setBajaDraft((prev) => ({ ...prev, observacion: value }));
                    setBajaFieldErrors((prev) => {
                      if (!prev.observacion) {
                        return prev;
                      }
                      const next = { ...prev };
                      delete next.observacion;
                      return next;
                    });
                  }}
                  placeholder="Motivo operativo y contexto de la baja..."
                />
                {bajaFieldErrors.observacion ? <div className="crm-altas-bajas-page__field-error">{bajaFieldErrors.observacion}</div> : null}
              </Form.Group>

              <div className="crm-altas-bajas-page__button-row crm-altas-bajas-page__button-row--baja">
                <button type="submit" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary crm-altas-bajas-page__btn--baja" disabled={cargas.guardandoBajaWorkflow}>
                  {cargas.guardandoBajaWorkflow ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Generando...
                    </>
                  ) : (
                    "Generar Baja"
                  )}
                </button>
              </div>
            </div>
          </Form>
        </section>
      </div>
    );

    if (altasBajasMode === "baja") {
      return (
        <AltasBajasWorkflow
          dateLabel={altasBajasDateLabel}
          timeLabel={altasBajasTimeLabel}
          mode={altasBajasMode}
          onModeChange={(nextMode) => {
            if (nextMode === "alta" || nextMode === "agrega") {
              activateAltaMode(nextMode);
              return;
            }
            setAltasBajasMode(nextMode);
          }}
          onRefresh={resetAltasBajas}
          isAltaAgregaMode={isAltaAgregaMode}
          altaWizardStep={altaWizardStep}
          altaWizardStatus={altaWizardStatus}
          altaWizardData={altaWizardData}
          altaWizardLegajos={altaWizardLegajos}
          onUserChange={handleAltaWizardUserChange}
          onSubmitBlanqueo={handleAltaBlanqueoSubmit}
          savingBlanqueo={cargas.guardandoBlanqueo}
          onSelectLegajo={handleAltaLegajoSelect}
          onLegajoChange={handleAltaWizardLegajoChange}
          onSubmitLegajo={handleAltaLegajoSubmit}
          loadingCatalogs={cargas.altaBajaCatalogos}
          savingLegajo={cargas.guardandoLegajo}
          catalogos={fichaCatalogos}
          onBackToStepOne={() => setAltaWizardStep(1)}
          altaLegajoDocDraft={altaLegajoDocDraft}
          setAltaLegajoDocDraft={setAltaLegajoDocDraft}
          docTypes={ALTA_LEGAJO_DOC_TYPES}
          onSubmitDoc={handleAltaLegajoDocumento}
          savingDoc={cargas.guardandoDocumentoLegajo}
          altaLegajoDocs={altaLegajoDocs}
          onDeleteDoc={handleEliminarDocumentoLegajo}
          onContinueToStepThree={() => setAltaWizardStep(3)}
          stepThreeContent={altaStepThreeContent}
          bajaContent={bajaContent}
        />
      );
    }

    if (!isAltaAgregaMode && altasBajasMode !== "baja") {
      return (
        <AltasBajasWorkflow
          dateLabel={altasBajasDateLabel}
          timeLabel={altasBajasTimeLabel}
          mode={altasBajasMode}
          onModeChange={(nextMode) => {
            if (nextMode === "alta" || nextMode === "agrega") {
              activateAltaMode(nextMode);
              return;
            }
            setAltasBajasMode(nextMode);
          }}
          onRefresh={resetAltasBajas}
          isAltaAgregaMode={isAltaAgregaMode}
          altaWizardStep={altaWizardStep}
          altaWizardStatus={altaWizardStatus}
          altaWizardData={altaWizardData}
          altaWizardLegajos={altaWizardLegajos}
          onUserChange={handleAltaWizardUserChange}
          onSubmitBlanqueo={handleAltaBlanqueoSubmit}
          savingBlanqueo={cargas.guardandoBlanqueo}
          onSelectLegajo={handleAltaLegajoSelect}
          onLegajoChange={handleAltaWizardLegajoChange}
          onSubmitLegajo={handleAltaLegajoSubmit}
          loadingCatalogs={cargas.altaBajaCatalogos}
          savingLegajo={cargas.guardandoLegajo}
          catalogos={fichaCatalogos}
          onBackToStepOne={() => setAltaWizardStep(1)}
          altaLegajoDocDraft={altaLegajoDocDraft}
          setAltaLegajoDocDraft={setAltaLegajoDocDraft}
          docTypes={ALTA_LEGAJO_DOC_TYPES}
          onSubmitDoc={handleAltaLegajoDocumento}
          savingDoc={cargas.guardandoDocumentoLegajo}
          altaLegajoDocs={altaLegajoDocs}
          onDeleteDoc={handleEliminarDocumentoLegajo}
          onContinueToStepThree={() => setAltaWizardStep(3)}
          stepThreeContent={altaStepThreeContent}
          bajaContent={null}
        />
      );
    }

    return (
      <div className="crm-postventa-workflow crm-postventa-altas-layout">
        <div className="crm-postventa-altas-header">
          <div className="crm-postventa-altas-header-copy">
            <span className="crm-postventa-kicker">CRM POSTVENTAS</span>
            <h2>Altas / Bajas</h2>
          </div>
          <div className="crm-postventa-altas-header-clock">
            <span>{altasBajasDateLabel}</span>
            <span className="crm-postventa-altas-header-separator" />
            <span>{altasBajasTimeLabel}</span>
          </div>
        </div>

        <div className="crm-postventa-altas-tabs-row">
        <div className="crm-postventa-herramientas-tabs" role="tablist" aria-label="Modos de altas y bajas">
          <button
            type="button"
            role="tab"
            aria-selected={altasBajasMode === "alta"}
            className={`crm-postventa-herramientas-tab ${altasBajasMode === "alta" ? "is-active" : ""}`}
            onClick={() => activateAltaMode("alta")}
          >
            Alta guiada
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={altasBajasMode === "agrega"}
            className={`crm-postventa-herramientas-tab ${altasBajasMode === "agrega" ? "is-active" : ""}`}
            onClick={() => activateAltaMode("agrega")}
          >
            Agregar terminal a comercio existente
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={altasBajasMode === "baja"}
            className={`crm-postventa-herramientas-tab ${altasBajasMode === "baja" ? "is-active" : ""}`}
            onClick={() => setAltasBajasMode("baja")}
          >
            Baja por terminal
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={altasBajasMode === "modificar"}
            className={`crm-postventa-herramientas-tab ${altasBajasMode === "modificar" ? "is-active" : ""}`}
            onClick={() => setAltasBajasMode("modificar")}
          >
            Modificar legajo
          </button>
        </div>
          <Button type="button" variant="outline-secondary" size="sm" className="crm-postventa-altas-refresh" onClick={resetAltasBajas} aria-label="Actualizar">
            <FontAwesomeIcon icon={faArrowsRotate} />
          </Button>
        </div>

        {altasBajasMode !== "baja" ? (
          <div className="d-grid gap-4">
            {!isAltaAgregaMode ? (
            <div className="crm-postventa-stepper">
              {[
                { id: 1, title: "Blanqueo", detail: altaWizardStatus.blanqueo || "Crear o reutilizar usuario por CUIT." },
                { id: 2, title: "Legajo", detail: altaWizardStatus.legajo || "Guardar legajo y cargar documentos." },
                { id: 3, title: "Alta CRM", detail: altaWizardStatus.alta || "Completar datos operativos y ejecutar el alta." },
              ].map((step) => (
                <article
                  key={step.id}
                  className={`crm-postventa-step-card ${altaWizardStep === step.id ? "is-active" : ""} ${altaWizardStep > step.id ? "is-done" : ""}`}
                >
                  <span>{step.id}</span>
                  <strong>{step.title}</strong>
                  <p>{step.detail}</p>
                </article>
              ))}
            </div>
            ) : null}

            <section className={`crm-postventa-panel crm-postventa-altas-main-stage ${darkMode ? "is-dark" : ""}`}>
              {!isAltaAgregaMode && altaWizardStep === 1 ? (
                <Form className="crm-postventa-form-grid crm-postventa-altas-step-one-grid" onSubmit={handleAltaBlanqueoSubmit}>
                  <div className="crm-postventa-panel-title crm-postventa-altas-step-title">
                    <span className="crm-postventa-altas-step-icon">
                      <FontAwesomeIcon icon={faCircleCheck} />
                    </span>
                    <div>
                      <small>Paso 1</small>
                      <h3>Blanqueo</h3>
                    </div>
                  </div>
                  <Form.Group>
                    <Form.Label>CUIT</Form.Label>
                    <Form.Control
                      value={altaWizardData.usuario.cuit}
                      onChange={(event) => handleAltaWizardUserChange("cuit", event.target.value)}
                      maxLength={11}
                      placeholder="Solo números"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Nombre / razón social</Form.Label>
                    <Form.Control
                      value={altaWizardData.usuario.nombre}
                      onChange={(event) => handleAltaWizardUserChange("nombre", event.target.value)}
                      placeholder="Nombre visible del usuario"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={altaWizardData.usuario.email}
                      onChange={(event) => handleAltaWizardUserChange("email", event.target.value)}
                      placeholder="mail@comercio.com"
                    />
                  </Form.Group>
                  <div className="crm-postventa-inline-note crm-postventa-altas-step-note">
                    El paso busca primero por CUIT en el flujo viejo. Si el usuario ya existe, lo reutiliza y actualiza; si no existe, lo crea.
                  </div>
                  <div className="d-flex gap-2 flex-wrap crm-postventa-altas-step-actions">
                    <Button type="submit" variant="success" disabled={cargas.guardandoBlanqueo}>
                      {cargas.guardandoBlanqueo ? "Guardando..." : "Guardar y continuar"}
                    </Button>
                  </div>
                </Form>
              ) : null}

              {!isAltaAgregaMode && altaWizardStep === 2 ? (
                <div className="d-grid gap-4">
                  <Form className="crm-postventa-form-grid" onSubmit={handleAltaLegajoSubmit}>
                    <div className="crm-postventa-panel-title">
                      <h3>Paso 2. Legajo</h3>
                    </div>
                    {altaWizardLegajos.length ? (
                      <Form.Group>
                        <Form.Label>Legajo existente para este usuario</Form.Label>
                        <Form.Select
                          value={altaWizardData.legajoId || ""}
                          onChange={handleAltaLegajoSelect}
                        >
                          <option value="">Crear uno nuevo</option>
                          {altaWizardLegajos.map((item) => (
                            <option key={item.id ?? item.Id} value={item.id ?? item.Id}>
                              {item.nroLegajo ?? item.NroLegajo} - {item.nombreDeFantasia ?? item.NombreDeFantasia}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    ) : null}

                    <Form.Group>
                      <Form.Label>N° de legajo sugerido</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.nroLegajo}
                        onChange={(event) => handleAltaWizardLegajoChange("nroLegajo", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Terminal</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.terminal}
                        onChange={(event) => handleAltaWizardLegajoChange("terminal", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Nombre de fantasía</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.nombreDeFantasia}
                        onChange={(event) => handleAltaWizardLegajoChange("nombreDeFantasia", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Razón social</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.razonSocial}
                        onChange={(event) => handleAltaWizardLegajoChange("razonSocial", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Tipo persona</Form.Label>
                      <Form.Select
                        value={altaWizardData.legajo.tipoPersona}
                        onChange={(event) => handleAltaWizardLegajoChange("tipoPersona", event.target.value)}
                      >
                        <option value="fisica">Física</option>
                        <option value="juridica">Jurídica</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Provincia</Form.Label>
                      <Form.Select
                        value={altaWizardData.legajo.provincia}
                        onChange={(event) => handleAltaWizardLegajoChange("provincia", event.target.value)}
                      >
                        <option value="">Seleccionar</option>
                        {fichaCatalogos.provincias.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Calle</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.calle}
                        onChange={(event) => handleAltaWizardLegajoChange("calle", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Número</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.numero}
                        onChange={(event) => handleAltaWizardLegajoChange("numero", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Piso / Depto</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.pisoDepto}
                        onChange={(event) => handleAltaWizardLegajoChange("pisoDepto", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Localidad</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.localidad}
                        onChange={(event) => handleAltaWizardLegajoChange("localidad", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Código postal</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.codigoPostal}
                        onChange={(event) => handleAltaWizardLegajoChange("codigoPostal", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Email contacto</Form.Label>
                      <Form.Control
                        type="email"
                        value={altaWizardData.legajo.emailContacto}
                        onChange={(event) => handleAltaWizardLegajoChange("emailContacto", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Celular / WhatsApp</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.celularWhatsapp}
                        onChange={(event) => handleAltaWizardLegajoChange("celularWhatsapp", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Asesor / gestor</Form.Label>
                      <Form.Select
                        value={altaWizardData.legajo.asesor}
                        onChange={(event) => handleAltaWizardLegajoChange("asesor", event.target.value)}
                      >
                        <option value="">Seleccionar</option>
                        {fichaCatalogos.asesores.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button type="button" variant="outline-secondary" onClick={() => setAltaWizardStep(1)}>
                        Volver
                      </Button>
                      <Button type="submit" variant="success" disabled={cargas.guardandoLegajo || cargas.altaBajaCatalogos}>
                        {cargas.guardandoLegajo ? "Guardando..." : "Guardar legajo y continuar"}
                      </Button>
                    </div>
                  </Form>

                  <section className={`crm-postventa-detail-card ${darkMode ? "is-dark" : ""}`}>
                    <div className="crm-postventa-panel-title">
                      <h4>Documentos del legajo</h4>
                      <span className="crm-postventa-card-label">
                        {altaWizardData.legajoId ? `Legajo #${altaWizardData.legajoId}` : "Guardá el legajo para habilitar archivos"}
                      </span>
                    </div>
                    <Form className="crm-postventa-docs-grid" onSubmit={handleAltaLegajoDocumento}>
                      <Form.Group>
                        <Form.Label>Tipo documento</Form.Label>
                        <Form.Select
                          value={altaLegajoDocDraft.nombreDocumento}
                          onChange={(event) => setAltaLegajoDocDraft((prev) => ({ ...prev, nombreDocumento: event.target.value }))}
                          disabled={!altaWizardData.legajoId}
                        >
                          {ALTA_LEGAJO_DOC_TYPES.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Archivo</Form.Label>
                        <Form.Control
                          type="file"
                          disabled={!altaWizardData.legajoId}
                          onChange={(event) =>
                            setAltaLegajoDocDraft((prev) => ({
                              ...prev,
                              file: event.target.files?.[0] || null,
                            }))
                          }
                        />
                      </Form.Group>
                      <div className="d-flex gap-2 flex-wrap align-items-end">
                        <Button type="submit" variant="outline-success" disabled={!altaWizardData.legajoId || cargas.guardandoDocumentoLegajo}>
                          {cargas.guardandoDocumentoLegajo ? "Subiendo..." : "Subir documento"}
                        </Button>
                        <Button type="button" variant="outline-secondary" onClick={() => setAltaWizardStep(3)} disabled={!altaWizardData.legajoId}>
                          Continuar al alta
                        </Button>
                      </div>
                    </Form>

                    <div className="crm-postventa-table-wrap mt-3">
                      <Table className="crm-postventa-table mb-0" responsive hover>
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Documento</th>
                            <th>Tipo</th>
                            <th>Tamaño</th>
                            <th>Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {altaLegajoDocs.length ? (
                            altaLegajoDocs.map((item) => (
                              <tr key={item.idDocumento ?? item.IdDocumento}>
                                <td>{item.idDocumento ?? item.IdDocumento}</td>
                                <td>{item.nombreDocumento ?? item.NombreDocumento}</td>
                                <td>{item.documentoTipo ?? item.DocumentoTipo ?? "-"}</td>
                                <td>{item.bytes ?? item.Bytes ?? 0}</td>
                                <td>
                                  <Button
                                    type="button"
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleEliminarDocumentoLegajo(item.idDocumento ?? item.IdDocumento)}
                                  >
                                    Eliminar
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center py-4 text-muted">
                                No hay documentos cargados todavía.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </section>
                </div>
              ) : null}

              {isAltaAgregaMode || altaWizardStep === 3 ? (
                <Form className="crm-postventa-form-grid" onSubmit={handleAltaWorkflowSubmit}>
                  <div className="crm-postventa-panel-title">
                    <h3>{isAltaAgregaMode ? "Alta terminal en comercio existente" : "Paso 3. Alta CRM"}</h3>
                  </div>
                  {isAltaAgregaMode ? (
                    <div className="crm-postventa-step-block crm-postventa-form-span-2">
                      <div className="crm-postventa-step-block-title">
                        <h4>Base para AGREGA</h4>
                        <span>Seleccioná una terminal o comercio existente para copiar sus datos fijos.</span>
                      </div>
                      <div className="crm-postventa-step-block-grid">
                        <Form.Group className="crm-postventa-form-span-2">
                          <Form.Label>Buscar terminal / comercio</Form.Label>
                          <div className="d-flex gap-2 flex-wrap">
                            <Form.Control
                              value={altaAgregaSearch}
                              onChange={(event) => setAltaAgregaSearch(event.target.value)}
                              placeholder="Comercio, terminal, CUIT o nombre"
                            />
                            <Button type="button" variant="outline-secondary" onClick={buscarBaseAgrega} disabled={altaAgregaLoading}>
                              {altaAgregaLoading ? "Buscando..." : "Buscar"}
                            </Button>
                          </div>
                        </Form.Group>
                        {altaAgregaResults.length ? (
                          <div className="crm-postventa-step-block-results crm-postventa-form-span-2">
                            {altaAgregaResults.slice(0, 8).map((item) => (
                              <Button
                                key={`${item.clienteId}-${item.nDeTerminal}-${item.nDeComercio}`}
                                type="button"
                                variant="outline-success"
                                className="text-start"
                                onClick={() => seleccionarBaseAgrega(item)}
                              >
                                {(item.nombreDeFantasia || item.razonSocial || "Sin nombre")} | Comercio {item.nDeComercio || "-"} | Terminal {item.nDeTerminal || "-"}
                              </Button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  {isAltaAgregaMode ? (
                    <div className="crm-postventa-inline-note crm-postventa-form-span-2">
                      Modo AGREGA activo. Este flujo reutiliza el alta legacy, copia los datos fijos del comercio base y deja editable solo la nueva terminal y los campos necesarios.
                    </div>
                  ) : null}
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Datos personales / comercio</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>N° terminal</Form.Label>
                    <Form.Control
                      value={altaWizardData.legajo.terminal}
                      readOnly={!isAltaAgregaMode}
                      onChange={isAltaAgregaMode ? (event) => handleAltaWizardLegajoChange("terminal", event.target.value) : undefined}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Legajo</Form.Label>
                    <Form.Control value={altaWizardData.legajo.nroLegajo} readOnly />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Estado</Form.Label>
                    <Form.Control value={altaWizardData.alta.estado || "ALTA"} readOnly />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Fecha de alta</Form.Label>
                    <Form.Control
                      type="date"
                      lang="es-AR"
                      value={altaWizardData.alta.fechaDeAlta || makeLocalToday()}
                      onChange={(event) => handleAltaWizardAltaChange("fechaDeAlta", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Mail</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.correo}
                      onChange={(event) => handleAltaWizardAltaChange("correo", event.target.value)}
                    />
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Contacto</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Teléfono 1</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.celular}
                      onChange={(event) => handleAltaWizardAltaChange("celular", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Puesto / cargo / referencia 1</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.referenciaTelefono}
                      onChange={(event) => handleAltaWizardAltaChange("referenciaTelefono", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Teléfono 2</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.telefonoAlternativo || altaWizardData.alta.telFijo}
                      onChange={(event) => handleAltaWizardAltaChange("telefonoAlternativo", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Puesto / cargo / referencia 2</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.referenciaTelefonoAlternativo}
                      onChange={(event) => handleAltaWizardAltaChange("referenciaTelefonoAlternativo", event.target.value)}
                    />
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Terminal / comercio</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Nombre del asesor</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.nombreDelAsesor}
                      onChange={(event) => handleAltaWizardAltaChange("nombreDelAsesor", event.target.value)}
                      disabled={fichaCatalogosLoading}
                    >
                      <option value="">Seleccionar</option>
                      {withCurrentOption(fichaCatalogos.asesores, altaWizardData.alta.nombreDelAsesor).map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Provincia</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.provincia}
                      onChange={(event) => handleAltaWizardAltaChange("provincia", event.target.value)}
                      disabled={fichaCatalogosLoading}
                    >
                      <option value="">Seleccionar</option>
                      {withCurrentOption(fichaCatalogos.provincias, altaWizardData.alta.provincia).map((item) => (
                        <option key={`provincia-${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Provincia fiscal</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.provincia3}
                      onChange={(event) => handleAltaWizardAltaChange("provincia3", event.target.value)}
                      disabled={fichaCatalogosLoading}
                    >
                      <option value="">Seleccionar</option>
                      {withCurrentOption(fichaCatalogos.provincias, altaWizardData.alta.provincia3).map((item) => (
                        <option key={`provincia3-${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Alicuota</Form.Label>
                    <Form.Control value={altaWizardData.alta.alicuota} readOnly />
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>AFIP</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Condición AFIP</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.condicionAFIP}
                      onChange={(event) => handleAltaWizardAltaChange("condicionAFIP", event.target.value)}
                    >
                      <option value="Monotributista">Monotributista</option>
                      <option value="Responsable Inscripto">Responsable Inscripto</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Actividad laboral / profesional</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.actividadLaboralProfesional}
                      onChange={(event) => handleAltaWizardAltaChange("actividadLaboralProfesional", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Estado AFIP</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.estadoAfip}
                      onChange={(event) => handleAltaWizardAltaChange("estadoAfip", event.target.value)}
                    >
                      {FICHA_FIXED_OPTIONS.estadoAfip.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Exento</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.exento}
                      onChange={(event) => handleAltaWizardAltaChange("exento", event.target.value)}
                    >
                      {FICHA_FIXED_OPTIONS.exento.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Rentas</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Estado rentas</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.estadoRentas}
                      onChange={(event) => handleAltaWizardAltaChange("estadoRentas", event.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {FICHA_FIXED_OPTIONS.estadoRentas.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Domicilio comercio</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.domicilioComercio}
                      onChange={(event) => handleAltaWizardAltaChange("domicilioComercio", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Localidad comercio</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.localidadComercio}
                      onChange={(event) => handleAltaWizardAltaChange("localidadComercio", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Código postal comercio</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.codigoPostalComercio}
                      onChange={(event) => handleAltaWizardAltaChange("codigoPostalComercio", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Link de pago</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.linkDePago}
                      onChange={(event) => handleAltaWizardAltaChange("linkDePago", event.target.value)}
                    >
                      {FICHA_FIXED_OPTIONS.siNo.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Bancario</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Banco</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.banco}
                      onChange={(event) => handleAltaWizardAltaChange("banco", event.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {withCurrentOption(fichaCatalogos.bancos, altaWizardData.alta.banco).map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Tipo cuenta</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.tipoCuenta}
                      onChange={(event) => handleAltaWizardAltaChange("tipoCuenta", event.target.value)}
                    >
                      {withCurrentOption(LEGACY_ALTA_TIPO_CUENTA_OPTIONS, altaWizardData.alta.tipoCuenta).map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>N° de cuenta / CBU</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.cbucvu}
                      onChange={(event) => handleAltaWizardAltaChange("cbucvu", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Alias</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.alias}
                      onChange={(event) => handleAltaWizardAltaChange("alias", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>CUIT bancario</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.cuitbancario}
                      onChange={(event) => handleAltaWizardAltaChange("cuitbancario", event.target.value)}
                      placeholder="Acepta 20-XXXXXXXX-X o 11 dÃ­gitos"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Titular / razón social bancaria</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.titularRazonSocial}
                      onChange={(event) => handleAltaWizardAltaChange("titularRazonSocial", event.target.value)}
                    />
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Terminal / operación</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>N° comercio solicitado</Form.Label>
                    {isAltaAgregaMode ? (
                      <Form.Control value={altaWizardData.alta.numeroComercioSolicitado} readOnly />
                    ) : (
                      <Form.Select
                        value={altaWizardData.alta.numeroComercioSolicitado}
                        onChange={(event) => handleAltaWizardAltaChange("numeroComercioSolicitado", event.target.value)}
                      >
                        <option value="">Seleccionar</option>
                        {withCurrentOption(altaWizardComercioOptions, altaWizardData.alta.numeroComercioSolicitado).map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>N° comercio actual</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.numeroComericoActual}
                      readOnly={isAltaAgregaMode}
                      onChange={isAltaAgregaMode ? undefined : (event) => handleAltaWizardAltaChange("numeroComericoActual", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Tipo alta</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.tipoAlta}
                      onChange={(event) => handleAltaWizardAltaChange("tipoAlta", event.target.value)}
                      disabled={isAltaAgregaMode}
                    >
                      <option value="NuevoAliado">Nuevo aliado</option>
                      <option value="AgregaTerminal">Agrega terminal</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Rubro</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.rubro}
                      onChange={(event) => handleAltaWizardAltaChange("rubro", event.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {withCurrentOption(fichaCatalogos.rubros, altaWizardData.alta.rubro).map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Altas</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.altas}
                      onChange={(event) => handleAltaWizardAltaChange("altas", event.target.value)}
                      disabled={isAltaAgregaMode}
                    >
                      {FICHA_FIXED_OPTIONS.altas.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Alta / baja / operación</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Baja</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.baja}
                      onChange={(event) => handleAltaWizardAltaChange("baja", event.target.value)}
                    >
                      <option value="">Sin valor</option>
                      {FICHA_FIXED_OPTIONS.bajas.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Rubro secundario</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.rubroSecundario}
                      onChange={(event) => handleAltaWizardAltaChange("rubroSecundario", event.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {withCurrentOption(fichaCatalogos.rubros, altaWizardData.alta.rubroSecundario).map((item) => (
                        <option key={`rubro-sec-${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>N° local</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.nroLocal}
                      onChange={(event) => handleAltaWizardAltaChange("nroLocal", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Código actividad</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.codActividad}
                      onChange={(event) => handleAltaWizardAltaChange("codActividad", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Actividad</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.actividad}
                      onChange={(event) => handleAltaWizardAltaChange("actividad", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Actividad 2</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.actividad2}
                      onChange={(event) => handleAltaWizardAltaChange("actividad2", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Código actividad rentas</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.codActividadRentas}
                      onChange={(event) => handleAltaWizardAltaChange("codActividadRentas", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Actividad rentas</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.actividadRentas}
                      onChange={(event) => handleAltaWizardAltaChange("actividadRentas", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="crm-postventa-form-span-2">
                    <Form.Label>Observaciones</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={altaWizardData.alta.observaciones}
                      onChange={(event) => handleAltaWizardAltaChange("observaciones", event.target.value)}
                      placeholder="Observaciones que quedarán en el alta final."
                    />
                  </Form.Group>
                  <div className="crm-postventa-inline-note crm-postventa-form-span-2">
                    El paso final crea el formulario de alta con los datos ya cargados en Blanqueo + Legajo y luego ejecuta el alta real del CRM viejo para no romper la lógica existente.
                  </div>
                  <div className="d-flex gap-2 flex-wrap crm-postventa-form-span-2">
                    {!isAltaAgregaMode ? (
                    <Button type="button" variant="outline-secondary" onClick={() => setAltaWizardStep(2)}>
                      Volver al legajo
                    </Button>
                    ) : null}
                    <Button type="submit" variant="success" disabled={cargas.guardandoAltaWorkflow}>
                      {cargas.guardandoAltaWorkflow ? "Generando alta..." : "Generar alta CRM"}
                    </Button>
                  </div>
                </Form>
              ) : null}
            </section>

            {!isAltaAgregaMode && altaWizardStep === 1 ? (
              <>
                <section className={`crm-postventa-panel crm-postventa-altas-collapsed-stage ${darkMode ? "is-dark" : ""}`}>
                  <div className="crm-postventa-altas-collapsed-head">
                    <div className="crm-postventa-altas-collapsed-title">
                      <span>2</span>
                      <div>
                        <small>Paso 2</small>
                        <strong>Legajo</strong>
                      </div>
                    </div>
                    <div className="crm-postventa-altas-collapsed-hint">* Completar Paso 1 para continuar con el Alta Guiada</div>
                  </div>
                </section>
                <section className={`crm-postventa-panel crm-postventa-altas-collapsed-stage ${darkMode ? "is-dark" : ""}`}>
                  <div className="crm-postventa-altas-collapsed-head">
                    <div className="crm-postventa-altas-collapsed-title">
                      <span>3</span>
                      <div>
                        <small>Paso 3</small>
                        <strong>Alta CRM</strong>
                      </div>
                    </div>
                    <div className="crm-postventa-altas-collapsed-hint">* Completar Paso 1 y Paso 2 para continuar con el Alta Guiada</div>
                  </div>
                </section>
              </>
            ) : null}
          </div>
        ) : (
          <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-panel-title">
              <h3>Baja por terminal</h3>
            </div>
            <Form className="crm-postventa-form-grid" onSubmit={handleBajaWorkflowSubmit}>
              <Form.Group>
                <Form.Label>Buscar terminal o comercio</Form.Label>
                <Form.Control
                  value={bajaLookupSearch}
                  onChange={(event) => setBajaLookupSearch(event.target.value)}
                  placeholder="Ingresar terminal, comercio o razón social"
                />
              </Form.Group>
              <div className="d-flex gap-2 flex-wrap align-items-end">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => buscarOpcionesBaja(bajaLookupSearch)}
                  disabled={bajaLookupLoading}
                >
                  {bajaLookupLoading ? "Buscando..." : "Buscar"}
                </Button>
              </div>
              {bajaLookupResults.length ? (
                <Form.Group className="crm-postventa-form-span-2">
                  <Form.Label>Resultados</Form.Label>
                  <Form.Select defaultValue="" onChange={handleSeleccionBajaResultado}>
                    <option value="">Seleccionar una terminal</option>
                    {bajaLookupResults.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.terminal || "-"} {item.comercio ? `| comercio ${item.comercio}` : ""} {item.nombre ? `| ${item.nombre}` : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              ) : null}
              <Form.Group>
                <Form.Label>Terminal</Form.Label>
                <Form.Control
                  value={bajaDraft.terminal}
                  onChange={(event) => setBajaDraft((prev) => ({ ...prev, terminal: event.target.value }))}
                  placeholder="Ingresar número de terminal"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Motivo</Form.Label>
                <Form.Select
                  value={bajaDraft.motivo}
                  onChange={(event) => setBajaDraft((prev) => ({ ...prev, motivo: event.target.value }))}
                >
                  <option value="BAJA">Baja</option>
                  <option value="BAJA POR FACTURACION">Baja por facturación</option>
                  <option value="BAJA POR POSNET">Baja por Posnet</option>
                  <option value="BAJA POR CAMBIO DE TERMINAL">Baja por cambio de terminal</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Fecha de baja</Form.Label>
                <Form.Control
                  type="date"
                  value={String(bajaDraft.fechaBaja || "").replace(/\//g, "-")}
                  onChange={(event) =>
                    setBajaDraft((prev) => ({
                      ...prev,
                      fechaBaja: event.target.value.replace(/-/g, "/"),
                    }))
                  }
                />
              </Form.Group>
              <Form.Group className="crm-postventa-form-span-2">
                <Form.Label>Observación</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={bajaDraft.observacion}
                  onChange={(event) => setBajaDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                  placeholder="Motivo operativo y contexto de la baja."
                />
              </Form.Group>
              <div className="crm-postventa-inline-note crm-postventa-form-span-2">
                Esta acción reutiliza el flujo viejo de Gestionar terminales (`/api/abm/baja`). La búsqueda es opcional: la baja se ejecuta siempre con la terminal cargada en el formulario.
              </div>
              <div className="d-flex gap-2 flex-wrap crm-postventa-form-span-2">
                <Button type="submit" variant="danger" disabled={cargas.guardandoBajaWorkflow}>
                  {cargas.guardandoBajaWorkflow ? "Procesando baja..." : "Registrar baja por terminal"}
                </Button>
              </div>
            </Form>
          </section>
        )}
      </div>
    );
  };

  const renderGestionIpcBody = () => {
    if (cargas.gestiones) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando gestiones...</div>;
    }

    if (errores.gestiones) {
      return <Alert variant="danger">{errores.gestiones}</Alert>;
    }

    const historial = Array.isArray(gestiones?.historial) ? gestiones.historial : [];
    const ultimaGestion = gestiones?.ultimaGestion || historial[0] || null;
    const rollosHistorial = Array.isArray(gestiones?.rollos?.historial) ? gestiones.rollos.historial : [];

    const focusGestionComposer = (scope = "ipc") => {
      const rules = gestionScopeRules[scope] || [];
      const states = GESTION_SCOPE_STATES[scope] || GESTION_STATES;

      setGestionComposerScope(scope);
      setGestionDraft((prev) => ({
        ...prev,
        reglaId: rules?.[0]?.id ? String(rules[0].id) : prev.reglaId,
        estadoGestion: states.includes(prev.estadoGestion) ? prev.estadoGestion : (states[0] || "Pendiente"),
        tipoIpc: scope === "ipc" ? "" : prev.tipoIpc,
        ipcMotivo: scope === "ipc" ? "" : prev.ipcMotivo,
      }));

      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => {
          document.getElementById("crm-postventa-gestion-form")?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
    };

    const resetGestionForm = () =>
      setGestionDraft({
        reglaId: currentGestionRules?.[0]?.id ? String(currentGestionRules[0].id) : "",
        estadoGestion: currentGestionStates[0] || "Pendiente",
        tipoIpc: "",
        ipcMotivo: "",
        canalContacto: "",
        resultadoLlamada: "",
        evidenciaContacto: "",
        observacion: "",
        fechaGestion: makeNowDateTimeLocal(),
        fechaProximoContacto: "",
      });

    const renderGestionScopeSummary = (scope) => {
      const items = gestionesPorScope[scope] || [];
      const latest = items[0];
      const rules = gestionScopeRules[scope] || [];

      return (
        <section key={scope} className={`crm-postventa-panel crm-postventa-gestion-summary-card ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-panel-title">
            <div>
              <h4>{GESTION_SCOPE_LABELS[scope]}</h4>
              <span className="crm-postventa-card-label">{items.length} registro(s)</span>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!rules.length}
              onClick={() => focusGestionComposer(scope)}
            >
              Nueva gestion
            </Button>
          </div>
          {latest ? (
            <>
              <dl className="crm-postventa-gestion-mini-list">
                <div>
                  <dt>Ultima gestion</dt>
                  <dd>{latest.fechaHora || "-"}</dd>
                </div>
                <div>
                  <dt>Proximo contacto</dt>
                  <dd>{latest.proximoContacto || "-"}</dd>
                </div>
                <div className="crm-postventa-gestion-mini-list-span-2">
                  <dt>Motivo</dt>
                  <dd title={latest.motivo || "-"}>
                    {truncateText(latest.motivo, 92)}
                  </dd>
                </div>
              </dl>
              <div className="d-flex gap-2 flex-wrap mt-3">
                <PostventaBadge value={latest.estado} tone={toneForGestionState(latest.estado)} />
                <PostventaBadge value={latest.prioridad} tone={toneForPriority(latest.prioridad)} />
              </div>
            </>
          ) : (
            <Alert variant="secondary" className="mb-0">{GESTION_SCOPE_EMPTY_MESSAGES[scope]}</Alert>
          )}
        </section>
      );
    };

    return (
      <>
      <div className="crm-gestion-unificada__top-panel crm-postventa-gestion-ipc-toppanel">
        <div className="crm-gestion-unificada__top-copy">
          <h3>GESTIONES IPC</h3>
          <p>Registro y seguimiento de gestiones IPC, alertas operativas y acciones proactivas.</p>
        </div>
        <div className="crm-gestion-unificada__top-actions">
          <button
            type="button"
            className="crm-gestion-unificada__generar"
            onClick={() => {
              setGestionIpcFormOpen(true);
              focusGestionComposer(gestionComposerScope);
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Nueva gestión
          </button>
          <button
            type="button"
            className="crm-gestion-unificada__refresh"
            onClick={() => clienteSeleccionadoId && cargarModulo(clienteSeleccionadoId, analyticsRange)}
            disabled={cargas.gestiones}
            aria-label="Actualizar"
          >
            <FontAwesomeIcon icon={faRotateRight} spin={cargas.gestiones} />
          </button>
        </div>
      </div>

      <div className="crm-postventa-gestion-grid crm-postventa-gestion-layout">
        <section className={`crm-postventa-panel crm-postventa-gestion-sidebar-panel ${darkMode ? "is-dark" : ""} ${gestionIpcSidebarOpen ? "is-open" : "is-collapsed"}`}>
          <div className="crm-postventa-panel-title crm-postventa-gestion-panel-title-row">
            <h3>Última gestión</h3>
            <button
              type="button"
              className="crm-postventa-gestion-toggle-btn"
              onClick={() => setGestionIpcSidebarOpen((prev) => !prev)}
            >
              {gestionIpcSidebarOpen ? "Ocultar" : "Ver detalle"}
            </button>
          </div>
          {ultimaGestion ? (
            <>
              <dl className="crm-postventa-gestion-mini-list">
                <div>
                  <dt>Fecha y hora</dt>
                  <dd>{ultimaGestion.fechaHora || "-"}</dd>
                </div>
                <div>
                  <dt>Usuario</dt>
                  <dd>{ultimaGestion.usuario || "-"}</dd>
                </div>
                <div>
                  <dt>Tipo</dt>
                  <dd>{getGestionTypeLabel(ultimaGestion)}</dd>
                </div>
                <div>
                  <dt>Telefono</dt>
                  <dd>{ultimaGestion.telefono || "-"}</dd>
                </div>
                <div className="crm-postventa-gestion-mini-list-span-2">
                  <dt>Motivo</dt>
                  <dd title={ultimaGestion.motivo || "-"}>{truncateText(ultimaGestion.motivo, 110)}</dd>
                </div>
                <div>
                  <dt>Estado</dt>
                  <dd>
                    <PostventaBadge value={ultimaGestion.estado} tone={toneForGestionState(ultimaGestion.estado)} />
                  </dd>
                </div>
                <div>
                  <dt>Prioridad</dt>
                  <dd>
                    <PostventaBadge value={ultimaGestion.prioridad} tone={toneForPriority(ultimaGestion.prioridad)} />
                  </dd>
                </div>
                <div className="crm-postventa-gestion-mini-list-span-2">
                  <dt>Proximo contacto</dt>
                  <dd>{ultimaGestion.proximoContacto || "-"}</dd>
                </div>
              </dl>
              <div className="crm-postventa-gestion-preview-grid">
                <div className="crm-postventa-gestion-preview-block">
                  <span className="crm-postventa-card-label">Accion sugerida</span>
                  <p className="crm-postventa-gestion-preview-text" title={ultimaGestion.accionSugerida || "-"}>
                    {truncateText(ultimaGestion.accionSugerida, 120)}
                  </p>
                </div>
                <div className="crm-postventa-gestion-preview-block">
                  <span className="crm-postventa-card-label">Mensaje sugerido</span>
                  <p className="crm-postventa-gestion-preview-text" title={ultimaGestion.mensajeSugerido || "-"}>
                    {truncateText(ultimaGestion.mensajeSugerido, 140)}
                  </p>
                </div>
              </div>
              <div className="crm-postventa-gestion-latest-actions">
                <Button variant="outline-secondary" size="sm" onClick={() => copiarMensajeSugerido(ultimaGestion, "Gestiones")}>
                  Copiar mensaje
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => abrirWhatsappSugerido(ultimaGestion, "Gestiones")}>
                  Abrir WhatsApp
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => abrirAccionAlerta(ultimaGestion, "Contactado", buildSuggestedObservation(ultimaGestion, "Contactado"))}>
                  Contactado
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => abrirAccionAlerta(ultimaGestion, "Pendiente", buildSuggestedObservation(ultimaGestion, "Pendiente"))}>
                  Posponer
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => abrirAccionAlerta(ultimaGestion, "Cerrado", buildSuggestedObservation(ultimaGestion, "Cerrado"))}>
                  Cerrar
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => abrirAccionAlerta(ultimaGestion, "Escalado", buildSuggestedObservation(ultimaGestion, "Escalado"))}>
                  Escalar
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setGestionDetalleItem(ultimaGestion)}>
                  Ver detalle
                </Button>
              </div>
            </>
          ) : (
            <Alert variant="secondary">Todavia no hay gestiones de postventa para este cliente.</Alert>
          )}
          {false ? (
            <DataList
              items={[
                { label: "Fecha y hora", value: gestiones.ultimaGestion.fechaHora },
                { label: "Usuario", value: gestiones.ultimaGestion.usuario },
                { label: "Motivo", value: gestiones.ultimaGestion.motivo },
                { label: "Motivo código", value: gestiones.ultimaGestion.motivoCodigo || "-" },
                { label: "Tipo", value: gestiones.ultimaGestion.esAutomatica ? "Automática" : (gestiones.ultimaGestion.tipo || "Manual") },
                {
                  label: "Prioridad",
                  value: gestiones.ultimaGestion.prioridad,
                  isBadge: true,
                  tone: toneForPriority(gestiones.ultimaGestion.prioridad),
                },
                {
                  label: "Estado",
                  value: gestiones.ultimaGestion.estado,
                  isBadge: true,
                  tone: toneForGestionState(gestiones.ultimaGestion.estado),
                },
                { label: "Descripción", value: gestiones.ultimaGestion.descripcion },
                { label: "Acción sugerida", value: gestiones.ultimaGestion.accionSugerida || "-" },
                { label: "Mensaje sugerido", value: gestiones.ultimaGestion.mensajeSugerido || "-" },
                { label: "Teléfono", value: gestiones.ultimaGestion.telefono || "-" },
                { label: "Próximo contacto", value: gestiones.ultimaGestion.proximoContacto },
              ]}
            />
          ) : (
            <Alert variant="secondary">Todavía no hay gestiones de postventa para este cliente.</Alert>
          )}

          {false && gestiones?.ultimaGestion ? (
            <div className="d-flex gap-2 flex-wrap mt-3">
              <Button variant="outline-secondary" onClick={() => copiarMensajeSugerido(gestiones.ultimaGestion, "Gestiones")}>
                Copiar mensaje
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirWhatsappSugerido(gestiones.ultimaGestion, "Gestiones")}>
                Abrir WhatsApp
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Contactado", buildSuggestedObservation(gestiones.ultimaGestion, "Contactado"))}>
                Marcar como contactado
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Pendiente", buildSuggestedObservation(gestiones.ultimaGestion, "Pendiente"))}>
                Posponer
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Cerrado", buildSuggestedObservation(gestiones.ultimaGestion, "Cerrado"))}>
                Cerrar
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Escalado", buildSuggestedObservation(gestiones.ultimaGestion, "Escalado"))}>
                Escalar
              </Button>
            </div>
          ) : null}

          <div className="crm-postventa-rollos crm-postventa-gestion-rollos mt-4">
            <div className="crm-postventa-panel-title mb-3">
              <div>
                <h4>Seguimiento de rollos</h4>
                <span className="crm-postventa-card-label">Bloque secundario y compacto.</span>
              </div>
              <div className="crm-postventa-gestion-rollos-actions">
                <Button variant="outline-secondary" size="sm" onClick={() => setGestionesRollosExpanded((prev) => !prev)}>
                  {gestionesRollosExpanded ? "Ocultar historial" : "Ver historial"}
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleRegistrarRollo}>
                  Registrar entrega
                </Button>
              </div>
            </div>
            <DataList
              items={[
                { label: "Ultima entrega", value: gestiones?.rollos?.ultimaEntrega || "-" },
                {
                  label: "Cantidad ultima entrega",
                  value: gestiones?.rollos?.cantidadUltimaEntrega > 0 ? String(gestiones.rollos.cantidadUltimaEntrega) : "-",
                },
                { label: "Observacion ultima entrega", value: gestiones?.rollos?.observacionUltimaEntrega || "-" },
              ]}
            />
            {gestionesRollosExpanded ? (
            <div className="crm-postventa-table-wrap crm-postventa-gestion-rollos-table-wrap mt-3">
              <Table className="crm-postventa-table mb-0" responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Cantidad</th>
                    <th>Usuario</th>
                    <th>Observacion</th>
                  </tr>
                </thead>
                <tbody>
                  {gestiones?.rollos?.historial?.length ? (
                    gestiones.rollos.historial.map((item) => (
                      <tr key={`rollo-${item.id}`}>
                        <td>{item.fechaEntrega}</td>
                        <td>{item.cantidad}</td>
                        <td>{item.usuario}</td>
                        <td>
                          <span className="crm-postventa-gestion-text-cell" title={item.observacion || "-"}>
                            {truncateText(item.observacion, 96)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center py-4 text-muted">
                        No hay entregas registradas para este cliente.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            ) : null}
          </div>
        </section>

        <section id="crm-postventa-gestion-form" className={`crm-postventa-panel crm-postventa-gestion-main-panel ${darkMode ? "is-dark" : ""} ${gestionIpcFormOpen ? "is-open" : "is-collapsed"}`}>
          <div className="crm-postventa-panel-title crm-postventa-gestion-panel-title-row">
            <h3>Nueva gestión</h3>
            <button
              type="button"
              className="crm-postventa-gestion-toggle-btn"
              onClick={() => setGestionIpcFormOpen((prev) => !prev)}
            >
              {gestionIpcFormOpen ? "Cancelar" : "+ Nueva gestión"}
            </button>
          </div>
          <div className="d-flex gap-2 flex-wrap mb-3 crm-postventa-gestion-scope-tabs">
            {["ipc", "operativa", "proactiva"].map((scope) => (
              <button
                key={scope}
                type="button"
                className={`crm-postventa-gestion-scope-chip ${gestionComposerScope === scope ? "is-active" : ""}`}
                onClick={() => setGestionComposerScope(scope)}
              >
                {GESTION_SCOPE_LABELS[scope]}
              </button>
            ))}
          </div>
          <Form className="crm-postventa-form-grid crm-postventa-gestion-form" onSubmit={handleCrearGestion}>
            <Form.Group>
              <Form.Label>Fecha/hora</Form.Label>
              <Form.Control
                type="datetime-local"
                value={gestionDraft.fechaGestion}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, fechaGestion: event.target.value }))
                }
              />
            </Form.Group>

            {gestionComposerScope === "ipc" ? (
              <Form.Group>
                <Form.Label>Tipo IPC</Form.Label>
                <Form.Select
                  value={gestionDraft.tipoIpc}
                  onChange={(event) =>
                    setGestionDraft((prev) => ({
                      ...prev,
                      tipoIpc: event.target.value,
                      ipcMotivo: "",
                    }))
                  }
                >
                  <option value="">Seleccionar</option>
                  {GESTION_IPC_TYPES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            ) : null}

            <Form.Group>
              <Form.Label>Motivo / acción</Form.Label>
              {gestionComposerScope === "ipc" ? (
                <Form.Select
                  value={gestionDraft.ipcMotivo}
                  onChange={(event) =>
                    setGestionDraft((prev) => ({ ...prev, ipcMotivo: event.target.value }))
                  }
                  disabled={!gestionDraft.tipoIpc}
                >
                  <option value="">{gestionDraft.tipoIpc ? "Seleccionar" : "Primero seleccione Tipo IPC"}</option>
                  {currentIpcMotivoOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Select
                  value={gestionDraft.reglaId}
                  onChange={(event) =>
                    setGestionDraft((prev) => ({ ...prev, reglaId: event.target.value }))
                  }
                >
                  <option value="">Seleccionar</option>
                  {currentGestionRules.map((regla) => (
                    <option key={regla.id} value={regla.id}>
                      {formatGestionRuleLabel(regla)}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>

            <Form.Group>
              <Form.Label>Estado de gestion</Form.Label>
              <Form.Select
                value={gestionDraft.estadoGestion}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, estadoGestion: event.target.value }))
                }
              >
                {currentGestionStates.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {usaProtocoloOperativo ? (
              <>
                <Form.Group>
                  <Form.Label>Canal de contacto</Form.Label>
                  <Form.Select
                    value={gestionDraft.canalContacto}
                    onChange={(event) =>
                      setGestionDraft((prev) => ({
                        ...prev,
                        canalContacto: event.target.value,
                        resultadoLlamada: event.target.value === "Llamada" ? prev.resultadoLlamada : "",
                        evidenciaContacto: event.target.value === "Llamada" ? prev.evidenciaContacto : "",
                      }))
                    }
                  >
                    <option value="">Seleccionar</option>
                    {CONTACT_CHANNEL_OPTIONS.map((item) => {
                      const enabled = canalesProtocoloHabilitados.includes(item);
                      return (
                        <option key={item} value={item} disabled={!enabled}>
                          {enabled ? item : `${item} (bloqueado)`}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>

                <div className="crm-postventa-inline-note crm-postventa-gestion-note">
                  Proxima accion obligatoria: {protocoloContacto?.proximaAccionObligatoria || "-"} | Motivo: {protocoloContacto?.motivo || "-"} | Evidencia requerida: {gestionDraft.canalContacto === "Llamada" ? "Si" : (protocoloContacto?.evidenciaRequerida ? "Si" : "No")}
                </div>

                {gestionDraft.canalContacto === "Llamada" ? (
                  <>
                    <Form.Group>
                      <Form.Label>Resultado de llamada</Form.Label>
                      <Form.Select
                        value={gestionDraft.resultadoLlamada}
                        onChange={(event) =>
                          setGestionDraft((prev) => ({ ...prev, resultadoLlamada: event.target.value }))
                        }
                      >
                        <option value="">Seleccionar</option>
                        {CALL_RESULT_OPTIONS.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Evidencia</Form.Label>
                      <Form.Control
                        type="text"
                        value={gestionDraft.evidenciaContacto}
                        onChange={(event) =>
                          setGestionDraft((prev) => ({ ...prev, evidenciaContacto: event.target.value }))
                        }
                        placeholder="Link, ruta, adjunto o referencia de evidencia"
                      />
                    </Form.Group>
                  </>
                ) : null}
              </>
            ) : null}

            <Form.Group>
              <Form.Label>Proximo contacto</Form.Label>
              <Form.Control
                type="datetime-local"
                value={gestionDraft.fechaProximoContacto}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))
                }
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Observación</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={gestionDraft.observacion}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, observacion: event.target.value }))
                }
                placeholder="Describí brevemente la interacción con el cliente."
              />
            </Form.Group>

            <div className="crm-postventa-inline-note crm-postventa-gestion-note">
              Usuario que atiende: {gestiones?.usuarioActual || "-"}
            </div>
            <div className="crm-postventa-inline-note crm-postventa-gestion-note">
              {gestionComposerScope === "operativa"
                ? "Las alertas gestionadas salen de Alertas y solo vuelven cuando vence el proximo contacto o al dia siguiente."
                : gestionComposerScope === "proactiva"
                  ? "Las proactivas se siguen desde este bloque y no se mezclan con Alertas operativas."
                  : "Las gestiones IPC mantienen su propio historial y no suman alertas por si solas."}
            </div>

            <div className="d-flex gap-2 flex-wrap crm-postventa-gestion-form-actions">
              <Button
                type="button"
                variant="outline-secondary"
                onClick={resetGestionForm}
              >
                Limpiar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                {cargas.guardandoGestion ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Guardando...
                  </>
                ) : (
                  "Guardar gestión"
                )}
              </Button>
            </div>
          </Form>

          <div className="crm-postventa-table-wrap crm-postventa-table-wrap-scroll crm-postventa-table-wrap-safe mt-4 crm-postventa-gestion-history-wrap">
            <Table className="crm-postventa-table crm-postventa-gestion-history-table mb-0" responsive hover>
              <thead className="table-light">
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Prioridad</th>
                  <th>Usuario</th>
                  <th>Motivo</th>
                  <th>Canal</th>
                  <th>Resultado llamada</th>
                  <th>Evidencia</th>
                  <th>Proxima accion</th>
                  <th>Acción sugerida</th>
                  <th>Mensaje sugerido</th>
                  <th>Estado</th>
                  <th>Próximo contacto</th>
                  <th>Observación</th>
                </tr>
              </thead>
              <tbody>
                {gestiones?.historial?.length ? (
                  gestiones.historial.map((item) => (
                    <tr key={item.id}>
                      <td>{item.fechaHora || "-"}</td>
                      <td>
                        <div className="crm-postventa-gestion-table-stack">
                          <strong>{GESTION_SCOPE_LABELS[resolveGestionScope(item)] || "Gestion"}</strong>
                          <span>{getGestionTypeLabel(item)}</span>
                        </div>
                      </td>
                      <td>
                        <PostventaBadge value={item.prioridad} tone={toneForPriority(item.prioridad)} />
                      </td>
                      <td>{item.usuario || "-"}</td>
                      <td>
                        <span className="crm-postventa-gestion-text-cell" title={item.motivo || "-"}>
                          {truncateText(item.motivo, 72)}
                        </span>
                      </td>
                      <td>{item.canalContacto || "-"}</td>
                      <td>{item.resultadoLlamada || "-"}</td>
                      <td>
                        <span className="crm-postventa-gestion-text-cell" title={item.evidenciaContacto || "-"}>
                          {truncateText(item.evidenciaContacto, 48)}
                        </span>
                      </td>
                      <td>
                        <span className="crm-postventa-gestion-text-cell" title={item.proximaAccionObligatoria || "-"}>
                          {truncateText(item.proximaAccionObligatoria, 48)}
                        </span>
                      </td>
                      <td>{item.accionSugerida || "-"}</td>
                      <td>{item.mensajeSugerido || "-"}</td>
                      <td>
                        <PostventaBadge value={item.estado} tone={toneForGestionState(item.estado)} />
                      </td>
                      <td>{item.proximoContacto || "-"}</td>
                      <td>
                        <div className="crm-postventa-gestion-row-actions">
                          <Button size="sm" variant="outline-secondary" onClick={() => setGestionDetalleItem(item)}>
                            Ver detalle
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => abrirEditarGestion(item)} disabled={!item.puedeEditar}>
                            Editar
                          </Button>
                          <Button size="sm" variant="outline-success" onClick={() => abrirFinalizarGestion(item)} disabled={!item.puedeFinalizar}>
                            Finalizar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={14} className="text-center py-4 text-muted">
                      No hay historial de gestiones para mostrar.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="crm-postventa-gestion-summary-grid mt-4">
            {["ipc", "operativa", "proactiva"].map((scope) => renderGestionScopeSummary(scope))}
          </div>

        <Modal show={Boolean(gestionDetalleItem)} onHide={() => setGestionDetalleItem(null)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Detalle de gestion</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-grid gap-3">
            <div className="crm-postventa-detail-meta">
              <div>
                <span className="crm-postventa-card-label">Fecha</span>
                <strong>{gestionDetalleItem?.fechaHora || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Usuario</span>
                <strong>{gestionDetalleItem?.usuario || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Tipo</span>
                <strong>{getGestionTypeLabel(gestionDetalleItem)}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Scope</span>
                <strong>{GESTION_SCOPE_LABELS[resolveGestionScope(gestionDetalleItem)] || "Gestion"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Estado</span>
                <strong>{gestionDetalleItem?.estado || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Prioridad</span>
                <strong>{gestionDetalleItem?.prioridad || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Telefono</span>
                <strong>{gestionDetalleItem?.telefono || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Proximo contacto</span>
                <strong>{gestionDetalleItem?.proximoContacto || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Canal</span>
                <strong>{gestionDetalleItem?.canalContacto || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Resultado llamada</span>
                <strong>{gestionDetalleItem?.resultadoLlamada || "-"}</strong>
              </div>
            </div>

            <div className="crm-postventa-gestion-detail-grid">
              <div>
                <span className="crm-postventa-card-label">Motivo</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.motivo || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Motivo codigo</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.motivoCodigo || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Accion sugerida</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.accionSugerida || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Mensaje sugerido</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.mensajeSugerido || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Descripcion</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.descripcion || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Observacion</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.observacion || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Evidencia</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.evidenciaContacto || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Proxima accion obligatoria</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.proximaAccionObligatoria || "-"}</div>
              </div>
            </div>

            <div>
              <span className="crm-postventa-card-label">Historial de modificaciones</span>
              {gestionDetalleItem?.historialModificaciones?.length ? (
                <div className="crm-postventa-table-wrap crm-postventa-table-wrap-safe mt-2">
                  <Table size="sm" className="crm-postventa-table crm-postventa-gestion-change-table mb-0" responsive>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Usuario</th>
                        <th>Accion</th>
                        <th>Estado</th>
                        <th>Observacion</th>
                        <th>Proximo contacto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gestionDetalleItem.historialModificaciones.map((cambio, index) => (
                        <tr key={`${cambio.fecha}-${index}`}>
                          <td>{cambio.fecha || "-"}</td>
                          <td>{cambio.usuario || "-"}</td>
                          <td>{cambio.accion || "-"}</td>
                          <td>{`${cambio.estadoAnterior || "-"} -> ${cambio.estadoNuevo || "-"}`}</td>
                          <td>{`${cambio.observacionAnterior || "-"} -> ${cambio.observacionNueva || "-"}`}</td>
                          <td>{`${cambio.proximoContactoAnterior || "-"} -> ${cambio.proximoContactoNuevo || "-"}`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="crm-postventa-detail-pre mt-2">Sin modificaciones registradas.</div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setGestionDetalleItem(null)}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={Boolean(gestionEditItem)} onHide={() => setGestionEditItem(null)} centered size="lg">
          <Form onSubmit={handleGuardarEdicionGestion}>
            <Modal.Header closeButton>
              <Modal.Title>Editar gestion</Modal.Title>
            </Modal.Header>
            <Modal.Body className="crm-postventa-form-grid">
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={gestionEditDraft.estadoResultante}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, estadoResultante: event.target.value }))}
                >
                  {GESTION_EDIT_STATES.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Proximo contacto</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={gestionEditDraft.fechaProximoContacto}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Prioridad</Form.Label>
                <Form.Select
                  value={gestionEditDraft.prioridad}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, prioridad: event.target.value }))}
                >
                  {["Alta", "Media", "Baja"].map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Canal</Form.Label>
                <Form.Select
                  value={gestionEditDraft.canalContacto}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, canalContacto: event.target.value }))}
                >
                  <option value="">Seleccionar</option>
                  {CONTACT_CHANNEL_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Resultado llamada</Form.Label>
                <Form.Control
                  type="text"
                  value={gestionEditDraft.resultadoLlamada}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, resultadoLlamada: event.target.value }))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Evidencia</Form.Label>
                <Form.Control
                  type="text"
                  value={gestionEditDraft.evidenciaContacto}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, evidenciaContacto: event.target.value }))}
                />
              </Form.Group>
              <Form.Group className="crm-postventa-form-span-2">
                <Form.Label>Observacion</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={gestionEditDraft.observacion}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setGestionEditItem(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                Guardar cambios
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={Boolean(gestionFinalizarItem)} onHide={() => setGestionFinalizarItem(null)} centered size="lg">
          <Form onSubmit={handleFinalizarGestionExistente}>
            <Modal.Header closeButton>
              <Modal.Title>Finalizar gestion</Modal.Title>
            </Modal.Header>
            <Modal.Body className="crm-postventa-form-grid">
              <Form.Group>
                <Form.Label>Resultado final</Form.Label>
                <Form.Select
                  value={gestionFinalizarDraft.estadoResultante}
                  onChange={(event) => setGestionFinalizarDraft((prev) => ({ ...prev, estadoResultante: event.target.value }))}
                >
                  <option value="Cerrado">Cerrado</option>
                  <option value="Derivado">Derivado</option>
                  <option value="Descartado">Descartado</option>
                  <option value="Sin respuesta">Sin respuesta</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Motivo de cierre</Form.Label>
                <Form.Select
                  value={gestionFinalizarDraft.motivoCierre}
                  onChange={(event) => setGestionFinalizarDraft((prev) => ({ ...prev, motivoCierre: event.target.value }))}
                >
                  {GESTION_CLOSE_REASONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Resultado llamada</Form.Label>
                <Form.Control
                  type="text"
                  value={gestionFinalizarDraft.resultadoLlamada}
                  onChange={(event) => setGestionFinalizarDraft((prev) => ({ ...prev, resultadoLlamada: event.target.value }))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Evidencia</Form.Label>
                <Form.Control
                  type="text"
                  value={gestionFinalizarDraft.evidenciaContacto}
                  onChange={(event) => setGestionFinalizarDraft((prev) => ({ ...prev, evidenciaContacto: event.target.value }))}
                />
              </Form.Group>
              <Form.Group className="crm-postventa-form-span-2">
                <Form.Label>Observacion final</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={gestionFinalizarDraft.observacionFinal}
                  onChange={(event) => setGestionFinalizarDraft((prev) => ({ ...prev, observacionFinal: event.target.value }))}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setGestionFinalizarItem(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                Finalizar
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={rolloModalOpen} onHide={() => setRolloModalOpen(false)} centered>
          <Form onSubmit={handleGuardarRollo}>
            <Modal.Header closeButton>
              <Modal.Title>Registrar nueva entrega</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="crm-postventa-form-grid">
                <Form.Group>
                  <Form.Label>Fecha entrega</Form.Label>
                  <Form.Control
                    type="date"
                    value={rolloDraft.fechaEntrega}
                    onChange={(event) => setRolloDraft((prev) => ({ ...prev, fechaEntrega: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Cantidad</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    value={rolloDraft.cantidad}
                    onChange={(event) => setRolloDraft((prev) => ({ ...prev, cantidad: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Observacion</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={rolloDraft.observacion}
                    onChange={(event) => setRolloDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                    placeholder="Detalle opcional de la entrega."
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="outline-secondary" onClick={() => setRolloModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoRollo}>
                {cargas.guardandoRollo ? "Guardando..." : "Guardar"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
        </section>
      </div>
      </>
    );
  };

  const renderGestion = () => {
    const gestionDateLabel = alertaHeaderClock.toLocaleDateString("es-AR");
    const gestionTimeLabel = alertaHeaderClock.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return (
      <div className="crm-postventa-workflow">
        <div className="crm-postventa-altas-header crm-postventa-altas-header--alert-style">
          <div className="crm-postventa-altas-header-copy">
            <span className="crm-postventa-kicker">CRM POSTVENTAS</span>
            <h2>Gestión</h2>
          </div>
          <div className="crm-postventa-altas-header-clock">
            <span>{gestionDateLabel}</span>
            <span className="crm-postventa-altas-header-separator" />
            <span>{gestionTimeLabel}</span>
          </div>
        </div>

        <div className="crm-postventa-herramientas-tabs" role="tablist" aria-label="Categorías de gestión">
          {GESTION_UNIFICADA_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={gestionUnificadaTab === tab.id}
              className={`crm-postventa-herramientas-tab ${gestionUnificadaTab === tab.id ? "is-active" : ""}`}
              onClick={() => setGestionUnificadaTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {gestionUnificadaTab === "ipc" ? (
          renderGestionIpcBody()
        ) : gestionUnificadaTab === "rollo" ? (
          <div className="crm-postventa-herramientas-disabled-panel" role="status">
            ROLLO: EN DESARROLLO
          </div>
        ) : (
          <GestionProcesoPanel slug={gestionUnificadaTab} />
        )}
      </div>
    );
  };

  const renderFacturacionFigma = () => {
    const rangeError = getRangeValidationMessage(analyticsRange);
    const resumen = facturacion?.resumen || {};
    const insights = facturacion?.insights || {};
    const headerClockLabel = formatHeaderDateTime(alertaHeaderClock);
    const terminalTabs = terminalesGrupo.length
      ? terminalesGrupo
      : clienteSeleccionadoId
        ? [{ clienteId: clienteSeleccionadoId, label: "Terminal 1" }]
        : [];
    const hasRows = filasFacturacion.length > 0;
    const showShell = (children) => (
      <div className="crm-postventa-facturacion-screen">
        <div className="crm-postventa-facturacion-header">
          <div>
            <span>CRM POSTVENTAS</span>
            <h2>Facturacion</h2>
          </div>
          <time>{headerClockLabel}</time>
        </div>
        {children}
      </div>
    );
    const clientCard = (
      <section className="crm-postventa-facturacion-client">
        <div>
          <h3>{clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Cliente seleccionado"}</h3>
          <div className="crm-postventa-facturacion-client-badges">
            <span>CUIT <strong>{clienteHeader.cuit || "-"}</strong></span>
            <span>Estado <strong className="is-positive">{clienteHeader.estado || "-"}</strong></span>
            <span>Terminal <strong>{clienteHeader.nDeTerminal || "-"}</strong></span>
            <span>Comercio <strong>{clienteHeader.nDeComercio || "-"}</strong></span>
            <span>Legajo <strong>{clienteHeader.legajo || "-"}</strong></span>
          </div>
        </div>
        <button type="button" className="crm-postventa-facturacion-client-button" onClick={() => setClienteModalOpen(true)}>
          <FontAwesomeIcon icon={faUserPen} />
          Cambiar Cliente
        </button>
      </section>
    );

    if (cargas.facturacion) {
      return showShell(
        <>
          <div className="crm-postventa-facturacion-skeleton is-client" />
          <div className="crm-postventa-facturacion-skeleton-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <span key={`facturacion-skeleton-metric-${index}`} className="crm-postventa-facturacion-skeleton" />
            ))}
          </div>
          <div className="crm-postventa-facturacion-skeleton is-wide" />
          <div className="crm-postventa-facturacion-skeleton is-table" />
        </>
      );
    }

    if (errores.facturacion) {
      return showShell(
        <Alert variant="danger" className="crm-postventa-facturacion-message">
          <div>No pudimos cargar la informacion de facturacion.</div>
          <Button size="sm" variant="outline-secondary" onClick={() => cargarModulo(clienteSeleccionadoId, analyticsRange)}>
            Reintentar
          </Button>
        </Alert>
      );
    }

    if (!facturacion?.tieneDatosReales) {
      return showShell(
        <>
          {clientCard}
          <Alert variant="secondary" className="crm-postventa-facturacion-message">
            {facturacion?.message || "No hay informacion de facturacion para el periodo seleccionado."}
          </Alert>
        </>
      );
    }

    const formatFacturacionModalDate = (value, emptyText = "-") => {
      const formatted = formatBillingDate(value);
      return formatted === "Sin datos" ? emptyText : formatted;
    };
    const formatFacturacionModalOperations = (value) => {
      if (value === undefined || value === null || value === "" || !Number.isFinite(Number(value))) {
        return "-";
      }

      return Number(value).toLocaleString("es-AR", { maximumFractionDigits: 0 });
    };
    const pluralizeFacturacionMeta = (count, singular, plural) => {
      const parsed = Number(count);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return "";
      }

      return `${parsed.toLocaleString("es-AR", { maximumFractionDigits: 0 })} ${parsed === 1 ? singular : plural}`;
    };
    const getFacturacionModalMeta = (row) => {
      const terminalCount = terminalesGrupo.length || (row?.terminal ? 1 : 0);
      const commerceCount = new Set(
        (terminalesGrupo.length ? terminalesGrupo : [{ comercio: row?.comercio }])
          .map((terminal) => terminal?.comercio)
          .filter(Boolean)
      ).size || (row?.comercio ? 1 : 0);

      return [
        row?.razonSocial || clienteHeader.razonSocial,
        row?.cuit ? `CUIT ${row.cuit}` : "",
        pluralizeFacturacionMeta(terminalCount, "TERMINAL", "TERMINALES"),
        pluralizeFacturacionMeta(commerceCount, "COMERCIO", "COMERCIOS"),
      ].filter(Boolean);
    };
    const closeFacturacionManagementModal = () => {
      setFacturacionAccionItem(null);
      setFacturacionDetalle(null);
      setFacturacionCuitCopiadoKey("");
    };
    const renderFacturacionManagementModal = () => {
      const row = facturacionAccionItem;
      const isDetailOpen = Boolean(row && facturacionDetalle);
      const modalRow = isDetailOpen ? facturacionDetalle : row;
      const title = String(modalRow?.nombreDeFantasia || modalRow?.razonSocial || clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Cliente").trim();
      const titleUpper = title ? title.toUpperCase() : "CLIENTE";
      const metaItems = getFacturacionModalMeta(modalRow);
      const whatsappPhone = resolvePreferredWhatsappPhone(modalRow);
      const detailRows = Array.isArray(modalRow?.serieDiaria) ? modalRow.serieDiaria : [];
      const copiedKey = `${modalRow?.cuit || ""}-${modalRow?.terminal || ""}-${modalRow?.comercio || ""}`;
      const cuitCopied = Boolean(copiedKey && facturacionCuitCopiadoKey === copiedKey);

      return (
        <Modal
          show={Boolean(row)}
          onHide={closeFacturacionManagementModal}
          centered
          className="facturacion-management-modal"
          dialogClassName="facturacion-management-modal__dialog"
          contentClassName="facturacion-management-modal__content"
          backdropClassName="facturacion-management-modal__backdrop"
          aria-labelledby="facturacion-management-modal-title"
        >
          <Modal.Body className="facturacion-management-modal__body">
            <div className="facturacion-management-modal__header">
              <h2 id="facturacion-management-modal-title">Gestión de facturación</h2>
              <button
                type="button"
                className="facturacion-management-modal__close"
                aria-label="Cerrar gestión de facturación"
                onClick={closeFacturacionManagementModal}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            {modalRow ? (
              <>
                <section className="facturacion-management-modal__commerce-card">
                  <div className="facturacion-management-modal__commerce-copy">
                    <strong>{titleUpper}</strong>
                    <p>{metaItems.length ? metaItems.join(" | ") : "No hay informacion del comercio disponible."}</p>
                    <button
                      type="button"
                      className="facturacion-management-modal__copy-cuit"
                      onClick={() => copiarCuitFacturacion(modalRow)}
                      disabled={!modalRow?.cuit}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                      {cuitCopied ? "CUIT copiado" : "Copiar CUIT"}
                    </button>
                  </div>
                  <div className="facturacion-management-modal__commerce-actions">
                    {whatsappPhone ? (
                      <button
                        type="button"
                        className="facturacion-management-modal__whatsapp"
                        aria-label={`Abrir WhatsApp de ${title}`}
                        onClick={() => abrirWhatsappSugerido(modalRow, "Facturacion")}
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="facturacion-management-modal__detail-button"
                      onClick={() => showFacturacionDetail(modalRow)}
                      disabled={isDetailOpen}
                    >
                      Ver detalle
                    </button>
                  </div>
                </section>

                {isDetailOpen ? (
                  <section className="facturacion-management-modal__detail">
                    <div className="facturacion-management-modal__history">
                      <h3>Detalle de facturacion</h3>
                      <div className="facturacion-management-modal__table-scroll">
                        {detailRows.length ? (
                          <Table className="facturacion-management-modal__table" hover>
                            <thead>
                              <tr>
                                <th>Fecha</th>
                                <th>Total bruto</th>
                                <th>Operaciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detailRows.map((item, index) => (
                                <tr key={`${modalRow.terminal || "terminal"}-${item.fecha || index}`}>
                                  <td>{formatFacturacionModalDate(item.fecha)}</td>
                                  <td>{formatBillingCurrency(item.totalBruto)}</td>
                                  <td>{formatFacturacionModalOperations(item.cantidadOperaciones)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        ) : (
                          <div className="facturacion-management-modal__empty">No hay movimientos de facturacion para mostrar.</div>
                        )}
                      </div>
                    </div>

                    <aside className="facturacion-management-modal__quick-summary">
                      <h3>Resumen rapido</h3>
                      <dl>
                        <div>
                          <dt>Monto hoy</dt>
                          <dd>{formatBillingCurrency(modalRow.montoHoy)}</dd>
                        </div>
                        <div>
                          <dt>Promedio 7 dias</dt>
                          <dd>{formatBillingCurrency(modalRow.promedio7Dias)}</dd>
                        </div>
                        <div>
                          <dt>Variacion</dt>
                          <dd>{percent(modalRow.variacion)}</dd>
                        </div>
                        <div>
                          <dt>Ultima actividad</dt>
                          <dd>{formatFacturacionModalDate(modalRow.ultimaActividad, "Sin actividad")}</dd>
                        </div>
                      </dl>
                    </aside>
                  </section>
                ) : null}

                <div className="facturacion-management-modal__final-actions">
                  <button
                    type="button"
                    className="facturacion-management-modal__primary-action"
                    onClick={() => ejecutarAccionFacturacion("seguimiento", modalRow)}
                  >
                    Marcar seguimiento
                  </button>
                  <button
                    type="button"
                    className="facturacion-management-modal__primary-action"
                    onClick={() => ejecutarAccionFacturacion("registrarGestion", modalRow)}
                  >
                    Registrar gestion
                  </button>
                </div>
              </>
            ) : (
              <div className="facturacion-management-modal__empty">No pudimos cargar la informacion del comercio.</div>
            )}
          </Modal.Body>
        </Modal>
      );
    };

    return showShell(
      <>
        {clientCard}

        <div className="crm-postventa-facturacion-date-grid">
          <Form.Group controlId="facturacion-desde">
            <Form.Label>Desde</Form.Label>
            <div className="crm-postventa-facturacion-date-control">
              <Form.Control
                type="date"
                value={analyticsRange.desde}
                aria-invalid={Boolean(rangeError)}
                onClick={openNativeDatePicker}
                onKeyDown={openNativeDatePickerFromKeyboard}
                onChange={(event) => setAnalyticsRange((prev) => ({ ...prev, desde: event.target.value }))}
              />
            </div>
          </Form.Group>
          <Form.Group controlId="facturacion-hasta">
            <Form.Label>Hasta</Form.Label>
            <div className="crm-postventa-facturacion-date-control">
              <Form.Control
                type="date"
                value={analyticsRange.hasta}
                aria-invalid={Boolean(rangeError)}
                onClick={openNativeDatePicker}
                onKeyDown={openNativeDatePickerFromKeyboard}
                onChange={(event) => setAnalyticsRange((prev) => ({ ...prev, hasta: event.target.value }))}
              />
            </div>
          </Form.Group>
          {rangeError ? <div className="crm-postventa-facturacion-field-error" role="alert">{rangeError}</div> : null}
        </div>

        <div className="crm-postventa-facturacion-toolbar">
          <div className="crm-postventa-facturacion-tabs" role="tablist" aria-label="Terminales de facturacion">
            {terminalTabs.length ? (
              terminalTabs.map((terminal, index) => (
                <button
                  key={terminal.clienteId}
                  type="button"
                  role="tab"
                  aria-selected={terminal.clienteId === clienteSeleccionadoId}
                  className={`crm-postventa-facturacion-tab ${terminal.clienteId === clienteSeleccionadoId ? "is-active" : ""}`}
                  onClick={() => handleCambiarTerminal(terminal)}
                >
                  TERMINAL {index + 1}
                </button>
              ))
            ) : (
              <span className="crm-postventa-facturacion-empty-inline">El cliente seleccionado no tiene terminales disponibles.</span>
            )}
          </div>
          <button
            type="button"
            className={`crm-postventa-facturacion-refresh ${cargas.facturacion ? "is-loading" : ""}`}
            aria-label="Actualizar facturacion"
            title="Actualizar facturacion"
            disabled={cargas.facturacion || Boolean(rangeError)}
            onClick={() => cargarModulo(clienteSeleccionadoId, analyticsRange)}
          >
            <FontAwesomeIcon icon={faArrowsRotate} />
          </button>
          {facturacionFiltroComercio ? (
            <Button size="sm" variant="outline-secondary" onClick={() => setFacturacionFiltroComercio("")}>
              Ver todo el grupo
            </Button>
          ) : null}
        </div>

        <div className="crm-postventa-facturacion-metrics">
          {[
            ["Monto hoy", resumen.montoHoy],
            ["Monto ayer", resumen.montoAyer],
            ["Promedio 3 dias", resumen.promedio3Dias],
            ["Promedio 7 dias", resumen.promedio7Dias],
          ].map(([label, value]) => (
            <article className="crm-postventa-facturacion-metric" key={label}>
              <strong>{formatBillingCurrency(value)}</strong>
              <span>{label}</span>
            </article>
          ))}
          <div className="crm-postventa-facturacion-variations">
            <article>
              <span>Variacion vs ayer</span>
              <strong>{percent(resumen.variacionVsAyer)}</strong>
            </article>
            <article>
              <span>Variacion vs 7 dias</span>
              <strong>{percent(resumen.variacionVs7Dias)}</strong>
            </article>
          </div>
        </div>

        <section className="crm-postventa-facturacion-operational">
          {[
            [String(resumen.terminales ?? 0).padStart(2, "0"), "Terminales"],
            [String(resumen.conFacturacion ?? 0).padStart(2, "0"), "Con facturacion"],
            [String(resumen.sinFacturacion ?? 0).padStart(2, "0"), "Sin facturacion"],
            [String(resumen.caidaFuerte ?? 0).padStart(2, "0"), "Caida fuerte"],
          ].map(([value, label]) => (
            <div className="crm-postventa-facturacion-operational-count" key={label}>
              <strong>{value}</strong>
              <span>{label}</span>
            </div>
          ))}
          <div className="crm-postventa-facturacion-date-summary">
            <span>Fecha Referida</span>
            <strong>{formatBillingDate(resumen.fechaReferencia || resumen.fechaHasta)}</strong>
          </div>
          <div className="crm-postventa-facturacion-date-summary">
            <span>Ultima Actividad</span>
            <strong>{formatBillingDate(resumen.ultimaActividad)}</strong>
          </div>
        </section>

        <section className="crm-postventa-facturacion-table-card">
          <div className="crm-postventa-facturacion-table-head">
            <h3>Terminales | Facturacion</h3>
            <span>CUIT del grupo: {resumen.cuitDelGrupo || clienteHeader.cuit || "-"}</span>
          </div>
          <div className="crm-postventa-facturacion-table-scroll">
            {hasRows ? (
              <Table className="crm-postventa-facturacion-table" hover>
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Terminal</th>
                    <th>Comercio</th>
                    <th>CUIT</th>
                    <th>Hoy</th>
                    <th>Ayer</th>
                    <th>Prom. 3d</th>
                    <th>Prom. 7d</th>
                    <th>Var. vs ayer</th>
                    <th>Var. vs 7d</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filasFacturacion.map((row) => (
                    <tr key={`${row.cuit}-${row.terminal}-${row.comercio}`}>
                      <td><span className="crm-postventa-facturacion-status">{row.estadoAlerta || "Sin datos"}</span></td>
                      <td>{row.terminal || "-"}</td>
                      <td>{row.comercio || "-"}</td>
                      <td>{row.cuit || "-"}</td>
                      <td>{formatBillingCurrency(row.montoHoy)}</td>
                      <td>{formatBillingCurrency(row.montoAyer)}</td>
                      <td>{formatBillingCurrency(row.promedio3Dias)}</td>
                      <td>{formatBillingCurrency(row.promedio7Dias)}</td>
                      <td>{percent(row.variacionVsAyer)}</td>
                      <td>{percent(row.variacion)}</td>
                      <td>
                        <button type="button" className="crm-postventa-facturacion-row-action" onClick={() => setFacturacionAccionItem(row)}>
                          Acciones
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="crm-postventa-facturacion-empty-block">No hay terminales con datos de facturacion para mostrar.</div>
            )}
          </div>
        </section>

        <section className="crm-postventa-facturacion-analysis">
          <article>
            <span>Motivo principal</span>
            <strong>{insights.motivoPrincipal || "No hay informacion suficiente para generar un analisis."}</strong>
          </article>
          <article>
            <span>Insight</span>
            <strong>{insights.insight || "No hay informacion suficiente para generar un analisis."}</strong>
          </article>
          <article>
            <span>Accion sugerida</span>
            <strong>{insights.accionSugerida || "No hay informacion suficiente para generar un analisis."}</strong>
          </article>
        </section>

        {renderFacturacionManagementModal()}

        <Modal show={Boolean(facturacionGestionItem)} onHide={() => setFacturacionGestionItem(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Registrar gestion desde Facturacion</Modal.Title>
          </Modal.Header>
          <Form onSubmit={guardarGestionDesdeFacturacion}>
            <Modal.Body>
              <div className="crm-postventa-subtle crm-postventa-action-summary p-0 mb-3">
                <strong>{facturacionGestionItem?.nombreDeFantasia || "Cliente"}</strong>
                <span>Comercio {facturacionGestionItem?.comercio || "-"}</span>
                <span>Terminal {facturacionGestionItem?.terminal || "-"}</span>
                <span>CUIT {facturacionGestionItem?.cuit || "-"}</span>
                <span>Telefono {buildFacturacionPhoneLines(facturacionGestionItem).join(" | ") || "-"}</span>
              </div>
              <div className="crm-postventa-form-grid">
                <Form.Group>
                  <Form.Label>Regla / accion</Form.Label>
                  <Form.Select
                    value={facturacionGestionDraft.reglaId}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, reglaId: event.target.value }))}
                  >
                    <option value="">Seleccionar</option>
                    {manualAlertaRules.map((regla) => (
                      <option key={regla.id} value={regla.id}>{formatGestionRuleLabel(regla)}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={facturacionGestionDraft.estadoGestion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, estadoGestion: event.target.value }))}
                  >
                    {(GESTION_SCOPE_STATES.operativa || GESTION_STATES).map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Fecha / hora</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={facturacionGestionDraft.fechaGestion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, fechaGestion: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Proximo contacto</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={facturacionGestionDraft.fechaProximoContacto}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Observacion</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={facturacionGestionDraft.observacion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setFacturacionGestionItem(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                {cargas.guardandoGestion ? "Guardando..." : "Guardar gestion"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {facturacionDetalle && !facturacionAccionItem ? (
          <div className="crm-postventa-detail-grid">
            <section className={`crm-postventa-detail-card ${darkMode ? "is-dark" : ""}`}>
              <div className="crm-postventa-panel-title">
                <h4>Detalle de facturacion</h4>
                <Button variant="outline-secondary" size="sm" onClick={() => setFacturacionDetalle(null)}>
                  Cerrar
                </Button>
              </div>
              <p className="mb-3">
                {facturacionDetalle.nombreDeFantasia || "Cliente"} - Terminal {facturacionDetalle.terminal || "-"} - Comercio {facturacionDetalle.comercio || "-"}
              </p>
              <div className="crm-postventa-detail-table-wrap">
                <Table className="crm-postventa-table mb-0" hover>
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Total bruto</th>
                      <th>Operaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturacionDetalle.serieDiaria?.length ? (
                      facturacionDetalle.serieDiaria.map((item) => (
                        <tr key={`${facturacionDetalle.terminal}-${item.fecha}`}>
                          <td>{item.fecha}</td>
                          <td>{formatBillingCurrency(item.totalBruto)}</td>
                          <td>{item.cantidadOperaciones || 0}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3}>No hay informacion de facturacion para el periodo seleccionado.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </section>
            <aside className={`crm-postventa-detail-card ${darkMode ? "is-dark" : ""}`}>
              <h4 className="mb-3">Resumen rapido</h4>
              <DataList
                items={[
                  { label: "Estado", value: facturacionDetalle.estadoAlerta, isBadge: true, tone: facturacionDetalle.estadoAlerta === "OK" ? "success" : "danger" },
                  { label: "Telefono", value: buildFacturacionPhoneLines(facturacionDetalle).join(" | ") || "-" },
                  { label: "Monto hoy", value: formatBillingCurrency(facturacionDetalle.montoHoy) },
                  { label: "Promedio 7 dias", value: formatBillingCurrency(facturacionDetalle.promedio7Dias) },
                  { label: "Variacion", value: percent(facturacionDetalle.variacion) },
                  { label: "Ultima actividad", value: formatBillingDate(facturacionDetalle.ultimaActividad) },
                  { label: "Accion sugerida", value: facturacionDetalle.accionSugerida || "-" },
                ]}
              />
            </aside>
          </div>
        ) : null}
      </>
    );
  };

  const renderSeguimiento = () => {
    if (cargas.seguimiento) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando seguimiento...</div>;
    }

    if (errores.seguimiento) {
      return <Alert variant="danger">{errores.seguimiento}</Alert>;
    }

    if (!seguimiento) {
      return <Alert variant="secondary">No hay seguimiento disponible para este cliente.</Alert>;
    }

    const metricasReales = seguimiento.metricas || [];
    const findMetrica = (label) => metricasReales.find((item) => item.label === label);
    const mainMetricLabels = ["Facturación total", "Promedio diario", "Ticket promedio", "Variación mensual"];
    const sideMetricLabels = ["Cantidad de operaciones", "Días sin facturar", "Última actividad"];
    const mainMetrics = mainMetricLabels.map(findMetrica).filter(Boolean);
    const sideMetrics = sideMetricLabels.map(findMetrica).filter(Boolean);

    const timelineItems = seguimiento.timeline || [];
    const visibleTimelineItems = seguimientoExpanded ? timelineItems : timelineItems.slice(0, 5);
    const hasMoreTimeline = timelineItems.length > 5;

    const seguimientoDateLabel = alertaHeaderClock.toLocaleDateString("es-AR");
    const seguimientoTimeLabel = alertaHeaderClock.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return (
      <div className="crm-postventa-ficha-screen">
        <div className="crm-postventa-ficha-header">
          <div className="crm-postventa-ficha-header-copy">
            <span>CRM POSTVENTAS</span>
            <h2>Seguimiento</h2>
          </div>
          <div className="crm-postventa-ficha-header-clock">
            {seguimientoDateLabel} <span>|</span> {seguimientoTimeLabel}
          </div>
        </div>

        <section className="crm-postventa-ficha-summary-card">
          <div className="crm-postventa-ficha-summary-copy">
            <h3>{clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Cliente"}</h3>
            <div className="crm-postventa-ficha-summary-badges">
              <CompactFichaBadge label="CUIT" value={clienteHeader.cuit} />
              <CompactFichaBadge label="Estado" value={clienteHeader.estado} accent={String(clienteHeader.estado || "").toUpperCase() === "ALTA"} />
              <CompactFichaBadge label="Terminal" value={clienteHeader.nDeTerminal} />
              <CompactFichaBadge label="Comercio" value={clienteHeader.nDeComercio} />
              <CompactFichaBadge label="Legajo" value={clienteHeader.legajo} />
            </div>
          </div>

          <button type="button" className="crm-postventa-ficha-change-client" onClick={() => setClienteModalOpen(true)}>
            <FontAwesomeIcon icon={faUserPen} />
            Cambiar Cliente
          </button>
        </section>

        <div className="crm-postventa-ficha-toolbar">
          <div className="crm-postventa-terminal-tabs crm-postventa-terminal-tabs--ficha" role="tablist" aria-label="Terminales del cliente">
            {terminalesGrupo.map((terminal, index) => (
              <button
                key={terminal.clienteId || terminal.nDeTerminal || terminal.terminal || index}
                type="button"
                role="tab"
                aria-selected={terminal.clienteId === clienteSeleccionadoId}
                className={`crm-postventa-terminal-tab crm-postventa-terminal-tab--ficha ${terminal.clienteId === clienteSeleccionadoId ? "is-active" : ""}`}
                onClick={() => handleCambiarTerminal(terminal)}
              >
                {`TERMINAL ${index + 1}`}
              </button>
            ))}
          </div>

          <div className="crm-postventa-ficha-actions">
            <button
              type="button"
              className={`crm-postventa-ficha-refresh-btn ${cargas.seguimiento ? "is-loading" : ""}`}
              onClick={() => cargarModulo(clienteSeleccionadoId, analyticsRange)}
              aria-label="Actualizar seguimiento"
              disabled={cargas.seguimiento}
            >
              <FontAwesomeIcon icon={faArrowsRotate} />
            </button>
          </div>
        </div>

        <div className="d-grid gap-4">
        {!seguimiento.tieneDatosReales && seguimiento.message ? (
          <Alert variant="secondary">{seguimiento.message}</Alert>
        ) : null}

        {mainMetrics.length || sideMetrics.length ? (
          <section className="crm-postventa-indicadores">
            <div className="crm-postventa-indicadores__main">
              {mainMetrics.map((item) => (
                <article className="crm-postventa-indicador-card" key={item.label}>
                  <span className="crm-postventa-indicador-card__label">{item.label}</span>
                  <div className="crm-postventa-indicador-card__value">{item.value}</div>
                </article>
              ))}
            </div>
            {sideMetrics.length ? (
              <div className="crm-postventa-indicadores__side">
                {sideMetrics.map((item) => (
                  <div className="crm-postventa-indicador-side" key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        <section className={`crm-postventa-panel crm-postventa-hoja-vida ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-panel-title">
            <h3>Hoja de vida del cliente</h3>
            <span className="crm-postventa-hoja-vida__count">Historial {timelineItems.length}</span>
          </div>

          <div className="crm-postventa-timeline">
            {visibleTimelineItems.length ? (
              visibleTimelineItems.map((item, index) => (
                <div className="crm-postventa-timeline__row" key={`${item.fecha}-${index}`}>
                  <span className="crm-postventa-timeline__dot" />
                  <article className="crm-postventa-timeline-card" onClick={() => setTimelineDetalle(item)}>
                    <div className="crm-postventa-timeline-card__header">
                      <h4>{item.titulo}</h4>
                      <span className="crm-postventa-timeline-card__date">{item.fecha}</span>
                    </div>

                    {item.usuario && item.usuario !== "-" ? (
                      <div className="crm-postventa-timeline-card__row">
                        <FontAwesomeIcon icon={faUser} />
                        <span><strong>Usuario:</strong> {item.usuario}</span>
                      </div>
                    ) : null}

                    <div className="crm-postventa-timeline-card__row">
                      <FontAwesomeIcon icon={faFileLines} />
                      <span><strong>Resumen:</strong> {item.resumen || item.descripcion || "Sin resumen disponible."}</span>
                    </div>

                    {item.detalleLineas?.length ? (
                      <div className="crm-postventa-timeline-card__row is-list">
                        <FontAwesomeIcon icon={faListUl} />
                        <div>
                          <strong>Observaciones completas:</strong>
                          <ul>
                            {item.detalleLineas.map((linea, lineaIndex) => (
                              <li key={lineaIndex}>{linea}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ) : null}
                  </article>
                </div>
              ))
            ) : (
              <Alert variant="secondary">No hay eventos de seguimiento para mostrar.</Alert>
            )}
          </div>

          {hasMoreTimeline ? (
            <button
              type="button"
              className="crm-postventa-timeline-showmore"
              onClick={() => setSeguimientoExpanded((prev) => !prev)}
            >
              {seguimientoExpanded ? "Ver menos" : "Ver más alertas"}
              <FontAwesomeIcon icon={faChevronDown} className={seguimientoExpanded ? "is-rotated" : ""} />
            </button>
          ) : null}
        </section>

        <Modal
          show={Boolean(timelineDetalle)}
          onHide={() => setTimelineDetalle(null)}
          centered
          size="lg"
          className="crm-postventa-history-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>{timelineDetalle?.titulo || "Detalle del evento"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="crm-postventa-detail-meta">
              <div>
                <span className="crm-postventa-card-label">Fecha</span>
                <strong>{timelineDetalle?.fecha || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Tipo</span>
                <strong>{getTimelineTipoDetalle(timelineDetalle)}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Usuario</span>
                <strong>{timelineDetalle?.usuario || "-"}</strong>
              </div>
            </div>

            <div className="mt-3">
              <span className="crm-postventa-card-label">Resumen</span>
              <p className="mb-0">{timelineDetalle?.resumen || timelineDetalle?.descripcion || "-"}</p>
            </div>

            <div className="mt-4">
              <span className="crm-postventa-card-label">Observaciones completas</span>
              {timelineDetalle?.detalleLineas?.length ? (
                <ul className="crm-postventa-detail-lines">
                  {timelineDetalle.detalleLineas.map((linea, index) => (
                    <li key={`${timelineDetalle.fecha}-${index}`}>{linea}</li>
                  ))}
                </ul>
              ) : (
                <div className="crm-postventa-detail-pre">
                  {timelineDetalle?.detalle || timelineDetalle?.resumen || "Sin detalle disponible."}
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setTimelineDetalle(null)}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
        </div>
      </div>
    );
  };

  const renderCampaniasZocoCheck = () => (
    <ZocoCheckCampaignsPanel darkMode={darkMode} isAdmin={canManageZocoCheck} />
  );

  const renderBlanqueos = () => (
    <div className="crm-postventa-blanqueos">
      <div className="crm-postventa-altas-header">
        <div className="crm-postventa-altas-header-copy">
          <span className="crm-postventa-kicker">CRM Postventa</span>
          <h2>Blanqueos</h2>
          <p>Alta, edición de tipo de usuario y restauración de clave.</p>
        </div>
      </div>
      <section className={`crm-postventa-panel crm-postventa-blanqueos-panel ${darkMode ? "is-dark" : ""}`}>
        <TablaUsuariosLiquidaciones />
      </section>
    </div>
  );

  const renderSection = () => {
    switch (section) {
      case "ficha":
        return renderFicha();
      case "facturacion":
        return renderFacturacionFigma();
      case "alertas":
        return renderAlertas();
      case "ordenes-pago":
        return renderOrdenesPagoFigma();
      case "cupones":
        return renderCuponesFigma();
      case "herramientas":
        return renderHerramientasIntegradas();
      case "altas-bajas":
        return renderAltasBajas();
      case "blanqueos":
        return renderBlanqueos();
      case "campanias-zoco-check":
        return renderCampaniasZocoCheck();
      case "gestion":
        return renderGestion();
      case "modales":
        return <ModalesPanel />;
      case "seguimiento":
        return renderSeguimiento();
      default:
        return renderAlertas();
    }
  };

  if (cargandoSesion) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const criticasKpiValue = (alertas?.kpis || []).find((item) => item.label === "Total alertas criticas")?.value ?? 0;
  const facturacionAlertCount = alertas?.alertasFacturacion?.length || 0;
  const operativasManualesCount = alertas?.filas?.filter((item) => !item.esAutomatica).length || 0;
  const alertaHeaderClockLabel = `${alertaHeaderClock.toLocaleDateString("es-AR")} | ${alertaHeaderClock.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false })}`;
  const alertaHeader = {
    kicker: "CRM Postventa",
    title: "Centro de alertas",
    chips: [
      `Facturación ${facturacionAlertCount}`,
      `Críticas ${criticasKpiValue}`,
      `Operativas manuales ${operativasManualesCount}`,
      alertaHeaderClockLabel,
    ],
  };
  const requiereClienteSeleccionado = !["alertas", "altas-bajas", "blanqueos", "campanias-zoco-check", "herramientas", "gestion", "modales"].includes(section);
  const isAltasBajasSection = section === "altas-bajas";
  const isBlanqueosSection = section === "blanqueos";
  const isZocoCampaignsSection = section === "campanias-zoco-check";
  const isFichaSection = section === "ficha";
  const topbarKicker =
    section === "alertas"
      ? alertaHeader.kicker
      : isZocoCampaignsSection
        ? "CRM Postventa"
      : isBlanqueosSection
        ? "CRM Postventa"
      : isAltasBajasSection
        ? "CRM Postventa"
        : clienteHeader.clienteSeleccionado || "Cliente seleccionado";
  const topbarTitle =
    section === "alertas"
      ? alertaHeader.title
      : isZocoCampaignsSection
        ? "Campanas Zoco Check"
      : isBlanqueosSection
        ? "Blanqueos"
      : isAltasBajasSection
        ? "Altas / Bajas"
        : clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Seleccioná un cliente";
  const topbarChips =
    section === "alertas"
      ? alertaHeader.chips
      : isZocoCampaignsSection
        ? ["Administracion de campanas y bootstrap del flujo mensual."]
      : isBlanqueosSection
        ? []
      : isAltasBajasSection
        ? []
        : [
            { label: "CUIT", value: clienteHeader.cuit || "-" },
            { label: "Estado", value: clienteHeader.estado || "-" },
            { label: "Terminal", value: clienteHeader.nDeTerminal || "-" },
            { label: "Comercio", value: clienteHeader.nDeComercio || "-" },
            { label: "Legajo", value: clienteHeader.legajo || "-" },
          ];
  const showTopbarClientButton = !isAltasBajasSection && !isBlanqueosSection && !isZocoCampaignsSection;
  const emptyStateTitle =
    section === "ordenes-pago"
      ? "Seleccioná un cliente para ver sus órdenes de pago."
      : section === "cupones"
        ? "Seleccioná un cliente para ver sus cupones."
        : "Seleccioná un cliente para ver la información";
  const emptyStateDescription =
    section === "ordenes-pago"
      ? "La consulta reutiliza las órdenes reales asociadas al cliente seleccionado en CRM Postventa."
      : section === "cupones"
        ? "El listado usa cupones reales del cliente seleccionado y respeta el mismo selector global."
        : "El módulo queda preparado sobre datos reales. Elegí un cliente/comercio para cargar ficha, facturación, herramientas, gestión y seguimiento.";
  const panelTitle =
    section === "ficha"
      ? "Ficha del cliente"
      : section === "facturacion"
        ? "Facturacion"
        : section === "alertas"
          ? "Alertas"
          : section === "ordenes-pago"
            ? "Órdenes de pago"
            : section === "cupones"
              ? "Cupones"
              : section === "herramientas"
                ? "Herramientas"
                : section === "altas-bajas"
                  ? "Altas / Bajas"
                : section === "blanqueos"
                  ? "Blanqueos"
                  : section === "campanias-zoco-check"
                    ? "Campanas Zoco Check"
                  : section === "gestion"
                    ? "Gestion"
                    : "Seguimiento";

  return (
    <div className="crm-postventa-page" translate="no">
      {section !== "alertas" && section !== "altas-bajas" && section !== "blanqueos" && section !== "facturacion" && section !== "ordenes-pago" && section !== "cupones" && section !== "herramientas" && section !== "gestion" && section !== "seguimiento" && section !== "modales" && !isFichaSection ? <TituloPagina title="CRM Postventa" /> : null}

      <div className="container crm-postventa-shell py-4">
        {section !== "alertas" && section !== "altas-bajas" && section !== "blanqueos" && section !== "facturacion" && section !== "ordenes-pago" && section !== "cupones" && section !== "herramientas" && section !== "gestion" && section !== "seguimiento" && section !== "modales" && !isFichaSection ? (
          <section className={`crm-postventa-topbar ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-topbar-copy">
              <span className="crm-postventa-kicker">{topbarKicker}</span>
              <h2>{topbarTitle}</h2>
              {topbarChips.length ? (
                <div className="crm-postventa-meta">
                  {topbarChips.map((item) =>
                    typeof item === "string" ? (
                      <span className="crm-postventa-chip" key={item}>
                        {item}
                      </span>
                    ) : (
                      <span className="crm-postventa-chip" key={item.label}>
                        {item.label}{" "}
                        <strong className={item.label === "Estado" && String(item.value).toUpperCase() === "ALTA" ? "is-estado-alta" : ""}>
                          {item.value}
                        </strong>
                      </span>
                    )
                  )}
                </div>
              ) : null}
            </div>

            {showTopbarClientButton ? (
              <Button className="crm-postventa-cta" variant="success" onClick={() => setClienteModalOpen(true)}>
                <FontAwesomeIcon className="me-2" icon={faUserPen} />
                {section === "alertas" ? "Seleccionar cliente" : "Cambiar cliente"}
              </Button>
            ) : null}
          </section>
        ) : null}

        {!clienteSeleccionadoId && requiereClienteSeleccionado ? (
          <section className="crm-postventa-empty-state mt-4">
            <FontAwesomeIcon icon={faBuilding} size="2x" />
            <h3>{emptyStateTitle}</h3>
            <p className="mb-0">{emptyStateDescription}</p>
            <Button variant="success" onClick={() => setClienteModalOpen(true)}>
              Elegir cliente
            </Button>
          </section>
        ) : section === "alertas" || section === "altas-bajas" || section === "blanqueos" || section === "facturacion" || section === "ordenes-pago" || section === "cupones" || section === "herramientas" || section === "gestion" || section === "seguimiento" || section === "modales" || isFichaSection ? (
          <section className="crm-postventa-alertas-layout">
            {renderSection()}
          </section>
        ) : (
          <section className={`crm-postventa-panel mt-4 ${darkMode ? "is-dark" : ""}`}>
            {terminalesGrupo.length > 0 && section !== "alertas" && section !== "altas-bajas" ? (
              <div className={`crm-postventa-terminal-switcher ${section === "seguimiento" ? "is-seguimiento" : ""}`}>
                <div className="crm-postventa-terminal-tabs">
                  {terminalesGrupo.map((terminal) => (
                    <button
                      key={terminal.clienteId}
                      type="button"
                      className={`crm-postventa-terminal-tab ${terminal.clienteId === clienteSeleccionadoId ? "is-active" : ""}`}
                      onClick={() => handleCambiarTerminal(terminal)}
                    >
                      <span>{terminal.label}</span>
                      {section === "seguimiento" ? null : (
                        <>
                          <small>{terminal.resumen || "Sin datos"}</small>
                          <small>Estado: {resolveTerminalStatusLabel(terminal)}</small>
                        </>
                      )}
                    </button>
                  ))}
                </div>
                {section === "seguimiento" ? (
                  <button
                    type="button"
                    className="crm-postventa-terminal-switcher-refresh"
                    onClick={() => cargarModulo(clienteSeleccionadoId, analyticsRange)}
                    aria-label="Actualizar seguimiento"
                  >
                    <FontAwesomeIcon icon={faArrowsRotate} />
                  </button>
                ) : null}
              </div>
            ) : null}

            {section === "seguimiento" ? null : (
              <div className="crm-postventa-panel-title">
                <h3>{panelTitle}</h3>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    if (section === "alertas") {
                      return;
                    }

                    if (section === "ordenes-pago") {
                      cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros);
                      return;
                    }

                    if (section === "cupones") {
                      cargarCupones(clienteSeleccionadoId, cuponesFiltros);
                      return;
                    }

                    if (section === "altas-bajas") {
                      if (clienteSeleccionadoId) {
                        cargarModulo(clienteSeleccionadoId, analyticsRange);
                      }
                      resetAltasBajas();
                      return;
                    }

                    cargarModulo(clienteSeleccionadoId, analyticsRange);
                  }}
                >
                  <FontAwesomeIcon className="me-2" icon={faArrowsRotate} />
                  Recargar
                </Button>
              </div>
            )}
            {renderSection()}
          </section>
        )}
      </div>

      <div className="py-4 mt-auto">
         
      </div>

      <ClienteSelectorModal
        show={clienteModalOpen}
        onHide={() => setClienteModalOpen(false)}
        search={clienteSearch}
        onSearchChange={setClienteSearch}
        loading={clienteLoading}
        error={clienteError}
        results={clienteGrupos}
        selectedKey={currentGroupKey}
        onSelect={setClientePendiente}
        onRetry={() => cargarClientes(clienteSearch)}
        onConfirm={() => seleccionarCliente(clientePendiente || ficha?.header)}
      />
    </div>
  );
};

export default CrmPostventa;
